import { RecurringScheduleStateService } from '../operations/recurring-schedules/recurring-schedule-state.service';
import { EzStandardStateComponent } from '@template-ui/components/ez-standard-state/ez-standard-state.component';
import { WorkflowStepChildWorkEditorComponent, type WorkflowChildWorkOwnedDocumentChange } from './workflow-step-child-work-editor.component';
import {
  WorkflowEditorPendingState,
  workflowEditorPendingOwnerScopes,
  type WorkflowEditorPendingScope,
} from './workflow-editor-pending-state';
import { WorkflowStepTimersEditorComponent, type WorkflowTimerOwnedDocumentChange } from './workflow-step-timers-editor.component';
import { savedStepTimerRows } from './workflow-step-timers-v2-authoring';
import { WorkflowAutomatedStepEditorComponent } from './workflow-automated-step-editor.component';
import { automatedStepFingerprint } from './workflow-automated-step-authoring';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import {
  WorkflowStepNotificationsComponent,
  WorkflowStepNotificationOwnerRequest,
} from './workflow-step-notifications.component';
import { WorkflowStepRequirementsEditorComponent } from './workflow-step-requirements-editor.component';
import { WorkflowStepComponentsEditorComponent } from './workflow-step-components-editor.component';
import type { WorkflowLookupSourceOwner } from './workflow-lookup-source-authoring.models';
import {
  capabilityReferencesShapeError,
  sameCapabilityReference,
  supportsWorkflowCapabilities,
  type WorkflowCapabilityCatalogEntry,
} from './workflow-capability-authoring';
import {
  commandIdentity,
  initializeOperationalStep,
  type StepAuthoringCatalog,
  type StepCommand,
} from './workflow-step-composition';
import {
  isRecoverableWorkflowDocument,
  reconcileWorkflowDraft,
  type DraftRecoveryChoice,
} from './workflow-draft-recovery';
import {
  workflowStepChecklist,
  type StepChecklistTarget,
} from './workflow-step-checklist';
import {
  workflowOverviewPresentation,
  type WorkflowOverviewAttention,
  type WorkflowOverviewStep,
  type WorkflowOverviewTarget,
} from './workflow-overview-presentation';
import {
  suggestWorkStepIdentity,
  availableStepSources,
  appendConnectedWorkStep,
} from './workflow-step-creation';
import {
  workflowStepPlacementPaths,
  insertStepOnWorkflowPath,
} from './workflow-path-authoring';
import { WorkflowNumberingSettingsComponent } from './workflow-numbering-settings.component';
import { WorkflowQuorumCalculatorComponent } from './workflow-quorum-calculator.component';
import { WorkflowNavigationSettingsComponent, type WorkflowNavigationOwnedDocumentChange } from './workflow-navigation-settings.component';
import { applyWorkflowDashboardRemoval, readWorkflowDashboardRemoval } from './workflow-dashboard-removal-authoring';
import { applyDashboardElement } from './workflow-dashboard-element-authoring';
import { WorkflowProcessDataEditorComponent, type WorkflowProcessDataOwnedDocumentChange } from './workflow-process-data-editor.component';
import { applyWorkflowProcessData } from './workflow-process-data-authoring';
import { applyWorkflowStepProgress } from './workflow-step-progress-authoring';
import type { WorkflowStepProgressOwnedDocumentChange } from './workflow-step-progress-editor.component';
import type { WorkflowStepBypassDataOwnerOrigin, WorkflowStepBypassDataRequest } from './workflow-step-bypass-data-request';
import { WorkflowAutomationEditorComponent } from './workflow-automation-editor.component';
import { WorkflowPresentationEditorComponent, type WorkflowDashboardRemovalRequest, type WorkflowDashboardSourceOwnedDocumentChange, type WorkflowDashboardElementOwnedDocumentChange, type WorkflowDashboardNameOwnedDocumentChange } from './workflow-presentation-editor.component';
import type { WorkflowNoticeRequirementRequest } from './workflow-detail-composer.component';
import { WorkflowOwnerViewState } from './workflow-owner-view-state';
import { WorkflowStatusEditorComponent, type WorkflowStatusOwnedDocumentChange } from './workflow-status-editor.component';
import type { WorkflowStatusOwnerRequest } from './workflow-status-owner-request';
import { emptyStatusConfiguration, replaceStatusConfiguration } from './workflow-status-authoring';
import { WorkflowDraftDryRunComponent } from './workflow-draft-dry-run.component';
import { WorkflowCapabilityPickerComponent } from './workflow-capability-picker.component';
import { requireWorkflowPayloadVersion } from './platform-workflow-definition-lifecycle.models';
import { CommonModule, DOCUMENT } from '@angular/common';
import { CdkMenu, CdkMenuItem, CdkMenuTrigger } from '@angular/cdk/menu';
import { WorkflowDesignerStepOutlineComponent, type WorkflowDesignerOutlineEntry } from './workflow-designer-step-outline.component';
import { WorkflowStepInspectionComponent } from './workflow-step-inspection.component';
import { WorkflowRouteInspectionComponent } from './workflow-route-inspection.component';
import { workflowRouteInspection } from './workflow-route-inspection';
import { directRoutePresentation } from './workflow-route-presentation';
import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
  ViewChildren,
  QueryList,
  computed,
  effect,
  inject,
  linkedSignal,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, type ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { ButtonCellRenderer } from '@app/shared/components/tables/ag-grid-cells/button-cell-renderer/button-cell-renderer.component';
import { RoleService } from '@app/shared/services/roles.service';
import { EzFormComponent } from '@template-ui/components/ez-form/ez-form.component';
import { UiLibraryModule } from '@template-ui/template-ui.module';
import {
  ButtonConfig,
  InputSelectOption,
  UiNavigationTab,
} from '@template-ui/types';
import { EzFormInputType, InputConfig } from '@template-ui/types/ez-form.types';
import {
  ColDef,
  GridApi,
  GridOptions,
  ICellRendererParams,
  SuppressKeyboardEventParams,
} from 'ag-grid-community';
import { firstValueFrom, forkJoin, Subscription } from 'rxjs';

import {
  WorkflowDefinitionAuthoringReferences,
  WorkflowDefinitionDiagnostic,
  WorkflowDefinitionDocument,
  WorkflowCapabilityReference,
  WorkflowDefinitionEdge,
  WorkflowDefinitionDraftDetail,
  WorkflowDefinitionDraftValidationResult,
  WorkflowDefinitionDraftPage,
  WorkflowDefinitionDraftQuery,
  WorkflowDefinitionDraftSummary,
  WorkflowDefinitionLifecycleDetail,
  WorkflowAllowedTransition,
  WorkflowDecisionRequirement,
  WorkflowDefinitionNode,
  WorkflowDefinitionSemanticChange,
  workflowDraftActionLabel,
  WorkflowDraftActionCode,
} from './platform-workflow-definition-lifecycle.models';
import type { WorkflowDefinitionVersionDetail } from './platform-workflow-registry-diagnostics.models';
import {
  createWorkflowDefinitionMutationIntent,
  PlatformWorkflowDefinitionLifecycleService,
  WorkflowDefinitionMutationIntent,
} from './platform-workflow-definition-lifecycle.service';
import { PlatformWorkflowDraftDecisionEditorComponent } from './platform-workflow-draft-decision-editor.component';
import { PlatformWorkflowDraftEdgeEditorComponent } from './platform-workflow-draft-edge-editor.component';
import { PlatformWorkflowDraftGraphPreviewComponent, type WorkflowDraftGraphPresentationState } from './platform-workflow-draft-graph-preview.component';
import { WorkflowDecisionInventoryComponent, type WorkflowDecisionInventoryContextEvent, type WorkflowDecisionInventoryOwnerEvent } from './workflow-decision-inventory.component';
import { readWorkflowDecisionInventory, type WorkflowDecisionOwnerRequest } from './workflow-decision-inventory';
import type { WorkflowAutomationCatalog } from './workflow-automation-authoring';
import { PlatformWorkflowDraftSemanticDiffComponent } from './platform-workflow-draft-semantic-diff.component';
import { PlatformWorkflowDraftTransitionEditorComponent } from './platform-workflow-draft-transition-editor.component';
import {
  WorkflowRegistryDiagnosticDisplayError,
  mapWorkflowRegistryDiagnosticError,
} from './platform-workflow-registry-diagnostics.models';
import { WorkflowDraftConfirmationService, type WorkflowDraftConfirmationRequest } from './workflow-draft-confirmation.service';
import { WorkflowDraftDirtyStateService } from './workflow-draft-dirty-state.service';
import { processDataShapeError } from './workflow-process-data';
import { WorkflowAssignmentPolicyComponent, workflowStepAssignmentSummary } from './workflow-assignment-policy.component';
import {
  WorkflowDefinitionLifecycleActionsComponent,
  clearArchivedDraftPublicationIntent,
} from './workflow-definition-lifecycle-actions.component';
import {
  workflowDraftExecutionSupport,
  workflowDraftPublicationReadiness,
} from './workflow-publication-readiness';
import {
  workflowReviewFindingGroups,
  nextWorkflowReviewFinding,
} from './workflow-review-findings';
import { projectWorkflowDraftGraph } from './workflow-draft-graph-projection';
import {
  workflowDecisionGateRequest,
  type WorkflowReadOnlyGraphDecisionGateRequest,
  type WorkflowReadOnlyGraphDecisionScopeRequest,
  type WorkflowReadOnlyGraphEdge,
  type WorkflowReadOnlyGraphNode,
  type WorkflowReadOnlyGraphSelection,
  type WorkflowReadOnlyGraphSelectionRequest,
} from './workflow-read-only-graph.models';

const WORKFLOW_DEFINITION_AUTHOR_PERMISSION = 'workflow_definition.author';
const WORKFLOW_DEFINITION_VALIDATE_PERMISSION = 'workflow_definition.validate';
const DEFAULT_DRAFT_PAGE_SIZE = 10;

const PAGE_SIZE_OPTIONS: InputSelectOption[] = [
  { value: 10, viewValue: '10' },
  { value: 25, viewValue: '25' },
  { value: 50, viewValue: '50' },
];

const VALIDATION_STATUS_OPTIONS: InputSelectOption[] = [
  { value: '', viewValue: 'All validation states' },
  { value: 'unvalidated', viewValue: 'Not validated' },
  { value: 'valid', viewValue: 'Valid' },
  { value: 'invalid', viewValue: 'Invalid' },
];

const SCOPE_TYPE_OPTIONS: InputSelectOption[] = [
  { value: 'global', viewValue: 'All locations' },
  { value: 'jurisdiction', viewValue: 'Jurisdiction' },
  { value: 'program', viewValue: 'Program' },
  { value: 'jurisdiction-program', viewValue: 'Jurisdiction and program' },
];

const CARDINALITY_OPTIONS: InputSelectOption[] = [
  { value: 'multiple', viewValue: 'Allow multiple open workflows' },
  {
    value: 'one_open_per_subject',
    viewValue: 'Allow one open workflow at a time',
  },
];

interface DraftFilterValues {
  search: string;
  workflowCode: string;
  validationStatusCode: string;
  pageSize: number;
}

type WorkflowStepIssue =
  | { key: string; source: 'local'; finding: WorkflowOverviewAttention }
  | { key: string; source: 'saved'; diagnostic: WorkflowDefinitionDiagnostic };

type DraftPrimaryFilterValues = Pick<
  DraftFilterValues,
  'search' | 'validationStatusCode'
>;

type DraftAdvancedFilterValues = Pick<
  DraftFilterValues,
  'workflowCode' | 'pageSize'
>;

type AuthoringMode = 'create' | 'clone';

export type WorkflowDraftWorkbenchView =
  | 'map'
  | 'capabilities'
  | 'setup'
  | 'steps'
  | 'routing'
  | 'decisions'
  | 'decision-inventory'
  | 'review';

export type WorkflowDraftRoutingView = 'paths' | 'stateChanges';

export type WorkflowSettingsTask = 'general' | 'navigation' | 'data' | 'numbering' |
  'hearing' | 'automation' | 'statuses' | 'technical';
export type WorkflowTechnicalView = 'identity' | 'modules' | 'assignment' | 'quorum';
export type WorkflowWorkView = 'overview' | 'assignment' | 'requirements' | 'components';

export type WorkflowMapInspectorPane =
  | 'overview'
  | 'path'
  | 'transitions'
  | 'decisions';

export type WorkflowDraftReviewView = 'readiness' | 'changes';

/** Source authority supplied by the actual published owner; direct callers remain compatible. */
export interface WorkflowPublishedDraftHandoff {
  readonly sourceDefinitionId: number;
  readonly sourceContext: string;
  readonly isCurrent: () => boolean;
  readonly onFailed: (error: WorkflowRegistryDiagnosticDisplayError) => void;
}

/** A qualified Open drafts request accepts navigation only after its existing discard guard. */
export interface WorkflowPublishedDraftNavigation {
  readonly isCurrent: () => boolean;
  readonly onOpened: () => void;
}

export interface WorkflowDraftWorkbenchState {
  readonly draftCode: string;
  readonly workflowCode: string;
  readonly revision: number;
  readonly lifecycleStatusCode?: 'archived';
}

interface WorkflowDraftWorkbenchNavigationItem {
  readonly id: WorkflowDraftWorkbenchView;
  readonly label: string;
  readonly icon: string;
}

interface WorkflowDesignerLocation {
  nodeId: string | null;
  edgeId: string | null;
  scope: 'behavior' | 'dashboard' | 'detail';
  section: 'details' | 'readiness' | 'routes' | 'transitions' | 'notifications' | 'timers' | 'children';
  pane: WorkflowMapInspectorPane;
  statusRuleFrom: string | null;
  statusRuleTo: string | null;
  notificationEvent: string | null;
  notificationReturn: boolean;
}

const DEFAULT_DESIGNER_LOCATION: WorkflowDesignerLocation = {
  nodeId: null,
  edgeId: null,
  scope: 'behavior',
  section: 'readiness',
  pane: 'overview',
  statusRuleFrom: null,
  statusRuleTo: null,
  notificationEvent: null,
  notificationReturn: false,
};

const WORKBENCH_NAVIGATION: readonly WorkflowDraftWorkbenchNavigationItem[] = [
  { id: 'map', label: 'Designer', icon: 'pi pi-sitemap' },
  { id: 'setup', label: 'Workflow settings', icon: 'pi pi-cog' },
  { id: 'review', label: 'Review & publish', icon: 'pi pi-file-check' },
];

interface DraftAuthoringValues {
  displayName: string;
  description: string;
  workflowCode: string;
  draftCode: string;
  definitionVersionCode: string;
  subjectTypeCode: string;
  scopeTypeCode: string;
  scopeKey: string;
  configurationCode: string;
  configurationVersionCode: string;
  instanceCardinalityCode: string;
}

interface DraftIdentityValues {
  displayName: string;
  description: string;
  payloadSchemaVersion: string;
  workflowCode: string;
  definitionVersionCode: string;
  subjectTypeCode: string;
  scopeTypeCode: string;
  scopeKey: string;
  configurationCode: string;
  configurationVersionCode: string;
  instanceCardinalityCode: string;
}

interface DraftNodeValues {
  id: string;
  typeCode: string;
  displayOrder: number;
  stepCode: string;
  displayName: string;
}

type WorkflowPendingEditorKind =
| 'node'
      | 'edge'
      | 'transition'
      | 'decision'
      | 'requirement'
      | 'timer'
      | 'child'
      | 'assignment'
      | 'step-assignment'
      | 'review'
      | 'dashboard'
      | 'staff-page'
      | 'work'
      | 'notification'
      | 'navigation'
      | 'process-data'
      | 'workflow-status'
      | 'automation';

type StepPlacementContext =
  | { kind: 'after' | 'before'; nodeId: string }
  | { kind: 'path'; edgeId: string; fingerprint: string }
  | { kind: 'global' | 'unconnected' };

interface FailedDraftLoad {
  draftId: number;
  updateUrl: boolean;
  preserveRecovery: boolean;
}

export interface WorkflowDefinitionCloneSelection {
  id?: number;
  workflowCode: string;
  definitionVersionCode: string;
  draftActionCode?: WorkflowDraftActionCode | null;
  editableDraftId?: number | null;
}

@Component({
  selector: 'phms-platform-workflow-draft-workspace',
  providers: [WorkflowOwnerViewState],
  host: { '[attr.inert]': 'confirmationFocusSuspended() ? "" : null' },
  imports: [
    WorkflowDesignerStepOutlineComponent,
    CdkMenu, CdkMenuItem, CdkMenuTrigger,
    WorkflowStepInspectionComponent,
    WorkflowRouteInspectionComponent,
    EzStandardStateComponent,
    UiButtonDirective,
    UiControlDirective,
    CommonModule,
    FormsModule,
    UiLibraryModule,
    PlatformWorkflowDraftDecisionEditorComponent,
    WorkflowDecisionInventoryComponent,
    WorkflowStepNotificationsComponent,
    WorkflowStepRequirementsEditorComponent,
    WorkflowStepComponentsEditorComponent,
    WorkflowStepTimersEditorComponent,
    WorkflowStepChildWorkEditorComponent,
    PlatformWorkflowDraftEdgeEditorComponent,
    WorkflowCapabilityPickerComponent,
    WorkflowNumberingSettingsComponent,
    WorkflowQuorumCalculatorComponent,
    WorkflowNavigationSettingsComponent,
    WorkflowProcessDataEditorComponent,
    WorkflowAutomationEditorComponent,
    WorkflowAutomatedStepEditorComponent,
    WorkflowPresentationEditorComponent,
    WorkflowStatusEditorComponent,
    WorkflowDraftDryRunComponent,
    PlatformWorkflowDraftGraphPreviewComponent,
    PlatformWorkflowDraftSemanticDiffComponent,
    PlatformWorkflowDraftTransitionEditorComponent,
    WorkflowAssignmentPolicyComponent,
    WorkflowDefinitionLifecycleActionsComponent,
  ],
  templateUrl: './platform-workflow-draft-workspace.component.html',
  styleUrls: [
    './platform-workflow-draft-workspace.component.scss',
    './platform-workflow-draft-workspace-designer.scss',
  ],
})
export class PlatformWorkflowDraftWorkspaceComponent {
  private readonly entryPointState = inject(RecurringScheduleStateService);
  private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly lifecycleService = inject(
    PlatformWorkflowDefinitionLifecycleService,
  );
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly roles = inject(RoleService);
  private readonly confirmation = inject(WorkflowDraftConfirmationService);
  private confirmationInProgress = false;
  private readonly dirtyState = inject(WorkflowDraftDirtyStateService);
  private readonly ownerViewState = inject(WorkflowOwnerViewState);
  readonly confirmationFocusSuspended = this.ownerViewState.focusSuspended;

  private readonly selectedDefinitionState =
    signal<WorkflowDefinitionCloneSelection | null>(null);
  private draftGridApi?: GridApi<WorkflowDefinitionDraftSummary>;
  private draftListSubscription?: Subscription;
  private draftLoadSubscription?: Subscription;
  private saveSubscription?: Subscription;
  private saveRequestId = 0;
  private validateSubscription?: Subscription;
  private authoringSubscription?: Subscription;
  private authoringReferencesSubscription?: Subscription;
  private draftListRequestId = 0;
  private draftLoadRequestId = 0;
  private loadingDraftId: number | null = null;
  private authoringReferencesRequestId = 0;
  private lastHydratedDraftAdvancedFilters: {
    workflowCode?: string;
    pageSize: number;
    includeArchived?: 'true' | 'false';
  } | null = null;
  private baselineDocumentJson = '';
  private readonly discardObjectIds = new WeakMap<object, number>();
  private nextDiscardObjectId = 0;
  private pendingStatusRuleRestore: {
    draftId: number;
    location: WorkflowDesignerLocation;
  } | null = null;
  private pendingNotificationRestore: {
    draftId: number;
    location: WorkflowDesignerLocation;
  } | null = null;
  private saveIntent: WorkflowDefinitionMutationIntent | null = null;
  private validationIntent: WorkflowDefinitionMutationIntent | null = null;
  private automaticValidationKey: string | null = null;
  private validationRequestId = 0;
  readonly automaticValidationFailed = signal(false);
  readonly capabilityInventoryExpanded = signal(false);
  readonly quorumCalculatorOpen = signal(false);
  private authoringIntent: WorkflowDefinitionMutationIntent | null = null;
  private publishedHandoffRequest = 0;
  private publishedHandoffIntent: { key: string; intent: WorkflowDefinitionMutationIntent } | null = null;
  private requestedDesignerLocation: {
    draftId: number;
    location: WorkflowDesignerLocation;
  } | null = null;
  private requestedWorkflowSettingsTask: { draftId: number; task: WorkflowSettingsTask } | null = null;
  private requestedTechnicalView: { draftId: number; view: WorkflowTechnicalView } | null = null;
  private lastDesignerLocationKey = JSON.stringify(DEFAULT_DESIGNER_LOCATION);
  private restoringDesignerLocation = false;

  @ViewChild('authoringForm')
  private authoringForm?: EzFormComponent;

  @ViewChild('identityForm')
  private identityForm?: EzFormComponent;

  @ViewChild('identityTechnicalForm')
  private identityTechnicalForm?: EzFormComponent;
  @ViewChild('identityTechnicalForm', { read: ElementRef })
  private identityTechnicalFormElement?: ElementRef<HTMLElement>;
  @ViewChild('technicalHeading')
  private technicalHeading?: ElementRef<HTMLElement>;
  @ViewChild('identityAvailabilityForm')
  private identityAvailabilityForm?: EzFormComponent;
  @ViewChild('identityAvailabilityForm', { read: ElementRef })
  private identityAvailabilityFormElement?: ElementRef<HTMLElement>;
  @ViewChild('identityAvailabilityDetails')
  private identityAvailabilityDetails?: ElementRef<HTMLDetailsElement>;
  @ViewChild('authoringAvailabilityForm')
  private authoringAvailabilityForm?: EzFormComponent;
  @ViewChild('authoringForm', { read: ElementRef })
  private authoringFormElement?: ElementRef<HTMLElement>;
  @ViewChild('authoringAvailabilityForm', { read: ElementRef })
  private authoringAvailabilityFormElement?: ElementRef<HTMLElement>;
  @ViewChild('authoringAvailabilityDetails')
  private authoringAvailabilityDetails?: ElementRef<HTMLDetailsElement>;
  @ViewChild(WorkflowNumberingSettingsComponent)
  private numberingSettingsEditor?: WorkflowNumberingSettingsComponent;
  readonly latestNumberingDocument = () => this.workingDocument();

  @ViewChild('nodeForm')
  private nodeForm?: EzFormComponent;

  @ViewChild('nodeForm', { read: ElementRef })
  private nodeFormElement?: ElementRef<HTMLElement>;

  @ViewChild('stepKeyInput')
  private stepKeyInput?: ElementRef<HTMLInputElement>;
  @ViewChild('stepTechnicalDetails')
  private stepTechnicalDetails?: ElementRef<HTMLDetailsElement>;
  @ViewChild('identityForm', { read: ElementRef })
  private identityFormElement?: ElementRef<HTMLElement>;

  @ViewChild('draftEditorHeading')
  private draftEditorHeading?: ElementRef<HTMLElement>;

  @ViewChild('draftListHeading')
  private draftListHeading?: ElementRef<HTMLElement>;

  @ViewChild('nodeEditorHeading')
  private nodeEditorHeading?: ElementRef<HTMLElement>;

  @ViewChild('routeInspection', { read: ElementRef })
  private routeInspection?: ElementRef<HTMLElement>;

  @ViewChild('stepInspection', { read: ElementRef })
  private stepInspection?: ElementRef<HTMLElement>;

  @ViewChild('mapInspectorHeading')
  private mapInspectorHeading?: ElementRef<HTMLElement>;

  @ViewChild('stepIssuesHeading')
  private stepIssuesHeading?: ElementRef<HTMLElement>;

  @ViewChild('stepIssuesDisclosure')
  private stepIssuesDisclosure?: ElementRef<HTMLDetailsElement>;

  @ViewChild('operationErrorSummary')
  private operationErrorSummary?: ElementRef<HTMLElement>;

  @ViewChild('reviewTabs', { read: ElementRef })
  private reviewTabs?: ElementRef<HTMLElement>;

  @ViewChild('reviewFindingsHeading')
  private reviewFindingsHeading?: ElementRef<HTMLElement>;

  @ViewChild('setupHeading')
  private setupHeading?: ElementRef<HTMLElement>;
  @ViewChild('hearingLifecycleHeading')
  private hearingLifecycleHeading?: ElementRef<HTMLElement>;
  @ViewChild('processDataHeading')
  private processDataHeading?: ElementRef<HTMLElement>;

  @ViewChild(PlatformWorkflowDraftEdgeEditorComponent)
  private edgeEditor?: PlatformWorkflowDraftEdgeEditorComponent;

  @ViewChild(PlatformWorkflowDraftTransitionEditorComponent)
  private transitionEditor?: PlatformWorkflowDraftTransitionEditorComponent;

  @ViewChild(PlatformWorkflowDraftDecisionEditorComponent)
  private decisionEditor?: PlatformWorkflowDraftDecisionEditorComponent;

  @ViewChild(WorkflowStepRequirementsEditorComponent)
  private requirementsEditor?: WorkflowStepRequirementsEditorComponent;

  @ViewChild(WorkflowStepNotificationsComponent)
  private stepNotificationsEditor?: WorkflowStepNotificationsComponent;

  @ViewChild('workflowAssignmentPolicyEditor')
  private assignmentPolicyEditor?: WorkflowAssignmentPolicyComponent;

  @ViewChild('stepAssignmentPolicyEditor')
  private stepAssignmentPolicyEditor?: WorkflowAssignmentPolicyComponent;

  @Input() entryPointsVisible = true;
  @Input() showAuthoringEntryButtons = true;
  @Input()
  set selectedDefinition(value: WorkflowDefinitionCloneSelection | null) {
    this.selectedDefinitionState.set(value);
    if (this.authoringMode() === 'clone') {
      this.authoringFormValues = {
        ...this.authoringFormValues,
        workflowCode: value?.workflowCode ?? '',
      };
    }
  }

  get selectedDefinition(): WorkflowDefinitionCloneSelection | null {
    return this.selectedDefinitionState();
  }

  @Output() readonly readAccessDenied = new EventEmitter<void>();

  @Output() readonly dirtyChange = new EventEmitter<boolean>();
  @Output() readonly workbenchStateChange =
    new EventEmitter<WorkflowDraftWorkbenchState | null>();
  @Output() readonly definitionPublished = new EventEmitter<{
    workflowCode: string;
    definitionVersionCode: string;
  }>();
  @Output() readonly editableDraftOpened = new EventEmitter<{
    workflowCode: string;
    definitionVersionCode: string;
  }>();

  readonly canCreateWorkflowDraft = this.roles.hasAnyPermissionSignal(
    WORKFLOW_DEFINITION_AUTHOR_PERMISSION,
  );
  private readonly archiveDetected = signal(false);
  readonly isArchivedDraft = computed(
    () =>
      this.archiveDetected() ||
      this.currentDraft()?.lifecycleStatusCode === 'archived',
  );
  readonly canAuthor = computed(
    () => this.canCreateWorkflowDraft() && !this.isArchivedDraft(),
  );
  private readonly validationPermission = this.roles.hasAnyPermissionSignal(
    WORKFLOW_DEFINITION_VALIDATE_PERMISSION,
  );
  readonly canValidate = computed(
    () => this.validationPermission() && !this.isArchivedDraft(),
  );
  readonly archivedPublishedVersion =
    signal<WorkflowDefinitionVersionDetail | null>(null);
  readonly archivedVersionLoading = signal(false);
  readonly archivedVersionError = signal(false);
  readonly archivedPendingInputs = signal<
    readonly { name: string; label: string; value: string; checked?: boolean }[]
  >([]);
  private readonly archivedBufferPending = signal(false);
  readonly archivedDraftActionLabel = computed(() =>
    workflowDraftActionLabel(this.archivedPublishedVersion()?.draftActionCode),
  );
  private archivedVersionRequest = 0;
  private archivedDraftRequest = 0;
  @Output() readonly publishedVersionRequested = new EventEmitter<{
    workflowCode: string;
    definitionVersionCode: string;
  }>();
  private readonly canPublishRevision = this.roles.hasAnyPermissionSignal(
    'workflow_definition.publish',
  );
  readonly lifecycleOperationInProgress = signal(false);
  readonly reviewPublicationReadiness = computed(() =>
    workflowDraftPublicationReadiness(
      this.isArchivedDraft() && this.currentDraft()
        ? { ...this.currentDraft()!, lifecycleStatusCode: 'archived' }
        : this.currentDraft(),
      {
        hasUnsavedChanges: this.hasOpenDraftUnsavedChanges(),
        canPublish: this.canPublishRevision(),
        actorUserId: this.roles.accessProfile?.()?.actor.userId,
        interactionBlocked: this.isDraftEditorInteractionBlocked(),
        validationInProgress: this.isValidating(),
        validationFailed: this.automaticValidationFailed(),
      },
    ),
  );
  readonly reviewExecutionSupport = computed(() =>
    workflowDraftExecutionSupport(this.currentDraft()),
  );
  readonly processRailExpanded = signal(true);
  readonly stepsDrawerOpen = signal(false);
  @ViewChild('stepsDrawerToggle') private stepsDrawerToggle?: ElementRef<HTMLButtonElement>;

  toggleStepsDrawer(): void {
    this.stepsDrawerOpen.update(open => !open);
    if (this.stepsDrawerOpen()) setTimeout(() =>
      this.hostElement.nativeElement.querySelector<HTMLElement>('.workflow-steps-drawer-close')?.focus(),
    );
  }

  closeStepsDrawer(): void {
    if (!this.stepsDrawerOpen()) return;
    this.stepsDrawerOpen.set(false);
    this.stepsDrawerToggle?.nativeElement.focus();
  }

  readonly behaviorSection = signal<
    'details' | 'readiness' | 'routes' | 'transitions' | 'notifications' | 'timers' | 'children'
  >('readiness');
  readonly behaviorSections = [
    { id: 'readiness', label: 'Completion requirements' },
    { id: 'routes', label: 'Outcomes & routing' },
    { id: 'transitions', label: 'Statuses' },
    { id: 'notifications', label: 'Saved notifications' },
    { id: 'details', label: 'Step properties' },
    { id: 'timers', label: 'Timers & escalation' },
    { id: 'children', label: 'Related work' },
  ] as const;
  readonly stepEditorNodeId = signal<string | null>(null);
  readonly stepEditorOpened = signal(false);
  readonly editingRouteSelection = signal<WorkflowReadOnlyGraphSelection | null>(null);
  readonly focusedRouteIndex = computed(() => {
    const selection = this.editingRouteSelection();
    const document = this.workingDocument();
    if (!selection || !document) return null;
    const matches = projectWorkflowDraftGraph(document).edges.filter(edge => edge.sourceId === selection.sourceId);
    return matches.length === 1 ? matches[0].sourceIndex : null;
  });
  readonly automatedRouteFocus = signal<{ routeOutcome: string; commandOutcome?: string; request: number } | null>(null);
  private automatedHeadingFocusRequest = 0;
  readonly isFocusedEditor = computed(() =>
    this.stepEditorNodeId() !== null || this.editingRouteSelection() !== null || this.isAddingStep(),
  );
  readonly isInspectingRoute = computed(() =>
    this.mapSelection()?.kind === 'edge' && this.editingRouteSelection() === null,
  );
  readonly selectedRouteInspection = computed(() =>
    workflowRouteInspection(this.workingDocument(), this.mapSelection()),
  );
  private editorMapReturnSelection: WorkflowReadOnlyGraphSelection | null = null;
  readonly isInspectingStep = computed(() => {
    const node = this.selectedMapNode();
    return !!node && this.mapSelection()?.isAuthorable === true &&
      ['human_step', 'automated_step', 'terminal'].includes(node.typeCode) &&
      !this.isAddingStep() && this.stepEditorNodeId() !== node.id;
  });
  readonly selectedStepOverview = computed(() =>
    this.workflowOverview().steps.find(step => step.nodeId === this.selectedMapNode()?.id) ?? null,
  );
  private readonly hasCurrentStepOwnerIdentity = computed(() => {
    const node = this.selectedMapNode();
    const selection = this.mapSelection();
    const current = node ? this.mapNodeSelectionBySourceId(node.id) : null;
    return selection?.isAuthorable === true && current?.isAuthorable === true &&
      selection.sourceIndex === current.sourceIndex &&
      selection.rendererId === current.rendererId && !!node &&
      ['human_step', 'automated_step', 'terminal'].includes(node.typeCode);
  });
  /** Catalog owners initialize only for the deliberately opened, exact current step. */
  readonly isStepEditorActive = computed(() => {
    const node = this.selectedMapNode();
    return this.activeWorkbenchView() === 'map' && !this.isAddingStep() &&
      this.hasCurrentStepOwnerIdentity() && !!node && this.stepEditorNodeId() === node.id;
  });

  readonly presentationOwnerScopes = ['detail', 'dashboard'] as const;
  /** Retain initialized owners for this node; inspecting another node starts empty. */
  private readonly initializedStepOwners = linkedSignal<
    { context: string; owner: string | null }, ReadonlySet<string>
  >({
    source: () => {
      const node = this.selectedMapNode();
      const scope = this.stepWorkspaceScope();
      return {
        context: JSON.stringify([this.currentDraft()?.id ?? null, node?.id ?? null, node?.typeCode ?? null]),
        owner: !this.isStepEditorActive() ? null : node?.typeCode === 'automated_step'
          ? 'automated' : scope === 'behavior' ? `behavior:${this.behaviorSection()}` : scope,
      };
    },
    computation: (source, previous) => {
      const owners = source.context === previous?.source.context
        ? new Set(previous.value) : new Set<string>();
      if (source.owner) owners.add(source.owner);
      return owners;
    },
  });

  private readonly workViewKey = computed(() => {
    const document = this.workingDocument(), node = this.selectedMapNode();
    return document && node?.typeCode === 'human_step'
      ? this.ownerViewState.key(document, node.id, 'work-view') : null;
  });
  readonly activeWorkView = linkedSignal(() => {
    const key = this.workViewKey();
    return key ? this.ownerViewState.read<WorkflowWorkView>(key) ?? 'overview' : 'overview';
  });
  readonly latestAssignmentDocument = () => this.workingDocument();
  readonly stepAssignmentOwnerContext = () => JSON.stringify([this.saveContextKey(), this.selectedMapNode()?.id, this.selectedMapNode()?.typeCode, this.stepEditorNodeId()]);
  /** Read the live view before retained automated inputs have reconciled a navigation or actor change. */
  readonly activeAutomatedView = (): 'activity' | 'routing' | 'settings' | null => {
    if (!this.isStepEditorActive() || this.selectedMapNode()?.typeCode !== 'automated_step' || this.mapInspectorPane() === 'path') return null;
    const view = this.activeStepScopeTab();
    return view === 'activity' || view === 'routing' || view === 'settings' ? view : null;
  };
  readonly workFormViewContext = () => JSON.stringify([
    this.currentDraft()?.id, this.currentDraft()?.draftCode, this.currentDraft()?.workflowCode,
    this.roles.accessProfile?.()?.actor?.actorId ?? null,
    this.canAuthor(), this.canCreateWorkflowDraft(), this.registryReadsSuspended(),
  ]);
  readonly workFormLookupOwner = (): WorkflowLookupSourceOwner | null => {
    const draft = this.currentDraft();
    if (!draft || !this.canAuthor() || this.isDraftEditorInteractionBlocked()) return null;
    return { draftId: draft.id, revision: draft.revision, etag: draft.eTag, authorityKey: this.saveContextKey(), context: {
      workflowCode: draft.document.workflowCode, subjectTypeCode: draft.document.subjectTypeCode,
      scopeTypeCode: draft.document.scopeTypeCode, scopeKey: draft.document.scopeKey ?? null,
    } };
  };
  readonly workAssignmentSummary = computed(() => workflowStepAssignmentSummary(this.workingDocument(), this.selectedMapNode()?.id ?? null,
    this.authoringReferences()?.roleCodes ?? [], { canAuthor: this.canAuthor(), referenceLoading: this.isLoadingAuthoringReferences(),
      referenceError: !!this.authoringReferencesError() }));
  readonly workViewTitle = computed(() => ({ overview: 'Work overview', assignment: 'Assignment',
    requirements: 'Completion requirements', components: 'Forms and actions' })[this.activeWorkView()]);
  @ViewChild('workViewHeading') private workViewHeading?: ElementRef<HTMLElement>;
  @ViewChild('workOverviewHeading', { read: ElementRef }) private workOverviewHeading?: ElementRef<HTMLElement>;
  readonly workOverviewRequirements = computed(() => this.selectedMapNode()?.declaration?.behavior.completionRequirements ?? []);

  private revealWorkView(view: WorkflowWorkView): void {
    this.activeWorkView.set(view);
    const key = this.workViewKey();
    if (key) this.ownerViewState.remember(key, view);
  }

  selectWorkView(view: WorkflowWorkView): void {
    if (this.isDraftEditorInteractionBlocked() || !this.isStepEditorActive() || this.selectedMapNode()?.typeCode !== 'human_step') return;
    this.requestMapEditorDeactivation(() => {
      this.revealWorkView(view);
      this.focusWorkDestination();
    }, () => undefined);
  }

  manageWorkComponents(kind: 'form' | 'action'): void {
    if (this.isDraftEditorInteractionBlocked() || !this.isStepEditorActive() || this.selectedMapNode()?.typeCode !== 'human_step') return;
    this.requestMapEditorDeactivation(() => {
      this.revealWorkView('components');
      this.focusWorkDestination(() => this.workComponentsEditor?.focusWorkSection(kind));
    }, () => undefined);
  }

  private focusWorkDestination(focus?: () => void): void {
    const context = () => JSON.stringify([this.saveContextKey(), this.selectedMapNode()?.id, this.stepEditorNodeId(),
      this.workViewKey(), this.activeWorkbenchView(), this.stepWorkspaceScope(), this.behaviorSection(), this.activeWorkView()]);
    const captured = context();
    setTimeout(() => {
      if (this.destroyRef.destroyed || this.ownerViewState.focusSuspended() || captured !== context()) return;
      if (this.activeWorkView() === 'components') {
        if (focus) focus();
        else this.workComponentsEditor?.focusWorkContext();
        return;
      }
      const heading = (this.activeWorkView() === 'overview' ? this.workOverviewHeading : this.workViewHeading)?.nativeElement;
      if (!heading?.isConnected || heading.closest('[hidden], [inert]')) return;
      heading.focus(); focus?.();
    });
  }

  /** Visibility and focus remain separate from whether a specialist was initialized. */
  isHumanStepOwnerActive(
    scope: 'behavior' | 'dashboard' | 'detail',
    section?: ReturnType<typeof this.behaviorSection>,
  ): boolean {
    return this.isStepEditorActive() && this.selectedMapNode()?.typeCode === 'human_step' &&
      this.stepWorkspaceScope() === scope &&
      (section === undefined || this.behaviorSection() === section);
  }

  isHumanStepOwnerInitialized(
    scope: 'behavior' | 'dashboard' | 'detail',
    section?: ReturnType<typeof this.behaviorSection>,
  ): boolean {
    return this.hasCurrentStepOwnerIdentity() && this.selectedMapNode()?.typeCode === 'human_step' &&
      this.initializedStepOwners().has(scope === 'behavior' ? `behavior:${section}` : scope);
  }

  readonly isAutomatedStepOwnerInitialized = computed(() =>
    this.hasCurrentStepOwnerIdentity() && this.selectedMapNode()?.typeCode === 'automated_step' &&
      this.initializedStepOwners().has('automated'),
  );
  private readonly browserDocument = inject(DOCUMENT);
  private stepEditorReturnFocus: HTMLElement | null = null;

  /** Return to the exact originating map item without replacing graph or outline DOM. */
  backToProcessMap(): void {
    if (this.isDraftEditorInteractionBlocked()) return;
    this.requestMapEditorDeactivation(() => {
      this.decisionInventoryOwnerReturn = null; this.decisionInventoryOrigin = null; this.decisionInventoryCheckAddMode.set(false);
      this.stepEditorNodeId.set(null);
      this.editingRouteSelection.set(null);
      this.automatedRouteFocus.set(null);
      const previous = this.editorMapReturnSelection;
      this.editorMapReturnSelection = null;
      if (previous) this.restoreMapInspection(previous);
      const focus = this.stepEditorReturnFocus;
      this.stepEditorReturnFocus = null;
      const draftId = this.currentDraft()?.id;
      const selectionKey = JSON.stringify(this.mapSelection());
      setTimeout(() => {
        if (this.currentDraft()?.id !== draftId || this.isFocusedEditor() ||
          this.activeWorkbenchView() !== 'map' || JSON.stringify(this.mapSelection()) !== selectionKey) return;
        if (focus?.isConnected && !focus.closest('[hidden]')) focus.focus({ preventScroll: true });
        else {
          const inspector = this.isInspectingRoute() ? this.routeInspection : this.stepInspection;
          (inspector?.nativeElement.querySelector<HTMLElement>('button:not([disabled])') ??
            inspector?.nativeElement ?? this.mapInspectorHeading?.nativeElement)?.focus({ preventScroll: true });
        }
      });
    }, () => undefined);
  }

  private clearFocusedMapContext(): void {
    this.stepEditorNodeId.set(null);
    this.editingRouteSelection.set(null);
    this.automatedRouteFocus.set(null);
    this.editorMapReturnSelection = null;
    this.stepEditorReturnFocus = null;
  }

  private restoreMapInspection(previous: WorkflowReadOnlyGraphSelection, updateUrl = true): void {
    const document = this.workingDocument();
    if (!document) return;
    const graph = projectWorkflowDraftGraph(document);
    const edges = graph.edges.filter(edge => edge.sourceId === previous.sourceId);
    const selection = previous.kind === 'node'
      ? this.mapNodeSelectionBySourceId(previous.sourceId)
      : edges.length === 1 ? this.graphSelectionForEdge(edges[0], graph.edges) : null;
    this.acceptMapSelection({ selection, origin: 'picker', moveFocusToInspector: false });
    if (!selection) this.editorMessage.set(
      'The previously selected item is no longer uniquely available. Select a current item on the process map.',
    );
    if (updateUrl) this.writeWorkbenchViewToUrl('map');
  }

  readonly stepWorkspaceScope = signal<'behavior' | 'dashboard' | 'detail'>(
    'behavior',
  );
  readonly stepWorkspaceScopeLabel = computed(
    () =>
      ({
        behavior: this.behaviorSection() === 'readiness'
          ? 'Work'
          : this.behaviorSection() === 'routes' ? 'Routing' : 'Settings',
        dashboard: 'Dashboard',
        detail: 'Staff page',
      })[this.stepWorkspaceScope()],
  );
  readonly editingLocation = computed(() => {
    if (this.activeWorkbenchView() === 'setup') return 'setup';
    const node = this.selectedMapNode();
    return node && ['human_step', 'automated_step'].includes(node.typeCode) &&
      !this.isAddingStep()
      ? `behavior:${node.id}`
      : 'map';
  });
  readonly editingLocationOptions = computed(() => [
    { value: 'map', label: 'Process map' },
    { value: 'setup', label: 'Workflow settings' },
    ...[...this.humanStepNodes(), ...this.automatedStepNodes()].map((node) => ({
      value: `behavior:${node.id}`,
      label: `Step · ${node.displayName || node.stepCode || node.id}`,
    })),
  ]);
  readonly workbenchNavigation = WORKBENCH_NAVIGATION;
  readonly activeWorkbenchTab = computed(() =>
    this.activeWorkbenchView() === 'setup' ? 'setup' : this.activeWorkbenchView() === 'review' ? 'review' : 'map',
  );
  readonly workbenchTabs = computed<readonly UiNavigationTab[]>(() =>
    this.workbenchNavigation.map((item) => ({
      id: item.id,
      label:
        item.id === 'review' && this.isArchivedDraft()
          ? 'Saved checks'
          : item.label,
      controlId: `workflow-workbench-tab-${item.id}`,
      pending: this.isWorkbenchViewPending(item.id),
      ...(item.id === 'review' && this.diagnostics().length
        ? {
            badge: String(this.diagnostics().length),
            badgeLabel: `${this.diagnostics().length} validation issues`,
          }
        : {}),
    })),
  );
  readonly activeStepScopeTab = computed(() => {
    const scope = this.stepWorkspaceScope();
    if (this.selectedMapNode()?.typeCode === 'automated_step') return this.behaviorSection() === 'routes' ? 'routing' : this.behaviorSection() === 'details' ? 'settings' : 'activity';
    if (scope !== 'behavior') return scope;
    return this.behaviorSection() === 'readiness'
      ? 'work'
      : this.behaviorSection() === 'routes'
        ? 'routing'
        : 'settings';
  });
  readonly stepScopeTabs = computed<readonly UiNavigationTab[]>(() =>
    this.selectedMapNode()?.typeCode === 'automated_step'
      ? [{ id: 'activity', label: 'Activity' }, { id: 'routing', label: 'Routing' }, { id: 'settings', label: 'Settings' }].map(section => ({ ...section, controlId: `workflow-step-section-${section.id}`, disabled: this.isDraftEditorInteractionBlocked() }))
      : this.selectedMapNode()?.typeCode === 'human_step' &&
    this.mapContextSupportsBehavior() &&
    !this.isAddingStep()
      ? [
          { id: 'work', label: 'Work' },
          { id: 'detail', label: 'Staff page' },
          { id: 'routing', label: 'Routing' },
          { id: 'dashboard', label: 'Dashboard' },
          { id: 'settings', label: 'Settings' },
        ].map((section) => ({
          ...section,
          controlId: `workflow-step-section-${section.id}`,
          disabled: this.isDraftEditorInteractionBlocked(),
        }))
      : [],
  );
  // Human steps use the single Step sections navigation above. Other node kinds
  // retain their existing inspector views.
  readonly behaviorTabs = computed<readonly UiNavigationTab[]>(() => []);
  readonly activeBehaviorTab = computed(() =>
    this.behaviorSection() === 'readiness' ||
    this.behaviorSection() === 'routes'
      ? this.behaviorSection()
      : 'advanced',
  );
  readonly advancedBehaviorTabs = computed<readonly UiNavigationTab[]>(() =>
    this.selectedMapNode()?.typeCode === 'human_step' && this.stepScopeTabs().length && this.activeStepScopeTab() === 'settings'
      ? this.behaviorSections
          .filter(
            (section) =>
              section.id === 'details' || section.id === 'timers' || section.id === 'children' ||
              section.id === 'transitions' ||
              (section.id === 'notifications' &&
                this.hasSavedStepNotifications()),
          )
          .map((section) => ({
            ...section,
            disabled: this.isDraftEditorInteractionBlocked(),
          }))
      : [],
  );
  readonly standardStepLoading = signal(false);
  readonly standardStepError = signal('');
  private standardStepSupport: {
    catalog: StepAuthoringCatalog;
    capabilities: WorkflowCapabilityCatalogEntry[];
  } | null = null;
  private standardStepRequest = 0;

  hasSavedStepNotifications(): boolean {
    const node = this.selectedMapNode();
    const rules = [
      ...(node?.allowedTransitions ?? []),
      ...(node?.requiredDecisions ?? []),
      ...(node?.allowedTransitions ?? []).flatMap(
        (item) => item?.requiredDecisions ?? [],
      ),
    ];
    return rules.some(
      (rule) => !!rule?.notificationMessage || !!rule?.notifications?.length,
    );
  }

  loadStandardStepSupport(): void {
    const draft = this.currentDraft(),
      document = this.workingDocument();
    if (
      !draft ||
      !document ||
      document.subjectTypeCode ||
      this.standardStepLoading()
    )
      return;
    const request = ++this.standardStepRequest;
    this.standardStepLoading.set(true);
    this.standardStepError.set('');
    this.standardStepSupport = null;
    forkJoin({
      catalog: this.lifecycleService.getStepAuthoringCatalog(document),
      capabilities: this.lifecycleService.getCapabilities(document),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (support) => {
          if (
            request !== this.standardStepRequest ||
            this.currentDraft()?.id !== draft.id
          )
            return;
          this.standardStepSupport = support;
          this.standardStepLoading.set(false);
        },
        error: () => {
          if (
            request !== this.standardStepRequest ||
            this.currentDraft()?.id !== draft.id
          )
            return;
          this.standardStepLoading.set(false);
          this.standardStepError.set(
            'Standard task controls could not be loaded. Retry before adding the step. Your entered details are kept.',
          );
        },
      });
  }
  readonly inspectorTabs = computed<readonly UiNavigationTab[]>(() => {
    if (
      this.mapSelection()?.kind === 'edge' ||
      this.isAddingStep() ||
      (this.selectedMapNode()?.typeCode === 'human_step' &&
        this.mapContextSupportsBehavior())
    )
      return [];
    return [
      { id: 'overview', label: 'Overview' },
      ...(this.mapContextSupportsBehavior()
        ? [
            {
              id: 'transitions',
              label: 'Statuses',
              pending: this.transitionFormDirty(),
              badge: String(this.mapInspectorStatusChangeCount()),
              ariaLabel: `${this.canAuthor() ? 'Edit' : 'View'} statuses, ${this.mapInspectorStatusChangeCount()} ${this.mapInspectorStatusChangeCount() === 1 ? 'entry' : 'entries'}${this.transitionFormDirty() ? ', changes to apply' : ''}`,
            },
            {
              id: 'decisions',
              label: 'Decisions',
              pending: this.decisionFormDirty(),
              badge: String(this.mapInspectorDecisionRuleCount()),
              ariaLabel: `${this.canAuthor() ? 'Edit' : 'View'} decisions, ${this.mapInspectorDecisionRuleCount()} saved rule${this.mapInspectorDecisionRuleCount() === 1 ? '' : 's'}${this.decisionFormDirty() ? ', changes to apply' : ''}`,
            },
          ]
        : []),
    ];
  });
  readonly activeWorkbenchView = signal<WorkflowDraftWorkbenchView>('map');
  readonly decisionInventoryInitialized = signal(false);
  readonly decisionInventoryLoading = signal(false);
  readonly decisionInventoryCheckAddMode = signal(false);
  readonly decisionInventoryError = signal('');
  private readonly decisionInventoryCatalog = signal<StepAuthoringCatalog | null>(null);
  private readonly decisionInventoryActivityCatalog = signal<WorkflowAutomationCatalog | null>(null);
  private readonly decisionInventoryCatalogKey = signal('');
  private decisionInventoryRequestedKey = '';
  private decisionInventoryRequest = 0;
  private decisionInventoryNavigation = 0;
  @ViewChild(WorkflowDecisionInventoryComponent) private decisionInventoryEditor?: WorkflowDecisionInventoryComponent;
  @ViewChild(PlatformWorkflowDraftGraphPreviewComponent) private inventoryGraph?: PlatformWorkflowDraftGraphPreviewComponent;
  @ViewChild('inventoryMapPanel', { read: ElementRef }) private inventoryMapPanel?: ElementRef<HTMLElement>;
  private decisionInventoryOrigin: {
    owner: string; location: WorkflowDesignerLocation; selection: WorkflowReadOnlyGraphSelection | null;
    focusedNodeId: string | null; focusedRoute: WorkflowReadOnlyGraphSelection | null; focusedRouteValue: string | null;
    automatedFocus: { routeOutcome: string; request: number } | null; focus: HTMLElement | null; camera: WorkflowDraftGraphPresentationState | null;
    scroll: { element: HTMLElement; top: number; left: number }[];
  } | null = null;
  private decisionInventoryOwnerReturn: string | null = null;
  readonly decisionInventory = computed(() => {
    const current = this.decisionInventoryReadKey() === this.decisionInventoryCatalogKey();
    return readWorkflowDecisionInventory(this.workingDocument(), current ? this.decisionInventoryCatalog() : null,
      current ? this.decisionInventoryActivityCatalog() : null);
  });
  readonly hasOpaqueDecisionCollections = computed(() => {
    const document = this.workingDocument();
    if (!document) return false;
    const opaque = (value: unknown) => value != null && !Array.isArray(value);
    if (opaque(document.requiredDecisions) || opaque(document.workflowTransitions)) return true;
    const transitions = Array.isArray(document.workflowTransitions) ? [...document.workflowTransitions] : [];
    for (const node of document.nodes) {
      if (opaque(node?.requiredDecisions) || opaque(node?.allowedTransitions)) return true;
      if (Array.isArray(node?.allowedTransitions)) transitions.push(...node.allowedTransitions);
    }
    return transitions.some(transition => opaque(transition?.requiredDecisions) || opaque(transition?.requiredDecisionKinds));
  });
  readonly decisionInventoryContext = () => JSON.stringify([this.decisionInventoryReadKey(), this.decisionInventoryCatalog(),
    this.decisionInventoryActivityCatalog()]);

  readonly activeWorkflowSettingsTask = signal<WorkflowSettingsTask>('general');
  readonly activeTechnicalView = signal<WorkflowTechnicalView>('identity');
  private readonly initializedTechnicalViews = signal<ReadonlySet<WorkflowTechnicalView>>(new Set(['identity']));
  readonly latestCapabilityDocument = () => this.workingDocument();
  readonly capabilityOwnerContext = () => this.saveContextKey();
  readonly technicalViewTitle = computed(() => ({ identity: 'Technical settings', modules: 'Modules & dependencies',
    assignment: 'Independent assignment policies', quorum: 'Quorum rule calculator' })[this.activeTechnicalView()]);
  private readonly initializedWorkflowSettingsTasks = signal<ReadonlySet<WorkflowSettingsTask>>(new Set(['general']));
  readonly workflowSettingsTabs = computed<readonly UiNavigationTab[]>(() => [
    { id: 'general', label: 'General' },
    { id: 'navigation', label: 'Staff navigation', pending: this.navigationFormDirty() },
    { id: 'data', label: 'Data between steps', pending: this.processDataFormDirty() },
    { id: 'numbering', label: 'Public references' },
    ...(this.hearingLifecycleAvailable() || this.workingDocument()?.subjectTypeCode === 'hearing_cycle' ||
      this.initializedWorkflowSettingsTasks().has('hearing')
      ? [{ id: 'hearing', label: 'Hearing setup' }] : []),
    { id: 'automation', label: 'Event automation', pending: this.automationFormDirty() },
    { id: 'statuses', label: 'Workflow statuses', pending: this.workflowStatusFormDirty() },
    { id: 'technical', label: 'Technical settings', pending: this.assignmentPolicyFormDirty() },
  ].map(tab => ({ ...tab, disabled: this.isDraftEditorInteractionBlocked() })));

  readonly isWorkflowStatusesOwnerActive = () => this.activeWorkbenchView() === 'setup' &&
    this.activeWorkflowSettingsTask() === 'statuses' && !this.isDraftEditorInteractionBlocked();
  readonly workflowStatusOwnerView = (): string | null => this.isWorkflowStatusesOwnerActive()
    ? JSON.stringify([this.workFormViewContext(), 'workflow-status-settings']) : null;
  readonly isNavigationOwnerActive = () => this.activeWorkbenchView() === 'setup' &&
    this.activeWorkflowSettingsTask() === 'navigation' && !this.isDraftEditorInteractionBlocked();
  readonly navigationOwnerView = (): string | null => this.isNavigationOwnerActive()
    ? JSON.stringify([this.workFormViewContext(), 'workflow-navigation']) : null;
  readonly dashboardRemovalView = (): string | null => this.canAuthor() && !this.isDraftEditorInteractionBlocked()
    && this.isHumanStepOwnerActive('dashboard') && this.mapInspectorPane() !== 'path'
    ? JSON.stringify([this.workFormViewContext(), this.stepAssignmentOwnerContext(), this.mapSelection(), this.designerLocation(), 'dashboard-removal']) : null;
  readonly dashboardSourceView = (): string | null => this.canAuthor() && !this.isDraftEditorInteractionBlocked()
    && this.isHumanStepOwnerActive('dashboard') && this.mapInspectorPane() !== 'path'
    ? JSON.stringify([this.workFormViewContext(), this.stepAssignmentOwnerContext(), this.mapSelection(), this.designerLocation(), 'dashboard-source']) : null;
  readonly dashboardElementView = (): string | null => this.canAuthor() && !this.isDraftEditorInteractionBlocked()
    && this.isHumanStepOwnerActive('dashboard') && this.mapInspectorPane() !== 'path'
    ? JSON.stringify([this.workFormViewContext(), this.stepAssignmentOwnerContext(), this.mapSelection(), this.designerLocation(), 'dashboard-element']) : null;
  readonly dashboardSettingsView = (): string | null => !this.isDraftEditorInteractionBlocked()
    && this.isHumanStepOwnerActive('dashboard') && this.mapInspectorPane() !== 'path'
    ? JSON.stringify([this.workFormViewContext(), this.stepAssignmentOwnerContext(), this.mapSelection(), this.designerLocation(), 'dashboard-settings']) : null;
  readonly dashboardNameSettlement = (): boolean => {
    const token = this.dashboardNameSettlementToken, editor = this.pageEditors?.find(owner => owner.scope === 'dashboard');
    return !!token && editor === token.editor && this.canAuthor() && !this.isDraftEditorInteractionBlocked()
      && token.nodeId === this.selectedMapNode()?.id && this.selectedMapNode()?.typeCode === 'human_step'
      && token.owner === JSON.stringify(this.stepAssignmentOwnerContext())
      && token.source === JSON.stringify(this.workingDocument())
      && token.view === this.dashboardSettingsView()
      && token.draft === JSON.stringify(editor.dashboardRenameSnapshot);
  };
  readonly pageSettingsView = (): string | null => !this.isDraftEditorInteractionBlocked()
    && this.isHumanStepOwnerActive('detail') && this.mapInspectorPane() !== 'path'
    ? JSON.stringify([this.workFormViewContext(), this.stepAssignmentOwnerContext(), this.mapSelection(), this.designerLocation(), 'staff-page-settings']) : null;
  readonly isProcessDataOwnerActive = () => this.activeWorkbenchView() === 'setup' &&
    this.activeWorkflowSettingsTask() === 'data' && !this.isDraftEditorInteractionBlocked();
  childWorkInteractionBlocked(): boolean {
    return this.isDraftEditorInteractionBlocked() || this.nodeFormDirty() || this.edgeFormDirty() ||
      this.requirementFormDirty() || this.reviewRoutingDirty() || this.transitionFormDirty() || this.decisionFormDirty() ||
      this.notificationFormDirty() || this.assignmentPolicyFormDirty() || this.stepAssignmentPolicyFormDirty() || this.timerFormDirty();
  }
  readonly isChildWorkOwnerActive = (): boolean => {
    if (this.childWorkInteractionBlocked() || this.isAddingStep() || this.mapSelection()?.kind !== 'node' || !this.mapSelection()?.isAuthorable) return false;
    return this.selectedMapNode()?.typeCode === 'terminal'
      ? this.isStepEditorActive() && this.activeWorkbenchView() === 'map' && this.mapInspectorPane() !== 'path'
      : this.isHumanStepOwnerActive('behavior', 'children') && this.mapInspectorPane() !== 'path';
  };
  readonly childWorkOwnerView = (): string | null => this.isChildWorkOwnerActive()
    ? JSON.stringify([this.workFormViewContext(), this.mapSelection(), this.designerLocation(), this.selectedMapNode()?.typeCode]) : null;
  timerInteractionBlocked(): boolean {
    return this.isDraftEditorInteractionBlocked() || this.nodeFormDirty() || this.edgeFormDirty() ||
      this.requirementFormDirty() || this.reviewRoutingDirty() || this.transitionFormDirty() || this.decisionFormDirty() ||
      this.notificationFormDirty() || this.assignmentPolicyFormDirty() || this.stepAssignmentPolicyFormDirty() || this.childWorkFormDirty();
  }
  readonly isTimerOwnerActive = (): boolean => !this.isDraftEditorInteractionBlocked() && !this.isAddingStep() &&
    this.mapSelection()?.kind === 'node' && !!this.mapSelection()?.isAuthorable &&
    this.isHumanStepOwnerActive('behavior', 'timers') && this.mapInspectorPane() !== 'path';
  readonly timerOwnerView = (): string | null => this.isTimerOwnerActive()
    ? JSON.stringify([this.workFormViewContext(), this.mapSelection(), this.designerLocation(), 'step-timers', this.timerInteractionBlocked()]) : null;
  processDataHasOtherPending(): boolean {
    if (!this.currentBypassDataLease())
      return this.pendingWorkbenchAreaCount() > (this.processDataFormDirty() ? 1 : 0);
    return this.editorPendingScopes().some(scope => scope.state() !== 'clean' &&
      scope.id !== 'status-change:transition-progress:step-progress' && scope.id !== 'process-data:process-data-fields');
  }
  readonly processDataOwnerView = (): string | null => this.isProcessDataOwnerActive()
    ? JSON.stringify([this.workFormViewContext(), 'process-data-settings', this.processDataHasOtherPending(),
      this.presentBypassDataLease()?.generation ?? null]) : null;
  readonly workflowStatusesView = (): string | null => {
    if (this.isDraftEditorInteractionBlocked() || this.activeWorkbenchView() !== 'map' || this.stepWorkspaceScope() !== 'behavior') return null;
    const section = this.behaviorSection(), pane = this.mapInspectorPane();
    if (!((this.selectedMapNode()?.typeCode === 'human_step' && this.isStepEditorActive() &&
        ((section === 'routes' && pane === 'overview') || (section === 'transitions' && pane === 'transitions'))) ||
      (!this.selectedMapNode() && pane === 'transitions'))) return null;
    return JSON.stringify([this.workFormViewContext(), this.mapSelection(), this.designerLocation(), this.stepEditorNodeId(), section, pane]);
  };

  readonly processNameEditable = computed(() => this.isProcessFirstDocument() &&
    this.canAuthor() && !this.isDraftEditorInteractionBlocked());
  readonly processNameError = computed(() => {
    if (!this.isProcessFirstDocument()) return '';
    const value = this.identityValuesState().displayName;
    const issue = workflowProcessNameIssue(value);
    return issue === 'required' ? 'Enter a process name.' : issue === 'maxlength' ? 'Use no more than 120 characters.' : '';
  });

  isWorkflowSettingsTaskInitialized(task: WorkflowSettingsTask): boolean {
    return this.initializedWorkflowSettingsTasks().has(task);
  }

  selectWorkflowSettingsTask(value: string): void {
    if (this.isDraftEditorInteractionBlocked() || value === this.activeWorkflowSettingsTask() ||
      !this.workflowSettingsTabs().some(tab => tab.id === value)) return;
    this.requestMapEditorDeactivation(() => {
      this.workflowStatusOrigin = null;
      this.revealWorkflowSettingsTask(value as WorkflowSettingsTask);
    }, () => undefined);
  }

  private revealWorkflowSettingsTask(task: WorkflowSettingsTask, updateUrl = true): void {
    this.workflowSettingsRevealGeneration++;
    this.initializedWorkflowSettingsTasks.update(tasks => new Set([...tasks, task]));
    this.activeWorkflowSettingsTask.set(task);
    this.activeWorkbenchView.set('setup');
    if (task === 'automation') this.automationExpanded.set(true);
    if (updateUrl) this.writeWorkbenchViewToUrl('setup');
  }

  isTechnicalViewInitialized(view: WorkflowTechnicalView): boolean {
    return this.initializedTechnicalViews().has(view);
  }

  selectTechnicalView(view: WorkflowTechnicalView): void {
    if (this.isDraftEditorInteractionBlocked() || this.activeTechnicalView() === view) return;
    this.requestMapEditorDeactivation(() => {
      this.revealTechnicalView(view);
      this.focusTechnicalDestination(() => this.technicalHeading);
    }, () => undefined);
  }

  private revealTechnicalView(view: WorkflowTechnicalView, updateUrl = true): void {
    this.initializedTechnicalViews.update(views => new Set([...views, view]));
    this.activeTechnicalView.set(view);
    if (view === 'modules') this.capabilityInventoryExpanded.set(true);
    if (view === 'quorum') this.quorumCalculatorOpen.set(true);
    this.revealWorkflowSettingsTask('technical', updateUrl);
  }

  reviewTechnicalValidation(): void {
    if (this.isDraftEditorInteractionBlocked()) return;
    this.selectWorkbenchTab('review');
    if (this.activeWorkbenchView() === 'review') this.focusTechnicalDestination(() => this.reviewFindingsHeading);
  }

  private focusTechnicalDestination(target: () => ElementRef<HTMLElement> | undefined): void {
    const context = () => JSON.stringify([this.saveContextKey(), this.activeWorkbenchView(),
      this.activeWorkflowSettingsTask(), this.activeTechnicalView()]);
    const captured = context();
    setTimeout(() => {
      if (this.destroyRef.destroyed || this.ownerViewState.focusSuspended() || captured !== context()) return;
      const element = target()?.nativeElement;
      if (element?.isConnected && !element.closest('[hidden], [inert]')) element.focus();
    });
  }
  readonly activeRoutingView = signal<WorkflowDraftRoutingView>('paths');
  readonly mapInspectorPane = signal<WorkflowMapInspectorPane>('overview');
  readonly mapSelection = signal<WorkflowReadOnlyGraphSelection | null>(null);
  readonly activeReviewView = signal<WorkflowDraftReviewView>('readiness');
  readonly reviewTabsConfig = computed<readonly UiNavigationTab[]>(() => [
    {
      id: 'readiness',
      label: 'Readiness',
      controlId: 'workflow-review-tab-readiness',
      ariaLabel: `Readiness, ${this.validationStateLabel()}, ${this.reviewFindingCountLabel()}`,
    },
    {
      id: 'changes',
      label: 'Saved changes',
      controlId: 'workflow-review-tab-changes',
    },
  ]);
  private readonly registryReadsSuspended = signal(false);
  private resumeAuthoringReferencesAfterAccess = false;
  readonly isLoadingDrafts = signal(false);
  readonly isLoadingDraft = signal(false);
  readonly isSaving = signal(false);
  readonly isValidating = signal(false);
  readonly isRunningAuthoringAction = signal(false);
  readonly draftPage = signal<WorkflowDefinitionDraftPage | null>(null);
  readonly draftListError =
    signal<WorkflowRegistryDiagnosticDisplayError | null>(null);
  readonly operationError =
    signal<WorkflowRegistryDiagnosticDisplayError | null>(null);
  readonly failedDraftLoad = signal<FailedDraftLoad | null>(null);
  readonly draftReadError =
    signal<WorkflowRegistryDiagnosticDisplayError | null>(null);
  @ViewChild('draftReadErrorSummary')
  private draftReadErrorSummary?: ElementRef<HTMLElement>;
  readonly draftReadState = computed(() =>
    isReadAccessDenied(this.draftReadError()) ? 'forbidden' : 'error',
  );
  readonly draftReadMessage = computed(() => {
    if (isReadAccessDenied(this.draftReadError()))
      return 'You do not have permission to view workflow drafts. Contact an administrator if you need access.';
    if (this.draftReadError()?.statusCode === 404)
      return 'The selected draft is no longer available. Its stale link was cleared; choose an available draft or clone a published version to continue.';
    return this.currentDraft()
      ? 'The selected draft could not be loaded. Your current draft and pending edits remain available. Try again.'
      : 'The selected draft could not be loaded. Try again.';
  });
  readonly currentDraft = signal<WorkflowDefinitionDraftDetail | null>(null);
  readonly workingDocument = signal<WorkflowDefinitionDocument | null>(null);
  readonly documentDirty = signal(false);
  readonly dryRunOpened = signal(false);
  readonly dryRunExpanded = signal(false);
  @ViewChild('dryRunSection')
  private dryRunSection?: ElementRef<HTMLDetailsElement>;
  readonly nodeFormDirty = signal(false);
  readonly inlineStepNameEditing = signal(false);
  readonly inlineStepNameError = signal('');
  @ViewChild('inlineStepNameInput')
  private inlineStepNameInput?: ElementRef<HTMLInputElement>;
  @ViewChild('renameStepButton')
  private renameStepButton?: ElementRef<HTMLButtonElement>;
  private inlineStepNameNodeId = '';
  private inlineStepNameOriginal: string | null = null;
  readonly canInlineRenameStep = computed(() => {
    const node = this.selectedMapNode();
    return this.canAuthor() && !this.isAddingStep() &&
      !this.isDraftEditorInteractionBlocked() &&
      this.mapSelection()?.isAuthorable === true &&
      node?.typeCode === 'human_step' &&
      this.workingDocument()?.nodes.filter((item) => item.id === node.id).length === 1;
  });
  readonly edgeFormDirty = signal(false);
  readonly reviewRoutingDirty = signal(false);
  readonly dashboardEditorDirty = signal(false);
  readonly staffPageEditorDirty = signal(false);
  readonly workComponentsDirty = signal(false);
  @ViewChild('workComponentsEditor')
  private workComponentsEditor?: WorkflowStepComponentsEditorComponent;
  @ViewChildren(WorkflowPresentationEditorComponent)
  private pageEditors?: QueryList<WorkflowPresentationEditorComponent>;
  private noticeRequirementFocusRequest = 0;
  private dashboardNameSettlementToken: {
    editor: WorkflowPresentationEditorComponent; owner: string; source: string; view: string | null;
    nodeId: string; draft: string; accepted: boolean; rejected: boolean;
  } | null = null;
  readonly transitionFormDirty = signal(false);
  readonly decisionFormDirty = signal(false);
  readonly requirementFormDirty = signal(false);
  readonly childWorkFormDirty = signal(false);
  @ViewChild(WorkflowStepChildWorkEditorComponent)
  private childWorkEditor?: WorkflowStepChildWorkEditorComponent;
  readonly timerFormDirty = signal(false);
  @ViewChild(WorkflowStepTimersEditorComponent)
  private timersEditor?: WorkflowStepTimersEditorComponent;
  private timerFocusRequest = 0;
  private timerSettlement: {
    editor: WorkflowStepTimersEditorComponent; owner: string; source: string; view: string | null; nodeId: string;
    draft: string; accepted: boolean; rejected: boolean;
  } | null = null;
  readonly notificationFormDirty = signal(false);
  readonly notificationReturnContext = signal<{
    draftId: number;
    nodeId: string;
    eventIdentity: string | null;
  } | null>(null);
  readonly canReturnToNotifications = computed(() => {
    const target = this.notificationReturnContext();
    return (
      !!target &&
      target.draftId === this.currentDraft()?.id &&
      target.nodeId === this.selectedMapNode()?.id &&
      this.selectedMapNode()?.typeCode === 'human_step' &&
      this.stepWorkspaceScope() === 'behavior' &&
      this.behaviorSection() === 'transitions'
    );
  });
  readonly assignmentPolicyFormDirty = signal(false);
  readonly processDataFormDirty = signal(false);
  readonly automationFormDirty = signal(false);
  readonly workflowStatusFormDirty = signal(false);
  @ViewChild('workflowStatusEditor') private workflowStatusEditor?: WorkflowStatusEditorComponent;
  private workflowStatusNavigation = 0;
  private workflowStatusOrigin: {
    owner: string; label: string; location: WorkflowDesignerLocation; selection: WorkflowReadOnlyGraphSelection | null;
    selectionValue: string | null; focusedNodeId: string | null; focusedRoute: WorkflowReadOnlyGraphSelection | null;
    focusedRouteValue: string | null; focus: HTMLElement | null; camera: WorkflowDraftGraphPresentationState | null;
    scroll: { element: HTMLElement; top: number; left: number }[];
  } | null = null;
  readonly automatedStepFormDirty = signal(false);
  @ViewChild(WorkflowAutomatedStepEditorComponent)
  private automatedStepEditor?: WorkflowAutomatedStepEditorComponent;
  readonly automationExpanded = signal(false);
  @ViewChild(WorkflowAutomationEditorComponent)
  private automationEditor?: WorkflowAutomationEditorComponent;
  @ViewChild('automationDetails')
  private automationDetails?: ElementRef<HTMLElement>;
  private readonly automationUndoHistory = signal<
    { before: WorkflowDefinitionDocument; after: string }[]
  >([]);
  private recordingAutomationEdit = false;
  readonly canUndoAutomation = computed(
    () =>
      !!this.automationUndoHistory().length &&
      this.canAuthor() &&
      !this.isDraftEditorInteractionBlocked() &&
      !this.hasUnappliedEditorChanges() &&
      this.automationUndoHistory().at(-1)?.after ===
        serializeDocument(this.workingDocument()!),
  );
  readonly navigationFormDirty = signal(false);
  @ViewChild(WorkflowNavigationSettingsComponent)
  private navigationSettingsEditor?: WorkflowNavigationSettingsComponent;
  private navigationSettlement: {
    editor: WorkflowNavigationSettingsComponent; owner: string; source: string; view: string | null;
    draft: string; accepted: boolean; rejected: boolean;
  } | null = null;
  private dashboardRemovalPending = false;
  @ViewChild(WorkflowProcessDataEditorComponent)
  private processDataEditor?: WorkflowProcessDataEditorComponent;
  private processDataFocusRequest = 0;
  private processDataFocusBarrier = 0;
  private workflowSettingsRevealGeneration = 0;
  private processDataSettlement: {
    editor: WorkflowProcessDataEditorComponent; owner: string; source: string; view: string | null;
    draft: string; accepted: boolean; rejected: boolean;
  } | null = null;
  private childWorkSettlement: {
    editor: WorkflowStepChildWorkEditorComponent; owner: string; source: string; view: string | null; nodeId: string;
    draft: string; accepted: boolean; rejected: boolean;
  } | null = null;
  private bypassDataGeneration = 0;
  private bypassDataLease: {
    generation: number; parent: PlatformWorkflowDraftTransitionEditorComponent; namespace: string;
    origin: WorkflowStepBypassDataOwnerOrigin; source: string; location: WorkflowDesignerLocation;
    camera: WorkflowDraftGraphPresentationState | null;
    progressReceipt?: { source: string; configuration: string; reference: string };
  } | null = null;
  readonly stepAssignmentPolicyFormDirty = signal(false);
  readonly authoringFormDirty = signal(false);
  readonly recoveryDocument = signal<WorkflowDefinitionDocument | null>(null);
  readonly hasReloadedLatestAfterConflict = signal(false);
  private readonly recoveryBase = signal<WorkflowDefinitionDocument | null>(
    null,
  );
  readonly recoveryChoices = signal<Record<string, DraftRecoveryChoice>>({});
  readonly recoveryStorageWarning = signal<string | null>(null);
  private recoveryStorageKey: string | null = null;
  private recoveryStorageOwnedValue: { key: string; value: string | null } | null = null;
  private recoveryOwnerIdentity: string | null = null;
  readonly recoveryComparison = computed(() => {
    const base = this.recoveryBase(),
      latest = this.currentDraft()?.document,
      local = this.recoveryDocument();
    return base && latest && local && this.hasReloadedLatestAfterConflict()
      ? reconcileWorkflowDraft(base, latest, local, this.recoveryChoices())
      : null;
  });
  readonly authoringMode = signal<AuthoringMode | null>(null);
  readonly authoringMessage = signal<string | null>(null);
  readonly editorMessage = signal<string | null>(null);
  readonly selectedNodeId = signal<string | null>(null);
  readonly isAddingStep = signal(false);
  newStepFrom = '';
  newStepTo = '';
  newStepPlacementMode: 'path' | 'connection' | 'unconnected' = 'unconnected';
  newStepPathId = '';
  newStepPlacementContextLabel = '';
  private newStepPlacementContext: StepPlacementContext = { kind: 'global' };
  private newStepPathSnapshot: {
    draftId: number;
    fingerprint: string;
    fromNodeId: string;
    toNodeId: string;
    label: string;
  } | null = null;
  private customNodeId = false;
  private customStepCode = false;
  readonly newStepSources = computed(() =>
    this.workingDocument() ? availableStepSources(this.workingDocument()!) : [],
  );
  readonly newStepUnfinishedSources = computed(() =>
    this.newStepSources().filter(
      (node) =>
        !this.workingDocument()?.edges.some(
          (edge) => edge?.fromNodeId === node.id,
        ),
    ),
  );
  readonly newStepPlacementPaths = computed(() =>
    this.workingDocument()
      ? workflowStepPlacementPaths(this.workingDocument()!)
      : [],
  );
  readonly newStepDestinations = computed(() =>
    this.nodes().filter((n) =>
      ['human_step', 'automated_step', 'terminal'].includes(n.typeCode),
    ),
  );
  readonly validationHandoff = signal<{
    diagnostic: WorkflowDefinitionDiagnostic;
    revision: number;
    findingId?: string;
  } | null>(null);
  readonly semanticReviewHandoff = signal<{
    summary: string;
    revision: number;
  } | null>(null);
  readonly authoringReferences =
    signal<WorkflowDefinitionAuthoringReferences | null>(null);
  readonly isLoadingAuthoringReferences = signal(false);
  readonly authoringReferencesError =
    signal<WorkflowRegistryDiagnosticDisplayError | null>(null);
  readonly structuredEditorResetVersion = signal(0);
  readonly behaviorResponsibilityFocus = signal<{
    request: number;
    dashboardId?: string;
  } | null>(null);
  readonly behaviorFormFocus = signal<{
    capability: StepCommand['capability'];
    request: number;
  } | null>(null);
  readonly notificationMessageFocus = signal<{
    path: string;
    nodeId: string;
    savedDocument?: WorkflowDefinitionDocument;
  } | null>(null);
  readonly stepActionFocus = signal<{
    capability: StepCommand['capability'];
    request: number;
    outcomes?: boolean;
    outcomeCode?: string;
  } | null>(null);
  readonly behaviorUndoHistory = signal<
    {
      before: WorkflowDefinitionDocument;
      after: string;
      nodeId: string;
      scope: 'behavior' | 'dashboard' | 'detail';
    }[]
  >([]);
  private recordingBehaviorEdit = false;
  readonly guardBehaviorSelection = () =>
    !this.stepAssignmentPolicyFormDirty() || this.flushPendingEditorChanges();
  readonly behaviorResponsibilitySummary = computed(() => {
    const item = this.selectedStepChecklist().find(
      (item) => item.id === 'responsibility',
    );
    if (item?.status === 'attention') return 'Responsibility needs attention';
    const policy = this.workingDocument()?.assignmentPolicies?.find(
      (policy) =>
        policy?.scopeCode === 'step' &&
        policy.nodeId === this.selectedMapNode()?.id,
    );
    if (!policy) return 'Use existing assignment behavior';
    if (policy.assignmentBasisCode === 'roles') {
      return `Assignment roles: ${
        (policy.eligibleRoleCodes ?? [])
          .filter((code): code is string => !!code)
          .map((code) =>
            code.replace(/^role[._-]/i, '').replace(/[._-]+/g, ' '),
          )
          .join(', ') || 'Choose roles'
      }`;
    }
    const modes: Record<string, string> = {
      queue_only: 'Shared team inbox',
      claim: 'Staff claim work',
      direct: 'Assign to a person',
      claim_or_direct: 'Staff claim or receive work',
    };
    const queueCode = policy.initialQueueCode;
    const queues = this.authoringReferences()?.queueCodes;
    const queueLabel = !queueCode
      ? 'Choose a team queue'
      : queues?.includes(queueCode)
        ? queueCode
            .replace(/^queue[._-]/i, '')
            .split(/[._-]/)
            .filter(Boolean)
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
            .join(' ')
        : `${queueCode}${queues ? ' (Unavailable)' : ''}`;
    return `${queueLabel} · ${modes[policy.assignmentModeCode] || policy.assignmentModeCode}`;
  });
  readonly canUndoBehavior = computed(() => {
    const previous = this.behaviorUndoHistory().at(-1);
    return (
      !!previous &&
      this.canAuthor() &&
      !this.isDraftEditorInteractionBlocked() &&
      !this.hasUnappliedEditorChanges() &&
      previous.nodeId === this.selectedMapNode()?.id &&
      previous.scope === this.stepWorkspaceScope() &&
      previous.after === serializeDocument(this.workingDocument()!)
    );
  });
  readonly draftQuery = signal<WorkflowDefinitionDraftQuery>({
    pageNumber: 1,
    pageSize: DEFAULT_DRAFT_PAGE_SIZE,
  });

  onDefinitionPublished(detail: WorkflowDefinitionLifecycleDetail): void {
    this.clearCurrentDraft(false);
    this.loadDrafts();
    this.definitionPublished.emit({
      workflowCode: detail.workflowCode,
      definitionVersionCode: detail.definitionVersionCode,
    });
  }

  readonly hasUnsavedChanges = computed(
    () =>
      this.archivedBufferPending() ||
      this.documentDirty() ||
      this.nodeFormDirty() ||
      this.edgeFormDirty() ||
      this.reviewRoutingDirty() ||
      this.dashboardEditorDirty() ||
      this.staffPageEditorDirty() ||
      this.workComponentsDirty() ||
      this.transitionFormDirty() ||
      this.decisionFormDirty() ||
      this.requirementFormDirty() ||
      this.timerFormDirty() ||
      this.childWorkFormDirty() ||
      this.notificationFormDirty() ||
      this.assignmentPolicyFormDirty() ||
      this.processDataFormDirty() ||
      this.automationFormDirty() ||
      this.workflowStatusFormDirty() ||
      this.automatedStepFormDirty() ||
      this.navigationFormDirty() ||
      this.stepAssignmentPolicyFormDirty() ||
      this.authoringFormDirty() ||
      this.recoveryDocument() !== null,
  );
  readonly hasOpenDraftUnsavedChanges = computed(
    () =>
      this.archivedBufferPending() ||
      this.documentDirty() ||
      this.nodeFormDirty() ||
      this.edgeFormDirty() ||
      this.reviewRoutingDirty() ||
      this.dashboardEditorDirty() ||
      this.staffPageEditorDirty() ||
      this.workComponentsDirty() ||
      this.transitionFormDirty() ||
      this.decisionFormDirty() ||
      this.requirementFormDirty() ||
      this.timerFormDirty() ||
      this.childWorkFormDirty() ||
      this.notificationFormDirty() ||
      this.assignmentPolicyFormDirty() ||
      this.processDataFormDirty() ||
      this.automationFormDirty() ||
      this.workflowStatusFormDirty() ||
      this.automatedStepFormDirty() ||
      this.navigationFormDirty() ||
      this.stepAssignmentPolicyFormDirty() ||
      this.recoveryDocument() !== null,
  );
  private flushingPendingChanges = false;
  readonly hasUnappliedEditorChanges = computed(
    () =>
      this.nodeFormDirty() ||
      this.edgeFormDirty() ||
      this.reviewRoutingDirty() ||
      this.dashboardEditorDirty() ||
      this.staffPageEditorDirty() ||
      this.workComponentsDirty() ||
      this.transitionFormDirty() ||
      this.decisionFormDirty() ||
      this.requirementFormDirty() ||
      this.timerFormDirty() ||
      this.childWorkFormDirty() ||
      this.notificationFormDirty() ||
      this.assignmentPolicyFormDirty() ||
      this.processDataFormDirty() ||
      this.automationFormDirty() ||
      this.workflowStatusFormDirty() ||
      this.automatedStepFormDirty() ||
      this.navigationFormDirty() ||
      this.stepAssignmentPolicyFormDirty(),
  );
  readonly isDraftMutationInProgress = computed(
    () =>
      this.isSaving() ||
      this.isValidating() ||
      this.isRunningAuthoringAction() ||
      this.lifecycleOperationInProgress(),
  );
  readonly isDraftEditorInteractionBlocked = computed(
    () =>
      this.registryReadsSuspended() ||
      this.isDraftMutationInProgress() ||
      this.isLoadingDraft() ||
      this.authoringMode() !== null ||
      (!this.isArchivedDraft() && this.recoveryDocument() !== null),
  );
  readonly areRecoveryActionsBlocked = computed(
    () =>
      !this.isRecoveryOwnerCurrent() ||
      this.registryReadsSuspended() ||
      this.isDraftMutationInProgress() ||
      this.isLoadingDraft() ||
      this.authoringMode() !== null ||
      !this.hasReloadedLatestAfterConflict(),
  );
  readonly nodes = computed(() =>
    [...(this.workingDocument()?.nodes ?? [])].sort(
      (left, right) =>
        left.displayOrder - right.displayOrder ||
        left.id.localeCompare(right.id),
    ),
  );
  readonly entryNodes = computed(() =>
    this.nodes().filter((node) => node.typeCode === 'entry'),
  );
  readonly humanStepNodes = computed(() =>
    this.nodes().filter((node) => node.typeCode === 'human_step'),
  );
  readonly automatedStepNodes = computed(() =>
    this.nodes().filter((node) => node.typeCode === 'automated_step'),
  );
  private readonly designerMapGraph = computed(() => {
    const document = this.workingDocument();
    return document ? projectWorkflowDraftGraph(document) : null;
  });
  readonly designerOutlineEntries = computed<readonly WorkflowDesignerOutlineEntry[]>(() => {
    const graph = this.designerMapGraph();
    const authored = graph?.nodes.filter(node => node.sourceIndex !== null) ?? [];
    const counts = new Map<string, number>();
    for (const node of authored) counts.set(node.sourceId, (counts.get(node.sourceId) ?? 0) + 1);
    const types: Record<string, [string, string]> = {
      'human-step': ['Staff task', 'pi-user'], 'automated-step': ['Automated', 'pi-bolt'],
      terminal: ['End', 'pi-flag'], 'routing-decision': ['Decision', 'pi-share-alt'],
    };
    return authored.filter(node => node.kind !== 'entry').sort((a, b) =>
      (a.displayOrder ?? Number.MAX_SAFE_INTEGER) - (b.displayOrder ?? Number.MAX_SAFE_INTEGER) ||
      (a.sourceIndex ?? 0) - (b.sourceIndex ?? 0)).map(node => {
      const inspectable = !!node.sourceId && counts.get(node.sourceId) === 1;
      const step = this.stepIssueGroups().find(group => group.step.sourceIndex === node.sourceIndex);
      return {
        key: node.id, nodeId: inspectable ? node.sourceId : null, label: node.label,
        typeLabel: types[node.kind]?.[0] ?? 'Retained entry', icon: types[node.kind]?.[1] ?? 'pi-info-circle',
        issueCount: step?.issues.length ?? (inspectable ? this.designerSavedNodeIssues(node.sourceId).length : 0),
        inspectable,
      };
    });
  });
  readonly designerStartEntries = computed(() => {
    const graph = this.designerMapGraph();
    if (!graph) return [];
    return graph.nodes.filter(node => node.kind === 'entry').map(entry => {
      const paths = graph.edges.filter(edge => edge.source === entry.id);
      return { key: entry.id, label: entry.label, detail: paths.length
        ? paths.map(path => path.toDisplayValue).join(', ') : 'No outgoing path recorded' };
    });
  });
  readonly designerEndCount = computed(() => this.designerMapGraph()?.nodes.filter(node => node.kind === 'terminal').length ?? 0);
  readonly designerIssueGroups = computed(() => this.stepIssueGroups().filter(group => group.issues.length));

  private designerSavedNodeIssues(nodeId: string): WorkflowDefinitionDiagnostic[] {
    if (this.currentDraft()?.document.nodes.filter(node => node?.id === nodeId).length !== 1 ||
      this.workingDocument()?.nodes.filter(node => node?.id === nodeId).length !== 1) return [];
    return this.diagnostics().filter(item => ['error', 'warning'].includes(item.severityCode) &&
      ((item.locationTypeCode === 'node' && item.locationId === nodeId) || item.path.startsWith(`$.nodes[id=${nodeId}]`)));
  }

  openOutlineIssues(nodeId: string): void {
    const group = this.stepIssueGroups().find(item => item.step.nodeId === nodeId);
    if (group) this.openStepIssues(group.step);
    else {
      const diagnostic = this.designerSavedNodeIssues(nodeId)[0];
      if (diagnostic) this.openSavedStepIssue(diagnostic); else this.inspectStepFromOutline(nodeId);
    }
  }

  private addStepMenuContext: string | null = null;
  captureAddStepMenuContext(): void { this.addStepMenuContext = this.addStepContext(); }
  private addStepContext(): string {
    return JSON.stringify([this.saveContextKey(), this.workingDocument(), this.mapSelection(),
      this.canAuthor(), this.isArchivedDraft()]);
  }
  addStepFromMenu(kind: 'work' | 'automated'): void {
    if (!this.addStepMenuContext || this.addStepMenuContext !== this.addStepContext() ||
      !this.canAuthor() || this.isArchivedDraft() || this.isDraftEditorInteractionBlocked()) return;
    this.addStepMenuContext = null;
    if (kind === 'work') this.beginAddMapStep(); else this.beginAddAutomatedStep();
  }

  readonly workflowOverview = computed(() =>
    workflowOverviewPresentation(this.workingDocument()),
  );
  private readonly retainedIssueStep = signal<{
    draftId: number;
    key: string;
  } | null>(null);
  readonly stepIssueGroups = computed(() =>
    this.workflowOverview().steps.map((step) => ({
      step,
      issues: [
        ...step.attention.map(
          (finding): WorkflowStepIssue => ({
            key: `local:${finding.key}`,
            source: 'local',
            finding,
          }),
        ),
        ...this.diagnosticsForStep(step.nodeId)
          .filter((diagnostic) =>
            ['error', 'warning'].includes(diagnostic.severityCode),
          )
          .map(
            (diagnostic, index): WorkflowStepIssue => ({
              key: `saved:${index}:${diagnostic.path}`,
              source: 'saved',
              diagnostic,
            }),
          ),
      ],
    })),
  );
  readonly selectedStepIssueGroup = computed(() => {
    if (this.isAddingStep()) return null;
    const selection = this.mapSelection();
    const retained = this.retainedIssueStep();
    return (
      this.stepIssueGroups().find(({ step }) =>
        selection?.kind === 'node'
          ? step.sourceIndex === selection.sourceIndex
          : !selection &&
            !!retained &&
            retained.draftId === this.currentDraft()?.id &&
            step.key === retained.key,
      ) ?? null
    );
  });
  readonly terminalNodes = computed(() =>
    this.nodes().filter((node) => node.typeCode === 'terminal'),
  );
  readonly selectedNode = computed(() => {
    const selectedNodeId = this.selectedNodeId();
    return (
      this.workingDocument()?.nodes.find(
        (node) => node.id === selectedNodeId,
      ) ?? null
    );
  });
  readonly selectedMapNode = computed(() => {
    const selection = this.mapSelection();
    const document = this.workingDocument();
    if (
      selection?.kind !== 'node' ||
      selection.sourceIndex === null ||
      !document
    ) {
      return null;
    }
    const projected = projectWorkflowDraftGraph(document).nodes.find(
      (candidate) =>
        candidate.sourceIndex === selection.sourceIndex &&
        candidate.sourceId === selection.sourceId,
    );
    return projected ? (document.nodes[selection.sourceIndex] ?? null) : null;
  });
  readonly stepHasDecisionChecks = computed(() => {
    const node = this.selectedMapNode();
    return (
      node?.typeCode === 'human_step' &&
      (!!node.requiredDecisions?.length ||
        (node.allowedTransitions ?? []).some(
          (transition) =>
            !!transition?.requiredDecisions?.length ||
            !!transition?.requiredDecisionKinds?.length,
        ))
    );
  });
  readonly selectedMapEdge = computed(() => {
    const selection = this.mapSelection();
    const document = this.workingDocument();
    if (
      selection?.kind !== 'edge' ||
      selection.sourceIndex === null ||
      !document
    ) {
      return null;
    }
    const projected = projectWorkflowDraftGraph(document).edges.find(
      (candidate) =>
        candidate.sourceIndex === selection.sourceIndex &&
        candidate.sourceId === selection.sourceId,
    );
    return projected ? (document.edges[selection.sourceIndex] ?? null) : null;
  });
  readonly selectedMapRendererId = computed(() => {
    const selection = this.mapSelection();
    const document = this.workingDocument();
    if (!selection || !document) {
      return null;
    }
    const graph = projectWorkflowDraftGraph(document);
    const candidates = selection.kind === 'node' ? graph.nodes : graph.edges;
    return (
      candidates.find(
        (candidate) =>
          candidate.sourceIndex === selection.sourceIndex &&
          candidate.sourceId === selection.sourceId,
      )?.id ?? null
    );
  });
  readonly mapContextSupportsBehavior = computed(() => {
    const selection = this.mapSelection();
    return (
      !selection ||
      (selection.kind === 'node' &&
        selection.isAuthorable &&
        this.selectedMapNode()?.typeCode === 'human_step')
    );
  });
  readonly mapInspectorTitle = computed(() => {
    if (this.isAddingStep()) {
      return 'New work step';
    }
    const selection = this.mapSelection();
    if (!selection) {
      return 'Workflow overview';
    }
    if (selection.kind === 'node') {
      const node = this.selectedMapNode();
      return (
        node?.displayName ||
        node?.stepCode ||
        selection.sourceId ||
        'Invalid map node'
      );
    }
    const edge = this.selectedMapEdge();
    return edge
      ? `${this.nodeDisplayLabel(edge.fromNodeId)} to ${this.nodeDisplayLabel(edge.toNodeId)}`
      : selection.sourceId || 'Invalid map path';
  });
  readonly mapInspectorTypeLabel = computed(() => {
    if (this.isAddingStep()) {
      return 'Work step';
    }
    const selection = this.mapSelection();
    if (!selection) {
      return 'Workflow behavior';
    }
    if (selection.kind === 'edge') {
      return 'Map path';
    }
    return this.nodeTypeLabel(this.selectedMapNode());
  });
  readonly selectedHumanStepPosition = computed(() => {
    const selection = this.mapSelection();
    const selectedMapNode = this.selectedMapNode();
    if (
      selection?.kind !== 'node' ||
      !selection.isAuthorable ||
      selectedMapNode?.typeCode !== 'human_step'
    ) {
      return null;
    }
    const index = this.humanStepNodes().findIndex(
      (node) => node === selectedMapNode,
    );
    return index === -1 ? null : index + 1;
  });
  readonly selectedMapStepIsInherited = computed(() => {
    const selection = this.mapSelection();
    const selectedMapNode = this.selectedMapNode();
    return (
      selection?.kind === 'node' &&
      selection.isAuthorable &&
      selectedMapNode?.typeCode === 'human_step' &&
      this.isInheritedStep(selectedMapNode.id)
    );
  });
  readonly routingPathCount = computed(
    () => this.workingDocument()?.edges.length ?? 0,
  );
  readonly routingStateChangeCount = computed(() => {
    const document = this.workingDocument();
    if (!document) {
      return 0;
    }

    return (
      (document.workflowTransitions?.length ?? 0) +
      document.nodes.reduce(
        (count, node) => count + (node.allowedTransitions?.length ?? 0),
        0,
      )
    );
  });
  readonly workflowStatusChangeCount = computed(
    () => this.workingDocument()?.workflowTransitions?.length ?? 0,
  );
  readonly workflowDecisionRuleCount = computed(() => {
    const document = this.workingDocument();
    if (!document) {
      return 0;
    }

    return (
      this.decisionRequirementCount(document.requiredDecisions) +
      (Array.isArray(document.workflowTransitions) ? document.workflowTransitions : []).reduce(
        (count, transition) =>
          count +
          (transition
            ? this.decisionRequirementCount(
                transition.requiredDecisions,
                transition.requiredDecisionKinds,
              )
            : 0),
        0,
      )
    );
  });
  readonly mapInspectorStatusChangeCount = computed(() =>
    this.selectedMapNode()?.typeCode === 'human_step'
      ? (this.selectedMapNode()?.allowedTransitions?.length ?? 0)
      : this.workflowStatusChangeCount(),
  );
  readonly mapInspectorDecisionRuleCount = computed(() =>
    this.selectedMapNode()?.typeCode === 'human_step'
      ? this.stepDecisionRuleCount(this.selectedMapNode())
      : this.workflowDecisionRuleCount(),
  );
  readonly canMoveSelectedStepEarlier = computed(() => {
    const position = this.selectedHumanStepPosition();
    return (
      this.canAuthor() &&
      position !== null &&
      position > 1 &&
      !this.nodeFormDirty() &&
      !(this.edgeFormDirty() || this.reviewRoutingDirty()) &&
      !this.dashboardEditorDirty() &&
      !this.staffPageEditorDirty() &&
      !this.workComponentsDirty() &&
      !this.transitionFormDirty() &&
      !this.decisionFormDirty() &&
      !this.requirementFormDirty() &&
      !this.timerFormDirty() &&
      !this.childWorkFormDirty() &&
      !this.notificationFormDirty() &&
      !this.isDraftEditorInteractionBlocked()
    );
  });
  readonly canMoveSelectedStepLater = computed(() => {
    const position = this.selectedHumanStepPosition();
    return (
      this.canAuthor() &&
      position !== null &&
      position < this.humanStepNodes().length &&
      !this.nodeFormDirty() &&
      !(this.edgeFormDirty() || this.reviewRoutingDirty()) &&
      !this.dashboardEditorDirty() &&
      !this.staffPageEditorDirty() &&
      !this.workComponentsDirty() &&
      !this.transitionFormDirty() &&
      !this.decisionFormDirty() &&
      !this.requirementFormDirty() &&
      !this.timerFormDirty() &&
      !this.childWorkFormDirty() &&
      !this.notificationFormDirty() &&
      !this.isDraftEditorInteractionBlocked()
    );
  });
  readonly selectedStepCanBeRemoved = computed(
    () =>
      this.canAuthor() &&
      this.selectedNode()?.typeCode === 'human_step' &&
      this.inheritedStepCode(this.selectedNode()!.id) === null &&
      !(this.edgeFormDirty() || this.reviewRoutingDirty()) &&
      !this.dashboardEditorDirty() &&
      !this.staffPageEditorDirty() &&
      !this.workComponentsDirty() &&
      !this.transitionFormDirty() &&
      !this.decisionFormDirty() &&
      !this.requirementFormDirty() &&
      !this.timerFormDirty() &&
      !this.childWorkFormDirty() &&
      !this.notificationFormDirty() &&
      !this.assignmentPolicyFormDirty() &&
      !this.stepAssignmentPolicyFormDirty(),
  );
  readonly diagnostics = computed(
    () => this.currentDraft()?.validationDiagnostics ?? [],
  );
  readonly reviewDiagnosticGroups = computed(() =>
    workflowReviewFindingGroups(
      this.currentDraft()?.document ?? null,
      this.diagnostics(),
    ),
  );
  readonly reviewFindings = computed(() =>
    this.reviewDiagnosticGroups().flatMap((group) => group.findings),
  );
  readonly firstReviewFinding = computed(() => {
    const actionable = this.reviewFindings().filter((finding) =>
      this.canNavigateReviewFinding(finding.diagnostic),
    );
    return actionable.find((finding) => finding.diagnostic.severityCode === 'error')
      ?? actionable[0] ?? null;
  });
  readonly reviewCorrectionPosition = computed(() => {
    const handoff = this.validationHandoff();
    if (!handoff || handoff.revision !== this.currentDraft()?.revision)
      return null;
    const index = this.reviewFindings().findIndex(
      (finding) => finding.id === handoff.findingId,
    );
    return index < 0
      ? null
      : { index: index + 1, total: this.reviewFindings().length };
  });
  readonly nextReviewFinding = computed(() => {
    const handoff = this.validationHandoff();
    if (handoff && !this.reviewCorrectionPosition()) return null;
    return nextWorkflowReviewFinding(
      this.reviewDiagnosticGroups(),
      handoff?.findingId ?? null,
      (finding) => this.canNavigateReviewFinding(finding.diagnostic),
    );
  });
  readonly reviewFindingCountLabel = computed(
    () =>
      ['error', 'warning', 'other']
        .map((severity) => {
          const count = this.diagnostics().filter((finding) =>
            severity === 'other'
              ? !['error', 'warning'].includes(finding.severityCode)
              : finding.severityCode === severity,
          ).length;
          const noun =
            severity === 'error'
              ? 'error'
              : severity === 'warning'
                ? 'warning'
                : 'other finding';
          return count ? `${count} ${noun}${count === 1 ? '' : 's'}` : null;
        })
        .filter(Boolean)
        .join(' · ') || 'No recorded findings',
  );

  readonly selectedStepChecklist = computed(() =>
    workflowStepChecklist(
      this.workingDocument(),
      this.selectedMapNode()?.id ?? null,
    ),
  );
  readonly checklistStatusLabels = {
    configured: 'Recorded',
    attention: 'Needs attention',
    inspect: 'Inspect',
    optional: 'Not added',
  };
  readonly selectedStepDiagnostics = computed(() => {
    const nodeId = this.selectedMapNode()?.id;
    return this.diagnosticsForStep(nodeId ?? null);
  });

  private diagnosticsForStep(
    nodeId: string | null,
  ): WorkflowDefinitionDiagnostic[] {
    const saved = this.currentDraft()?.document;
    // Findings belong to the saved revision. Never attach them by outline position
    // or guess between duplicate identities in either copy.
    if (
      !nodeId ||
      saved?.nodes.filter(
        (node) => node?.id === nodeId && node.typeCode === 'human_step',
      ).length !== 1 ||
      this.workingDocument()?.nodes.filter(
        (node) => node?.id === nodeId && node.typeCode === 'human_step',
      ).length !== 1
    )
      return [];
    const policyIds = (this.currentDraft()?.document.assignmentPolicies ?? [])
      .filter(
        (policy) => policy?.scopeCode === 'step' && policy.nodeId === nodeId,
      )
      .map((policy) => policy!.id);
    return this.diagnostics().filter(
      (diagnostic) =>
        (diagnostic.locationTypeCode === 'node' &&
          diagnostic.locationId === nodeId) ||
        diagnostic.path.startsWith(`$.nodes[id=${nodeId}]`) ||
        (diagnostic.locationTypeCode === 'assignment_policy' &&
          !!diagnostic.locationId &&
          policyIds.includes(diagnostic.locationId)) ||
        policyIds.some((id) =>
          diagnostic.path.startsWith(`$.assignmentPolicies[id=${id}]`),
        ) ||
        (diagnostic.locationTypeCode === 'edge' &&
          saved.edges.filter((edge) => edge?.id === diagnostic.locationId)
            .length === 1 &&
          saved.edges.some(
            (edge) =>
              edge?.id === diagnostic.locationId && edge.fromNodeId === nodeId,
          )),
    );
  }
  readonly draftChangeLabel = computed(() => {
    if (this.isArchivedDraft())
      return this.hasOpenDraftUnsavedChanges()
        ? 'Local copy retained'
        : 'Archived · read only';
    if (this.isSaving()) {
      return 'Saving draft';
    }
    if (this.isLoadingDraft()) {
      return 'Loading draft';
    }
    if (this.recoveryDocument()) {
      return this.hasReloadedLatestAfterConflict()
        ? 'Conflict copy ready to review'
        : 'Save conflict';
    }
    if (this.hasUnappliedEditorChanges() || this.documentDirty()) {
      return 'Unsaved changes';
    }
    return 'All changes saved';
  });
  readonly draftChangeDetail = computed(() => {
    if (this.isArchivedDraft())
      return this.hasOpenDraftUnsavedChanges()
        ? 'Download the retained local document and pending fields before leaving. This archived draft cannot be saved.'
        : 'This is the retained source draft. Create or continue a draft from its published version to make changes.';
    if (this.isSaving()) {
      return 'Saving your changes. Wait for confirmation before leaving this draft.';
    }
    if (this.isLoadingDraft()) {
      return 'Loading the latest saved draft revision.';
    }
    if (this.recoveryDocument()) {
      return this.hasReloadedLatestAfterConflict()
        ? 'Independent edits are combined. Choose which edit to keep for any setting changed in both copies.'
        : 'Reload the latest saved revision to continue conflict recovery.';
    }
    if (this.hasUnappliedEditorChanges()) {
      return 'Save includes valid edits in open forms. Any incomplete field will be shown before saving.';
    }
    if (this.documentDirty()) {
      return this.activeWorkbenchView() === 'map'
        ? 'Your changes are shown in the map and ready to save.'
        : 'Your changes are ready to save to this draft.';
    }
    return 'The working document matches the saved draft revision.';
  });
  readonly validationStateLabel = computed(() => {
    if (this.isValidating()) return 'Checking saved revision';
    if (this.automaticValidationFailed()) return 'Checks unavailable';
    const statusCode = this.currentDraft()?.validationStatusCode;
    switch (statusCode) {
      case 'valid':
        return 'Valid';
      case 'invalid':
        return 'Validation issues';
      default:
        return 'Not validated';
    }
  });
  readonly reviewValidationDetail = computed(() => {
    const draft = this.currentDraft();
    if (!draft) {
      return 'No saved draft is open.';
    }
    if (this.isValidating())
      return `Checking saved revision ${draft.revision} automatically.`;
    if (this.automaticValidationFailed())
      return `The checks for saved revision ${draft.revision} could not finish. Retry the checks before publishing.`;

    const issueCount = this.diagnostics().length;
    let detail: string;
    switch (draft.validationStatusCode) {
      case 'valid':
        detail = `Saved revision ${draft.revision} passed validation.`;
        break;
      case 'invalid':
        detail =
          `Saved revision ${draft.revision} has ${issueCount} validation ` +
          `issue${issueCount === 1 ? '' : 's'}.`;
        break;
      default:
        detail = `Saved revision ${draft.revision} has not been validated.`;
        break;
    }

    return this.hasOpenDraftUnsavedChanges()
      ? `${detail} Local changes are not included.`
      : detail;
  });
  readonly reviewSavedStateLabel = computed(() =>
    this.hasOpenDraftUnsavedChanges()
      ? 'Local work pending'
      : 'Saved copy is current',
  );
  readonly reviewSavedStateDetail = computed(() => {
    if (this.isArchivedDraft())
      return 'These checks describe the retained saved revision. Archived drafts cannot be changed, checked again or republished.';
    if (this.hasUnappliedEditorChanges()) {
      return 'Save includes valid edits in open forms and identifies anything that needs correction.';
    }
    if (this.hasOpenDraftUnsavedChanges()) {
      return 'Save the applied changes before validation or comparison can include them.';
    }
    return 'Validation and comparison both use this exact saved revision.';
  });
  readonly pendingWorkbenchView = computed<WorkflowDraftWorkbenchView | null>(
    () => {
      if (
        this.assignmentPolicyFormDirty() ||
        this.processDataFormDirty() ||
        this.automationFormDirty() ||
        this.workflowStatusFormDirty() ||
        this.navigationFormDirty()
      ) {
        return 'setup';
      }
      if (
        this.nodeFormDirty() ||
        this.automatedStepFormDirty() ||
        this.stepAssignmentPolicyFormDirty()
      ) {
        return 'map';
      }
      if (
        this.edgeFormDirty() ||
        this.reviewRoutingDirty() ||
        this.dashboardEditorDirty() ||
        this.staffPageEditorDirty() ||
        this.workComponentsDirty() ||
        this.transitionFormDirty() ||
        this.decisionFormDirty() ||
        this.requirementFormDirty() || (this.timerFormDirty() || this.childWorkFormDirty()) ||
        this.notificationFormDirty()
      ) {
        return 'map';
      }
      return null;
    },
  );
  readonly pendingWorkbenchAreaCount = computed(
    () =>
      Number(this.nodeFormDirty()) +
      Number(this.edgeFormDirty() || this.reviewRoutingDirty()) +
      Number(this.dashboardEditorDirty()) +
      Number(this.staffPageEditorDirty()) +
      Number(this.workComponentsDirty()) +
      Number(this.transitionFormDirty()) +
      Number(this.decisionFormDirty()) +
      Number(this.requirementFormDirty()) + Number(this.timerFormDirty()) + Number(this.childWorkFormDirty()) +
      Number(this.notificationFormDirty()) +
      Number(this.assignmentPolicyFormDirty()) +
      Number(this.processDataFormDirty()) +
      Number(this.automationFormDirty()) +
      Number(this.workflowStatusFormDirty()) +
      Number(this.automatedStepFormDirty()) +
      Number(this.navigationFormDirty()) +
      Number(this.stepAssignmentPolicyFormDirty()),
  );
  readonly pageRangeLabel = computed(() => {
    const page = this.draftPage();
    if (!page || page.totalCount === 0 || page.items.length === 0) {
      const totalLabel = page?.totalCount === 1 ? 'draft' : 'drafts';
      return page?.totalCount
        ? `0 of ${page.totalCount} ${totalLabel}`
        : '0 drafts';
    }

    const start = (page.pageNumber - 1) * page.pageSize + 1;
    const end = start + page.items.length - 1;
    const totalLabel = page.totalCount === 1 ? 'draft' : 'drafts';
    return `${start}-${end} of ${page.totalCount} ${totalLabel}`;
  });

  readonly draftResultCountLabel = computed(() => {
    const total = this.draftPage()?.totalCount ?? 0;
    return total === 1 ? '1 draft' : `${total} drafts`;
  });

  readonly hasActiveDraftFilters = computed(() =>
    Boolean(
      this.draftQuery().search ||
      this.draftQuery().workflowCode ||
      this.draftQuery().baseDefinitionVersionId ||
      this.draftQuery().validationStatusCode,
    ),
  );

  readonly draftListAsOfLabel = computed(() =>
    formatDateTime(this.draftPage()?.asOfUtc),
  );

  readonly draftListStatusMessage = computed(() => {
    if (this.isLoadingDrafts() && this.draftPage()) {
      return 'Updating drafts. Current results remain available.';
    }
    return this.isLoadingDrafts() ? 'Loading drafts.' : '';
  });

  readonly draftListReadState = computed(() =>
    isReadAccessDenied(this.draftListError()) ? 'forbidden' : 'error',
  );
  readonly draftListErrorMessage = computed(() => {
    if (isReadAccessDenied(this.draftListError()))
      return 'You do not have permission to view workflow drafts. Contact an administrator if you need access.';
    return (this.draftPage()?.items.length ?? 0) > 0
      ? 'Drafts could not be refreshed. Showing the most recently loaded drafts. Try again.'
      : 'Drafts could not be loaded. Try again.';
  });

  readonly primaryDraftFilterFormConfig: InputConfig[] = [
    {
      name: 'search',
      type: EzFormInputType.TEXT,
      label: 'Search drafts',
      placeholder: 'Draft or workflow code',
      row: 1,
      width: '2x',
    },
    {
      name: 'validationStatusCode',
      type: EzFormInputType.SELECT,
      label: 'Validation',
      options: VALIDATION_STATUS_OPTIONS,
      row: 1,
      width: '1x',
    },
  ];

  readonly advancedDraftFilterFormConfig: InputConfig[] = [
    {
      name: 'workflowCode',
      type: EzFormInputType.TEXT,
      label: 'Exact workflow code',
      placeholder: 'For example, phms-intake-lifecycle',
      row: 1,
      width: '2x',
    },
    {
      name: 'pageSize',
      type: EzFormInputType.SELECT,
      label: 'Rows per page',
      options: PAGE_SIZE_OPTIONS,
      row: 1,
      width: '1x',
    },
  ];

  primaryDraftFilterFormValues: DraftPrimaryFilterValues = {
    search: '',
    validationStatusCode: '',
  };

  advancedDraftFilterFormValues: DraftAdvancedFilterValues = {
    workflowCode: '',
    pageSize: DEFAULT_DRAFT_PAGE_SIZE,
  };

  get draftFilterFormValues(): DraftFilterValues {
    return {
      ...this.primaryDraftFilterFormValues,
      ...this.advancedDraftFilterFormValues,
    };
  }

  set draftFilterFormValues(value: DraftFilterValues) {
    this.primaryDraftFilterFormValues = {
      search: value.search,
      validationStatusCode: value.validationStatusCode,
    };
    this.advancedDraftFilterFormValues = {
      workflowCode: value.workflowCode,
      pageSize: value.pageSize,
    };
  }

  advancedDraftFiltersOpen = false;

  private readonly authoringValuesState = signal(this.emptyAuthoringValues());
  get authoringFormValues(): DraftAuthoringValues {
    return this.authoringValuesState();
  }
  set authoringFormValues(values: DraftAuthoringValues) {
    this.authoringValuesState.set(values);
  }
  authoringFormConfig: InputConfig[] = [];
  authoringAvailabilityFormConfig: InputConfig[] = [];
  private readonly identityValuesState = signal(this.emptyIdentityValues());
  get identityFormValues(): DraftIdentityValues {
    return this.identityValuesState();
  }
  set identityFormValues(values: DraftIdentityValues) {
    this.identityValuesState.set(values);
  }
  identityFormConfig: InputConfig[] = this.buildIdentityFormConfig();
  identityAvailabilityFormConfig: InputConfig[] =
    this.buildAvailabilityFormConfig('edit');
  identityTechnicalFormConfig: InputConfig[] = [
    requiredTextInput('definitionVersionCode', 'Version code', 1),
  ];

  setupSummary(values: DraftAuthoringValues | DraftIdentityValues): string {
    if (
      this.authoringMode() === 'create' ||
      ('payloadSchemaVersion' in values &&
        ['1.10', '1.11', '1.12', '1.13', '1.14', '1.15', '1.16', '1.17', '1.18', '1.19', '1.20', '1.21', '1.22'].includes(
          values.payloadSchemaVersion,
        ))
    )
      return `${values.displayName.trim() || 'Name your business process'} · Each run has its own identity. Actions pass named data between steps.`;
    const subject = values.subjectTypeCode
      ? this.setupCodeLabel(values.subjectTypeCode)
      : 'Choose the work this workflow manages';
    const scope =
      values.scopeTypeCode === 'global'
        ? 'global scope'
        : `${SCOPE_TYPE_OPTIONS.find((option) => option.value === values.scopeTypeCode)?.viewValue ?? values.scopeTypeCode}${values.scopeKey ? ': ' + values.scopeKey : ': key required'}`;
    const instances =
      values.instanceCardinalityCode === 'one_open_per_subject'
        ? 'one open workflow per subject'
        : values.instanceCardinalityCode === 'multiple'
          ? 'multiple open workflows per subject'
          : `retained instance rule: ${values.instanceCardinalityCode}`;
    return `${subject} · ${scope} · ${instances}.`;
  }

  setupCodeLabel(code: string): string {
    const text = code.replace(/[_-]+/g, ' ');
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  scopeKeyGuidance(scope: string): string {
    return scope === 'global'
      ? 'Global workflows use no jurisdiction or program key.'
      : scope === 'jurisdiction-program'
        ? 'Enter the jurisdiction and program codes as jurisdiction/program.'
        : scope === 'jurisdiction'
          ? 'Enter the jurisdiction code this workflow applies to.'
          : scope === 'program'
            ? 'Enter the program code this workflow applies to.'
            : 'This saved scope is not supported by the current editor. Its value is retained for inspection.';
  }

  setupPublicationSummary(): string {
    if (this.isArchivedDraft())
      return 'This source draft is archived. Its published version is available from the banner above.';
    const draft = this.currentDraft();
    if (draft?.validationStatusCode === 'valid')
      return 'The saved revision passed validation. Review & publish shows the remaining publication checks and separate activation support.';
    return 'Keep building your process. Review & publish explains anything that needs attention before it can be used.';
  }

  availabilitySummary(
    values: DraftAuthoringValues | DraftIdentityValues,
  ): string {
    if (values.scopeTypeCode === 'global') return 'All locations';
    const label =
      SCOPE_TYPE_OPTIONS.find((option) => option.value === values.scopeTypeCode)
        ?.viewValue ?? values.scopeTypeCode;
    return `${label}${values.scopeKey ? ': ' + values.scopeKey : ': choose a location'}`;
  }

  private refreshSetupFormConfigs(): void {
    this.identityFormConfig = this.buildIdentityFormConfig();
    this.identityAvailabilityFormConfig =
      this.buildAvailabilityFormConfig('edit');
    if (this.authoringMode()) {
      this.authoringFormConfig = this.buildAuthoringFormConfig(
        this.authoringMode()!,
      );
      this.authoringAvailabilityFormConfig =
        this.buildAvailabilityFormConfig('create');
    }
  }
  nodeFormValues: DraftNodeValues = this.emptyNodeValues();
  nodeFormConfig: InputConfig[] = this.buildNodeFormConfig(false, false);

  readonly draftColumnDefs: ColDef<WorkflowDefinitionDraftSummary>[] = [
    {
      headerName: 'Draft',
      autoHeight: true,
      wrapText: true,
      flex: 1.45,
      minWidth: 180,
      valueGetter: ({ data }) => draftIdentitySearchValue(data),
      cellClass: 'workflow-draft-cell workflow-draft-cell--stacked',
      cellRenderer: (
        params: ICellRendererParams<WorkflowDefinitionDraftSummary>,
      ) => renderDraftIdentity(params.data),
    },
    {
      field: 'revision',
      headerName: 'Revision',
      flex: 0.62,
      minWidth: 82,
      valueFormatter: ({ value }) => String(value ?? ''),
    },
    {
      headerName: 'Validation',
      flex: 1.15,
      minWidth: 175,
      valueGetter: ({ data }) =>
        data ? draftValidationStatus(data).searchValue : '',
      cellClass: 'workflow-draft-cell workflow-draft-cell--stacked',
      cellRenderer: (
        params: ICellRendererParams<WorkflowDefinitionDraftSummary>,
      ) => renderDraftValidationStatus(params.data),
    },
    {
      field: 'lastContentUpdatedUtc',
      headerName: 'Updated',
      flex: 0.82,
      minWidth: 120,
      valueFormatter: ({ value }) => formatDate(value),
    },
    {
      headerName: 'Open',
      width: 116,
      minWidth: 116,
      maxWidth: 116,
      sortable: false,
      resizable: false,
      cellRenderer: ButtonCellRenderer,
      cellRendererParams: {
        label: 'Open draft',
        onClick: (params: { rowData: WorkflowDefinitionDraftSummary }) => {
          this.requestOpenDraft(params.rowData);
        },
      },
    },
  ];

  readonly draftGridOptions: GridOptions<WorkflowDefinitionDraftSummary> = {
    rowSelection: {
      mode: 'singleRow',
      enableClickSelection: false,
      checkboxes: false,
    },
    rowHeight: 64,
    headerHeight: 42,
    getRowId: ({ data }) => String(data.id),
    onGridReady: (event) => {
      if (event.api.isDestroyed?.()) return;
      this.draftGridApi = event.api;
      event.api.setGridAriaProperty('label', 'Saved workflow drafts');
      this.syncDraftGridSelection();
    },
    onGridPreDestroyed: (event) => {
      if (this.draftGridApi === event.api) this.draftGridApi = undefined;
    },
    onRowDataUpdated: () => this.syncDraftGridSelection(),
  };

  readonly defaultDraftColumnDef: ColDef<WorkflowDefinitionDraftSummary> = {
    sortable: true,
    resizable: true,
    suppressMovable: true,
    floatingFilter: false,
    suppressHeaderFilterButton: true,
    suppressKeyboardEvent: (params) => this.onDraftGridKeyboardEvent(params),
    cellStyle: {
      display: 'flex',
      alignItems: 'center',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  };

  readonly draftFilterButtons = computed<ButtonConfig[]>(() => [
    {
      id: 'applyDraftFilters',
      buttonText: this.isLoadingDrafts() ? 'Loading...' : 'Search',
      disabled: this.isLoadingDrafts(),
    },
    {
      id: 'clearDraftFilters',
      buttonText: 'Clear filters',
      severity: 'secondary',
      disabled: this.isLoadingDrafts(),
    },
    {
      id: 'reloadDrafts',
      buttonText: 'Refresh',
      severity: 'secondary',
      disabled: this.isLoadingDrafts(),
    },
  ]);

  readonly authoringEntryButtons = computed<ButtonConfig[]>(() => [
    {
      id: 'createDraft',
      buttonText: 'New workflow',
      disabled:
        !this.canCreateWorkflowDraft() ||
        this.isDraftEditorInteractionBlocked(),
      hidden: !this.canCreateWorkflowDraft(),
    },
    {
      id: 'cloneVersion',
      buttonText: workflowDraftActionLabel(
        this.selectedDefinition?.draftActionCode,
      ),
      severity: 'secondary',
      disabled:
        !this.canCreateWorkflowDraft() ||
        !this.selectedDefinition ||
        !this.selectedDefinition.draftActionCode ||
        this.isDraftEditorInteractionBlocked(),
      hidden: !this.canCreateWorkflowDraft() || !this.selectedDefinition,
    },
  ]);

  readonly authoringActionButtons = computed<ButtonConfig[]>(() => [
    {
      id: 'submitAuthoring',
      buttonText: this.isRunningAuthoringAction()
        ? 'Working...'
        : this.authoringMode() === 'clone'
          ? 'Clone version'
          : 'Create workflow',
      disabled: this.isDraftMutationInProgress(),
    },
    {
      id: 'cancelAuthoring',
      buttonText: 'Cancel',
      severity: 'secondary',
      disabled: this.isDraftMutationInProgress(),
    },
  ]);

  readonly editorActionButtons = computed<ButtonConfig[]>(() => [
    {
      id: 'validateDraft', buttonText: this.isValidating() ? 'Validating...' : 'Validate', severity: 'secondary',
      hidden: !this.canValidate(),
      disabled: !this.currentDraft() || this.isArchivedDraft() || !this.canValidate() ||
        this.hasUnsavedChanges() || this.isDraftEditorInteractionBlocked(),
    },
    {
      id: 'connectedDryRun',
      buttonText: 'Connected dry run',
      severity: 'secondary',
      disabled: this.isDraftEditorInteractionBlocked(),
    },
    {
      id: 'saveDraft',
      buttonText: this.isSaving() ? 'Saving...' : 'Save draft',
      disabled:
        !this.canAuthor() ||
        !(this.documentDirty() || this.hasUnappliedEditorChanges()) ||
        this.isDraftEditorInteractionBlocked(),
      hidden: !this.canAuthor(),
    },
    {
      id: 'cancelEdits',
      buttonText: 'Cancel edits',
      severity: 'secondary',
      disabled:
        !this.hasOpenDraftUnsavedChanges() ||
        this.isDraftEditorInteractionBlocked(),
      hidden: !this.canAuthor(),
    },
  ]);
  readonly primaryEditorActionButtons = computed(() =>
    this.editorActionButtons().filter((button) => ['validateDraft', 'saveDraft'].includes(button.id)),
  );
  readonly secondaryEditorActionButtons = computed(() =>
    this.editorActionButtons().filter((button) => !['validateDraft', 'saveDraft'].includes(button.id)),
  );

  readonly nodeActionButtons = computed<ButtonConfig[]>(() => [
    {
      id: 'applyNode',
      buttonText: this.isAddingStep() ? 'Add work step' : 'Apply step changes',
      disabled:
        !this.canAuthor() ||
        !this.nodeFormDirty() ||
        this.isDraftEditorInteractionBlocked(),
      hidden: !this.canAuthor(),
    },
    {
      id: 'resetNode',
      buttonText: this.isAddingStep() ? 'Cancel' : 'Reset form',
      severity: 'secondary',
      disabled:
        (!this.isAddingStep() && !this.nodeFormDirty()) ||
        this.isDraftEditorInteractionBlocked(),
      hidden: !this.canAuthor(),
    },
    {
      id: 'removeNode',
      buttonText: 'Remove step',
      severity: 'secondary',
      disabled:
        !this.selectedStepCanBeRemoved() ||
        this.isAddingStep() ||
        this.isDraftEditorInteractionBlocked(),
      hidden: !this.canAuthor(),
    },
  ]);

  readonly paginationButtons = computed<ButtonConfig[]>(() => {
    const page = this.draftPage();
    const totalPages = page
      ? Math.max(1, Math.ceil(page.totalCount / page.pageSize))
      : 1;
    const pageNumber = page?.pageNumber ?? this.draftQuery().pageNumber;
    return [
      {
        id: 'previousDraftPage',
        buttonText: 'Previous',
        severity: 'secondary',
        disabled: this.isLoadingDrafts() || pageNumber <= 1,
      },
      {
        id: 'nextDraftPage',
        buttonText: 'Next',
        severity: 'secondary',
        disabled: this.isLoadingDrafts() || pageNumber >= totalPages,
      },
    ];
  });

  constructor() {
    effect(() => {
      if (this.decisionInventoryCheckAddMode() && (this.activeWorkbenchView() !== 'map'
        || this.mapInspectorPane() !== 'decisions' || this.mapSelection() !== null))
        untracked(() => this.decisionInventoryCheckAddMode.set(false));
    });
    effect(() => {
      if (this.activeWorkbenchView() !== 'decision-inventory' || !this.currentDraft() || !this.workingDocument()) return;
      const key = this.decisionInventoryReadKey();
      if (this.registryReadsSuspended()) return;
      untracked(() => {
        this.decisionInventoryInitialized.set(true);
        if (key !== this.decisionInventoryRequestedKey) void this.loadDecisionInventoryCatalogs();
      });
    });
    effect(() => {
      const draft = this.currentDraft();
      if (
        this.activeWorkbenchView() === 'review' &&
        draft &&
        !this.hasOpenDraftUnsavedChanges() &&
        this.canValidate() &&
        !this.isDraftEditorInteractionBlocked() &&
        (draft.validationStatusCode === 'unvalidated' ||
          (draft.validationStatusCode === 'invalid' &&
            draft.validationDiagnostics.length === 0))
      )
        untracked(() => this.validateAutomatically());
    });
    effect(() => {
      const isDirty = this.hasUnsavedChanges();
      this.dirtyChange.emit(isDirty);
      this.dirtyState.setDirty(isDirty);
    });
    effect(() => {
      this.dirtyState.setOperationInProgress(this.isDraftMutationInProgress());
    });
    this.destroyRef.onDestroy(() => {
      this.pendingStatusRuleRestore = null;
      this.pendingNotificationRestore = null;
      this.dirtyState.setDirty(false);
      this.dirtyState.setOperationInProgress(false);
    });

    let routeStateInitialized = false;
    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((queryParams) => {
        const explicitRegistryView = queryParams
          .get('registryView')
          ?.trim()
          .toLowerCase();
        const parentOwnsExternalDeactivation =
          routeStateInitialized &&
          routeResolvesToExternalRegistryContext(queryParams);
        const nextQuery = readDraftQuery(queryParams);
        const requestedWorkbenchView = queryParams.get('draftView');
        const nextSettingsTask: WorkflowSettingsTask | null = requestedWorkbenchView === 'automation'
          ? 'automation' : requestedWorkbenchView === 'capabilities' ? 'technical' : null;
        const nextTechnicalView: WorkflowTechnicalView | null = requestedWorkbenchView === 'capabilities' ? 'modules' : null;
        const nextWorkbenchView = readWorkbenchView(requestedWorkbenchView);
        const nextRoutingView = readRoutingView(queryParams.get('routingView'));
        const nextReviewView = readReviewView(queryParams.get('reviewView'));
        const legacyMapPane = readLegacyMapInspectorPane(
          requestedWorkbenchView,
          nextRoutingView,
        );
        const requestedDraftId = readPositiveInteger(queryParams.get('draft'));
        if (
          requestedDraftId === this.currentDraft()?.id &&
          this.loadingDraftId !== null &&
          this.loadingDraftId !== requestedDraftId
        )
          this.cancelDraftLoad();
        const designerLocation = readDesignerLocation(queryParams);
        if (legacyMapPane) {
          designerLocation.pane = legacyMapPane;
          if (legacyMapPane === 'decisions' || legacyMapPane === 'transitions')
            designerLocation.section = 'transitions';
        }
        const locationChanged =
          JSON.stringify(designerLocation) !==
          JSON.stringify(this.designerLocation());
        const sameDraft =
          requestedDraftId !== null &&
          requestedDraftId === this.currentDraft()?.id;
        if (
          !parentOwnsExternalDeactivation &&
          sameDraft &&
          routeStateInitialized &&
          (locationChanged ||
            nextWorkbenchView !== this.activeWorkbenchView() ||
            (nextSettingsTask !== null && nextSettingsTask !== this.activeWorkflowSettingsTask()) ||
            (nextTechnicalView !== null && nextTechnicalView !== this.activeTechnicalView())) &&
          (this.isDraftEditorInteractionBlocked() ||
            !this.flushPendingEditorChanges())
        ) {
          this.writeWorkbenchViewToUrl(this.activeWorkbenchView());
          return;
        }
        this.requestedDesignerLocation =
          requestedDraftId === null
            ? null
            : { draftId: requestedDraftId, location: designerLocation };
        this.requestedWorkflowSettingsTask = requestedDraftId && nextSettingsTask
          ? { draftId: requestedDraftId, task: nextSettingsTask } : null;
        this.requestedTechnicalView = requestedDraftId && nextTechnicalView
          ? { draftId: requestedDraftId, view: nextTechnicalView } : null;
        if (!parentOwnsExternalDeactivation) {
          if (nextSettingsTask) {
            this.revealWorkflowSettingsTask(nextSettingsTask, false);
            if (nextTechnicalView) this.revealTechnicalView(nextTechnicalView, false);
          }
          if (sameDraft && !legacyMapPane && locationChanged)
            this.restoreDesignerLocation(designerLocation);
          this.lastDesignerLocationKey = JSON.stringify(
            this.designerLocation(),
          );
          if (nextWorkbenchView !== this.activeWorkbenchView()) {
            this.activeWorkbenchView.set(nextWorkbenchView);
          }
          if (nextRoutingView !== this.activeRoutingView()) {
            this.activeRoutingView.set(nextRoutingView);
          }
          if (nextReviewView !== this.activeReviewView()) {
            this.activeReviewView.set(nextReviewView);
          }
          if (legacyMapPane) {
            this.setInspectorPane(legacyMapPane);
          }
        }
        if (!draftQueriesEqual(nextQuery, this.draftQuery())) {
          this.setDraftFilterValues(nextQuery);
          this.loadDrafts(nextQuery);
        } else if (!routeStateInitialized) {
          this.setDraftFilterValues(nextQuery);
          this.loadDrafts(nextQuery);
        }

        const draftId =
          explicitRegistryView === 'versions' ||
          explicitRegistryView === 'version'
            ? null
            : readPositiveInteger(queryParams.get('draft'));
        if (
          draftId &&
          this.currentDraft()?.id !== draftId &&
          this.loadingDraftId !== draftId
        ) {
          this.requestOpenDraftById(draftId, false);
        } else if (
          routeStateInitialized &&
          !draftId &&
          !parentOwnsExternalDeactivation
        ) {
          if (this.isLoadingDraft() && !this.currentDraft()) {
            this.cancelDraftLoad();
          } else if (this.currentDraft()) {
            if (this.isDraftMutationInProgress()) {
              this.showOperationError(
                localValidationError(
                  'Draft action in progress',
                  'Wait for the current draft operation to finish before closing the editor.',
                ),
              );
              this.writeDraftIdToUrl(this.currentDraft()!.id);
            } else {
              this.requestDiscardThen(
                () => this.clearCurrentDraft(false),
                'draft',
                () => this.writeDraftIdToUrl(this.currentDraft()?.id ?? null),
              );
            }
          }
        }
        if (
          !parentOwnsExternalDeactivation &&
          ['steps', 'routing', 'decisions'].includes(
            requestedWorkbenchView ?? '',
          )
        ) {
          void this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
              draftView: null,
              routingView: null,
              ...this.designerLocationQuery(designerLocation),
            },
            queryParamsHandling: 'merge',
            preserveFragment: true,
            replaceUrl: true,
          });
        }
        routeStateInitialized = true;
      });

    this.loadAuthoringReferences();

    effect(() => {
      const target = this.notificationReturnContext();
      if (
        target &&
        (target.draftId !== this.currentDraft()?.id ||
          target.nodeId !== this.selectedMapNode()?.id ||
          this.stepWorkspaceScope() !== 'behavior' ||
          this.behaviorSection() !== 'transitions')
      ) {
        this.notificationReturnContext.set(null);
      }
    });

    effect(() => {
      const location = this.designerLocation();
      const draft = this.currentDraft();
      const key = JSON.stringify(location);
      if (
        draft &&
        !this.isLoadingDraft() &&
        !this.isAddingStep() &&
        !this.restoringDesignerLocation &&
        key !== this.lastDesignerLocationKey
      ) {
        this.lastDesignerLocationKey = key;
        this.writeWorkbenchViewToUrl(this.activeWorkbenchView());
      }
    });
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (!this.hasUnsavedChanges()) {
      return;
    }

    event.preventDefault();
    event.returnValue = '';
  }

  requestExternalSelectionChange(
    action: () => void,
    onCancel?: () => void,
  ): void {
    if (this.isDraftMutationInProgress()) {
      this.showOperationError(
        localValidationError(
          'Draft action in progress',
          'Wait for the current draft operation to finish before changing definitions.',
        ),
      );
      onCancel?.();
      return;
    }

    // Opening a draft is a cancellable read. Invalidate it before the parent
    // changes registry context so a late response cannot reopen the workbench.
    if (this.isLoadingDraft()) {
      this.cancelDraftLoad();
    }
    this.requestDiscardThen(
      () => {
        if (this.currentDraft()) {
          this.clearCurrentDraft(false);
        }
        action();
      },
      'draft',
      onCancel,
      false,
    );
  }

  onDraftFilterButtonClick(button: ButtonConfig): void {
    switch (button.id) {
      case 'applyDraftFilters':
        this.applyDraftFilters();
        break;
      case 'clearDraftFilters':
        this.clearDraftFilters();
        break;
      case 'reloadDrafts':
        this.loadDrafts();
        break;
      default:
        break;
    }
  }

  onPaginationButtonClick(button: ButtonConfig): void {
    const pageNumber = this.draftQuery().pageNumber;
    if (button.id === 'previousDraftPage' && pageNumber > 1) {
      this.loadDrafts(
        { ...this.draftQuery(), pageNumber: pageNumber - 1 },
        true,
      );
    } else if (button.id === 'nextDraftPage') {
      this.loadDrafts(
        { ...this.draftQuery(), pageNumber: pageNumber + 1 },
        true,
      );
    }
  }

  toggleAdvancedDraftFilters(): void {
    this.advancedDraftFiltersOpen = !this.advancedDraftFiltersOpen;
  }

  setIncludeArchivedDrafts(include: boolean): void {
    if (this.isLoadingDrafts()) return;
    this.loadDrafts(
      {
        ...this.draftQuery(),
        pageNumber: 1,
        includeArchived: include ? 'true' : undefined,
      },
      true,
    );
  }

  setIncludeHiddenDrafts(include: boolean): void {
    if (!this.isLoadingDrafts() && this.draftPage()?.sharedVisibilityAvailable === true)
      this.loadDrafts({ ...this.draftQuery(), pageNumber: 1, includeHidden: include ? 'true' : undefined }, true);
  }

  onDraftFilterSubmit(event: Event): void {
    event.preventDefault();
    if (!this.isLoadingDrafts()) {
      this.applyDraftFilters();
    }
  }

  applyDraftFilters(): void {
    this.loadDrafts(
      {
        search: normalizeOptional(this.draftFilterFormValues.search),
        workflowCode: normalizeOptional(
          this.draftFilterFormValues.workflowCode,
        ),
        baseDefinitionVersionId: normalizeOptional(this.draftFilterFormValues.workflowCode) === this.draftQuery().workflowCode
          ? this.draftQuery().baseDefinitionVersionId : undefined,
        includeHidden: this.draftQuery().includeHidden,
        validationStatusCode: normalizeOptional(
          this.draftFilterFormValues.validationStatusCode,
        ),
        pageNumber: 1,
        ...(this.draftQuery().includeArchived === 'true'
          ? { includeArchived: 'true' as const }
          : {}),
        pageSize: normalizePageSize(this.draftFilterFormValues.pageSize),
      },
      true,
    );
  }

  clearDraftFilters(): void {
    this.draftFilterFormValues = {
      search: '',
      workflowCode: '',
      validationStatusCode: '',
      pageSize: DEFAULT_DRAFT_PAGE_SIZE,
    };
    this.advancedDraftFiltersOpen = false;
    this.loadDrafts({ pageNumber: 1, pageSize: DEFAULT_DRAFT_PAGE_SIZE }, true);
  }

  retryDraftList(): void {
    if (!this.isLoadingDrafts()) this.loadDrafts();
  }

  /** A committed registry metadata change invalidates any older discovery response. */
  refreshDraftDiscovery(): void { this.loadDrafts(); }

  /** Catalog and draft reads share the same global WorkflowDefinitionRead grant. */
  resumeConfirmedRegistryReadAccess(): void {
    const denied =
      isReadAccessDenied(this.draftReadError()) ||
      isReadAccessDenied(this.draftListError());
    if (!denied && !this.registryReadsSuspended()) return;
    this.registryReadsSuspended.set(false);
    if (isReadAccessDenied(this.draftReadError()))
      this.draftReadError.set(null);
    if (isReadAccessDenied(this.draftListError()))
      this.draftListError.set(null);
    if (isReadAccessDenied(this.operationError()))
      this.operationError.set(null);
    if (!this.currentDraft()) this.loadDrafts();
    if (this.resumeAuthoringReferencesAfterAccess) {
      this.resumeAuthoringReferencesAfterAccess = false;
      this.loadAuthoringReferences();
    }
  }

  suspendRegistryReadAccess(statusCode: 401 | 403 = 403): void {
    this.registryReadsSuspended.set(true);
    this.draftListSubscription?.unsubscribe();
    this.draftListRequestId++;
    this.draftPage.set(null);
    this.isLoadingDrafts.set(false);
    this.cancelDraftLoad();
    this.resumeAuthoringReferencesAfterAccess ||=
      this.isLoadingAuthoringReferences() ||
      isReadAccessDenied(this.authoringReferencesError());
    this.authoringReferencesSubscription?.unsubscribe();
    this.authoringReferencesRequestId++;
    this.isLoadingAuthoringReferences.set(false);
    ++this.archivedDraftRequest;
    ++this.archivedVersionRequest;
    if (this.archivedVersionLoading()) this.archivedVersionError.set(true);
    this.archivedVersionLoading.set(false);
    this.failedDraftLoad.set(null);
    this.draftReadError.set({
      title: 'Access denied',
      messages: [],
      statusCode,
    });
  }

  private denyDraftReadAccess(
    error: WorkflowRegistryDiagnosticDisplayError,
  ): void {
    this.suspendRegistryReadAccess(error.statusCode === 401 ? 401 : 403);
    this.readAccessDenied.emit();
  }

  retryFailedDraftLoad(): void {
    const failedLoad = this.failedDraftLoad();
    if (
      !failedLoad ||
      this.isDraftMutationInProgress() ||
      this.isLoadingDraft()
    ) {
      return;
    }

    const action = () =>
      this.loadDraft(
        failedLoad.draftId,
        failedLoad.updateUrl,
        failedLoad.preserveRecovery,
      );
    if (failedLoad.preserveRecovery) {
      action();
    } else {
      this.requestDiscardThen(action);
    }
  }

  onAuthoringEntryButtonClick(button: ButtonConfig): void {
    if (button.id === 'createDraft') {
      this.beginCreateDraft();
    } else if (button.id === 'cloneVersion') {
      this.beginCloneVersion();
    }
  }

  onAuthoringActionButtonClick(button: ButtonConfig): void {
    if (button.id === 'cancelAuthoring') {
      this.requestCloseAuthoringForm();
    } else if (button.id === 'submitAuthoring') {
      this.submitAuthoring();
    }
  }

  onAuthoringFormChange(
    values?: unknown,
    area: 'basics' | 'availability' = 'basics',
  ): void {
    const previousScope = this.authoringFormValues.scopeTypeCode;
    if (values && typeof values === 'object') {
      const keys =
        this.authoringMode() === 'clone'
          ? ['draftCode']
          : area === 'availability'
            ? ['scopeTypeCode', 'scopeKey']
            : ['displayName', 'description'];
      this.authoringFormValues = {
        ...this.authoringFormValues,
        ...this.setupFieldPatch(values, keys),
      };
    }
    if (
      previousScope !== this.authoringFormValues.scopeTypeCode &&
      this.authoringMode() === 'create'
    )
      this.refreshSetupFormConfigs();
    this.authoringFormDirty.set(true);
    this.authoringIntent = null;
    this.authoringMessage.set(null);
  }

  beginCreateDraft(): void {
    if (
      !this.canCreateWorkflowDraft() ||
      this.isDraftMutationInProgress() ||
      this.isLoadingDraft() ||
      this.authoringMode() !== null
    ) {
      return;
    }

    const action = () => {
      this.authoringMode.set('create');
      this.authoringFormDirty.set(false);
      this.authoringFormValues = this.emptyAuthoringValues();
      const identifier = crypto.randomUUID().replaceAll('-', '');
      this.authoringFormValues = {
        ...this.authoringFormValues,
        workflowCode: `process-${identifier}`,
        draftCode: `draft-${identifier}`,
        definitionVersionCode: 'v1',
      };
      this.authoringFormConfig = this.buildAuthoringFormConfig('create');
      this.authoringAvailabilityFormConfig =
        this.buildAvailabilityFormConfig('create');
      this.authoringIntent = null;
      this.authoringMessage.set(null);
      this.operationError.set(null);
    };
    this.requestDiscardThen(action);
  }

  beginWorkflowDraft(): void {
    const workflowCode = this.draftQuery().workflowCode;
    if (!workflowCode || this.currentDraft() || !this.entryPointsVisible ||
      !this.canCreateWorkflowDraft() || this.isDraftEditorInteractionBlocked()) return;
    const scope = JSON.stringify(this.draftQuery());
    const actor = JSON.stringify(this.roles.accessProfile?.()?.actor ?? null);
    const current = () => !this.destroyRef.destroyed && this.entryPointsVisible &&
      !this.currentDraft() && !this.registryReadsSuspended() && this.canCreateWorkflowDraft() &&
      this.authoringMode() === null && scope === JSON.stringify(this.draftQuery()) &&
      actor === JSON.stringify(this.roles.accessProfile?.()?.actor ?? null);
    this.isRunningAuthoringAction.set(true);
    this.operationError.set(null);
    this.lifecycleService.getCatalog({ workflowCode, pageNumber: 1, pageSize: 1 })
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: page => {
          this.isRunningAuthoringAction.set(false);
          if (!current()) return;
          const source = page.items[0];
          if (!source || source.workflowCode !== workflowCode || !source.publishedUtc ||
            !Number.isInteger(source.id) || source.id <= 0 || !source.definitionVersionCode?.trim() ||
            !['create', 'continue', 'ambiguous'].includes(source.draftActionCode ?? '')) {
            this.showOperationError(localValidationError('No published version to start from',
              'Open an existing draft below. A new revision can be started after this workflow has been published.'));
            return;
          }
          if (source.draftActionCode === 'ambiguous') {
            this.openWorkflowDrafts(workflowCode, source.id);
            this.authoringMessage.set('Choose an existing draft for the latest published version below.');
            return;
          }
          this.editPublishedVersion(source, {
            sourceDefinitionId: source.id,
            sourceContext: JSON.stringify([source.id, source.workflowCode, source.definitionVersionCode, source.eTag]),
            isCurrent: current,
            onFailed: error => { if (current()) this.showOperationError(error); },
          });
        },
        error: error => {
          this.isRunningAuthoringAction.set(false);
          if (current()) this.showOperationError(mapWorkflowRegistryDiagnosticError(error));
        },
      });
  }

  beginCloneVersion(): void {
    const definition = this.selectedDefinition;
    if (
      !definition ||
      !['create', 'continue', 'ambiguous'].includes(
        definition.draftActionCode ?? '',
      )
    )
      return;
    if (definition.draftActionCode === 'ambiguous') {
      if (!Number.isInteger(definition.id) || Number(definition.id) <= 0) {
        this.showOperationError(
          localValidationError(
            'Publication identity unavailable',
            'Open this published version again before choosing its drafts.',
          ),
        );
        return;
      }
      this.openWorkflowDrafts(definition.workflowCode, definition.id);
    } else this.editPublishedVersion(definition);
  }
  openPublishedSource(): void {
    const draft = this.currentDraft();
    if (!draft?.publishedDefinitionVersionCode) return;
    this.publishedVersionRequested.emit({
      workflowCode: draft.workflowCode,
      definitionVersionCode: draft.publishedDefinitionVersionCode,
    });
  }

  openArchivedDraftAction(): void {
    const version = this.archivedPublishedVersion();
    if (
      !version ||
      this.archivedVersionLoading() ||
      !this.canCreateWorkflowDraft()
    )
      return;
    if (version.draftActionCode === 'ambiguous')
      this.openWorkflowDrafts(version.workflowCode, version.id);
    else if (
      version.draftActionCode === 'create' ||
      version.draftActionCode === 'continue'
    )
      this.editPublishedVersion(version);
  }

  discardArchivedLocalChanges(): void {
    if (!this.isArchivedDraft() || !this.hasOpenDraftUnsavedChanges()) return;
    const context = this.discardContextKey('draft');
    void this.confirmDraftChange({
        heading: 'Discard local changes?',
        message:
          'Download a local copy first if you want to keep these changes. The archived server document will remain unchanged.',
        confirmText: 'Discard local changes',
        cancelText: 'Keep changes here',
      })
      .then((confirmed) => {
        if (this.destroyRef.destroyed) return;
        if (!confirmed || !this.isArchivedDraft()) return;
        if (context !== this.discardContextKey('draft')) {
          this.reportStaleDiscard();
          this.focusAfterRender(() => this.draftEditorHeading);
          return;
        }
        this.resetDraftToBaseline();
      });
  }

  openWorkflowDrafts(
    workflowCode: string,
    baseDefinitionVersionId?: number,
    includeArchived = false,
    navigation?: WorkflowPublishedDraftNavigation,
  ): void {
    const actor = JSON.stringify(this.roles.accessProfile?.()?.actor ?? null);
    const stillCurrent = () =>
      !this.destroyRef.destroyed &&
      actor === JSON.stringify(this.roles.accessProfile?.()?.actor ?? null) &&
      !this.isDraftMutationInProgress() &&
      !this.isLoadingDraft() &&
      !this.registryReadsSuspended() &&
      baseDefinitionVersionId !== undefined &&
      Number.isInteger(baseDefinitionVersionId) &&
      baseDefinitionVersionId > 0 &&
      !!navigation?.isCurrent();
    this.requestDiscardThen(
      () => {
        if (navigation && !stillCurrent()) return;
        this.clearCurrentDraft(true);
        this.advancedDraftFilterFormValues = {
          ...this.advancedDraftFilterFormValues,
          workflowCode,
        };
        this.loadDrafts(
          {
            ...this.draftQuery(),
            workflowCode,
            baseDefinitionVersionId,
            includeArchived: includeArchived ? 'true' : 'false',
            pageNumber: 1,
          },
          true,
        );
        navigation?.onOpened();
      },
      'draft',
      undefined,
      !navigation,
      false,
      navigation ? stillCurrent : undefined,
    );
  }
  clearDraftDiscoveryScope(): void {
    this.loadDrafts({ ...this.draftQuery(), workflowCode: undefined, baseDefinitionVersionId: undefined, pageNumber: 1 }, true);
  }

  clearDraftBaseVersion(): void {
    if (!this.isLoadingDrafts()) this.loadDrafts({ ...this.draftQuery(), baseDefinitionVersionId: undefined, pageNumber: 1 }, true);
  }

  draftBaseVersionLabel(): string | null {
    const id = this.draftQuery().baseDefinitionVersionId;
    if (!id) return null;
    return this.draftPage()?.items.find(row => row.baseDefinitionVersionId === id)?.baseDefinitionVersionCode || `Publication #${id}`;
  }

  retryArchivedVersion(): void {
    const draft = this.currentDraft();
    if (draft?.lifecycleStatusCode === 'archived')
      this.loadArchivedPublishedVersion(draft);
  }

  private loadArchivedPublishedVersion(
    draft: WorkflowDefinitionDraftDetail,
  ): void {
    if (this.registryReadsSuspended()) return;
    const versionCode = draft.publishedDefinitionVersionCode;
    this.archivedPublishedVersion.set(null);
    this.archivedVersionError.set(false);
    if (!versionCode) return;
    const request = ++this.archivedVersionRequest;
    this.archivedVersionLoading.set(true);
    this.lifecycleService
      .getPublishedVersion(draft.workflowCode, versionCode)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (version) => {
          if (
            request !== this.archivedVersionRequest ||
            this.currentDraft()?.id !== draft.id
          )
            return;
          this.archivedVersionLoading.set(false);
          if (
            version.workflowCode !== draft.workflowCode ||
            version.definitionVersionCode !== versionCode ||
            version.definitionDocumentStatusCode !== 'readable' ||
            !version.draftActionCode
          ) {
            this.archivedVersionError.set(true);
            return;
          }
          this.archivedPublishedVersion.set(version);
        },
        error: (error: unknown) => {
          if (
            request !== this.archivedVersionRequest ||
            this.currentDraft()?.id !== draft.id
          )
            return;
          this.archivedVersionLoading.set(false);
          this.archivedVersionError.set(true);
          const displayError = mapWorkflowRegistryDiagnosticError(error);
          if (isReadAccessDenied(displayError))
            this.denyDraftReadAccess(displayError);
        },
      });
  }

  private handleArchivedDraftFailure(
    error: WorkflowRegistryDiagnosticDisplayError,
  ): boolean {
    if (
      ![
        error.problemCode,
        error.reasonCode,
        ...(error.issueCodes ?? []),
      ].includes('workflow.definition.draft.archived')
    )
      return false;
    const draft = this.currentDraft();
    if (!draft) return true;
    this.retainCapturedInputs(this.capturePendingInputs());
    this.archivedBufferPending.set(this.hasOpenDraftUnsavedChanges());
    const local = this.recoveryDocument() ?? this.workingDocument();
    if (local && this.archivedBufferPending()) {
      const base = this.recoveryBase() ?? draft.document;
      this.recoveryBase.set(cloneDocument(base));
      this.recoveryStorageKey = this.storageKeyForDraft(draft);
      this.persistRecovery(base, local);
    }
    this.archiveDetected.set(true);
    clearArchivedDraftPublicationIntent(draft.id);
    this.saveIntent = null;
    this.validationIntent = null;
    this.cancelValidation();
    this.lifecycleOperationInProgress.set(false);
    const request = ++this.archivedDraftRequest;
    this.lifecycleService
      .getDraft(draft.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (archived) => {
          if (
            request !== this.archivedDraftRequest ||
            this.currentDraft()?.id !== draft.id ||
            archived.lifecycleStatusCode !== 'archived'
          )
            return;
          this.applyLoadedDraft(archived);
          this.loadDrafts();
        },
        error: (failure) => {
          if (
            request !== this.archivedDraftRequest ||
            this.currentDraft()?.id !== draft.id
          )
            return;
          const displayError = mapWorkflowRegistryDiagnosticError(failure);
          if (isReadAccessDenied(displayError)) {
            this.denyDraftReadAccess(displayError);
            return;
          }
          this.showOperationError(displayError, {
            draftId: draft.id,
            updateUrl: false,
            preserveRecovery: true,
          });
        },
      });
    return true;
  }

  private preserveArchivedLocalChanges(
    draft: WorkflowDefinitionDraftDetail,
  ): void {
    this.failedDraftLoad.set(null);
    this.operationError.set(null);
    const previous = this.currentDraft();
    const local = this.recoveryDocument() ?? this.workingDocument();
    this.retainCapturedInputs(this.capturePendingInputs());
    this.archivedBufferPending.set(true);
    if (previous && local) {
      const base = this.recoveryBase() ?? previous.document;
      this.recoveryBase.set(cloneDocument(base));
      this.recoveryStorageKey = this.storageKeyForDraft(previous);
      this.persistRecovery(base, local);
    }
    this.cancelValidation();
    this.saveIntent = null;
    this.validationIntent = null;
    this.lifecycleOperationInProgress.set(false);
    if (this.currentDraft()?.id !== draft.id) {
      this.ownerViewState.clear();
      this.behaviorResponsibilityFocus.set(null);
      this.stepEditorNodeId.set(null);
      this.stepEditorOpened.set(false);
      this.editingRouteSelection.set(null);
      this.automatedRouteFocus.set(null);
      this.editorMapReturnSelection = null;
      this.stepsDrawerOpen.set(false);
      this.stepEditorReturnFocus = null;
    }
    this.currentDraft.set(draft);
    this.workbenchStateChange.emit({
      draftCode: draft.draftCode,
      workflowCode: draft.workflowCode,
      revision: draft.revision,
      ...(draft.lifecycleStatusCode === 'archived'
        ? { lifecycleStatusCode: 'archived' as const }
        : {}),
    });
    this.editorMessage.set(
      'This draft was published while you were editing. Your local changes remain here. Download a local copy before leaving; this archived draft cannot be changed.',
    );
    this.loadArchivedPublishedVersion(draft);
  }

  private capturePendingInputs(): readonly {
    name: string;
    label: string;
    value: string;
    checked?: boolean;
  }[] {
    const fields = Array.from(
      this.hostElement.nativeElement.querySelectorAll<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >('input,textarea,select'),
    ).map((field) => ({
      name: field.name || field.id,
      label:
        field.getAttribute('aria-label') ||
        field.closest('label')?.textContent?.trim() ||
        field.name ||
        field.id,
      value: field.value,
      ...(field instanceof HTMLInputElement &&
      ['checkbox', 'radio'].includes(field.type)
        ? { checked: field.checked }
        : {}),
    }));
    const routeInputs = [
      ...Array.from(this.pageEditors ?? []).flatMap(editor => editor.capturePendingRouteInputs?.() ?? []),
      ...(this.workComponentsEditor?.capturePendingRouteInputs?.() ?? []),
      ...(this.automatedStepEditor?.capturePendingRouteInputs?.() ?? []),
      ...(this.workflowStatusEditor?.capturePendingStatusInputs?.() ?? []),
      ...(this.transitionEditor?.capturePendingStepStatusInputs?.() ?? []),
      ...(this.processDataEditor?.capturePendingDataInputs?.() ?? []),
      ...(this.childWorkEditor?.capturePendingChildWorkInputs?.() ?? []),
      ...(this.timersEditor?.capturePendingTimerInputs?.() ?? []),
      ...(this.navigationSettingsEditor?.capturePendingNavigationInputs?.() ?? []),
    ];
    const capturedRoutes = new Set<string>();
    return [...fields, ...routeInputs.filter(field => {
      const identity = JSON.stringify([field.name, field.value]);
      if (capturedRoutes.has(identity)) return false;
      capturedRoutes.add(identity);
      return true;
    })];
  }

  /** Opens the server-selected editable draft without guessing its identity or state. */
  editPublishedVersion(
    definition: Pick<
      WorkflowDefinitionLifecycleDetail,
      'workflowCode' | 'definitionVersionCode'
    >,
    handoff?: WorkflowPublishedDraftHandoff,
  ): void {
    const workflowCode = definition.workflowCode,
      definitionVersionCode = definition.definitionVersionCode;
    const sourceDefinitionId = handoff?.sourceDefinitionId,
      sourceContext = handoff?.sourceContext;
    const actor = JSON.stringify(this.roles.accessProfile?.()?.actor ?? null);
    const sourceCurrent = () =>
      !this.destroyRef.destroyed &&
      this.canCreateWorkflowDraft() &&
      actor === JSON.stringify(this.roles.accessProfile?.()?.actor ?? null) &&
      (!handoff ||
        (sourceDefinitionId !== undefined &&
          Number.isInteger(sourceDefinitionId) &&
          sourceDefinitionId > 0 &&
          !!sourceContext &&
          handoff.sourceDefinitionId === sourceDefinitionId &&
          handoff.sourceContext === sourceContext &&
          handoff.isCurrent()));
    const canBegin = () =>
      sourceCurrent() &&
      !this.isDraftMutationInProgress() &&
      !this.isLoadingDraft() &&
      this.authoringMode() === null;
    if (!canBegin()) return;

    const action = () => {
      if (!canBegin()) return;
      const request = ++this.publishedHandoffRequest;
      const key = JSON.stringify([
        workflowCode,
        definitionVersionCode,
        sourceDefinitionId,
        sourceContext,
        actor,
      ]);
      let handoffIntent = this.publishedHandoffIntent;
      if (
        !handoffIntent ||
        handoffIntent.key !== key ||
        this.authoringIntent !== handoffIntent.intent
      ) {
        const intent = createWorkflowDefinitionMutationIntent();
        handoffIntent = { key, intent };
        this.publishedHandoffIntent = handoffIntent;
        this.authoringIntent = intent;
      }
      const intent = handoffIntent.intent;
      this.isRunningAuthoringAction.set(true);
      this.authoringMessage.set(null);
      this.operationError.set(null);
      const localContext = this.discardContextKey('draft');
      const ownsReply = () =>
        request === this.publishedHandoffRequest &&
        this.authoringIntent === intent &&
        sourceCurrent() &&
        localContext === this.discardContextKey('draft');
      const fail = (error: WorkflowRegistryDiagnosticDisplayError) => {
        if (handoff) {
          this.operationError.set(error);
          handoff.onFailed(error);
        } else this.showOperationError(error);
      };
      this.authoringSubscription?.unsubscribe();
      this.authoringSubscription = this.lifecycleService
        .cloneVersion(workflowCode, definitionVersionCode, {}, intent)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (draft) => {
            const current = ownsReply();
            if (request === this.publishedHandoffRequest)
              this.isRunningAuthoringAction.set(false);
            if (!current) return;
            if (
              handoff &&
              (draft.workflowCode !== workflowCode ||
                draft.baseDefinitionVersionId !== sourceDefinitionId)
            ) {
              fail(
                localValidationError(
                  'Draft source could not be confirmed',
                  'The returned draft does not match this published source. Refresh this version before trying again.',
                ),
              );
              return;
            }
            if (this.authoringIntent === intent) this.authoringIntent = null;
            if (this.publishedHandoffIntent?.intent === intent)
              this.publishedHandoffIntent = null;
            const focusOrigin = this.browserDocument.activeElement;
            this.applyLoadedDraft(draft);
            this.activeWorkbenchView.set('map');
            const message = `Draft opened from published ${definitionVersionCode}.`;
            if (!handoff) this.authoringMessage.set(message);
            this.editorMessage.set(message);
            const openedDocument = this.workingDocument();
            this.writeDraftIdToUrl(draft.id);
            this.loadDrafts();
            this.editableDraftOpened.emit({
              workflowCode,
              definitionVersionCode,
            });
            this.focusAfterRender(
              () => this.draftEditorHeading,
              () => {
                const active = this.browserDocument.activeElement;
                return (
                  request === this.publishedHandoffRequest &&
                  !this.destroyRef.destroyed &&
                  actor ===
                    JSON.stringify(
                      this.roles.accessProfile?.()?.actor ?? null,
                    ) &&
                  this.currentDraft()?.id === draft.id &&
                  this.currentDraft()?.revision === draft.revision &&
                  this.currentDraft()?.eTag === draft.eTag &&
                  this.workingDocument() === openedDocument &&
                  this.activeWorkbenchView() === 'map' &&
                  !this.hostElement.nativeElement.closest(
                    '[hidden],[inert],[aria-hidden="true"]',
                  ) &&
                  (!active ||
                    active === focusOrigin ||
                    active === this.browserDocument.body ||
                    active === this.browserDocument.documentElement ||
                    !active.isConnected ||
                    !!active.closest('[hidden],[inert]'))
                );
              },
            );
          },
          error: (error: unknown) => {
            const current = ownsReply();
            if (request === this.publishedHandoffRequest)
              this.isRunningAuthoringAction.set(false);
            if (current) fail(mapWorkflowRegistryDiagnosticError(error));
          },
        });
    };
    this.requestDiscardThen(
      action,
      'draft',
      undefined,
      !handoff,
      false,
      canBegin,
    );
  }
  requestCloseAuthoringForm(): void {
    if (this.confirmationInProgress || this.ownerViewState.focusSuspended()) return;
    if (this.isRunningAuthoringAction()) {
      this.showOperationError(
        localValidationError(
          'Draft action in progress',
          'Wait for the current create or clone request to finish before closing this setup.',
        ),
      );
      return;
    }

    if (!this.authoringFormDirty()) {
      this.closeAuthoringForm();
      return;
    }

    const context = this.discardContextKey('draft');
    void this.confirmDraftChange({
        heading: 'Discard draft setup?',
        message: 'The values entered for this new draft will be lost.',
        confirmText: 'Discard setup',
        cancelText: 'Keep editing',
      })
      .then((confirmed) => {
        if (this.destroyRef.destroyed) return;
        if (confirmed) {
          if (context !== this.discardContextKey('draft')) {
            this.reportStaleDiscard();
            this.focusAuthoringInput();
            return;
          }
          this.closeAuthoringForm();
        }
      });
  }

  private closeAuthoringForm(): void {
    this.authoringSubscription?.unsubscribe();
    this.isRunningAuthoringAction.set(false);
    this.authoringMode.set(null);
    this.authoringFormDirty.set(false);
    this.authoringIntent = null;
    this.authoringMessage.set(null);
    this.operationError.set(null);
  }

  submitAuthoring(): void {
    const mode = this.authoringMode();
    if (!mode || !this.canAuthor() || this.isDraftMutationInProgress()) {
      return;
    }
    const basicsValid = this.ensureFormValid(this.authoringForm);
    const availabilityValid =
      mode !== 'create' ||
      !this.authoringAvailabilityForm ||
      this.ensureFormValid(this.authoringAvailabilityForm);
    if (!basicsValid || !availabilityValid) {
      if (!basicsValid)
        this.focusInvalidFormField(
          this.authoringForm,
          () => this.authoringFormElement,
          () => undefined,
        );
      else {
        if (this.authoringAvailabilityDetails)
          this.authoringAvailabilityDetails.nativeElement.open = true;
        this.focusInvalidFormField(
          this.authoringAvailabilityForm,
          () => this.authoringAvailabilityFormElement,
          () => undefined,
        );
      }
      return;
    }
    if (this.hasOpenDraftUnsavedChanges()) {
      this.showOperationError(
        localValidationError(
          'Open draft has unsaved changes',
          'Cancel this draft setup, then save or cancel the open draft before creating another one.',
        ),
      );
      return;
    }

    const draftCode = this.authoringFormValues.draftCode.trim();
    if (mode === 'create' && !this.authoringFormValues.displayName.trim()) {
      this.showOperationError(
        localValidationError(
          'Workflow setup incomplete',
          'Enter a process name.',
        ),
      );
      return;
    }
    if (mode === 'create' && !this.isCreateScopeShapeValid()) {
      if (this.authoringAvailabilityDetails)
        this.authoringAvailabilityDetails.nativeElement.open = true;
      this.focusInvalidFormField(
        this.authoringAvailabilityForm,
        () => this.authoringAvailabilityFormElement,
        () => undefined,
        'scopeKey',
      );
      return;
    }

    this.isRunningAuthoringAction.set(true);
    this.operationError.set(null);
    this.authoringMessage.set(null);
    this.authoringIntent ??= createWorkflowDefinitionMutationIntent();
    const request =
      mode === 'clone'
        ? this.lifecycleService.cloneVersion(
            this.selectedDefinition!.workflowCode,
            this.selectedDefinition!.definitionVersionCode,
            { draftCode },
            this.authoringIntent,
          )
        : this.lifecycleService.createDraft(
            {
              workflowCode: this.authoringFormValues.workflowCode.trim(),
              draftCode,
              document: this.buildNewDocument(),
            },
            this.authoringIntent,
          );

    this.authoringSubscription?.unsubscribe();
    this.authoringSubscription = request
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (draft) => {
          this.isRunningAuthoringAction.set(false);
          this.authoringIntent = null;
          this.authoringMode.set(null);
          this.authoringFormDirty.set(false);
          this.authoringMessage.set(
            mode === 'clone'
              ? 'Version cloned into a new draft.'
              : 'Draft created.',
          );
          this.applyLoadedDraft(draft);
          this.focusAfterRender(() => this.draftEditorHeading);
          this.writeDraftIdToUrl(draft.id);
          this.loadDrafts();
        },
        error: (error: unknown) => {
          this.isRunningAuthoringAction.set(false);
          this.showOperationError(mapWorkflowRegistryDiagnosticError(error));
        },
      });
  }

  requestOpenDraft(summary: WorkflowDefinitionDraftSummary): void {
    this.requestOpenDraftById(summary.id, true);
  }

  requestOpenDraftById(draftId: number, updateUrl = true): void {
    if (this.isDraftMutationInProgress()) {
      this.showOperationError(
        localValidationError(
          'Draft action in progress',
          'Wait for the current operation to finish before opening another draft.',
        ),
      );
      this.writeDraftIdToUrl(this.currentDraft()?.id ?? null);
      this.syncDraftGridSelection();
      return;
    }

    if (
      this.currentDraft()?.id === draftId ||
      this.loadingDraftId === draftId
    ) {
      return;
    }

    this.requestDiscardThen(
      () => {
        if (this.authoringMode()) {
          this.closeAuthoringForm();
        }
        this.loadDraft(draftId, updateUrl);
      },
      'draft',
      () => this.syncDraftGridSelection(),
    );
    this.syncDraftGridSelection();
  }

  closeDraft(): void {
    if (this.isDraftMutationInProgress()) {
      this.showOperationError(
        localValidationError(
          'Draft action in progress',
          'Wait for the current draft operation to finish before closing the editor.',
        ),
      );
      return;
    }

    this.requestDiscardThen(() => {
      this.clearCurrentDraft(true);
      this.focusAfterRender(() =>
        this.currentDraft() ? undefined : this.draftListHeading,
      );
    });
  }

  selectWorkbenchView(
    view: WorkflowDraftWorkbenchView,
    updateUrl = true,
  ): void {
    if (
      view !== this.activeWorkbenchView() &&
      this.hasUnappliedEditorChanges() &&
      !this.flushingPendingChanges &&
      !this.flushPendingEditorChanges()
    )
      return;
    const normalizedView = this.normalizeWorkbenchView(view);
    if (normalizedView !== 'map') this.retainedIssueStep.set(null);
    this.activeWorkbenchView.set(normalizedView);
    if (normalizedView === 'setup') this.refreshSetupFormConfigs();
    if (updateUrl && this.currentDraft()) {
      this.writeWorkbenchViewToUrl(normalizedView);
    }
  }

  private decisionInventoryReadKey(): string {
    return JSON.stringify([this.saveContextKey(), this.workingDocument()]);
  }

  private decisionInventoryPresentationOwner(): string {
    return JSON.stringify([this.currentDraft()?.id, this.currentDraft()?.draftCode,
      this.roles.accessProfile?.()?.actor?.actorId ?? null]);
  }

  async loadDecisionInventoryCatalogs(): Promise<void> {
    const document = this.workingDocument();
    if (!document || !this.currentDraft() || this.activeWorkbenchView() !== 'decision-inventory' || this.registryReadsSuspended()) return;
    const key = this.decisionInventoryReadKey(), request = ++this.decisionInventoryRequest;
    this.decisionInventoryRequestedKey = key; this.decisionInventoryCatalogKey.set('');
    this.decisionInventoryCatalog.set(null); this.decisionInventoryActivityCatalog.set(null);
    this.decisionInventoryLoading.set(true); this.decisionInventoryError.set('');
    const hasActivities = Array.isArray(document.nodes) && document.nodes.some(node => node?.typeCode === 'automated_step');
    const results = await Promise.allSettled([
      Promise.resolve().then(() => firstValueFrom(this.lifecycleService.getStepAuthoringCatalog(document))),
      Promise.resolve().then(() => hasActivities ? firstValueFrom(this.lifecycleService.getAutomatedActivityCatalog(document)) : null),
    ] as const);
    if (this.destroyRef.destroyed || request !== this.decisionInventoryRequest || key !== this.decisionInventoryReadKey()) return;
    this.decisionInventoryCatalogKey.set(key);
    this.decisionInventoryCatalog.set(results[0].status === 'fulfilled' ? results[0].value : null);
    this.decisionInventoryActivityCatalog.set(results[1].status === 'fulfilled' ? results[1].value : null);
    this.decisionInventoryLoading.set(false);
    if (results.some(result => result.status === 'rejected')) this.decisionInventoryError.set(
      'Some installed action information is unavailable. Saved configuration remains visible. Retry to check current availability.');
  }

  private scheduleDecisionInventoryPresentation(action: () => void): void {
    const request = ++this.decisionInventoryNavigation, key = this.decisionInventoryReadKey();
    const view = this.activeWorkbenchView();
    const location = () => JSON.stringify([this.mapSelection(), this.designerLocation(), this.stepEditorNodeId(),
      this.activeWorkView(), this.stepActionFocus(), this.automatedRouteFocus()]);
    const expectedLocation = location(), focus = this.browserDocument.activeElement;
    setTimeout(() => {
      if (request !== this.decisionInventoryNavigation || key !== this.decisionInventoryReadKey() || view !== this.activeWorkbenchView()
        || expectedLocation !== location() || this.ownerViewState.focusSuspended() || this.destroyRef.destroyed) return;
      const currentFocus = this.browserDocument.activeElement as HTMLElement | null;
      if (currentFocus !== focus && currentFocus !== this.browserDocument.body && currentFocus?.isConnected
        && !currentFocus.closest('[hidden],[inert]')) return;
      action();
    });
  }

  private decisionInventoryRouteValue(selection: WorkflowReadOnlyGraphSelection | null): string | null {
    if (selection?.kind !== 'edge') return null;
    const edges = this.workingDocument()?.edges.filter(edge => edge?.id === selection.sourceId) ?? [];
    return edges.length === 1 ? JSON.stringify(edges[0]) : null;
  }

  openDecisionInventory(): void {
    if (this.isDraftEditorInteractionBlocked()) return;
    const focus = this.browserDocument.activeElement as HTMLElement | null;
    this.requestMapEditorDeactivation(() => {
      const panel = this.inventoryMapPanel?.nativeElement;
      const elements = [panel?.parentElement, panel?.querySelector<HTMLElement>('.workflow-map-inspector__body'),
        panel?.querySelector<HTMLElement>('.workflow-graph-view__projected-inspector'),
        panel?.closest('.workflow-designer-layout')?.querySelector<HTMLElement>('#workflow-process-steps')];
      this.decisionInventoryOrigin = {
        owner: this.decisionInventoryPresentationOwner(), location: this.designerLocation(), selection: this.mapSelection(),
        focusedNodeId: this.stepEditorNodeId(), focusedRoute: this.editingRouteSelection(), focusedRouteValue: this.decisionInventoryRouteValue(this.editingRouteSelection()), automatedFocus: this.automatedRouteFocus(), focus, camera: this.inventoryGraph?.capturePresentationState() ?? null,
        scroll: Array.from(new Set(elements.filter((element): element is HTMLElement => !!element)))
          .map(element => ({ element, top: element.scrollTop, left: element.scrollLeft })),
      };
      this.decisionInventoryOwnerReturn = null;
      this.decisionInventoryInitialized.set(true); this.selectWorkbenchView('decision-inventory');
      this.scheduleDecisionInventoryPresentation(() => this.decisionInventoryEditor?.focusHeading());
    }, () => undefined);
  }

  backFromDecisionInventory(event: WorkflowDecisionInventoryContextEvent): void {
    if (this.activeWorkbenchView() !== 'decision-inventory' || event.context !== this.decisionInventoryContext()
      || this.isDraftEditorInteractionBlocked()) return;
    this.requestMapEditorDeactivation(() => {
      const origin = this.decisionInventoryOrigin;
      this.decisionInventoryOrigin = null; this.decisionInventoryOwnerReturn = null;
      this.selectWorkbenchView('map', false);
      if (!origin || origin.owner !== this.decisionInventoryPresentationOwner()) {
        this.writeWorkbenchViewToUrl('map');
        this.scheduleDecisionInventoryPresentation(() => this.mapInspectorHeading?.nativeElement.focus({ preventScroll: true })); return;
      }
      if (origin.selection) this.restoreMapInspection(origin.selection, false);
      this.restoreDesignerLocation(origin.location);
      this.stepEditorNodeId.set(origin.focusedNodeId === this.selectedMapNode()?.id ? origin.focusedNodeId : null);
      const restoredRoute = origin.focusedRoute ? this.mapEdgeSelectionBySourceId(origin.focusedRoute.sourceId) : null;
      if (restoredRoute && origin.focusedRouteValue !== null && this.decisionInventoryRouteValue(restoredRoute) === origin.focusedRouteValue)
        this.editingRouteSelection.set(restoredRoute);
      if (origin.automatedFocus && this.selectedMapNode()?.typeCode === 'automated_step') this.automatedRouteFocus.set(origin.automatedFocus);
      this.writeWorkbenchViewToUrl('map');
      this.scheduleDecisionInventoryPresentation(() => {
        if (origin.camera) this.inventoryGraph?.restorePresentationState(origin.camera);
        for (const position of origin.scroll) if (position.element.isConnected) {
          position.element.scrollTop = position.top; position.element.scrollLeft = position.left;
        }
        if (origin.focus?.isConnected && !origin.focus.closest('[hidden],[inert]')) origin.focus.focus({ preventScroll: true });
        else this.mapInspectorHeading?.nativeElement.focus({ preventScroll: true });
      });
    }, () => undefined);
  }

  canReturnToDecisionInventory(): boolean {
    return this.activeWorkbenchView() === 'map' && this.decisionInventoryOwnerReturn === this.decisionInventoryPresentationOwner();
  }

  returnToDecisionInventory(): void {
    if (!this.canReturnToDecisionInventory() || this.isDraftEditorInteractionBlocked()) return;
    this.requestMapEditorDeactivation(() => {
      this.decisionInventoryCheckAddMode.set(false);
      this.selectWorkbenchView('decision-inventory');
      this.scheduleDecisionInventoryPresentation(() => this.decisionInventoryEditor?.focusSelection());
    }, () => undefined);
  }

  private currentDecisionInventoryOwner(request: WorkflowDecisionOwnerRequest): WorkflowDecisionOwnerRequest | null {
    const inventory = this.decisionInventory();
    const matches = [...inventory.results, ...inventory.completionRequirements, ...inventory.moduleChecks, ...inventory.genericChecks]
      .flatMap(row => row.owners).filter(owner => JSON.stringify(owner) === JSON.stringify(request));
    return matches.length === 1 ? matches[0] : null;
  }

  openDecisionInventoryOwner(event: WorkflowDecisionInventoryOwnerEvent): void {
    if (!event?.request || this.activeWorkbenchView() !== 'decision-inventory' || this.isDraftEditorInteractionBlocked()
      || event.context !== this.decisionInventoryContext() || !this.currentDecisionInventoryOwner(event.request)) return;
    this.requestMapEditorDeactivation(() => {
      const request = event.context === this.decisionInventoryContext() ? this.currentDecisionInventoryOwner(event.request) : null;
      if (!request) { this.editorMessage.set('This source changed. Review the current inventory before opening its owner.'); return; }
      const document = this.workingDocument();
      const nodes = request.nodeId === null ? [] : document?.nodes.filter(node => node.id === request.nodeId) ?? [];
      if (request.nodeId !== null && nodes.length !== 1) return;
      const node = nodes[0];
      const commands = node?.declaration?.behavior.commands ?? [];
      const command = request.command ? commands.filter(item => sameCapabilityReference(item.capability, request.command!)
        && commandIdentity(item) === commandIdentity({ capability: request.command! })) : [];
      if (request.command && node?.typeCode !== 'automated_step' && command.length !== 1) return;
      this.activeWorkbenchView.set('map'); this.decisionInventoryCheckAddMode.set(false);
      if (node && !this.openMapNodeById(node.id, false, 'overview', true, false)) {
        this.activeWorkbenchView.set('decision-inventory');
        this.editorMessage.set('This saved source is unavailable in its editor. Its configuration remains visible in the inventory.');
        return;
      }
      this.decisionInventoryOwnerReturn = this.decisionInventoryPresentationOwner();
      if (request.kind === 'generic-check') {
        if (!node) { this.clearFocusedMapContext(); this.mapSelection.set(null); }
        this.setInspectorPane('decisions');
      } else if (node?.typeCode === 'automated_step') {
        this.stepWorkspaceScope.set('behavior');
        this.behaviorSection.set(request.kind === 'routing' ? 'routes' : 'readiness');
        this.mapInspectorPane.set('overview');
        if (request.kind === 'routing' && request.outcomeCode)
          this.requestAutomatedResultFocus(node, request.outcomeCode);
        else this.automatedRouteFocus.set(null);
      } else if (request.kind === 'work-requirement') {
        this.stepActionFocus.set(null); this.behaviorFormFocus.set(null);
        this.stepWorkspaceScope.set('behavior'); this.behaviorSection.set('readiness'); this.mapInspectorPane.set('overview');
        this.revealWorkView('requirements');
      } else if (request.kind === 'routing' && command[0]) this.openStepOutcomes(command[0], request.outcomeCode);
      else if (request.kind === 'action' && command[0]) this.openStepActions(command[0]);
      this.writeWorkbenchViewToUrl('map');
      const key = this.decisionInventoryReadKey();
      this.scheduleDecisionInventoryPresentation(() => {
        if (key !== this.decisionInventoryReadKey() || !this.canReturnToDecisionInventory() || this.ownerViewState.focusSuspended()
          || request.nodeId !== null && this.selectedMapNode()?.id !== request.nodeId) return;
        if (request.kind === 'generic-check') {
          if (this.decisionEditor) this.decisionEditor.document = this.workingDocument();
          if (!this.decisionEditor?.focusSavedInventoryCheck(request))
            this.editorMessage.set('The saved check changed or has pending input. No replacement check was started.');
        } else if (request.kind === 'work-requirement' && request.requirementId)
          this.requirementsEditor?.focusRequirementCard(request.requirementId);
        else if (node?.typeCode === 'automated_step') {
          if (request.kind === 'activity') this.focusAutomatedActivityHeading();
        }
        else this.workViewHeading?.nativeElement.focus({ preventScroll: true });
      });
    }, () => undefined);
  }

  addDecisionInventoryCheck(event: WorkflowDecisionInventoryContextEvent): void {
    if (this.activeWorkbenchView() !== 'decision-inventory' || event.context !== this.decisionInventoryContext()
      || this.isDraftEditorInteractionBlocked() || !this.canAuthor()) return;
    this.requestMapEditorDeactivation(() => {
      if (event.context !== this.decisionInventoryContext() || !this.canAuthor()) return;
      this.clearFocusedMapContext(); this.mapSelection.set(null); this.activeWorkbenchView.set('map');
      this.setInspectorPane('decisions'); this.decisionInventoryCheckAddMode.set(true);
      this.decisionInventoryOwnerReturn = this.decisionInventoryPresentationOwner(); this.writeWorkbenchViewToUrl('map');
      const key = this.decisionInventoryReadKey();
      this.scheduleDecisionInventoryPresentation(() => {
        if (key !== this.decisionInventoryReadKey() || !this.canReturnToDecisionInventory() || !this.decisionInventoryCheckAddMode()
          || this.ownerViewState.focusSuspended() || !this.canAuthor()) return;
        if (!this.decisionEditor?.beginInventoryCheckAdd())
          this.editorMessage.set('Check editing is unavailable. Review the retained check declarations and current reference choices. No check was added.');
      });
    }, () => undefined);
  }

  openWorkflowSettings(task?: WorkflowSettingsTask): void {
    if (this.isDraftEditorInteractionBlocked()) return;
    this.requestMapEditorDeactivation(() => {
      this.workflowStatusOrigin = null;
      this.revealWorkflowSettingsTask(task ?? this.activeWorkflowSettingsTask());
      this.refreshSetupFormConfigs();
      this.scheduleWorkflowSettingsEntryFocus();
    }, () => undefined);
  }

  private scheduleWorkflowSettingsEntryFocus(): void {
    const generation = this.workflowSettingsRevealGeneration, task = this.activeWorkflowSettingsTask(), owner = this.saveContextKey(),
      source = JSON.stringify(this.workingDocument()), focused = this.browserDocument.activeElement, barrier = this.processDataFocusBarrier;
    setTimeout(() => {
      if (this.destroyRef.destroyed || barrier !== this.processDataFocusBarrier || generation !== this.workflowSettingsRevealGeneration || this.activeWorkbenchView() !== 'setup' ||
        task !== this.activeWorkflowSettingsTask() || owner !== this.saveContextKey() || source !== JSON.stringify(this.workingDocument()) ||
        this.isDraftEditorInteractionBlocked() || this.ownerViewState.focusSuspended()) return;
      const active = this.browserDocument.activeElement;
      if (active !== focused && active !== this.browserDocument.body && active !== this.browserDocument.documentElement &&
        active?.isConnected && !active.closest('[hidden],[inert]')) return;
      const target = this.setupHeading?.nativeElement;
      if (target?.isConnected && !target.closest('[hidden],[inert]')) target.focus();
    });
  }

  openHearingLifecycleSettings(): void {
    if (this.isDraftEditorInteractionBlocked()) return;
    this.requestMapEditorDeactivation(() => {
      this.revealWorkflowSettingsTask('hearing');
      this.focusAfterRender(() => this.hearingLifecycleHeading);
    }, () => undefined);
  }

  openNumberingGeneralSettings(): void {
    if (this.isDraftEditorInteractionBlocked()) return;
    this.requestMapEditorDeactivation(() => {
      this.revealWorkflowSettingsTask('general');
      this.refreshSetupFormConfigs();
      if (this.identityAvailabilityDetails) this.identityAvailabilityDetails.nativeElement.open = true;
      if (!this.canAuthor()) {
        this.focusAfterRender(() => this.setupHeading);
        return;
      }
      const field = this.identityFormValues.scopeTypeCode !== 'global' && !this.identityFormValues.scopeKey.trim()
        ? 'scopeKey' : 'scopeTypeCode';
      this.focusInvalidFormField(this.identityAvailabilityForm, () => this.identityAvailabilityFormElement,
        () => this.setupHeading, field);
    }, () => undefined);
  }

  readonly hearingLifecycleAvailable = computed(() =>
    this.authoringReferences()?.subjectTypeCodes.includes('hearing_cycle') === true,
  );
  readonly hearingStartingValuesIssue = computed(() =>
    processDataShapeError(this.workingDocument()?.processData),
  );
  readonly hearingRequiredStartingValues = computed(() =>
    !this.hearingStartingValuesIssue()
      ? this.workingDocument()?.processData?.fields.filter(field => field.requiredAtStart === true) ?? []
      : [],
  );

  reviewHearingStartingValues(key?: string): void {
    if (this.isDraftEditorInteractionBlocked()) return;
    const targetKey = key ?? this.hearingRequiredStartingValues()[0]?.key;
    this.requestMapEditorDeactivation(() => {
      this.revealWorkflowSettingsTask('data');
      this.scheduleProcessDataFocus(targetKey === undefined ? undefined : editor => editor.focusField(targetKey, 'requiredAtStart', true));
    }, () => undefined);
  }

  private scheduleProcessDataFocus(action?: (editor: WorkflowProcessDataEditorComponent) => boolean | void): void {
    this.processDataFocusBarrier++;
    const request = ++this.processDataFocusRequest, owner = this.workFormViewContext(), source = JSON.stringify(this.workingDocument()),
      view = this.processDataOwnerView(), focused = this.browserDocument.activeElement;
    setTimeout(() => {
      const current = () => !this.destroyRef.destroyed && request === this.processDataFocusRequest && this.isProcessDataOwnerActive()
        && owner === this.workFormViewContext() && source === JSON.stringify(this.workingDocument())
        && view === this.processDataOwnerView() && !this.ownerViewState.focusSuspended();
      if (!current()) return;
      const active = this.browserDocument.activeElement;
      if (active !== focused && active !== this.browserDocument.body && active !== this.browserDocument.documentElement &&
        active?.isConnected && !active.closest('[hidden],[inert]')) return;
      const editor = this.processDataEditor;
      if (editor && action) {
        if (action(editor) === false) {
          this.editorMessage.set('The requested shared value is no longer uniquely available. Review the retained shared-data settings.');
        } else return;
      }
      if (!current()) return;
      const target = this.processDataHeading?.nativeElement;
      if (!target?.isConnected || target.closest('[hidden],[inert]')) return;
      target.focus({ preventScroll: true });
      if (current() && this.browserDocument.activeElement === target && target.isConnected && !target.closest('[hidden],[inert]'))
        target.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    });
  }

  private revealProcessDataPath(path: string): void {
    this.revealWorkflowSettingsTask('data');
    const explicitKey = /\.fields\[key=([^\]]+)\]/.exec(path)?.[1], index = /\.fields\[(\d+)\]/.exec(path)?.[1];
    const savedFields = this.currentDraft()?.document.processData?.fields;
    const key = explicitKey ?? (index !== undefined && Array.isArray(savedFields) ? savedFields[Number(index)]?.key : undefined);
    const control = /\.requiredAtStart(?:\.|$)/.test(path) ? 'requiredAtStart' : /\.recordTypeCode(?:\.|$)/.test(path) ? 'recordTypeCode'
      : /\.type(?:\.|$)/.test(path) ? 'type' : /\.key(?:\.|$)/.test(path) ? 'key' : 'name';
    this.scheduleProcessDataFocus(key === undefined && !/\.fields\[/.test(path) ? undefined
      : editor => key === undefined ? false : editor.focusField(key, control, true));
  }

  useHearingLifecycle(): void {
    const document = this.workingDocument();
    if (!document || !this.canAuthor() || this.isDraftEditorInteractionBlocked()
      || !this.hearingLifecycleAvailable() || document.subjectTypeCode === 'hearing_cycle') return;
    this.setWorkingDocument({ ...document, subjectTypeCode: 'hearing_cycle' });
    this.identityFormValues = { ...this.identityFormValues, subjectTypeCode: 'hearing_cycle' };
    this.refreshSetupFormConfigs();
    this.focusAfterRender(() => this.hearingLifecycleHeading);
  }

  onEditingLocationChange(value: string, control: HTMLSelectElement): void {
    this.selectEditingLocation(value);
    // A rejected navigation leaves the model unchanged; restore the native control too.
    control.value = this.editingLocation();
  }

  selectEditingLocation(value: string): void {
    if (this.isDraftEditorInteractionBlocked()) return;
    if (value === 'map') {
      this.showWholeWorkflowOverview();
      return;
    }
    if (value === 'setup') {
      this.openWorkflowSettings();
      return;
    }
    const separator = value.indexOf(':');
    const scope = value.slice(0, separator);
    const nodeId = value.slice(separator + 1);
    if (
      separator < 0 ||
      !['behavior', 'dashboard', 'detail'].includes(scope) ||
      ![...this.humanStepNodes(), ...this.automatedStepNodes()].some(
        (node) => node.id === nodeId,
      )
    )
      return;
    if (this.automatedStepNodes().some((node) => node.id === nodeId)) {
      if (scope !== 'behavior') return;
      this.requestMapEditorDeactivation(
        () => {
          this.openMapNodeById(nodeId, true, 'overview');
          this.writeWorkbenchViewToUrl('map');
        },
        () => undefined,
      );
      return;
    }
    const workspaceScope =
      scope === 'behavior' &&
      this.activeWorkbenchView() === 'map' &&
      this.selectedMapNode()?.typeCode === 'human_step' &&
      !this.isAddingStep()
        ? this.stepWorkspaceScope()
        : (scope as 'behavior' | 'dashboard' | 'detail');
    if (this.selectedMapNode()?.id === nodeId && !this.isAddingStep()) {
      if (!this.flushPendingEditorChanges()) return;
      this.stepEditorNodeId.set(nodeId);
      this.stepEditorOpened.set(true);
      this.selectWorkbenchView('map');
      this.selectStepWorkspaceScope(workspaceScope);
      if (workspaceScope === 'behavior')
        this.selectBehaviorSection('readiness');
      this.writeWorkbenchViewToUrl('map');
      return;
    }
    this.requestMapEditorDeactivation(
      () => {
        if (!this.openMapNodeById(nodeId, true)) return;
        this.stepWorkspaceScope.set(workspaceScope);
        if (workspaceScope === 'behavior')
          this.selectBehaviorSection('readiness');
        this.writeWorkbenchViewToUrl('map');
      },
      () => undefined,
    );
  }

  readonly isProcessFirstDocument = computed(() =>
    ['1.10', '1.11', '1.12', '1.13', '1.14', '1.15', '1.16', '1.17', '1.18', '1.19', '1.20', '1.21', '1.22'].includes(
      this.workingDocument()?.payloadSchemaVersion ?? '',
    ),
  );

  selectRoutingView(view: WorkflowDraftRoutingView, updateUrl = true): void {
    if (!this.flushingPendingChanges && !this.flushPendingEditorChanges())
      return;
    this.activeRoutingView.set(view);
    this.activeWorkbenchView.set('map');
    this.setInspectorPane(view === 'paths' ? 'path' : 'transitions');
    if (updateUrl && this.currentDraft()) {
      this.writeWorkbenchViewToUrl('map');
    }
  }

  selectReviewView(view: WorkflowDraftReviewView, updateUrl = true): void {
    this.activeReviewView.set(view);
    if (updateUrl && this.currentDraft()) {
      this.writeReviewViewToUrl(view);
    }
  }

  selectReviewTab(id: string): void {
    if (id === 'readiness' || id === 'changes') this.selectReviewView(id);
  }

  private reviewTabElement(
    view: WorkflowDraftReviewView,
  ): ElementRef<HTMLElement> | undefined {
    const element = this.reviewTabs?.nativeElement.querySelector<HTMLElement>(
      `#workflow-review-tab-${view}`,
    );
    return element ? new ElementRef(element) : undefined;
  }

  @ViewChild('reviewPublicationActions')
  private reviewPublicationActions?: WorkflowDefinitionLifecycleActionsComponent;

  reviewPrimaryActionLabel(): string | null {
    if (this.lifecycleOperationInProgress())
      return (
        this.reviewPublicationActions?.publishActionLabel() ?? 'Publishing…'
      );
    if (this.isSaving()) return 'Saving…';
    if (this.isValidating()) return 'Checking…';
    return this.reviewNextActionLabel();
  }

  reviewPrimaryActionDisabled(): boolean {
    // Render from the parent's current revision; the child receives its inputs
    // later in this render. publishDraft rechecks its own guards and fields.
    return this.isDraftEditorInteractionBlocked();
  }

  reviewNextAction(): void {
    if (this.isDraftEditorInteractionBlocked()) return;
    switch (this.reviewPublicationReadiness().code) {
      case 'save_required':
        this.saveDraft();
        return;
      case 'validation_required':
        if (this.automaticValidationFailed()) this.retryAutomaticValidation();
        else this.validateAutomatically();
        return;
      case 'validation_invalid':
        if (this.diagnostics().length) {
          const next = this.firstReviewFinding();
          if (next) this.focusValidationDiagnostic(next.diagnostic, next.id);
          else this.focusAfterRender(() => this.reviewFindingsHeading);
        } else if (this.automaticValidationFailed())
          this.retryAutomaticValidation();
        return;
      case 'ready':
        void this.reviewPublicationActions?.publishDraft();
        return;
    }
  }

  readonly reviewNextActionLabel = computed(() => {
    switch (this.reviewPublicationReadiness().code) {
      case 'save_required':
        return this.canAuthor() ? 'Save changes' : null;
      case 'validation_required':
        return this.canValidate() && this.automaticValidationFailed()
          ? 'Retry checks'
          : null;
      case 'validation_invalid':
        return this.diagnostics().length
          ? 'Review findings'
          : this.canValidate() && this.automaticValidationFailed()
            ? 'Retry checks'
            : null;
      case 'operation_in_progress':
        return null;
      case 'ready':
        return 'Publish';
      default:
        return null;
    }
  });

  focusNextReviewFinding(): void {
    if (this.isDraftEditorInteractionBlocked()) return;
    const next = this.nextReviewFinding();
    if (next) this.focusValidationDiagnostic(next.diagnostic, next.id);
  }

  private canNavigateReviewFinding(
    diagnostic: WorkflowDefinitionDiagnostic,
  ): boolean {
    return (
      !this.isPublicationSettingsDiagnostic(diagnostic) &&
      this.canFocusDiagnostic(diagnostic)
    );
  }

  returnToValidationReview(): void {
    if (this.isDraftEditorInteractionBlocked()) return;
    this.selectWorkbenchView('review');
    if (this.activeWorkbenchView() !== 'review') return;
    this.selectReviewView('readiness');
    this.focusAfterRender(() => this.reviewFindingsHeading);
  }

  returnToSavedChanges(): void {
    if (this.isDraftEditorInteractionBlocked()) return;
    this.selectWorkbenchView('review');
    if (this.activeWorkbenchView() !== 'review') return;
    this.selectReviewView('changes');
    this.focusAfterRender(() => this.reviewTabElement('changes'));
  }

  isWorkbenchViewPending(view: WorkflowDraftWorkbenchView): boolean {
    switch (view) {
      case 'setup':
        return (
          this.assignmentPolicyFormDirty() ||
          this.processDataFormDirty() ||
          this.automationFormDirty() ||
          this.navigationFormDirty() ||
          this.workflowStatusFormDirty()
        );
      case 'map':
        return (
          this.assignmentPolicyFormDirty() ||
          this.processDataFormDirty() ||
          this.automationFormDirty() ||
          this.workflowStatusFormDirty() ||
          this.automatedStepFormDirty() ||
          this.navigationFormDirty() ||
          this.nodeFormDirty() ||
          this.stepAssignmentPolicyFormDirty() ||
          this.edgeFormDirty() ||
          this.reviewRoutingDirty() ||
          this.dashboardEditorDirty() ||
          this.staffPageEditorDirty() ||
          this.workComponentsDirty() ||
          this.transitionFormDirty() ||
          this.decisionFormDirty() ||
          this.requirementFormDirty() || (this.timerFormDirty() || this.childWorkFormDirty()) ||
          this.notificationFormDirty()
        );
      default:
        return false;
    }
  }

  returnToPendingChanges(): void {
    if (this.workflowStatusFormDirty()) { this.focusPendingEditor('workflow-status'); return; }
    if (this.automatedStepFormDirty()) {
      this.activeWorkbenchView.set('map');
      this.automatedStepEditor?.focusPendingChanges();
      return;
    }
    if (this.childWorkFormDirty()) {
      this.focusPendingEditor('child');
      return;
    }
    if (this.timerFormDirty()) {
      this.focusPendingEditor('timer');
      return;
    }
    if (!this.pendingWorkbenchView()) return;
    this.focusPendingEditor(
      this.automationFormDirty()
        ? 'automation'
        : this.navigationFormDirty()
          ? 'navigation'
          : this.processDataFormDirty()
            ? 'process-data'
            : this.assignmentPolicyFormDirty()
              ? 'assignment'
              : this.stepAssignmentPolicyFormDirty()
                ? 'step-assignment'
                : this.nodeFormDirty()
                  ? 'node'
                  : this.dashboardEditorDirty()
                    ? 'dashboard'
                    : this.staffPageEditorDirty()
                      ? 'staff-page'
                      : this.workComponentsDirty()
                        ? 'work'
                        : this.reviewRoutingDirty()
                          ? 'review'
                          : this.edgeFormDirty()
                            ? 'edge'
                            : this.requirementFormDirty()
                              ? 'requirement'
                              : this.transitionFormDirty()
                                ? 'transition'
                                : this.notificationFormDirty()
                                  ? 'notification'
                                  : 'decision',
    );
  }

  private focusPendingEditor(
    kind: WorkflowPendingEditorKind,
    exactFocus?: () => void,
  ): void {
    if (kind === 'workflow-status') {
      this.revealWorkflowSettingsTask('statuses');
      this.scheduleWorkflowStatusFocus(() => exactFocus ? exactFocus() : this.workflowStatusEditor?.focusPendingChanges());
      return;
    }
    if (kind === 'edge' && this.isInspectingRoute()) {
      const editor = this.edgeEditor;
      if (!editor || (!editor.pendingChanges() && !editor.isAddingEdge())) {
        this.focusAfterRender(() => this.routeInspection);
        return;
      }
      const current = this.mapSelection();
      const selection = current?.kind === 'edge' && current.sourceId === editor.selectedEdgeId()
        ? current
        : editor.selectedEdgeId() ? this.mapEdgeSelectionBySourceId(editor.selectedEdgeId()!) : null;
      this.stepEditorNodeId.set(null);
      this.editingRouteSelection.set(selection);
      this.mapSelection.set(selection);
    }
    if (kind === 'node' && this.inlineStepNameEditing()) {
      this.focusAfterRender(() => this.inlineStepNameInput);
      return;
    }
    if (kind === 'automation') {
      if (this.automatedStepFormDirty()) {
        this.activeWorkbenchView.set('map');
        if (exactFocus) setTimeout(exactFocus);
        else this.automatedStepEditor?.focusPendingChanges();
        return;
      }
      if (exactFocus) {
        this.revealWorkflowSettingsTask('automation');
        this.automationExpanded.set(true);
        this.writeWorkbenchViewToUrl('setup');
        setTimeout(() => {
          exactFocus();
        });
        return;
      }
      this.openAutomation();
      return;
    }
    if (kind === 'navigation') {
      this.revealWorkflowSettingsTask('navigation');
      this.writeWorkbenchViewToUrl('setup');
      setTimeout(() => exactFocus ? exactFocus() : this.navigationSettingsEditor?.focusPendingChanges());
      return;
    }
    if (kind === 'process-data') {
      this.revealWorkflowSettingsTask('data');
      this.scheduleProcessDataFocus(editor => exactFocus ? exactFocus() : editor.focusPendingChanges(true));
      return;
    }
    if (kind === 'assignment') {
      this.revealTechnicalView('assignment');
      this.writeWorkbenchViewToUrl('setup');
      if (exactFocus) setTimeout(exactFocus);
      else this.assignmentPolicyEditor?.focusPendingChanges();
      return;
    }
    if (kind === 'dashboard' || kind === 'staff-page') {
      const scope = kind === 'dashboard' ? 'dashboard' : 'detail';
      this.stepWorkspaceScope.set(scope);
      this.activeWorkbenchView.set('map');
      this.setInspectorPane('overview');
      this.writeWorkbenchViewToUrl('map');
      setTimeout(() => {
        if (exactFocus) exactFocus();
        else this.pageEditors?.find(editor => editor.scope === scope)?.focusPendingChanges();
      });
      return;
    }
    this.stepWorkspaceScope.set('behavior');
    this.behaviorSection.set(
      kind === 'review' || kind === 'edge'
        ? 'routes'
        : kind === 'notification'
          ? 'notifications'
          : kind === 'child'
            ? this.selectedMapNode()?.typeCode === 'terminal' ? 'details' : 'children'
          : kind === 'timer'
            ? 'timers'
          : kind === 'requirement' || kind === 'work' || kind === 'step-assignment'
            ? 'readiness'
            : kind === 'decision' || kind === 'transition'
              ? 'transitions'
              : 'details',
    );
    if (kind === 'step-assignment' && !exactFocus) this.requestResponsibilityFocus();
    if (kind === 'step-assignment') this.revealWorkView('assignment');
    if (kind === 'requirement') this.revealWorkView('requirements');
    if (kind === 'work') this.revealWorkView('components');
    this.activeWorkbenchView.set('map');
    this.setInspectorPane(
      kind === 'edge'
        ? 'path'
        : kind === 'transition'
          ? 'transitions'
          : kind === 'decision'
            ? 'decisions'
            : 'overview',
    );
    this.writeWorkbenchViewToUrl('map');
    if (kind === 'timer') {
      this.focusTimerPendingChanges(exactFocus);
      return;
    }
    if (exactFocus) {
      setTimeout(exactFocus);
      return;
    }
    if (kind === 'step-assignment') return;
    if (kind === 'work') {
      setTimeout(() => this.workComponentsEditor?.focusPendingChanges());
      return;
    }
    if (kind === 'edge') this.edgeEditor?.focusPendingChanges();
    else if (kind === 'transition')
      this.transitionEditor?.focusPendingChanges();
    else if (kind === 'decision') this.decisionEditor?.focusPendingChanges();
    else if (kind === 'child') setTimeout(() => this.childWorkEditor?.focusPendingChanges());
    else if (kind === 'requirement')
      this.requirementsEditor?.focusPendingChanges();
    else if (kind === 'notification')
      setTimeout(() => this.stepNotificationsEditor?.focusPendingChanges());
    else if (kind === 'node') {
      if (!this.nodeForm?.theFormGroup?.invalid && this.stepKeyValidationError)
        this.focusInvalidStepKey();
      else
        this.focusInvalidFormField(
          this.nodeForm,
          () => this.nodeFormElement,
          () => this.nodeEditorHeading,
        );
    } else
      setTimeout(() =>
        this.pageEditors
          ?.find((editor) => editor.scope === 'behavior')
          ?.focusPendingChanges(),
      );
  }

  inspectStepFromOutline(nodeId: string | null): void {
    if (!nodeId || this.isDraftEditorInteractionBlocked()) return;
    this.requestMapEditorDeactivation(() => {
      if (this.openMapNodeById(nodeId, false, 'overview', false)) {
        this.stepEditorNodeId.set(null);
        this.closeStepsDrawer();
      }
    }, () => undefined);
  }

  openStepWorkspace(): void {
    const node =
      this.selectedMapNode()?.typeCode === 'human_step'
        ? this.selectedMapNode()
        : this.humanStepNodes()[0];
    if (node) this.openNodeFromGraph(node.id);
  }

  openNodeFromGraph(nodeId: string): void {
    if (this.isDraftEditorInteractionBlocked()) return;
    const initiatingControl = this.browserDocument.activeElement as HTMLElement | null;
    this.requestMapEditorDeactivation(
      () => {
        this.editorMapReturnSelection = this.mapNodeSelectionBySourceId(nodeId);
        this.editingRouteSelection.set(null);
        this.automatedRouteFocus.set(null);
        this.openMapNodeById(nodeId, true);
        this.stepEditorReturnFocus = initiatingControl;
      },
      () => undefined,
    );
  }

  openEdgeFromGraph(edgeId: string): void {
    const document = this.workingDocument();
    if (!document || this.isDraftEditorInteractionBlocked()) return;
    const graph = projectWorkflowDraftGraph(document);
    const matches = graph.edges.filter(edge => edge.sourceId === edgeId);
    if (matches.length !== 1) return;
    this.openRoutingEditor(this.graphSelectionForEdge(matches[0], graph.edges));
  }

  /** Deliberate owner handoff; a selected path never enters an editor by itself. */
  openRoutingEditor(selection: WorkflowReadOnlyGraphSelection): void {
    if (this.isDraftEditorInteractionBlocked()) return;
    const initiatingControl = this.browserDocument.activeElement as HTMLElement | null;
    this.requestMapEditorDeactivation(() => {
      const document = this.workingDocument();
      const route = workflowRouteInspection(document, selection);
      if (!document || !route?.canOpenEditor || selection.sourceIndex === null) {
        this.editorMessage.set('The route changed or is unavailable. Select its current declaration before opening the editor.');
        return;
      }
      const edge = document.edges[selection.sourceIndex];
      const source = this.routeEditorOwner(document, edge);
      if (source && this.mapNodeSelectionBySourceId(source.id)?.isAuthorable) {
        const savedCommands = Array.isArray(source.declaration?.behavior.commands) ? source.declaration.behavior.commands : [];
        const outcomeCommands = source.typeCode === 'human_step' && edge.outcomeCode
          ? savedCommands.filter(command => {
            if (!command?.capability?.key) return false;
            const row = this.humanOutcomeInventoryRow(document, source.id, command, edge.outcomeCode!);
            return row?.edgeIds.length === 1 && row.edgeIds[0] === edge.id;
          }) : [];
        if (outcomeCommands.length === 1) {
          if (!this.openMapNodeById(source.id, false, 'overview', true, false)) return;
          this.openStepOutcomes(outcomeCommands[0], edge.outcomeCode!);
          this.editorMapReturnSelection = selection;
          this.stepEditorReturnFocus = initiatingControl;
          this.writeWorkbenchViewToUrl('map');
          return;
        }
        if (source.typeCode !== 'automated_step' && this.edgeEditor &&
          !this.edgeEditor.reviewExactEdge(document, selection.sourceId, selection.sourceIndex, true)) {
          this.editorMessage.set('The path editor has unfinished input or its target changed. Review that input before opening another route.');
          this.focusPendingEditor('edge');
          return;
        }
        this.openMapNodeById(source.id, false, source.typeCode === 'automated_step' ? 'overview' : 'path', true, false);
        this.editingRouteSelection.set(selection);
        this.stepWorkspaceScope.set('behavior');
        this.behaviorSection.set('routes');
        if (source.typeCode === 'automated_step') {
          if (!this.requestAutomatedResultFocus(source, edge.outcomeCode ?? null))
            this.focusAfterRender(() => this.mapInspectorHeading);
        } else if (!this.edgeEditor) this.reviewRouteAfterRender(selection, true);
      } else if (!this.openMapEdgeBySourceId(selection.sourceId, true)) return;
      this.editorMapReturnSelection = selection;
      this.stepEditorReturnFocus = initiatingControl;
      this.writeWorkbenchViewToUrl('map');
    }, () => undefined);
  }

  private routeEditorOwner(
    document: WorkflowDefinitionDocument,
    edge: WorkflowDefinitionEdge,
  ): WorkflowDefinitionNode | null {
    const sources = document.nodes.filter(node => node?.id === edge.fromNodeId);
    if (sources.length !== 1) return null;
    const source = sources[0];
    if (source.typeCode === 'human_step' || source.typeCode === 'automated_step') return source;
    if (source.typeCode !== 'routing_decision') return null;
    const incoming = document.edges.filter(path => path?.toNodeId === source.id);
    if (incoming.length !== 1 || incoming[0].outcomeCode || incoming[0].pathKindCode) return null;
    const owners = document.nodes.filter(node => node?.id === incoming[0].fromNodeId && node.typeCode === 'human_step');
    return owners.length === 1 && document.edges.filter(path => path?.fromNodeId === owners[0].id).length === 1
      ? owners[0] : null;
  }

  openDecisionGateFromGraph(
    received: WorkflowReadOnlyGraphDecisionGateRequest,
  ): void {
    const document = this.workingDocument();
    if (!document || this.isDraftEditorInteractionBlocked()) {
      return;
    }

    const graph = projectWorkflowDraftGraph(document);
    const gate =
      graph.workflowDecisionGates?.find(
        (candidate) => candidate.id === received.gateId,
      ) ??
      graph.nodes
        .flatMap((node) => node.decisionGates ?? [])
        .find((candidate) => candidate.id === received.gateId) ??
      null;
    const request = gate ? workflowDecisionGateRequest(gate) : null;
    if (!request) {
      return;
    }

    const nodeId = request.ownerSourceNodeId;
    const stepSelection =
      nodeId === null ? null : this.mapNodeSelectionBySourceId(nodeId);
    const stepNode =
      stepSelection?.sourceIndex === null ||
      stepSelection?.sourceIndex === undefined
        ? null
        : (document.nodes[stepSelection.sourceIndex] ?? null);
    if (
      nodeId !== null &&
      (!stepSelection?.isAuthorable ||
        stepSelection.sourceIndex !== request.ownerSourceNodeIndex ||
        stepNode?.typeCode !== 'human_step')
    ) {
      return;
    }

    this.requestMapEditorDeactivation(
      () => {
        this.isAddingStep.set(false);
        this.activeWorkbenchView.set('map');
        if (nodeId === null) {
          this.mapSelection.set(null);
          this.setInspectorPane('decisions');
        } else {
          this.acceptMapSelection({
            selection: stepSelection,
            origin: 'visual',
            moveFocusToInspector: false,
          });
          this.setInspectorPane('decisions');
        }
        this.decisionEditor?.focusDecisionGate(request);
        this.writeWorkbenchViewToUrl('map');
      },
      () => undefined,
    );
  }

  openDecisionScopeFromGraph(
    received: WorkflowReadOnlyGraphDecisionScopeRequest,
  ): void {
    if (this.isDraftEditorInteractionBlocked()) {
      return;
    }
    const resolved = this.resolveDecisionScopeRequest(received);
    if (!resolved) {
      return;
    }

    this.requestMapEditorDeactivation(
      () => {
        if (this.isDraftEditorInteractionBlocked()) {
          return;
        }
        const current = this.resolveDecisionScopeRequest(received);
        if (!current) {
          return;
        }

        this.isAddingStep.set(false);
        this.activeWorkbenchView.set('map');
        if (current.selection === null) {
          this.mapSelection.set(null);
        } else {
          this.acceptMapSelection({
            selection: current.selection,
            origin: 'visual',
            moveFocusToInspector: false,
          });
        }
        this.setInspectorPane('decisions');
        this.decisionEditor?.focusDecisionOverview(current.stepNodeId);
        this.writeWorkbenchViewToUrl('map');
      },
      () => undefined,
    );
  }

  onMapSelectionRequested(
    request: WorkflowReadOnlyGraphSelectionRequest,
  ): void {
    this.decisionInventoryNavigation++;
    if (this.isDraftEditorInteractionBlocked()) {
      return;
    }
    this.requestMapEditorDeactivation(
      () => {
        this.stepEditorNodeId.set(null);
        this.editingRouteSelection.set(null);
        this.automatedRouteFocus.set(null);
        this.editorMapReturnSelection = null;
        this.acceptMapSelection(request);
        if (request.moveFocusToInspector) {
          if (this.isInspectingStep()) this.focusAfterRender(() => this.stepInspection);
          else if (this.isInspectingRoute()) this.focusAfterRender(() => this.routeInspection);
        }
      },
      () => undefined,
    );
  }

  private setInspectorPane(pane: WorkflowMapInspectorPane): void {
    this.mapInspectorPane.set(pane);
    if (pane === 'transitions' || pane === 'decisions') {
      const node = this.selectedMapNode();
      if (node?.typeCode === 'human_step') {
        this.stepEditorOpened.set(true);
        this.stepEditorNodeId.set(node.id);
      }
      this.stepWorkspaceScope.set('behavior');
      this.behaviorSection.set('transitions');
    }
  }

  selectMapInspectorPane(pane: WorkflowMapInspectorPane): void {
    if (!this.flushingPendingChanges && !this.flushPendingEditorChanges())
      return;
    this.stepWorkspaceScope.set('behavior');
    if (
      pane !== 'overview' &&
      pane !== 'path' &&
      !this.mapContextSupportsBehavior()
    ) {
      return;
    }
    this.activeWorkbenchView.set('map');
    this.setInspectorPane(pane);
    if (pane === 'overview') this.behaviorSection.set('details');
    if (pane === 'transitions') {
      this.focusMapTransitionContext();
    } else if (pane === 'decisions') {
      this.focusMapDecisionContext();
    }
    this.writeWorkbenchViewToUrl('map');
  }

  showWholeWorkflowOverview(): void {
    this.requestMapEditorDeactivation(
      () => {
        this.stepEditorNodeId.set(null);
        this.editingRouteSelection.set(null);
        this.automatedRouteFocus.set(null);
        this.editorMapReturnSelection = null;
        this.inlineStepNameEditing.set(false);
        this.inlineStepNameError.set('');
        this.retainedIssueStep.set(null);
        this.selectWorkbenchView('map');
        this.isAddingStep.set(false);
        this.mapSelection.set(null);
        this.setInspectorPane('overview');
        this.writeWorkbenchViewToUrl('map');
        this.focusAfterRender(() => this.mapInspectorHeading);
      },
      () => undefined,
    );
  }

  beginAddMapStep(): void {
    const node = this.selectedMapNode(),
      edge = this.selectedMapEdge();
    this.beginStepPlacement(
      edge && this.mapSelection()?.isAuthorable
        ? { kind: 'path', edgeId: edge.id, fingerprint: JSON.stringify(edge) }
        : node &&
            this.mapSelection()?.isAuthorable &&
            ['entry', 'human_step', 'routing_decision'].includes(node.typeCode)
          ? { kind: 'after', nodeId: node.id }
          : node?.typeCode === 'terminal' && this.mapSelection()?.isAuthorable
            ? { kind: 'before', nodeId: node.id }
            : { kind: 'global' },
    );
  }

  beginAddStepAfter(): void {
    const node = this.selectedMapNode();
    if (node?.typeCode === 'human_step' && this.mapSelection()?.isAuthorable)
      this.beginStepPlacement({ kind: 'after', nodeId: node.id });
  }

  beginAddStepBefore(): void {
    const node = this.selectedMapNode();
    if (node?.typeCode === 'human_step' && this.mapSelection()?.isAuthorable)
      this.beginStepPlacement({ kind: 'before', nodeId: node.id });
  }

  beginInsertStepOnSelectedPath(): void {
    const edge = this.selectedMapEdge();
    if (edge && this.mapSelection()?.isAuthorable)
      this.beginStepPlacement({
        kind: 'path',
        edgeId: edge.id,
        fingerprint: JSON.stringify(edge),
      });
  }

  beginAddMapPath(): void {
    this.requestMapEditorDeactivation(
      () => {
        this.mapSelection.set(null);
        this.setInspectorPane('path');
        this.edgeEditor?.beginAddEdge();
      },
      () => undefined,
    );
  }

  beginAddWorkflowStatusChange(): void {
    this.requestMapEditorDeactivation(
      () => {
        this.mapSelection.set(null);
        this.setInspectorPane('transitions');
        this.transitionEditor?.focusTransitionPath(
          '$.workflowTransitions',
          null,
          'review',
        );
        this.transitionEditor?.beginAddTransition();
      },
      () => undefined,
    );
  }

  onWorkflowStatusDocumentChange(event: WorkflowStatusOwnedDocumentChange): void {
    const current = this.workingDocument();
    if (!current || !this.canAuthor() || this.isDraftEditorInteractionBlocked() ||
      (!this.isWorkflowStatusesOwnerActive() && !this.flushingPendingChanges) || event.nodeId !== null ||
      event.owner !== JSON.stringify(this.capabilityOwnerContext()) || event.source !== JSON.stringify(current) ||
      event.view !== this.workflowStatusEditor?.workspaceView) return;
    try {
      // The status owner may only use its existing writer; no graph or sibling mutation is accepted.
      const expected = replaceStatusConfiguration(current, null, event.document.statusConfiguration ?? emptyStatusConfiguration());
      if (JSON.stringify(expected) !== JSON.stringify(event.document)) return;
      this.setWorkingDocument(expected);
    } catch { return; }
  }

  onWorkflowStatusesOwnerRequested(request: WorkflowStatusOwnerRequest): void {
    const valid = () => {
      const view = this.workflowStatusesView(), document = this.workingDocument();
      const node = this.selectedMapNode();
      return !this.isDraftEditorInteractionBlocked() && !!document && view !== null && request.view === view &&
        request.owner === JSON.stringify(this.stepAssignmentOwnerContext()) && request.source === JSON.stringify(document) &&
        (request.origin !== 'step-statuses' || this.transitionEditor?.stepStatusesWorkspace === true &&
          request.localContext !== undefined && this.transitionEditor.workflowStatusesRequestContext() !== null &&
          request.localContext === this.transitionEditor.workflowStatusesRequestContext()) &&
        (request.origin === 'workflow-checks' ? request.nodeId === null && !node && this.mapInspectorPane() === 'transitions' :
          request.nodeId === node?.id && node?.typeCode === 'human_step' &&
          this.behaviorSection() === (request.origin === 'routing' ? 'routes' : 'transitions'));
    };
    if (!valid() || !this.flushPendingEditorChanges() || !valid()) return;
    const path = request.path === undefined ? undefined : this.savedWorkflowStatusPath(request.path) ?? '$.statusConfiguration';
    this.enterWorkflowStatuses(request.origin === 'routing' ? 'Back to Routing' :
      request.origin === 'step-statuses' ? 'Back to step settings' : 'Back to process map', path);
  }

  openWorkflowStatuses(sourceNodeId: string | null = null, returnSection: 'transitions' | 'routes' = 'transitions'): void {
    const owner = this.workFormViewContext(), view = this.workflowStatusesView();
    if (this.isDraftEditorInteractionBlocked() || (sourceNodeId !== null &&
      (sourceNodeId !== this.selectedMapNode()?.id || this.selectedMapNode()?.typeCode !== 'human_step')) ||
      !this.flushPendingEditorChanges() || owner !== this.workFormViewContext() || view !== this.workflowStatusesView()) return;
    this.enterWorkflowStatuses(sourceNodeId ? returnSection === 'routes' ? 'Back to Routing' : 'Back to step settings' :
      'Back to process map');
  }

  private workflowStatusSelectionValue(selection: WorkflowReadOnlyGraphSelection | null): string | null {
    if (!selection) return null;
    const rows = selection.kind === 'node' ? this.workingDocument()?.nodes : this.workingDocument()?.edges;
    const matches = rows?.filter(row => row?.id === selection.sourceId) ?? [];
    return matches.length === 1 ? JSON.stringify(matches[0]) : null;
  }

  private enterWorkflowStatuses(label: string, path?: string): void {
    const panel = this.inventoryMapPanel?.nativeElement;
    const elements = [panel?.parentElement, panel?.querySelector<HTMLElement>('.workflow-map-inspector__body'),
      panel?.closest('.workflow-designer-layout')?.querySelector<HTMLElement>('#workflow-process-steps')];
    this.workflowStatusOrigin = this.activeWorkbenchView() === 'map' ? {
      owner: this.workFormViewContext(), label, location: this.designerLocation(), selection: this.mapSelection(),
      selectionValue: this.workflowStatusSelectionValue(this.mapSelection()), focusedNodeId: this.stepEditorNodeId(),
      focusedRoute: this.editingRouteSelection(), focusedRouteValue: this.decisionInventoryRouteValue(this.editingRouteSelection()),
      focus: this.browserDocument.activeElement as HTMLElement | null, camera: this.inventoryGraph?.capturePresentationState() ?? null,
      scroll: Array.from(new Set(elements.filter((element): element is HTMLElement => !!element)))
        .map(element => ({ element, top: element.scrollTop, left: element.scrollLeft })),
    } : null;
    this.revealWorkflowSettingsTask('statuses');
    this.scheduleWorkflowStatusFocus(() => path ? this.workflowStatusEditor?.focusSavedPath(path) : this.workflowStatusEditor?.focusHeading());
  }

  workflowStatusesBackLabel(): string | null {
    return this.workflowStatusOrigin?.owner === this.workFormViewContext() ? this.workflowStatusOrigin.label : null;
  }

  returnToStepStatuses(): void {
    const origin = this.workflowStatusOrigin;
    if (!origin || !this.isWorkflowStatusesOwnerActive() || origin.owner !== this.workFormViewContext() ||
      this.isDraftEditorInteractionBlocked() || !this.flushPendingEditorChanges() ||
      origin !== this.workflowStatusOrigin || origin.owner !== this.workFormViewContext()) return;
    if (origin.selection && (origin.selectionValue === null ||
      this.workflowStatusSelectionValue(origin.selection) !== origin.selectionValue)) {
      this.editorMessage.set('The original step or path changed. Return through the Designer to inspect its current setup.');
      return;
    }
    this.workflowStatusOrigin = null;
    this.selectWorkbenchView('map', false);
    this.restoreDesignerLocation(origin.location);
    this.stepEditorNodeId.set(origin.focusedNodeId === this.selectedMapNode()?.id ? origin.focusedNodeId : null);
    const route = origin.focusedRoute ? this.mapEdgeSelectionBySourceId(origin.focusedRoute.sourceId) : null;
    if (route && origin.focusedRouteValue !== null && this.decisionInventoryRouteValue(route) === origin.focusedRouteValue)
      this.editingRouteSelection.set(route);
    this.writeWorkbenchViewToUrl('map');
    this.scheduleDecisionInventoryPresentation(() => {
      if (origin.owner !== this.workFormViewContext()) return;
      if (origin.camera) this.inventoryGraph?.restorePresentationState(origin.camera);
      for (const position of origin.scroll) if (position.element.isConnected) {
        position.element.scrollTop = position.top; position.element.scrollLeft = position.left;
      }
      if (origin.focus?.isConnected && !origin.focus.closest('[hidden],[inert]')) origin.focus.focus({ preventScroll: true });
      else this.mapInspectorHeading?.nativeElement.focus({ preventScroll: true });
    });
  }

  private scheduleWorkflowStatusFocus(action: () => void): void {
    const request = ++this.workflowStatusNavigation, owner = this.workFormViewContext();
    const source = JSON.stringify(this.workingDocument()), focus = this.browserDocument.activeElement;
    const view = this.workflowStatusEditor?.workspaceView;
    setTimeout(() => {
      if (request !== this.workflowStatusNavigation || owner !== this.workFormViewContext() ||
        source !== JSON.stringify(this.workingDocument()) || !this.isWorkflowStatusesOwnerActive() ||
        (view !== undefined && view !== this.workflowStatusEditor?.workspaceView) || this.destroyRef.destroyed || this.ownerViewState.focusSuspended()) return;
      const current = this.browserDocument.activeElement as HTMLElement | null;
      if (current !== focus && current !== this.browserDocument.body && current?.isConnected && !current.closest('[hidden],[inert]')) return;
      action();
    });
  }

  beginAddWorkflowDecisionRule(): void {
    this.requestMapEditorDeactivation(
      () => {
        this.decisionInventoryCheckAddMode.set(false);
        this.mapSelection.set(null);
        this.setInspectorPane('decisions');
        this.decisionEditor?.focusDecisionPath(
          '$.requiredDecisions',
          null,
          'review',
        );
        this.decisionEditor?.beginAddDecision();
      },
      () => undefined,
    );
  }

  openDecisionRulesForTransition(request: {
    scopeCode: 'workflow' | 'step';
    stepNodeId: string | null;
    transitionIndex: number;
  }): void {
    if (
      this.isDraftEditorInteractionBlocked() ||
      !this.flushPendingEditorChanges()
    )
      return;
    const document = this.workingDocument();
    const owners =
      document?.nodes.filter((node) => node?.id === request.stepNodeId) ?? [];
    const transitions =
      request.scopeCode === 'workflow'
        ? document?.workflowTransitions
        : owners.length === 1 && owners[0].typeCode === 'human_step'
          ? owners[0].allowedTransitions
          : null;
    if (
      !Number.isInteger(request.transitionIndex) ||
      request.transitionIndex < 0 ||
      !Array.isArray(transitions) ||
      !transitions[request.transitionIndex]
    )
      return;
    if (request.scopeCode === 'step') {
      if (
        !request.stepNodeId ||
        !this.openMapNodeById(request.stepNodeId, false, 'decisions')
      )
        return;
    } else {
      this.mapSelection.set(null);
    }
    this.activeWorkbenchView.set('map');
    this.setInspectorPane('decisions');
    this.decisionEditor?.focusTransitionDecisions(request);
    this.writeWorkbenchViewToUrl('map');
  }

  onIdentityFormChange(
    values: unknown,
    area?: 'basics' | 'technical' | 'availability',
  ): void {
    const document = this.workingDocument();
    if (
      !document ||
      !this.canAuthor() ||
      this.isDraftEditorInteractionBlocked()
    ) {
      return;
    }

    const modernIdentity = [
      '1.10',
      '1.11',
      '1.12',
      '1.13',
      '1.14',
      '1.15',
      '1.16',
      '1.17', '1.18', '1.19', '1.20', '1.21', '1.22',
    ].includes(document.payloadSchemaVersion);
    const basicsKeys = [
      ...(modernIdentity ? ['displayName', 'description'] : []),
      ...(!modernIdentity
        ? ['subjectTypeCode', 'instanceCardinalityCode']
        : this.canEditProcessCardinality(document)
          ? ['instanceCardinalityCode']
          : []),
    ];
    const keys =
      area === 'technical'
        ? ['definitionVersionCode']
        : area === 'availability'
          ? ['scopeTypeCode', 'scopeKey']
          : [
              ...basicsKeys,
              ...(area
                ? []
                : ['definitionVersionCode', 'scopeTypeCode', 'scopeKey']),
            ];
    const nextValues = {
      ...this.identityFormValues,
      ...this.setupFieldPatch(values, keys),
    };
    const scopeChanged =
      nextValues.scopeTypeCode !== this.identityFormValues.scopeTypeCode;
    this.identityFormValues = nextValues;
    if (scopeChanged)
      this.identityAvailabilityFormConfig =
        this.buildAvailabilityFormConfig('edit');
    this.setWorkingDocument({
      ...document,
      ...(['1.10', '1.11', '1.12', '1.13', '1.14', '1.15', '1.16', '1.17', '1.18', '1.19', '1.20', '1.21', '1.22'].includes(
        document.payloadSchemaVersion,
      )
        ? {
            displayName: nextValues.displayName,
            description: nextValues.description,
          }
        : {}),
      definitionVersionCode: nextValues.definitionVersionCode,
      subjectTypeCode:
        ['1.10', '1.11', '1.12', '1.13', '1.14', '1.15', '1.16', '1.17', '1.18', '1.19', '1.20', '1.21', '1.22'].includes(
          document.payloadSchemaVersion,
        ) && !document.subjectTypeCode
          ? null
          : nextValues.subjectTypeCode,
      scopeTypeCode: nextValues.scopeTypeCode,
      scopeKey:
        nextValues.scopeTypeCode === 'global' ? null : nextValues.scopeKey,
      instanceCardinalityCode: nextValues.instanceCardinalityCode,
    });
  }

  private setupFieldPatch(
    values: unknown,
    keys: string[],
  ): Record<string, string> {
    if (!values || typeof values !== 'object') return {};
    return Object.fromEntries(
      keys
        .filter(
          (key) => typeof (values as Record<string, unknown>)[key] === 'string',
        )
        .map((key) => [key, (values as Record<string, string>)[key]]),
    );
  }

  selectNode(node: WorkflowDefinitionNode, focusEditor = true): void {
    if (this.isDraftEditorInteractionBlocked()) {
      return;
    }

    const selectCurrentNode = () => {
      const currentNode = this.workingDocument()?.nodes.find(
        (candidate) => candidate.id === node.id,
      );
      if (!currentNode) return;
      this.loadNodeForm(currentNode);
      if (focusEditor) this.focusAfterRender(() => this.nodeEditorHeading);
    };
    this.requestMapEditorDeactivation(selectCurrentNode, () => undefined);
  }

  beginAddAutomatedStep(): void {
    this.beginStepPlacement({ kind: 'unconnected' }, 'automated_step');
  }
  private newStepTypeCode = 'human_step';

  beginAddStep(): void {
    this.beginStepPlacement({ kind: 'unconnected' });
  }

  private beginStepPlacement(
    context: StepPlacementContext,
    typeCode = 'human_step',
  ): void {
    if (!this.canAuthor() || this.isDraftEditorInteractionBlocked()) return;
    const draftId = this.currentDraft()?.id;
    this.requestMapEditorDeactivation(
      () => {
        const document = this.workingDocument();
        if (
          !document ||
          !this.canAuthor() ||
          this.isDraftEditorInteractionBlocked() ||
          this.currentDraft()?.id !== draftId
        )
          return;
        if (
          (context.kind === 'after' || context.kind === 'before') &&
          document.nodes.filter(
            (node) =>
              node?.id === context.nodeId &&
              (context.kind === 'after'
                ? ['entry', 'human_step', 'automated_step', 'routing_decision']
                : ['human_step', 'automated_step', 'terminal']
              ).includes(node.typeCode),
          ).length !== 1
        )
          return;
        if (
          context.kind === 'path' &&
          (document.edges.filter((edge) => edge?.id === context.edgeId)
            .length !== 1 ||
            JSON.stringify(
              document.edges.find((edge) => edge?.id === context.edgeId),
            ) !== context.fingerprint)
        ) {
          this.showOperationError(
            localValidationError(
              'Placement changed',
              'The selected path changed while editor changes were applied. Select the path again before adding a step.',
            ),
          );
          return;
        }
        const nextOrder =
          [...this.humanStepNodes(), ...this.automatedStepNodes()].reduce(
            (maximum, node) => Math.max(maximum, node.displayOrder),
            0,
          ) + 10;
        this.isAddingStep.set(true);
        this.newStepTypeCode = typeCode;
        if (
          typeCode === 'human_step' &&
          !document.subjectTypeCode &&
          !this.standardStepSupport
        )
          this.loadStandardStepSupport();
        this.newStepFrom = '';
        this.newStepTo = '';
        this.newStepPlacementContext = context;
        this.newStepPlacementMode =
          context.kind === 'unconnected' ? 'unconnected' : 'path';
        this.newStepPathId = '';
        this.newStepPathSnapshot = null;
        this.newStepPlacementContextLabel =
          context.kind === 'after' || context.kind === 'before'
            ? `${context.kind === 'after' ? 'After' : 'Before'} ${this.stepPlacementNodeLabel(context.nodeId)}`
            : context.kind === 'path'
              ? 'On the selected path'
              : 'Choose where this step belongs';
        this.customNodeId = false;
        this.customStepCode = false;
        this.validationHandoff.set(null);
        this.semanticReviewHandoff.set(null);
        this.mapSelection.set(null);
        this.selectedNodeId.set(null);
        this.activeWorkbenchView.set('map');
        this.setInspectorPane('overview');
        this.nodeFormValues = {
          id: '',
          typeCode,
          displayOrder: nextOrder,
          stepCode: '',
          displayName: '',
        };
        this.nodeFormConfig = this.buildNodeFormConfig(true, false);
        this.nodeFormDirty.set(false);
        const related = this.relatedStepPlacementPaths();
        const eligible = related.filter((path) => path.eligible);
        if (this.newStepPlacementMode === 'path' && eligible.length === 1)
          this.selectNewStepPath(eligible[0].edgeId, false);
        if (context.kind === 'path')
          this.selectNewStepPath(context.edgeId, false);
        this.editorMessage.set(null);
        this.focusAfterRender(() => this.nodeEditorHeading);
        this.writeWorkbenchViewToUrl('map');
      },
      () => undefined,
    );
  }

  relatedStepPlacementPaths() {
    const context = this.newStepPlacementContext,
      paths = this.newStepPlacementPaths(),
      document = this.workingDocument();
    if (context.kind === 'path')
      return paths.filter((path) => path.edgeId === context.edgeId);
    if (context.kind === 'before')
      return paths.filter((path) => path.toNodeId === context.nodeId);
    if (context.kind === 'after') {
      const routers =
        document?.edges
          .filter((edge) => edge?.fromNodeId === context.nodeId)
          .map((edge) =>
            document.nodes.find((node) => node?.id === edge.toNodeId),
          )
          .filter((node) => node?.typeCode === 'routing_decision')
          .map((node) => node!.id) ?? [];
      return paths.filter(
        (path) =>
          path.fromNodeId === context.nodeId ||
          routers.includes(path.fromNodeId),
      );
    }
    return paths;
  }

  orderedStepPlacementPaths() {
    const related = new Set(this.relatedStepPlacementPaths());
    return [...this.newStepPlacementPaths()].sort(
      (a, b) => Number(related.has(b)) - Number(related.has(a)),
    );
  }

  changeNewStepPlacementMode(
    mode: 'path' | 'connection' | 'unconnected',
  ): void {
    if (!this.canAuthor() || this.isDraftEditorInteractionBlocked()) return;
    this.newStepPlacementMode = mode;
    this.newStepFrom = '';
    this.newStepTo = '';
    this.newStepPathId = '';
    this.newStepPathSnapshot = null;
    this.nodeFormDirty.set(true);
  }

  selectNewStepPath(edgeId: string, markDirty = true): void {
    if (!this.canAuthor() || this.isDraftEditorInteractionBlocked()) return;
    const paths = this.newStepPlacementPaths().filter(
      (path) => path.edgeId === edgeId,
    );
    const edges =
      this.workingDocument()?.edges.filter((edge) => edge?.id === edgeId) ?? [];
    this.newStepPathId = edgeId;
    this.newStepPathSnapshot =
      paths.length === 1 && paths[0].eligible && edges.length === 1
        ? {
            draftId: this.currentDraft()!.id,
            fingerprint: JSON.stringify(edges[0]),
            fromNodeId: edges[0].fromNodeId,
            toNodeId: edges[0].toNodeId,
            label: paths[0].label,
          }
        : null;
    if (markDirty) this.nodeFormDirty.set(true);
  }

  get newStepPlacementStale(): boolean {
    const snapshot = this.newStepPathSnapshot;
    if (!snapshot) return false;
    const matches =
      this.workingDocument()?.edges.filter(
        (edge) => edge?.id === this.newStepPathId,
      ) ?? [];
    return (
      snapshot.draftId !== this.currentDraft()?.id ||
      matches.length !== 1 ||
      JSON.stringify(matches[0]) !== snapshot.fingerprint
    );
  }

  get newStepPlacementPreview(): string {
    const from =
      this.newStepPlacementMode === 'path'
        ? this.newStepPathSnapshot?.fromNodeId
        : this.newStepFrom;
    const to =
      this.newStepPlacementMode === 'path'
        ? this.newStepPathSnapshot?.toNodeId
        : this.newStepTo;
    return [
      from ? this.stepPlacementNodeLabel(from) : null,
      this.nodeFormValues.displayName.trim() || 'New work step',
      to ? this.stepPlacementNodeLabel(to) : null,
    ]
      .filter(Boolean)
      .join(' → ');
  }

  get newStepSelectedPathLabel(): string | null {
    return this.newStepPathSnapshot?.label ?? null;
  }

  get newStepUnavailablePathCount(): number {
    return this.newStepPlacementPaths().filter((path) => !path.eligible).length;
  }

  get newStepContextPlacementIssue(): string | null {
    const paths = this.newStepPathId
      ? this.newStepPlacementPaths().filter(
          (path) => path.edgeId === this.newStepPathId,
        )
      : this.relatedStepPlacementPaths();
    return paths.length === 1 && !paths[0].eligible ? paths[0].reason : null;
  }

  stepPlacementNodeLabel(id: string): string {
    const node = this.workingDocument()?.nodes.find((item) => item?.id === id);
    return node?.typeCode === 'terminal'
      ? 'Complete workflow'
      : node?.typeCode === 'entry'
        ? 'Start process'
        : node?.displayName || node?.stepCode || id;
  }

  onNodeFormChange(values: unknown): void {
    if (this.isDraftEditorInteractionBlocked()) {
      return;
    }

    const nextValues = values as Partial<DraftNodeValues>;
    const selectedNode = this.selectedNode();
    if (this.isAddingStep()) {
      this.customNodeId ||=
        nextValues.id !== undefined && nextValues.id !== this.nodeFormValues.id;
      this.customStepCode ||=
        nextValues.stepCode !== undefined &&
        nextValues.stepCode !== this.nodeFormValues.stepCode;
    }
    this.nodeFormValues = {
      id: nextValues.id ?? this.nodeFormValues.id ?? selectedNode?.id ?? '',
      typeCode:
        (this.isAddingStep() ? this.newStepTypeCode : selectedNode?.typeCode) ??
        nextValues.typeCode ??
        this.nodeFormValues.typeCode ??
        selectedNode?.typeCode ??
        'human_step',
      displayOrder: Number(
        nextValues.displayOrder ??
          this.nodeFormValues.displayOrder ??
          selectedNode?.displayOrder ??
          0,
      ),
      stepCode:
        nextValues.stepCode ??
        this.nodeFormValues.stepCode ??
        selectedNode?.stepCode ??
        '',
      displayName:
        nextValues.displayName ??
        this.nodeFormValues.displayName ??
        selectedNode?.displayName ??
        '',
    };
    if (this.isAddingStep() && this.workingDocument()) {
      const suggestion = suggestWorkStepIdentity(
        this.nodeFormValues.displayName,
        this.workingDocument()!,
      );
      if (!this.customNodeId) this.nodeFormValues.id = suggestion.id;
      if (!this.customStepCode)
        this.nodeFormValues.stepCode = suggestion.stepCode;
    }
    this.nodeFormDirty.set(true);
    this.saveIntent = null;
    this.validationIntent = null;
  }

  beginInlineStepName(): void {
    if (!this.canInlineRenameStep() || !this.flushPendingEditorChanges()) return;
    const node = this.selectedMapNode();
    if (!node || !this.canInlineRenameStep()) return;
    this.inlineStepNameNodeId = node.id;
    this.inlineStepNameOriginal = node.displayName ?? null;
    this.nodeFormValues = this.nodeToFormValues(node);
    this.inlineStepNameError.set('');
    this.inlineStepNameEditing.set(true);
    setTimeout(() => {
      this.inlineStepNameInput?.nativeElement.focus();
      this.inlineStepNameInput?.nativeElement.select();
    });
  }

  changeInlineStepName(value: string): void {
    if (!this.inlineStepNameEditing() || !this.canInlineRenameStep()) return;
    this.inlineStepNameError.set('');
    this.onNodeFormChange({ displayName: value });
  }

  finishInlineStepName(restoreFocus = false): void {
    if (!this.inlineStepNameEditing()) return;
    if (this.applyInlineStepName() && restoreFocus)
      this.focusAfterRender(() => this.renameStepButton);
  }

  onInlineStepNameFocusOut(event: FocusEvent): void {
    if (!(event.currentTarget as HTMLElement).contains(event.relatedTarget as Node | null))
      this.finishInlineStepName();
  }

  cancelInlineStepName(): void {
    if (!this.inlineStepNameEditing()) return;
    this.inlineStepNameEditing.set(false);
    this.inlineStepNameError.set('');
    this.resetNodeForm();
    this.focusAfterRender(() => this.renameStepButton);
  }

  private applyInlineStepName(): boolean {
    if (!this.canInlineRenameStep()) return false;
    const document = this.workingDocument();
    const node = this.selectedMapNode();
    if (!document || !node || node.id !== this.inlineStepNameNodeId) return false;
    if (!this.nodeFormDirty()) {
      this.inlineStepNameEditing.set(false);
      this.inlineStepNameError.set('');
      return true;
    }
    const name = this.nodeFormValues.displayName.trim();
    const error = !name ? 'Enter a step name.' : name.length > 160
      ? 'Use 160 characters or fewer.'
      : (node.displayName ?? null) !== this.inlineStepNameOriginal
        ? 'This step name changed. Cancel and try again.' : '';
    if (error) {
      this.inlineStepNameError.set(error);
      this.focusAfterRender(() => this.inlineStepNameInput);
      return false;
    }
    if (name !== node.displayName)
      this.onBehaviorDocumentChange({
        ...document,
        nodes: document.nodes.map((item) => item.id === node.id
          ? { ...item, displayName: name } : item),
      });
    this.inlineStepNameEditing.set(false);
    this.inlineStepNameError.set('');
    this.nodeFormDirty.set(false);
    this.nodeFormValues = this.nodeToFormValues(this.selectedMapNode()!);
    return true;
  }

  changeNewStepField(
    field: 'id' | 'stepCode' | 'displayOrder',
    value: string,
  ): void {
    this.onNodeFormChange({ ...this.nodeFormValues, [field]: value });
  }

  get newStepInsertionTarget(): WorkflowDefinitionNode | undefined {
    const edge = this.workingDocument()?.edges.find(
      (e) => e.fromNodeId === this.newStepFrom,
    );
    return this.nodes().find((n) => n.id === edge?.toNodeId);
  }

  newStepSourceLabel(node: WorkflowDefinitionNode): string {
    const edge = this.workingDocument()?.edges.find(
      (e) => e.fromNodeId === node.id,
    );
    const target = this.nodes().find((n) => n.id === edge?.toNodeId);
    const name = node.displayName || node.stepCode || node.id;
    return target
      ? `${name} → ${target.displayName || target.stepCode || target.id} (insert between)`
      : name;
  }

  changeNewStepConnection(side: 'from' | 'to', value: string): void {
    if (!this.canAuthor() || this.isDraftEditorInteractionBlocked()) return;
    this.newStepPlacementMode = 'connection';
    if (side === 'from') {
      const wasInsertion = !!this.newStepInsertionTarget;
      this.newStepFrom = value;
      if (this.newStepInsertionTarget)
        this.newStepTo = this.newStepInsertionTarget.id;
      else if (wasInsertion) this.newStepTo = '';
    } else this.newStepTo = value;
    this.nodeFormDirty.set(true);
  }

  moveSelectedStepEarlier(): void {
    this.moveSelectedStep(-1);
  }

  moveSelectedStepLater(): void {
    this.moveSelectedStep(1);
  }

  onNodeActionButtonClick(button: ButtonConfig): void {
    switch (button.id) {
      case 'applyNode':
        this.applyNodeChanges();
        break;
      case 'resetNode':
        this.resetNodeForm();
        break;
      case 'removeNode':
        this.requestRemoveStep();
        break;
      default:
        break;
    }
  }

  onRoutingDocumentChange(document: WorkflowDefinitionDocument): void {
    const inspectingRoute = this.isInspectingRoute();
    const stepOwner = this.stepEditorNodeId();
    if (!this.canAuthor() || this.isDraftEditorInteractionBlocked()) {
      return;
    }

    if (this.selectedMapNode()?.typeCode === 'human_step')
      this.onBehaviorDocumentChange(document);
    else this.setWorkingDocument(document);
    if (this.mapInspectorPane() === 'path' && !inspectingRoute && !stepOwner) {
      const selectedId = this.edgeEditor?.selectedEdgeId();
      const selectedIndex = this.edgeEditor?.selectedEdgeIndex() ?? null;
      const graph = projectWorkflowDraftGraph(document);
      const selectedEdge =
        selectedIndex === null
          ? null
          : (graph.edges.find((edge) => selectedId ? edge.sourceId === selectedId : edge.sourceIndex === selectedIndex) ??
            null);
      this.mapSelection.set(
        selectedEdge
          ? this.graphSelectionForEdge(selectedEdge, graph.edges)
          : null,
      );
    }
    this.editorMessage.set(
      'Routing changes applied locally. Save the draft to persist them.',
    );
  }

  onDecisionDocumentChange(document: WorkflowDefinitionDocument): void {
    if (!this.canAuthor() || this.isDraftEditorInteractionBlocked()) {
      return;
    }

    if (this.selectedMapNode()?.typeCode === 'human_step')
      this.onBehaviorDocumentChange(document);
    else this.setWorkingDocument(document);
    this.editorMessage.set(
      'Decision gate changes applied locally. Save the draft to persist them.',
    );
  }

  onNumberingDocumentChange(document: WorkflowDefinitionDocument): void {
    const latest = this.workingDocument();
    if (
      !latest || document.workflowCode !== latest.workflowCode ||
      !this.canAuthor() ||
      this.isDraftEditorInteractionBlocked() ||
      this.hasUnappliedEditorChanges()
    )
      return;
    this.setWorkingDocument({
      ...latest,
      payloadSchemaVersion: requireWorkflowPayloadVersion(latest.payloadSchemaVersion, '1.6'),
      triggers: latest.triggers ?? [],
      numbering: document.numbering,
    });
    this.identityFormValues = {
      ...this.identityFormValues,
      payloadSchemaVersion: this.workingDocument()!.payloadSchemaVersion,
    };
    this.editorMessage.set(
      'Numbering changes applied locally. Save the draft; publication checks run automatically.',
    );
  }
  onAutomationDocumentChange(document: WorkflowDefinitionDocument): void {
    const before = this.workingDocument();
    if (
      !before ||
      !this.canAuthor() ||
      this.isDraftEditorInteractionBlocked() ||
      (this.hasUnappliedEditorChanges() && !this.flushingPendingChanges)
    )
      return;
    const after = serializeDocument(document);
    if (after === serializeDocument(before)) return;
    const history = this.automationUndoHistory();
    this.recordingAutomationEdit = true;
    try {
      this.setWorkingDocument(document);
    } finally {
      this.recordingAutomationEdit = false;
    }
    this.automationUndoHistory.set([
      ...history.slice(-29),
      { before: cloneDocument(before), after },
    ]);
    this.identityFormValues = {
      ...this.identityFormValues,
      payloadSchemaVersion: document.payloadSchemaVersion,
    };
    this.editorMessage.set(
      'Automation applied locally. Save the draft to review its exact declaration. Graph action dispatch remains unavailable.',
    );
  }
  undoAutomation(): void {
    if (!this.canUndoAutomation()) return;
    const history = this.automationUndoHistory(),
      previous = history.at(-1)!;
    this.setWorkingDocument(previous.before);
    this.identityFormValues = {
      ...this.identityFormValues,
      payloadSchemaVersion: previous.before.payloadSchemaVersion,
    };
    this.timersEditor?.discardPendingChanges();
    this.childWorkEditor?.discardPendingChanges();
    this.automationEditor?.discardPendingChanges();
    this.automatedStepEditor?.discardPendingChanges();
    this.automationUndoHistory.set(history.slice(0, -1));
    this.editorMessage.set(
      'The last applied automation change was undone locally.',
    );
  }
  openAutomation(path = '$.triggers'): void {
    this.revealWorkflowSettingsTask('automation');
    this.automationExpanded.set(true);
    this.writeWorkbenchViewToUrl('setup');
    setTimeout(() => {
      if (this.automationEditor) this.automationEditor.focusPath(path);
      else if (!this.ownerViewState.focusSuspended()) this.automationDetails?.nativeElement.focus();
    });
  }

  /** The guarded workspace accepts only the Data owner's stamped, narrow writer. */
  onProcessDataDocumentChange(_document: WorkflowDefinitionDocument): void {
    // Legacy standalone outputs cannot authorize a guarded workspace mutation.
  }

  onChildWorkDocumentChange(_document: WorkflowDefinitionDocument): void {
    // A guarded workspace does not accept the standalone, unstamped output.
  }

  onTimerDocumentChange(_document: WorkflowDefinitionDocument): void {
    // The guarded timer workspace accepts only the leaf's synchronous owned receipt.
  }

  onTimerOwnedDocumentChange(event: WorkflowTimerOwnedDocumentChange): void {
    const current = this.workingDocument(), editor = this.timersEditor, token = this.timerSettlement;
    const reject = () => { if (token) token.rejected = true; };
    if (!event || !current || !editor || !editor.hasPendingChanges() || !this.canAuthor() || this.isDraftEditorInteractionBlocked() ||
      this.mapSelection()?.kind !== 'node' || !this.mapSelection()?.isAuthorable || this.selectedMapNode()?.typeCode !== 'human_step' ||
      event.nodeId !== this.selectedMapNode()?.id || event.nodeId !== editor.nodeId ||
      event.owner !== JSON.stringify(this.stepAssignmentOwnerContext()) || event.source !== JSON.stringify(current)) { reject(); return; }
    if (token ? token.editor !== editor || token.owner !== event.owner || token.source !== event.source || token.view !== event.view || token.nodeId !== event.nodeId
      : !this.isTimerOwnerActive() || this.timerInteractionBlocked() || event.view === null || event.view !== this.timerOwnerView()) { reject(); return; }
    try {
      const draft = JSON.stringify(event.draft);
      if (draft !== JSON.stringify(editor.draftSnapshot) || token && token.draft !== draft || !editor.isCurrentOwnedDocumentChange(event, current)) { reject(); return; }
      if (JSON.stringify(event.document) === JSON.stringify(current)) this.workingDocument.set(cloneDocument(current));
      else this.onBehaviorDocumentChange(event.document);
      if (JSON.stringify(this.workingDocument()) !== JSON.stringify(event.document)) { reject(); return; }
      if (token) token.accepted = true;
    } catch { reject(); }
  }

  private settleTimer(document: WorkflowDefinitionDocument | null, apply: () => boolean): boolean {
    const editor = this.timersEditor, nodeId = this.selectedMapNode()?.id;
    if (!document || !editor || !nodeId || editor.nodeId !== nodeId || this.selectedMapNode()?.typeCode !== 'human_step' ||
      this.mapSelection()?.kind !== 'node' || !this.mapSelection()?.isAuthorable || !this.canAuthor() || this.isDraftEditorInteractionBlocked() ||
      this.timerSettlement || JSON.stringify(document) !== JSON.stringify(this.workingDocument())) return false;
    if (!editor.hasPendingChanges()) return true;
    if (editor.pendingEditorScopes(() => document).some(scope => !['clean', 'valid', 'invalid'].includes(scope.state()))) return false;
    const token = { editor, nodeId, owner: JSON.stringify(this.stepAssignmentOwnerContext()), source: JSON.stringify(document),
      view: this.timerOwnerView(), draft: JSON.stringify(editor.draftSnapshot), accepted: false, rejected: false };
    const previousBlocked = editor.interactionBlocked, previousAuthor = editor.canAuthor;
    this.timerSettlement = token; editor.interactionBlocked = false; editor.canAuthor = this.canAuthor();
    try { return apply() && token.accepted && !token.rejected; }
    finally { this.timerSettlement = null; editor.interactionBlocked = previousBlocked; editor.canAuthor = previousAuthor; }
  }

  private focusTimerPendingChanges(exactFocus?: () => void, path?: string): void {
    const request = ++this.timerFocusRequest, editor = this.timersEditor, nodeId = this.selectedMapNode()?.id,
      owner = this.stepAssignmentOwnerContext(), source = JSON.stringify(this.workingDocument()), view = this.timerOwnerView(),
      draft = editor ? JSON.stringify(editor.draftSnapshot) : null, focused = this.browserDocument.activeElement;
    setTimeout(() => {
      if (this.destroyRef.destroyed || request !== this.timerFocusRequest || this.ownerViewState.focusSuspended() ||
        !this.isTimerOwnerActive() || nodeId !== this.selectedMapNode()?.id || owner !== this.stepAssignmentOwnerContext() ||
        source !== JSON.stringify(this.workingDocument()) || view !== this.timerOwnerView() ||
        editor && (editor !== this.timersEditor || draft !== JSON.stringify(editor.draftSnapshot))) return;
      const active = this.browserDocument.activeElement;
      if (active !== focused && active !== this.browserDocument.body && active !== this.browserDocument.documentElement &&
        active?.isConnected && !active.closest('[hidden],[inert]')) return;
      if (exactFocus) exactFocus();
      else this.timersEditor?.focusPendingChanges(true, path);
    });
  }

  onChildWorkOwnedDocumentChange(event: WorkflowChildWorkOwnedDocumentChange): void {
    const current = this.workingDocument(), editor = this.childWorkEditor, token = this.childWorkSettlement;
    const reject = () => { if (token) token.rejected = true; };
    if (!event || !current || !editor || !editor.hasPendingChanges() || !this.canAuthor() || this.isDraftEditorInteractionBlocked() ||
      event.nodeId !== this.selectedMapNode()?.id || event.nodeId !== editor.nodeId ||
      event.owner !== JSON.stringify(this.stepAssignmentOwnerContext()) || event.source !== JSON.stringify(current)) { reject(); return; }
    if (token ? token.editor !== editor || token.owner !== event.owner || token.source !== event.source || token.view !== event.view || token.nodeId !== event.nodeId
      : !this.isChildWorkOwnerActive() || event.view === null || event.view !== this.childWorkOwnerView()) { reject(); return; }
    try {
      const draft = JSON.stringify(event.draft);
      if (draft !== JSON.stringify(editor.draftSnapshot) || token && token.draft !== draft || !editor.isCurrentOwnedDocumentChange(event, current)) { reject(); return; }
      // The leaf's synchronous validator recomputes the complete narrow proposal.
      // A no-op still returns an explicit receipt without manufacturing Undo.
      if (JSON.stringify(event.document) === JSON.stringify(current)) this.workingDocument.set(cloneDocument(current));
      else this.onBehaviorDocumentChange(event.document);
      if (JSON.stringify(this.workingDocument()) !== JSON.stringify(event.document)) { reject(); return; }
      if (token) token.accepted = true;
    } catch { reject(); }
  }

  private settleChildWork(document: WorkflowDefinitionDocument | null, apply: () => boolean): boolean {
    const editor = this.childWorkEditor, nodeId = this.selectedMapNode()?.id;
    if (!document || !editor || !nodeId || editor.nodeId !== nodeId || !this.canAuthor() || this.isDraftEditorInteractionBlocked() ||
      this.childWorkSettlement || JSON.stringify(document) !== JSON.stringify(this.workingDocument())) return false;
    if (!editor.hasPendingChanges()) return true;
    if (!editor.canSettleExistingChanges()) return false;
    const token = { editor, nodeId, owner: JSON.stringify(this.stepAssignmentOwnerContext()), source: JSON.stringify(document),
      view: this.childWorkOwnerView(), draft: JSON.stringify(editor.draftSnapshot), accepted: false, rejected: false };
    const previousBlocked = editor.interactionBlocked, previousAuthor = editor.canAuthor;
    this.childWorkSettlement = token; editor.interactionBlocked = false; editor.canAuthor = this.canAuthor();
    try { return apply() && token.accepted && !token.rejected; }
    finally { this.childWorkSettlement = null; editor.interactionBlocked = previousBlocked; editor.canAuthor = previousAuthor; }
  }

  private sameBypassDataOrigin(left: WorkflowStepBypassDataOwnerOrigin, right: WorkflowStepBypassDataOwnerOrigin): boolean {
    return left.nodeId === right.nodeId && left.namespace === right.namespace && left.localContext === right.localContext &&
      left.reference === right.reference && left.configuration === right.configuration &&
      right.state !== 'stale' && (left.state === 'clean') === (right.state === 'clean') &&
      // A clean document echo can display the canonical, trimmed saved value.
      // Pending input must retain every raw scalar and its persistent edit intent.
      (left.state === 'clean' || JSON.stringify(left.draft) === JSON.stringify(right.draft));
  }

  /** Root-owned presentation remains stable while retained children reconcile an acknowledgement. */
  private presentBypassDataLease() {
    const lease = this.bypassDataLease;
    if (!lease || lease.parent !== this.transitionEditor || lease.namespace !== this.workFormViewContext() ||
      lease.source !== JSON.stringify(this.workingDocument())) return null;
    return lease;
  }

  /** Mutation and navigation additionally require the actual retained child's current baseline. */
  private currentBypassDataLease() {
    const lease = this.presentBypassDataLease();
    if (!lease) return null;
    const origin = lease.parent.readBypassDataOrigin();
    return origin && this.sameBypassDataOrigin(lease.origin, origin) ? lease : null;
  }

  onBypassDataRequested(request: WorkflowStepBypassDataRequest): void {
    const parent = this.transitionEditor, origin = parent?.readBypassDataOrigin(), namespace = this.workFormViewContext();
    if (!request || !parent || parent.currentlyEmittingBypassDataRequest !== request || !origin || origin.state === 'stale' ||
      !this.canAuthor() || this.isDraftEditorInteractionBlocked() || !parent.progressOwnerActive() ||
      request.nodeId !== this.selectedMapNode()?.id || request.nodeId !== origin.nodeId ||
      request.owner !== JSON.stringify(this.stepAssignmentOwnerContext()) || request.owner !== origin.owner ||
      request.source !== JSON.stringify(this.workingDocument()) || request.source !== origin.source ||
      !request.view || request.view !== this.workflowStatusesView() || request.localContext !== origin.localContext ||
      !request.originContext || JSON.stringify(request.draft) !== JSON.stringify(origin.draft)) return;
    const target = request.target;
    if (!target || (target.kind === 'add-boolean' ? Object.keys(target).length !== 1
      : target.kind !== 'review-field' || Object.keys(target).length !== 2 || !target.key || target.key !== origin.draft.values.processDataKey)) return;
    const entry = ++this.bypassDataGeneration;
    const enter = () => {
      const current = parent.readBypassDataOrigin();
      if (entry !== this.bypassDataGeneration || parent !== this.transitionEditor || namespace !== this.workFormViewContext() ||
        !current || !this.sameBypassDataOrigin(origin, current) || request.view !== this.workflowStatusesView() ||
        !this.canAuthor() || this.isDraftEditorInteractionBlocked()) return;
      this.bypassDataLease = { generation: entry, parent, namespace, origin: structuredClone(current), source: JSON.stringify(this.workingDocument()),
        location: this.designerLocation(), camera: this.inventoryGraph?.capturePresentationState() ?? null };
      this.revealWorkflowSettingsTask('data');
      this.scheduleProcessDataFocus(editor => target.kind === 'add-boolean' ? editor.focusAddValue(true) : editor.focusField(target.key, 'type', true));
    };
    if (origin.state === 'clean') this.requestMapEditorDeactivation(enter, () => undefined);
    else {
      const leaves = this.editorPendingScopes().filter(scope => scope.id === 'status-change:transition-progress:step-progress');
      if (leaves.length !== 1 || !['valid', 'invalid'].includes(leaves[0].state())) return;
      this.requestMapEditorDeactivation(enter, () => undefined, 'status-change:transition-progress:step-progress');
    }
  }

  bypassDataReturnLabel(): string | null {
    const lease = this.presentBypassDataLease();
    return lease && this.isProcessDataOwnerActive() ? 'Back to bypass' : null;
  }

  returnToBypassAfterData(): void {
    const lease = this.currentBypassDataLease();
    if (!lease || !this.isProcessDataOwnerActive() || !this.canAuthor() || this.isDraftEditorInteractionBlocked() ||
      this.processDataHasOtherPending()) return;
    if (!this.settleProcessData(this.workingDocument(), () => this.processDataEditor?.applyPendingChanges(this.workingDocument()!) ?? false)) {
      this.scheduleProcessDataFocus(editor => editor.focusPendingChanges(true));
      return;
    }
    if (this.currentBypassDataLease() !== lease) return;
    this.activeWorkbenchView.set('map');
    this.restoreDesignerLocation(lease.location);
    this.stepEditorNodeId.set(lease.origin.nodeId);
    this.writeWorkbenchViewToUrl('map');
    // The exact retained owner now owns focus; older Data and generic entry queues yield.
    this.processDataFocusRequest++; this.processDataFocusBarrier++;
    if (!lease.parent.returnToBypassFromData()) {
      this.editorMessage.set('The original bypass input changed. Review the current step before returning.');
      return;
    }
    if (lease.camera) this.inventoryGraph?.restorePresentationState(lease.camera);
    this.bypassDataLease = null;
  }

  /** The Parent proves its actual child and private settlement before exposing this event. */
  onStepProgressOwnedDocumentChange(event: WorkflowStepProgressOwnedDocumentChange): void {
    const parent = this.transitionEditor, current = this.workingDocument();
    if (!event || !parent || parent.currentlyEmittingProgressChange !== event || !current || !this.canAuthor() ||
      this.isDraftEditorInteractionBlocked() || event.nodeId !== this.selectedMapNode()?.id ||
      event.owner !== JSON.stringify(this.stepAssignmentOwnerContext()) || event.source !== JSON.stringify(current) ||
      JSON.stringify(event.draft) !== JSON.stringify(parent.pendingStepProgressDraft) || !parent.pendingStepProgressDraft ||
      !(parent.progressOwnerActive() || this.flushingPendingChanges && parent.isSettlingStepProgress)) return;
    try {
      const expected = applyWorkflowStepProgress(current, event.nodeId, event.draft.values, event.draft.intent,
        { contracts: this.authoringReferences()?.routingContracts ?? [], loading: this.isLoadingAuthoringReferences() });
      if (JSON.stringify(expected) !== JSON.stringify(event.document)) return;
      const lease = this.currentBypassDataLease();
      if (JSON.stringify(expected) === JSON.stringify(current)) this.workingDocument.set(cloneDocument(current));
      else this.onBehaviorDocumentChange(expected);
      if (lease && JSON.stringify(this.workingDocument()) === JSON.stringify(expected)) {
        const node = expected.nodes.filter(item => item.id === event.nodeId);
        if (node.length !== 1) return;
        lease.progressReceipt = { source: JSON.stringify(expected), configuration: JSON.stringify(node[0].stepProgressConfiguration ?? null), reference: lease.origin.reference };
        queueMicrotask(() => this.finalizeBypassDataProgressReceipt());
      }
    } catch { /* An unavailable current contract retains the actual child input. */ }
  }

  private finalizeBypassDataProgressReceipt(): void {
    const lease = this.bypassDataLease, receipt = lease?.progressReceipt;
    if (!lease || !receipt || lease.parent !== this.transitionEditor || lease.namespace !== this.workFormViewContext() ||
      receipt.source !== JSON.stringify(this.workingDocument())) return;
    const origin = lease.parent.readBypassDataOrigin();
    if (!origin || origin.state !== 'clean' || origin.nodeId !== lease.origin.nodeId || origin.namespace !== lease.origin.namespace ||
      origin.localContext !== lease.origin.localContext || origin.configuration !== receipt.configuration || origin.reference !== receipt.reference) return;
    lease.origin = structuredClone(origin); lease.source = receipt.source; delete lease.progressReceipt;
  }

  onProcessDataOwnedDocumentChange(event: WorkflowProcessDataOwnedDocumentChange): void {
    const current = this.workingDocument(), editor = this.processDataEditor, token = this.processDataSettlement;
    const reject = () => { if (token) token.rejected = true; };
    if (!event || !current || !editor || !editor.hasPendingChanges() || !this.canAuthor() || this.isDraftEditorInteractionBlocked() ||
      event.owner !== JSON.stringify(this.capabilityOwnerContext()) || event.source !== JSON.stringify(current)) { reject(); return; }
    if (token ? token.editor !== editor || token.owner !== event.owner || token.source !== event.source || token.view !== event.view :
      !this.isProcessDataOwnerActive() || this.processDataHasOtherPending() || event.view === null || event.view !== this.processDataOwnerView()) { reject(); return; }
    try {
      const draft = JSON.stringify(event.draft);
      if (draft !== JSON.stringify(editor.draftSnapshot) || token && draft !== token.draft) { reject(); return; }
      const expected = applyWorkflowProcessData(current, event.draft.fields);
      if (JSON.stringify(expected) !== JSON.stringify(event.document)) { reject(); return; }
      const lease = this.currentBypassDataLease();
      if (token) token.accepted = true;
      this.setWorkingDocument(expected);
      if (lease && lease === this.bypassDataLease && lease.parent === this.transitionEditor && lease.namespace === this.workFormViewContext()) {
        const origin = lease.parent.readBypassDataOrigin();
        if (origin && this.sameBypassDataOrigin(lease.origin, origin)) lease.source = JSON.stringify(expected);
      }
      this.editorMessage.set('Process data applied locally. Save the draft to persist it.');
    } catch { reject(); }
  }

  private settleProcessData(document: WorkflowDefinitionDocument | null, apply: () => boolean): boolean {
    const editor = this.processDataEditor;
    if (!document || !editor || !this.canAuthor() || this.isDraftEditorInteractionBlocked() || this.processDataSettlement ||
      JSON.stringify(document) !== JSON.stringify(this.workingDocument())) return false;
    if (!editor.hasPendingChanges()) return true;
    if (editor.pendingEditorScopes(() => document).some(scope => {
      const state = scope.state(); return state !== 'clean' && state !== 'valid' && state !== 'invalid';
    })) return false;
    const token = { editor, owner: JSON.stringify(this.capabilityOwnerContext()), source: JSON.stringify(document),
      view: this.processDataOwnerView(), draft: JSON.stringify(editor.draftSnapshot), accepted: false, rejected: false };
    const previousBlocked = editor.interactionBlocked, previousAuthor = editor.canAuthor;
    this.processDataSettlement = token; editor.interactionBlocked = false; editor.canAuthor = this.canAuthor();
    try { return apply() && token.accepted && !token.rejected; }
    finally { this.processDataSettlement = null; editor.interactionBlocked = previousBlocked; editor.canAuthor = previousAuthor; }
  }

  onNavigationDocumentChange(_document: WorkflowDefinitionDocument): void {
    // The retained workspace accepts only the leaf's synchronous owned receipt.
  }
  onNavigationOwnedDocumentChange(event: WorkflowNavigationOwnedDocumentChange): void {
    const current = this.workingDocument(), editor = this.navigationSettingsEditor, token = this.navigationSettlement;
    const reject = () => { if (token) token.rejected = true; };
    if (!event || !current || !editor || !editor.hasPendingChanges() || !this.canAuthor() || this.isDraftEditorInteractionBlocked()
      || event.owner !== JSON.stringify(this.capabilityOwnerContext()) || event.source !== JSON.stringify(current)) { reject(); return; }
    if (token ? token.editor !== editor || token.owner !== event.owner || token.source !== event.source || token.view !== event.view
      : !this.isNavigationOwnerActive() || event.view === null || event.view !== this.navigationOwnerView()
        || this.editorPendingScopes().some(scope => scope.id !== 'navigation:workflow-navigation' && scope.state() !== 'clean')) { reject(); return; }
    if (!editor.isCurrentOwnedDocumentChange(event, current) || JSON.stringify(event.draft) !== JSON.stringify(editor.draftSnapshot)
      || token && token.draft !== JSON.stringify(event.draft)) { reject(); return; }
    if (JSON.stringify(event.document) === JSON.stringify(current)) this.workingDocument.set(cloneDocument(current));
    else this.setWorkingDocument(event.document);
    if (JSON.stringify(this.workingDocument()) !== JSON.stringify(event.document)) { reject(); return; }
    if (token) token.accepted = true;
    this.identityFormValues = {
      ...this.identityFormValues,
      payloadSchemaVersion: event.document.payloadSchemaVersion,
    };
    this.editorMessage.set(
      'Navigation changes applied locally. Save the draft to persist them.',
    );
  }
  private settleNavigation(document: WorkflowDefinitionDocument | null, apply: () => boolean): boolean {
    const editor = this.navigationSettingsEditor;
    if (!document || !editor || this.navigationSettlement || !this.canAuthor() || this.isDraftEditorInteractionBlocked()
      || JSON.stringify(document) !== JSON.stringify(this.workingDocument())) return false;
    if (!editor.hasPendingChanges()) return true;
    if (editor.pendingEditorScopes(() => document).some(scope => !['clean', 'valid', 'invalid'].includes(scope.state()))) return false;
    const token = { editor, owner: JSON.stringify(this.capabilityOwnerContext()), source: JSON.stringify(document),
      view: this.navigationOwnerView(), draft: JSON.stringify(editor.draftSnapshot), accepted: false, rejected: false };
    const disabled = editor.disabled; this.navigationSettlement = token; editor.disabled = false;
    try { return apply() && token.accepted && !token.rejected; }
    finally { this.navigationSettlement = null; editor.disabled = disabled; }
  }

  onDashboardNameOwnedDocumentChange(event: WorkflowDashboardNameOwnedDocumentChange): void {
    const editor = this.pageEditors?.find(owner => owner.scope === 'dashboard'), current = this.workingDocument(), token = this.dashboardNameSettlementToken;
    const reject = () => { if (token) token.rejected = true; };
    if (!event || !editor || !current || !this.canAuthor() || this.isDraftEditorInteractionBlocked()
      || this.mapSelection()?.kind !== 'node' || !this.mapSelection()?.isAuthorable || this.selectedMapNode()?.typeCode !== 'human_step'
      || event.nodeId !== this.selectedMapNode()?.id || event.nodeId !== editor.node?.id
      || event.owner !== JSON.stringify(this.stepAssignmentOwnerContext()) || event.source !== JSON.stringify(current)) { reject(); return; }
    if (token ? !this.dashboardNameSettlement() || token.view !== event.view
      : !event.view || event.view !== this.dashboardSettingsView() || this.confirmationInProgress || this.ownerViewState.focusSuspended()) { reject(); return; }
    try {
      if (JSON.stringify(event.draft) !== JSON.stringify(editor.dashboardRenameSnapshot)
        || !editor.isCurrentDashboardNameChange(event, current)) { reject(); return; }
      if (JSON.stringify(event.document) === JSON.stringify(current)) this.workingDocument.set(cloneDocument(current));
      else this.onBehaviorDocumentChange(event.document);
      if (JSON.stringify(this.workingDocument()) !== JSON.stringify(event.document)) { reject(); return; }
      if (token) token.accepted = true;
    } catch { reject(); }
  }

  private settleDashboardName(document: WorkflowDefinitionDocument | null, apply: () => boolean): boolean {
    const editor = this.pageEditors?.find(owner => owner.scope === 'dashboard'), nodeId = this.selectedMapNode()?.id;
    if (!document || !editor || !nodeId || editor.node?.id !== nodeId || this.selectedMapNode()?.typeCode !== 'human_step'
      || this.mapSelection()?.kind !== 'node' || !this.mapSelection()?.isAuthorable || !this.canAuthor()
      || this.isDraftEditorInteractionBlocked() || this.dashboardNameSettlementToken
      || JSON.stringify(document) !== JSON.stringify(this.workingDocument())) return false;
    if (!editor.hasDashboardRename()) return apply();
    const token = { editor, nodeId, owner: JSON.stringify(this.stepAssignmentOwnerContext()), source: JSON.stringify(document),
      view: this.dashboardSettingsView(), draft: JSON.stringify(editor.dashboardRenameSnapshot), accepted: false, rejected: false };
    const disabled = editor.disabled, queuePending = editor.queueChangesPending;
    this.dashboardNameSettlementToken = token; editor.disabled = false; editor.queueChangesPending = this.stepAssignmentPolicyFormDirty();
    try { return apply() && token.accepted && !token.rejected; }
    finally { this.dashboardNameSettlementToken = null; editor.disabled = disabled; editor.queueChangesPending = queuePending; }
  }

  onDashboardSourceOwnedDocumentChange(event: WorkflowDashboardSourceOwnedDocumentChange): void {
    const editor = this.pageEditors?.find(owner => owner.scope === 'dashboard'), current = this.workingDocument();
    if (!event || !editor || !current || !this.dashboardSourceView() || this.confirmationInProgress
      || event.nodeId !== this.selectedMapNode()?.id || event.owner !== JSON.stringify(this.stepAssignmentOwnerContext())
      || event.source !== JSON.stringify(current) || event.view !== this.dashboardSourceView()
      || this.editorPendingScopes().some(scope => scope.state() !== 'clean')
      || !editor.isCurrentDashboardSourceChange(event, current)) return;
    this.onBehaviorDocumentChange(event.document);
  }

  onDashboardElementOwnedDocumentChange(event: WorkflowDashboardElementOwnedDocumentChange): void {
    const editor = this.pageEditors?.find(owner => owner.scope === 'dashboard'), current = this.workingDocument();
    if (!event || !editor || !current || !editor.componentCatalog || !this.dashboardElementView() || this.confirmationInProgress || this.ownerViewState.focusSuspended()
      || event.nodeId !== this.selectedMapNode()?.id || event.owner !== JSON.stringify(this.stepAssignmentOwnerContext())
      || event.source !== JSON.stringify(current) || event.view !== this.dashboardElementView()
      || this.editorPendingScopes().some(scope => scope.state() !== 'clean') || !editor.isCurrentDashboardElementChange(event, current)) return;
    try {
      const proposal = applyDashboardElement(current, { nodeId: event.nodeId, dashboardId: event.dashboardId, reference: event.reference }, editor.componentCatalog, editor.componentCapabilities, event.choice);
      if (JSON.stringify(proposal.document) !== JSON.stringify(event.document) || JSON.stringify(proposal.selection) !== JSON.stringify(event.selection)) return;
      this.onBehaviorDocumentChange(proposal.document);
    } catch { this.editorMessage.set('This dashboard element or its source changed. Review the current choices before adding it.'); }
  }

  async onDashboardRemovalRequested(request: WorkflowDashboardRemovalRequest): Promise<void> {
    const editor = this.pageEditors?.find(owner => owner.scope === 'dashboard');
    const document = this.workingDocument();
    if (!request || !editor || !document || this.dashboardRemovalPending || !this.dashboardRemovalView()
      || request.owner !== JSON.stringify(this.stepAssignmentOwnerContext()) || request.source !== JSON.stringify(document)
      || request.view !== this.dashboardRemovalView() || request.nodeId !== this.selectedMapNode()?.id
      || !editor.isCurrentDashboardRemovalRequest(request)) return;
    this.dashboardRemovalPending = true;
    try {
      readWorkflowDashboardRemoval(document, request);
      if (this.processNameError()) {
        this.revealWorkflowSettingsTask('general'); this.ensureFormValid(this.identityForm);
        this.focusInvalidFormField(this.identityForm, () => this.identityFormElement, () => this.setupHeading, 'displayName');
        return;
      }
      const labelIds = new Set(editor.dashboardRemovalLabelScopes(request).map(scope => `dashboard:${scope.id}`));
      const allowed = (scope: WorkflowEditorPendingScope) => labelIds.has(scope.id) || scope.id === 'navigation:workflow-navigation';
      const unrelated = this.editorPendingScopes().find(scope => !allowed(scope) && scope.state() !== 'clean');
      if (unrelated) {
        this.editorMessage.set(`Finish ${unrelated.label || 'the other pending input'} before removing this dashboard. Your input is retained.`);
        unrelated.focus(); return;
      }
      for (const id of [...labelIds, 'navigation:workflow-navigation']) {
        const scope = this.editorPendingScopes().find(scope => scope.id === id);
        if (!scope || scope.state() === 'clean') continue;
        if (scope.kind === 'modal-candidate' || scope.state() === 'stale' || !scope.apply?.() || scope.state() !== 'clean') {
          this.editorMessage.set(`Review ${scope.label} before removing this dashboard. Your input is retained.`);
          scope.focus(); return;
        }
      }
      const latest = this.workingDocument();
      if (!latest || this.pageEditors?.find(owner => owner.scope === 'dashboard') !== editor || !editor.dashboardRemovalOwnerCurrent(request)
        || request.view !== this.dashboardRemovalView() || request.owner !== JSON.stringify(this.stepAssignmentOwnerContext())) return;
      const plan = readWorkflowDashboardRemoval(latest, request), source = JSON.stringify(latest);
      const menu = plan.hasMenuOverride
        ? ` Its custom Staff menu entry${plan.menuOverrideLabel ? ` “${plan.menuOverrideLabel}”` : ''} will also be removed.`
        : ' There is no custom Staff menu entry for this dashboard.';
      const confirmed = await this.confirmDraftChange({ heading: 'Remove dashboard?',
        message: `Remove dashboard “${plan.dashboardLabel}” from this draft?${menu} Other dashboards, Work, records and published versions are kept. Save remains separate.`,
        confirmText: 'Remove dashboard', cancelText: 'Keep dashboard' });
      if (!confirmed) return;
      const current = this.workingDocument();
      if (!current || this.destroyRef.destroyed || this.confirmationInProgress || this.ownerViewState.focusSuspended()
        || !this.dashboardRemovalView() || request.view !== this.dashboardRemovalView()
        || request.owner !== JSON.stringify(this.stepAssignmentOwnerContext()) || source !== JSON.stringify(current)
        || this.pageEditors?.find(owner => owner.scope === 'dashboard') !== editor || !editor.dashboardRemovalOwnerCurrent(request)
        || this.editorPendingScopes().some(scope => scope.state() !== 'clean')) {
        this.editorMessage.set('The dashboard or editing context changed. Review its current settings before removing it.'); return;
      }
      const next = applyWorkflowDashboardRemoval(current, request, plan.fingerprint);
      this.onBehaviorDocumentChange(next);
      if (JSON.stringify(this.workingDocument()) !== JSON.stringify(next)) return;
      this.editorMessage.set(plan.hasMenuOverride
        ? 'Dashboard and its matching custom menu settings removed locally. Undo restores them; save the draft to persist the change.'
        : 'Dashboard removed locally. Undo restores it; save the draft to persist the change.');
    } catch (error) {
      this.editorMessage.set(error instanceof Error ? error.message : 'The selected dashboard could not be removed. Its saved settings are retained.');
    } finally { this.dashboardRemovalPending = false; }
  }

  stepUsesCommand(code: string): boolean {
    return (
      this.selectedMapNode()?.declaration?.behavior?.commands?.some(
        (command) =>
          command?.capability?.key?.provider === 'workflow' &&
          command.capability.key.kind === 'command' &&
          command.capability.key.version === 1 &&
          command.capability.key.code === code,
      ) ?? false
    );
  }
  openStepPageSetup(nodeId: string): void {
    if (
      this.isDraftEditorInteractionBlocked() ||
      !this.flushPendingEditorChanges()
    )
      return;
    if (!this.openMapNodeById(nodeId, false, 'overview')) return;
    this.stepWorkspaceScope.set('dashboard');
  }

  openOverviewStepTarget(
    nodeId: string | null,
    target: WorkflowOverviewTarget,
  ): void {
    if (
      !nodeId ||
      this.isDraftEditorInteractionBlocked() ||
      !this.flushPendingEditorChanges()
    )
      return;
    // Resolve after applying pending buffers; an overview row may describe an older render.
    const selection = this.mapNodeSelectionBySourceId(nodeId);
    const document = this.workingDocument();
    if (
      !selection?.isAuthorable ||
      selection.sourceIndex === null ||
      document?.nodes[selection.sourceIndex]?.typeCode !== 'human_step'
    )
      return;
    if (!this.openMapNodeById(nodeId, false, 'overview')) return;
    if (target === 'requirements') this.selectBehaviorSection('readiness');
    else if (target === 'decisions') this.openStepDecisionChecks();
    else this.openStepChecklistTarget(target);
    // Write the final scope, not the intermediate step selection.
    this.writeWorkbenchViewToUrl('map');
    this.focusAfterRender(() => this.mapInspectorHeading);
  }

  stepIssues(step: WorkflowOverviewStep): WorkflowStepIssue[] {
    return (
      this.stepIssueGroups().find((group) => group.step.key === step.key)
        ?.issues ?? []
    );
  }

  openStepIssues(step: WorkflowOverviewStep): void {
    if (
      this.isDraftEditorInteractionBlocked() ||
      !this.flushPendingEditorChanges()
    )
      return;
    const current = this.workflowOverview().steps.find((candidate) =>
      step.nodeId
        ? candidate.nodeId === step.nodeId
        : candidate.key === step.key &&
          candidate.identityState === step.identityState &&
          candidate.label === step.label,
    );
    if (!current || !this.currentDraft()) return;
    this.retainedIssueStep.set(null);
    if (current.nodeId) {
      if (!this.openMapNodeById(current.nodeId, false, 'overview')) return;
    } else {
      // A retained step may be inspected without inventing an editable identity.
      this.activeWorkbenchView.set('map');
      this.isAddingStep.set(false);
      this.mapSelection.set(null);
      this.setInspectorPane('overview');
      this.retainedIssueStep.set({
        draftId: this.currentDraft()!.id,
        key: current.key,
      });
    }
    this.writeWorkbenchViewToUrl('map');
    setTimeout(() => {
      if (this.stepIssuesDisclosure)
        this.stepIssuesDisclosure.nativeElement.open = true;
      this.stepIssuesHeading?.nativeElement.focus();
    });
  }

  localIssueFocusLabel(finding: WorkflowOverviewAttention): string {
    if (!finding.nodeId) return 'Inspect process map';
    if (finding.requirementId) return 'Open requirement';
    if (finding.edgeId) return 'Open path';
    const labels: Partial<Record<WorkflowOverviewTarget, string>> = {
      requirements: 'Open requirements section',
      responsibility: 'Open assignment section',
      actions: 'Open staff actions section',
      paths: 'Open routing section',
      decisions: 'Open decision checks section',
    };
    return labels[finding.target] ?? 'Open step section';
  }

  openLocalStepIssue(finding: WorkflowOverviewAttention): void {
    if (
      this.isDraftEditorInteractionBlocked() ||
      !this.flushPendingEditorChanges()
    )
      return;
    const current = this.workflowOverview().attention.find(
      (candidate) =>
        candidate.key === finding.key && candidate.nodeId === finding.nodeId,
    );
    if (!current) return;
    if (!current.nodeId) {
      this.showWholeWorkflowOverview();
      return;
    }
    if (current.edgeId) {
      this.openMapEdgeBySourceId(current.edgeId, true);
      return;
    }
    this.openOverviewStepTarget(current.nodeId, current.target);
    if (current.requirementId) {
      this.revealWorkView('requirements');
      const nodeId = current.nodeId;
      const requirementId = current.requirementId;
      setTimeout(() => {
        if (
          this.selectedMapNode()?.id === nodeId &&
          this.behaviorSection() === 'readiness'
        )
          this.requirementsEditor?.focusRequirementCard(requirementId);
      });
    }
  }

  openSavedStepIssue(diagnostic: WorkflowDefinitionDiagnostic): void {
    if (this.canFocusDiagnostic(diagnostic)) {
      this.focusValidationDiagnostic(diagnostic);
      return;
    }
    if (
      this.isDraftEditorInteractionBlocked() ||
      !this.flushPendingEditorChanges()
    )
      return;
    this.selectWorkbenchView('review');
    this.activeReviewView.set('readiness');
    this.focusAfterRender(() => this.reviewFindingsHeading);
  }
  openStepPaths(): void {
    if (
      this.isDraftEditorInteractionBlocked() ||
      !this.flushPendingEditorChanges()
    )
      return;
    const step = this.selectedMapNode();
    this.stepWorkspaceScope.set('behavior');
    this.behaviorSection.set('routes');
    this.selectMapInspectorPane('path');
    if (step?.typeCode === 'human_step') this.selectStepPathContext(step, true);
  }
  private selectStepPathContext(
    step: WorkflowDefinitionNode,
    moveFocusToInspector: boolean,
  ): void {
    const clearSelection = () => {
      this.edgeEditor?.selectedEdgeId.set(null);
      this.edgeEditor?.selectedEdgeIndex.set(null);
      this.edgeEditor?.isAddingEdge.set(false);
      this.edgeEditor?.statusMessage.set(null);
      this.edgeEditor?.errorMessage.set(null);
    };
    // The mounted path editor otherwise retains its unrelated default first edge.
    clearSelection();
    setTimeout(() => {
      if (
        this.selectedMapNode()?.id !== step.id ||
        this.mapInspectorPane() !== 'path' ||
        this.stepWorkspaceScope() !== 'behavior' ||
        this.behaviorSection() !== 'routes' ||
        this.isDraftEditorInteractionBlocked() ||
        this.hasUnappliedEditorChanges()
      )
        return;
      const document = this.workingDocument();
      if (!document) return;
      const outgoing = document.edges.flatMap((edge, index) =>
        edge?.fromNodeId === step.id ? [index] : [],
      );
      const graph = projectWorkflowDraftGraph(document);
      const edge =
        outgoing.length === 1
          ? graph.edges.find(
              (candidate) => candidate.sourceIndex === outgoing[0],
            )
          : null;
      if (edge && this.graphSelectionForEdge(edge, graph.edges).isAuthorable) {
        this.edgeEditor?.reviewEdgeAt(outgoing[0], moveFocusToInspector);
        return;
      }
      clearSelection();
      const label = step.displayName || step.stepCode || step.id;
      this.edgeEditor?.statusMessage.set(
        outgoing.length === 0
          ? `${label} has no outgoing path. Use Workflow overview to select or add a connector.`
          : outgoing.length > 1
            ? `${label} has ${outgoing.length} outgoing paths. Use Workflow overview to choose a connector.`
            : `${label}'s outgoing path needs repair. Use Workflow overview to inspect the connector.`,
      );
      if (moveFocusToInspector) this.edgeEditor?.focusPendingChanges();
    });
  }
  openBehaviorForm(command: StepCommand): void {
    const draftId = this.currentDraft()?.id;
    const nodeId = this.selectedMapNode()?.id;
    if (
      this.isDraftEditorInteractionBlocked() ||
      !this.flushPendingEditorChanges() ||
      this.currentDraft()?.id !== draftId || this.selectedMapNode()?.id !== nodeId
    )
      return;
    const current = this.currentOwnerCommand(command);
    if (!current) return;
    this.stepActionFocus.set(null);
    this.revealWorkView('components');
    this.behaviorFormFocus.set({
      capability: current.capability,
      request: (this.behaviorFormFocus()?.request ?? 0) + 1,
    });
    this.stepWorkspaceScope.set('behavior');
    this.behaviorSection.set('readiness');
    this.mapInspectorPane.set('overview');
  }

  openStepActions(command?: StepCommand): void {
    const draftId = this.currentDraft()?.id;
    const nodeId = this.selectedMapNode()?.id;
    if (
      this.isDraftEditorInteractionBlocked() ||
      !this.flushPendingEditorChanges() ||
      this.currentDraft()?.id !== draftId || this.selectedMapNode()?.id !== nodeId
    )
      return;
    const requested = command ?? this.selectedMapNode()?.declaration?.behavior.commands[0];
    const current = requested ? this.currentOwnerCommand(requested) : null;
    if (requested && !current) return;
    this.behaviorFormFocus.set(null);
    this.revealWorkView('components');
    this.stepActionFocus.set(current ? {
      capability: current.capability,
      request: (this.stepActionFocus()?.request ?? 0) + 1,
    } : null);
    this.stepWorkspaceScope.set('behavior');
    this.behaviorSection.set('readiness');
    this.mapInspectorPane.set('overview');
  }

  openStepOutcomes(command: StepCommand, outcomeCode?: string): void {
    const draftId = this.currentDraft()?.id;
    const nodeId = this.selectedMapNode()?.id;
    if (
      this.isDraftEditorInteractionBlocked() ||
      !this.flushPendingEditorChanges() ||
      this.currentDraft()?.id !== draftId || this.selectedMapNode()?.id !== nodeId
    )
      return;
    const current = this.currentOwnerCommand(command);
    if (!current || (outcomeCode !== undefined && (!Array.isArray(current.outcomes) || current.outcomes.filter(outcome => outcome?.code === outcomeCode).length !== 1))) return;
    this.behaviorFormFocus.set(null);
    this.stepActionFocus.set({
      capability: current.capability,
      request: (this.stepActionFocus()?.request ?? 0) + 1,
      outcomes: true,
      ...(outcomeCode === undefined ? {} : { outcomeCode }),
    });
    this.stepWorkspaceScope.set('behavior');
    this.behaviorSection.set('routes');
    this.mapInspectorPane.set('overview');
  }

  openHumanDirectPath(): void {
    if (this.isDraftEditorInteractionBlocked() || !this.isHumanStepOwnerActive('behavior', 'routes') || this.mapInspectorPane() === 'path') return;
    const nodeId = this.selectedMapNode()!.id, owner = this.workFormViewContext();
    this.requestMapEditorDeactivation(() => {
      if (owner !== this.workFormViewContext() || this.selectedMapNode()?.id !== nodeId ||
        !this.isHumanStepOwnerActive('behavior', 'routes') || this.mapInspectorPane() === 'path') return;
      const document = this.workingDocument();
      const edges = document?.edges.filter(edge => edge.fromNodeId === nodeId) ?? [];
      const selection = document && directRoutePresentation(document, nodeId).state === 'direct' && edges.length === 1
        ? this.mapEdgeSelectionBySourceId(edges[0].id) : null;
      if (!selection) { this.editorMessage.set('This step has no unique direct path. Inspect its retained workflow paths.'); return; }
      this.onMapSelectionRequested({ selection, origin: 'picker', moveFocusToInspector: true });
      this.writeWorkbenchViewToUrl('map');
    }, () => undefined);
  }

  /** Inspect only the uniquely saved branch for this result; never substitute its router entry. */
  openHumanOutcomePath(request: { command: StepCommand; outcomeCode: string }): void {
    if (this.isDraftEditorInteractionBlocked() || !this.isHumanStepOwnerActive('behavior', 'routes') || this.mapInspectorPane() === 'path') return;
    const nodeId = this.selectedMapNode()!.id, owner = this.workFormViewContext();
    const original = this.currentOwnerCommand(request.command);
    if (!original || JSON.stringify(original) !== JSON.stringify(request.command)) return;
    this.requestMapEditorDeactivation(() => {
      if (owner !== this.workFormViewContext() || this.selectedMapNode()?.id !== nodeId ||
        !this.isHumanStepOwnerActive('behavior', 'routes') || this.mapInspectorPane() === 'path') return;
      const document = this.workingDocument(), current = this.currentOwnerCommand(request.command);
      const row = document && current && this.humanOutcomeInventoryRow(document, nodeId, current, request.outcomeCode);
      const selection = row?.edgeIds.length === 1 ? this.mapEdgeSelectionBySourceId(row.edgeIds[0]) : null;
      if (!selection) {
        this.editorMessage.set('This result has no unique saved path. Inspect the retained workflow paths; no other connector was selected.');
        return;
      }
      // Reading the branch does not enter a path editor or change its declaration.
      this.onMapSelectionRequested({ selection, origin: 'picker', moveFocusToInspector: true });
      this.writeWorkbenchViewToUrl('map');
    }, () => undefined);
  }

  /** Reveal this retained buffer's actual field owner without asking it to flush itself. */
  revealAutomatedPendingSection(request: { nodeId: string; section: 'activity' | 'routing'; owner: string; source: string; view: string | null }): void {
    if (!request || !['activity', 'routing'].includes(request.section) || !this.activeAutomatedView()
      || request.nodeId !== this.selectedMapNode()?.id || !this.isAutomatedStepOwnerInitialized()
      || !this.currentAutomatedOutput(request, request.nodeId)
      || !this.automatedStepEditor || this.isDraftEditorInteractionBlocked()
      || this.ownerViewState.focusSuspended()) return;
    if (!this.automatedStepEditor.hasPendingChanges()) {
      this.selectStepScopeTab(request.section);
      if (this.activeAutomatedView() === request.section) {
        this.writeWorkbenchViewToUrl('map');
        if (request.section === 'activity') this.focusAutomatedActivityHeading();
      }
      return;
    }
    this.stepWorkspaceScope.set('behavior');
    this.behaviorSection.set(request.section === 'routing' ? 'routes' : 'readiness');
    this.mapInspectorPane.set('overview');
    this.writeWorkbenchViewToUrl('map');
  }

  private focusAutomatedActivityHeading(): void {
    const nodeId = this.selectedMapNode()?.id, owner = this.stepAssignmentOwnerContext();
    const document = JSON.stringify(this.workingDocument()), request = ++this.automatedHeadingFocusRequest;
    const focused = this.browserDocument.activeElement;
    setTimeout(() => {
      if (this.destroyRef.destroyed || request !== this.automatedHeadingFocusRequest || this.activeAutomatedView() !== 'activity'
        || nodeId !== this.selectedMapNode()?.id || owner !== this.stepAssignmentOwnerContext()
        || document !== JSON.stringify(this.workingDocument()) || this.ownerViewState.focusSuspended()) return;
      const active = this.browserDocument.activeElement;
      if (active !== focused && active !== this.browserDocument.body && active !== this.browserDocument.documentElement
        && active?.isConnected && !active.closest('[hidden],[inert]')) return;
      const heading = this.hostElement.nativeElement.querySelector<HTMLElement>('phms-workflow-automated-step-editor h4');
      if (heading?.isConnected && !heading.closest('[hidden],[inert]')) heading.focus({ preventScroll: true });
    });
  }

  /** Inspect only the currently saved, unique ordinary path for this exact installed output. */
  openAutomatedOutcomePath(request: { activity: WorkflowCapabilityReference; commandOutcome: string; routeOutcome: string; owner: string; source: string; view: string | null }): void {
    if (!request || capabilityReferencesShapeError([request.activity]) || this.activeAutomatedView() !== 'routing'
      || this.isDraftEditorInteractionBlocked()) return;
    const document = this.workingDocument(), nodeId = this.selectedMapNode()?.id;
    const source = document && nodeId && this.currentAutomatedOutput(request, nodeId) ? this.automatedOutcomePath(document, nodeId, request) : null;
    if (!source || !nodeId) return;
    const owner = this.stepAssignmentOwnerContext();
    this.requestMapEditorDeactivation(() => {
      const current = this.workingDocument();
      if (owner !== this.stepAssignmentOwnerContext() || this.activeAutomatedView() !== 'routing'
        || this.selectedMapNode()?.id !== nodeId || !current) return;
      const latest = this.automatedOutcomePath(current, nodeId, request);
      const selection = latest && latest.witness === source.witness ? this.mapEdgeSelectionBySourceId(latest.edgeId) : null;
      if (!selection) {
        this.editorMessage.set('This activity result has no unique current saved path. Its retained settings remain available; no other connector was selected.');
        return;
      }
      this.onMapSelectionRequested({ selection, origin: 'picker', moveFocusToInspector: true });
      this.writeWorkbenchViewToUrl('map');
    }, () => undefined);
  }

  private currentAutomatedOutput(request: { owner: string; source: string; view: string | null }, nodeId: string): boolean {
    const document = this.workingDocument();
    if (!document || request.view !== this.activeAutomatedView() || request.owner !== JSON.stringify(this.stepAssignmentOwnerContext())) return false;
    try { return request.source === automatedStepFingerprint(document, nodeId); } catch { return false; }
  }

  private automatedOutcomePath(document: WorkflowDefinitionDocument, nodeId: string,
    request: { activity: WorkflowCapabilityReference; commandOutcome: string; routeOutcome: string }): { edgeId: string; witness: string } | null {
    if (typeof request.commandOutcome !== 'string' || !request.commandOutcome.trim()
      || typeof request.routeOutcome !== 'string' || !request.routeOutcome.trim()) return null;
    const nodes = document.nodes.filter(node => node?.id === nodeId);
    const automation = nodes.length === 1 && nodes[0].typeCode === 'automated_step' ? nodes[0].automation : null;
    if (!automation?.command || capabilityReferencesShapeError([automation.command]) || !sameCapabilityReference(automation.command, request.activity) || !Array.isArray(automation.outcomes)) return null;
    const mappings = automation.outcomes.filter(mapping => mapping?.commandOutcome === request.commandOutcome || mapping?.routeOutcome === request.routeOutcome);
    if (mappings.length !== 1 || mappings[0].commandOutcome !== request.commandOutcome || mappings[0].routeOutcome !== request.routeOutcome) return null;
    const rows = readWorkflowDecisionInventory(document, null).results.filter(row => row.nodeId === nodeId && row.kind === 'automated-output'
      && row.command && sameCapabilityReference(row.command, request.activity) && row.outcomeCode === request.routeOutcome
      && row.owners.some(owner => owner.kind === 'routing'));
    if (rows.length !== 1 || rows[0].edgeIds.length !== 1 || rows[0].destinationNodeIds.length !== 1 || rows[0].isReturn !== false) return null;
    const edges = document.edges.filter(edge => edge?.id === rows[0].edgeIds[0]);
    if (edges.length !== 1 || edges[0].fromNodeId !== nodeId || edges[0].outcomeCode !== request.routeOutcome || edges[0].pathKindCode != null) return null;
    const targets = document.nodes.filter(node => node?.id === edges[0].toNodeId);
    if (targets.length !== 1 || targets[0].id === nodeId || !['human_step', 'automated_step', 'routing_decision', 'terminal'].includes(targets[0].typeCode)) return null;
    return { edgeId: edges[0].id, witness: JSON.stringify([automation.command, mappings[0], edges[0]]) };
  }

  /** A missing destination can still select its unique saved output for inspection and repair. */
  private requestAutomatedResultFocus(node: WorkflowDefinitionNode, routeOutcome: string | null): boolean {
    const outcomes = node.typeCode === 'automated_step' && Array.isArray(node.automation?.outcomes) ? node.automation.outcomes : [];
    const matches = routeOutcome ? outcomes.filter(outcome => outcome?.routeOutcome === routeOutcome) : [];
    if (matches.length !== 1 || typeof matches[0].commandOutcome !== 'string' || !matches[0].commandOutcome.trim()
      || outcomes.filter(outcome => outcome?.commandOutcome === matches[0].commandOutcome).length !== 1) {
      this.automatedRouteFocus.set(null); return false;
    }
    this.automatedRouteFocus.set({ routeOutcome: matches[0].routeOutcome, commandOutcome: matches[0].commandOutcome,
      request: (this.automatedRouteFocus()?.request ?? 0) + 1 });
    return true;
  }

  private humanOutcomeInventoryRow(document: WorkflowDefinitionDocument, nodeId: string, command: StepCommand, outcomeCode: string) {
    const rows = readWorkflowDecisionInventory(document, null).results.filter(row => row.nodeId === nodeId && row.outcomeCode === outcomeCode &&
      row.command && sameCapabilityReference(row.command, command.capability) && row.owners.some(owner => owner.kind === 'routing'));
    return rows.length === 1 ? rows[0] : null;
  }

  /** Completion requirements remain at their existing Work owner. */
  openStepRoutingRequirements(): void {
    if (this.isDraftEditorInteractionBlocked() || !this.isHumanStepOwnerActive('behavior', 'routes') || this.mapInspectorPane() === 'path') return;
    const owner = this.workFormViewContext(), nodeId = this.selectedMapNode()!.id;
    this.requestMapEditorDeactivation(() => {
      if (owner !== this.workFormViewContext() || this.selectedMapNode()?.id !== nodeId ||
        !this.isHumanStepOwnerActive('behavior', 'routes') || this.mapInspectorPane() === 'path') return;
      this.stepActionFocus.set(null); this.behaviorFormFocus.set(null);
      this.stepWorkspaceScope.set('behavior'); this.behaviorSection.set('readiness'); this.mapInspectorPane.set('overview');
      this.revealWorkView('requirements'); this.writeWorkbenchViewToUrl('map'); this.focusWorkDestination();
    }, () => undefined);
  }

  onNoticeRequirementRequested(
    editor: WorkflowPresentationEditorComponent,
    request: WorkflowNoticeRequirementRequest,
  ): void {
    const document = this.workingDocument(),
      view = this.pageSettingsView();
    const currentEditor = () =>
      this.pageEditors?.toArray().includes(editor) === true &&
      editor.scope === 'detail' &&
      this.isHumanStepOwnerActive('detail') &&
      this.mapInspectorPane() !== 'path';
    if (
      !request ||
      !document ||
      !view ||
      !currentEditor() ||
      this.isDraftEditorInteractionBlocked() ||
      request.view !== view ||
      request.source !== JSON.stringify(document) ||
      request.nodeId !== this.selectedMapNode()?.id ||
      !editor.isCurrentNoticeRequirementRequest(request, document) ||
      !this.noticeRequirementMatches(request, document)
    )
      return;
    const owner = this.saveContextKey(),
      target = structuredClone(request),
      focusRequest = ++this.noticeRequirementFocusRequest;
    this.requestMapEditorDeactivation(
      () => {
        const latest = this.workingDocument();
        if (
          this.destroyRef.destroyed ||
          focusRequest !== this.noticeRequirementFocusRequest ||
          owner !== this.saveContextKey() ||
          view !== this.pageSettingsView() ||
          !currentEditor() ||
          !latest ||
          this.isDraftEditorInteractionBlocked() ||
          !editor.isNoticeRequirementTargetCurrent(target, latest) ||
          !this.noticeRequirementMatches(target, latest)
        ) {
          this.editorMessage.set(
            'The notice content or completion requirement changed. Review its current settings before opening Work.',
          );
          return;
        }
        this.stepActionFocus.set(null);
        this.behaviorFormFocus.set(null);
        this.stepWorkspaceScope.set('behavior');
        this.behaviorSection.set('readiness');
        this.mapInspectorPane.set('overview');
        this.revealWorkView('requirements');
        this.writeWorkbenchViewToUrl('map');
        const source = JSON.stringify(latest),
          before = this.browserDocument.activeElement;
        const canFocus = (): boolean => {
          const active = this.browserDocument.activeElement,
            host = this.hostElement.nativeElement;
          if (
            this.destroyRef.destroyed ||
            focusRequest !== this.noticeRequirementFocusRequest ||
            this.ownerViewState.focusSuspended() ||
            owner !== this.saveContextKey() ||
            source !== JSON.stringify(this.workingDocument()) ||
            !this.isHumanStepOwnerActive('behavior', 'readiness') ||
            this.activeWorkView() !== 'requirements' ||
            this.selectedMapNode()?.id !== target.nodeId ||
            this.stepEditorNodeId() !== target.nodeId ||
            !host.isConnected ||
            host.closest('[hidden],[inert],[aria-hidden="true"]')
          )
            return false;
          if (
            active !== before &&
            active !== this.browserDocument.body &&
            active?.isConnected &&
            !active.closest('[hidden],[inert],[aria-hidden="true"]')
          )
            return false;
          const current = this.workingDocument();
          return !!current && this.noticeRequirementMatches(target, current);
        };
        setTimeout(() => {
          if (!canFocus()) return;
        if (!this.requirementsEditor?.inspectRequirementCard(target.requirementId, canFocus))
            this.editorMessage.set(
            'The exact notice requirement is unavailable for inspection. Current input and saved settings are retained.',
            );
        });
      },
      () => undefined,
    );
  }

  private noticeRequirementMatches(
    request: WorkflowNoticeRequirementRequest,
    document: WorkflowDefinitionDocument,
  ): boolean {
    const nodes = document.nodes.filter((node) => node?.id === request.nodeId);
    if (nodes.length !== 1 || nodes[0].typeCode !== 'human_step') return false;
    const requirements = nodes[0].declaration?.behavior?.completionRequirements;
    const matches = (Array.isArray(requirements) ? requirements : []).filter(
      (requirement) => requirement?.id === request.requirementId,
    );
    return (
      matches.length === 1 &&
      JSON.stringify(matches[0]) === JSON.stringify(request.requirement)
    );
  }

  private currentOwnerCommand(command: StepCommand): StepCommand | null {
    const nodes = this.workingDocument()?.nodes.filter(node => node.id === this.selectedMapNode()?.id) ?? [];
    if (nodes.length !== 1 || nodes[0].typeCode !== 'human_step') return null;
    const commands = nodes[0].declaration?.behavior.commands;
    const matches = (Array.isArray(commands) ? commands : [])
      .filter(item => item?.capability?.key && commandIdentity(item) === commandIdentity(command));
    return matches.length === 1 && sameCapabilityReference(matches[0].capability, command.capability)
      ? matches[0] : null;
  }

  onBehaviorDocumentChange(document: WorkflowDefinitionDocument): void {
    const before = this.workingDocument(),
      nodeId = this.selectedMapNode()?.id;
    if (
      !before ||
      !nodeId ||
      !this.canAuthor() ||
      this.isDraftEditorInteractionBlocked()
    )
      return;
    const after = serializeDocument(document);
    if (serializeDocument(before) === after) return;
    const previous = this.behaviorUndoHistory();
    const history =
      previous.at(-1)?.after === serializeDocument(before) &&
      previous.at(-1)?.nodeId === nodeId
        ? previous
        : [];
    this.recordingBehaviorEdit = true;
    try {
      this.setWorkingDocument(document);
    } finally {
      this.recordingBehaviorEdit = false;
    }
    this.behaviorUndoHistory.set([
      ...history.slice(-29),
      {
        before: cloneDocument(before),
        after,
        nodeId,
        scope: this.stepWorkspaceScope(),
      },
    ]);
    this.identityFormValues = {
      ...this.identityFormValues,
      payloadSchemaVersion: document.payloadSchemaVersion,
    };
    this.editorMessage.set(null);
  }

  undoBehavior(): void {
    if (!this.canUndoBehavior()) return;
    const history = this.behaviorUndoHistory(),
      previous = history.at(-1)!;
    this.setWorkingDocument(previous.before);
    this.identityFormValues = {
      ...this.identityFormValues,
      payloadSchemaVersion: previous.before.payloadSchemaVersion,
    };
    this.resetStructuredEditors();
    if (previous.before.nodes.some((node) => node?.id === previous.nodeId))
      this.resetNodeForm();
    else {
      this.mapSelection.set(null);
      this.selectedNodeId.set(null);
      this.mapDocumentToForms(previous.before);
      this.setInspectorPane('overview');
    }
    this.behaviorUndoHistory.set(history.slice(0, -1));
    this.editorMessage.set(null);
  }

  onPresentationDocumentChange(document: WorkflowDefinitionDocument): void {
    if (
      !this.canAuthor() ||
      this.isDraftEditorInteractionBlocked() ||
      (this.hasUnappliedEditorChanges() && !this.flushingPendingChanges)
    )
      return;
    this.setWorkingDocument(document);
    this.identityFormValues = {
      ...this.identityFormValues,
      payloadSchemaVersion: document.payloadSchemaVersion,
    };
    this.editorMessage.set(null);
  }

  onCapabilityDocumentChange(document: WorkflowDefinitionDocument): void {
    const latest = this.workingDocument();
    if (
      !latest || document.workflowCode !== latest.workflowCode ||
      document.definitionVersionCode !== latest.definitionVersionCode ||
      JSON.stringify(document.packReference ?? null) !== JSON.stringify(latest.packReference ?? null) ||
      (!supportsWorkflowCapabilities(latest.payloadSchemaVersion) &&
        !(['1.0', '1.1', '1.2', '1.3'].includes(latest.payloadSchemaVersion) && document.payloadSchemaVersion === '1.4')) ||
      !this.canAuthor() ||
      this.isDraftEditorInteractionBlocked() ||
      this.hasUnappliedEditorChanges()
    )
      return;
    this.setWorkingDocument({ ...latest, capabilityReferences: document.capabilityReferences,
      payloadSchemaVersion: document.payloadSchemaVersion === '1.4' && ['1.0', '1.1', '1.2', '1.3'].includes(latest.payloadSchemaVersion)
        ? '1.4' : latest.payloadSchemaVersion });
    this.identityFormValues = {
      ...this.identityFormValues,
      payloadSchemaVersion: this.workingDocument()!.payloadSchemaVersion,
    };
    this.editorMessage.set(
      'Capability changes applied locally. Save the draft; publication checks run automatically.',
    );
  }

  onAssignmentPolicyDocumentChange(document: WorkflowDefinitionDocument): void {
    if (!this.canAuthor() || this.isDraftEditorInteractionBlocked()) {
      return;
    }

    this.setWorkingDocument(document);
    this.identityFormValues = {
      ...this.identityFormValues,
      payloadSchemaVersion: document.payloadSchemaVersion,
    };
    this.editorMessage.set(null);
  }

  onEdgePendingChangesChange(pending: boolean): void {
    this.edgeFormDirty.set(pending);
    this.saveIntent = null;
    this.validationIntent = null;
  }

  onTransitionPendingChangesChange(pending: boolean): void {
    this.transitionFormDirty.set(pending);
    this.saveIntent = null;
    this.validationIntent = null;
  }

  onDecisionPendingChangesChange(pending: boolean): void {
    this.decisionFormDirty.set(pending);
    this.saveIntent = null;
    this.validationIntent = null;
  }

  onAssignmentPolicyPendingChangesChange(pending: boolean): void {
    this.assignmentPolicyFormDirty.set(pending);
    this.saveIntent = null;
    this.validationIntent = null;
  }
  onProcessDataPendingChangesChange(pending: boolean): void {
    this.processDataFormDirty.set(pending);
    this.saveIntent = null;
    this.validationIntent = null;
  }

  onWorkComponentsPendingChangesChange(pending: boolean): void {
    this.workComponentsDirty.set(pending);
    this.saveIntent = null;
    this.validationIntent = null;
  }

  onPageEditorPendingChangesChange(
    scope: WorkflowPresentationEditorComponent['scope'],
    pending: boolean,
  ): void {
    if (scope === 'dashboard') this.dashboardEditorDirty.set(pending);
    else if (scope === 'detail') this.staffPageEditorDirty.set(pending);
    else this.reviewRoutingDirty.set(pending);
    this.saveIntent = null;
    this.validationIntent = null;
  }

  onStepAssignmentPolicyPendingChangesChange(pending: boolean): void {
    this.stepAssignmentPolicyFormDirty.set(pending);
    this.saveIntent = null;
    this.validationIntent = null;
  }

  retryAuthoringReferences(): void {
    this.loadAuthoringReferences();
  }

  applyNodeChanges(): void {
    if (this.inlineStepNameEditing()) {
      this.applyInlineStepName();
      return;
    }
    if (
      !this.canAuthor() ||
      this.isDraftEditorInteractionBlocked() ||
      !this.ensureFormValid(this.nodeForm)
    ) {
      return;
    }

    const document = this.workingDocument();
    if (!document) {
      return;
    }

    if (this.stepKeyValidationError) {
      this.showOperationError(
        localValidationError(
          'Step key is incomplete',
          this.stepKeyValidationError,
        ),
      );
      this.focusInvalidStepKey();
      return;
    }

    if (
      this.isAddingStep() &&
      (!this.nodeFormValues.displayName.trim() ||
        !Number.isFinite(this.nodeFormValues.displayOrder) ||
        this.nodeFormValues.displayOrder < 0)
    ) {
      this.showOperationError(
        localValidationError(
          'Step is incomplete',
          'Enter a step name and a nonnegative list order.',
        ),
      );
      return;
    }
    const nodeId = this.nodeFormValues.id.trim();
    const stepCode = this.nodeFormValues.stepCode.trim();
    if (!nodeId || !stepCode) {
      this.showOperationError(
        localValidationError(
          'Step is incomplete',
          'A stable node ID and step code are required.',
        ),
      );
      return;
    }

    const existingNode = this.selectedNode();
    if (
      this.isAddingStep() &&
      document.nodes.some((node) => node.id === nodeId)
    ) {
      this.showOperationError(
        localValidationError(
          'Node ID is already in use',
          'Choose a unique stable node ID for the new step.',
        ),
      );
      return;
    }
    if (
      document.nodes.some(
        (node) =>
          ['human_step', 'automated_step'].includes(node.typeCode) &&
          node.id !== existingNode?.id &&
          node.stepCode === stepCode,
      )
    ) {
      this.showOperationError(
        localValidationError(
          'Step key is already in use',
          'Choose a unique step key. Runtime step records use this value as their compatibility key.',
        ),
      );
      return;
    }

    const inheritedStepCode = existingNode
      ? this.inheritedStepCode(existingNode.id)
      : null;
    const nextNode: WorkflowDefinitionNode = existingNode
      ? {
          ...existingNode,
          displayOrder: Number(this.nodeFormValues.displayOrder),
          stepCode: inheritedStepCode ?? stepCode,
          displayName:
            normalizeOptional(this.nodeFormValues.displayName) ?? null,
        }
      : {
          id: nodeId,
          typeCode: this.nodeFormValues.typeCode,
          displayOrder: Number(this.nodeFormValues.displayOrder),
          stepCode,
          displayName:
            normalizeOptional(this.nodeFormValues.displayName) ?? null,
          allowedTransitions: null,
          requiredDecisions: null,
          ...(this.nodeFormValues.typeCode === 'human_step'
            ? {
                stepProgressConfiguration: {
                  startMode: 'assigned_open' as const,
                },
              }
            : {}),
        };

    let nextDocument: WorkflowDefinitionDocument;
    try {
      if (
        !existingNode &&
        this.newStepPlacementMode === 'path' &&
        (!this.newStepPathSnapshot || this.newStepPlacementStale)
      )
        throw new Error(
          this.newStepPlacementStale
            ? 'The selected path changed. Review its current placement before adding the step. Your step details have been kept.'
            : 'Choose an available path for this step, or choose Add unconnected.',
        );
      if (
        !existingNode &&
        this.newStepPlacementMode === 'connection' &&
        this.newStepFrom &&
        (document.nodes.filter((node) => node?.id === this.newStepFrom)
          .length !== 1 ||
          document.edges.some((edge) => edge?.fromNodeId === this.newStepFrom))
      )
        throw new Error(
          'The chosen source changed or already has an outgoing path. Choose an unfinished step again, or insert on an existing path. Your step details have been kept.',
        );
      nextDocument = existingNode
        ? {
            ...document,
            nodes: document.nodes.map((node) =>
              node.id === existingNode.id ? nextNode : node,
            ),
          }
        : this.newStepPlacementMode === 'path'
          ? insertStepOnWorkflowPath(document, nextNode, this.newStepPathId)
          : appendConnectedWorkStep(
              document,
              nextNode,
              this.newStepFrom,
              this.newStepTo,
            );
      if (
        !existingNode &&
        nextNode.typeCode === 'human_step' &&
        !document.subjectTypeCode
      ) {
        if (!this.standardStepSupport)
          throw new Error(
            this.standardStepLoading()
              ? 'Standard task controls are loading. Your step details are kept; add the step when loading finishes.'
              : 'Reload the standard task controls before adding this step. Your step details are kept.',
          );
        try {
          nextDocument = initializeOperationalStep(
            nextDocument,
            nodeId,
            this.standardStepSupport.catalog,
            this.standardStepSupport.capabilities,
          );
        } catch (error) {
          this.standardStepSupport = null;
          this.standardStepError.set(
            error instanceof Error
              ? error.message
              : 'Reload standard task controls before adding this step.',
          );
          throw error;
        }
      }
    } catch (error) {
      this.showOperationError(
        localValidationError(
          'Step could not be connected',
          error instanceof Error ? error.message : 'Review the selected paths.',
        ),
      );
      return;
    }
    if (!existingNode)
      nextDocument = {
        ...nextDocument,
        payloadSchemaVersion: requireWorkflowPayloadVersion(
          nextDocument.payloadSchemaVersion,
          nextNode.typeCode === 'automated_step' ? '1.15' : '1.12',
        ),
      };
    this.operationError.set(null);
    if (existingNode && this.selectedMapNode()?.id === existingNode.id)
      this.onBehaviorDocumentChange(nextDocument);
    else if (!existingNode) {
      this.recordingBehaviorEdit = true;
      try {
        this.setWorkingDocument(nextDocument);
      } finally {
        this.recordingBehaviorEdit = false;
      }
      this.behaviorUndoHistory.set([
        {
          before: cloneDocument(document),
          after: serializeDocument(nextDocument),
          nodeId: nextNode.id,
          scope: 'behavior',
        },
      ]);
    } else this.setWorkingDocument(nextDocument);
    this.isAddingStep.set(false);
    this.selectedNodeId.set(nextNode.id);
    this.nodeFormDirty.set(false);
    this.nodeFormValues = this.nodeToFormValues(
      nextDocument.nodes.find((node) => node.id === nextNode.id)!,
    );
    this.nodeFormConfig = this.buildNodeFormConfig(
      false,
      inheritedStepCode !== null,
    );
    this.openMapNodeById(nextNode.id, false, 'overview');
    if (!existingNode) this.behaviorSection.set('readiness');
    this.editorMessage.set(
      existingNode
        ? 'Step changes applied locally.'
        : this.newStepPlacementMode === 'path' ||
            this.newStepFrom ||
            this.newStepTo
          ? 'Step and selected paths added. Add its components below, then save the draft.'
          : 'Step added. It is not connected yet. Add its components below, then configure paths before validation.',
    );
  }

  resetNodeForm(): void {
    if (this.isAddingStep()) {
      this.isAddingStep.set(false);
      this.nodeFormDirty.set(false);
      const firstNode =
        this.humanStepNodes()[0] ??
        this.entryNodes()[0] ??
        this.terminalNodes()[0];
      if (firstNode) {
        this.loadNodeForm(firstNode);
      }
      return;
    }

    const node = this.selectedNode();
    if (node) {
      this.loadNodeForm(node);
    }
  }

  private removeSelectedStep(): void {
    const document = this.workingDocument();
    const node = this.selectedNode();
    if (
      !document ||
      !node ||
      !['human_step', 'automated_step'].includes(node.typeCode) ||
      !this.canAuthor()
    ) {
      return;
    }

    this.setWorkingDocument({
      ...document,
      nodes: document.nodes.filter((candidate) => candidate.id !== node.id),
      edges: document.edges.filter(
        (edge) =>
          !edge || (edge.fromNodeId !== node.id && edge.toNodeId !== node.id),
      ),
      ...(document.assignmentPolicies
        ? {
            assignmentPolicies: document.assignmentPolicies.filter(
              (policy) =>
                policy?.scopeCode !== 'step' || policy.nodeId !== node.id,
            ),
          }
        : {}),
    });
    this.resetStructuredEditors();
    this.nodeFormDirty.set(false);
    this.isAddingStep.set(false);
    this.mapSelection.set(null);
    this.setInspectorPane('overview');
    const nextNode =
      this.humanStepNodes()[0] ??
      this.entryNodes()[0] ??
      this.terminalNodes()[0];
    if (nextNode) {
      this.loadNodeForm(nextNode);
    } else {
      this.selectedNodeId.set(null);
    }
    this.editorMessage.set(
      `${node.displayName || node.stepCode || node.id} and its connected map paths were removed locally.`,
    );
  }

  stepPosition(node: WorkflowDefinitionNode): number | null {
    const index = this.humanStepNodes().findIndex(
      (candidate) => candidate.id === node.id,
    );
    return index === -1 ? null : index + 1;
  }

  stepTransitions(
    node: WorkflowDefinitionNode | null | undefined,
  ): WorkflowAllowedTransition[] {
    return (Array.isArray(node?.allowedTransitions) ? node.allowedTransitions : []).filter(
      (transition): transition is WorkflowAllowedTransition =>
        transition !== null,
    );
  }

  stepDecisionRuleCount(
    node: WorkflowDefinitionNode | null | undefined,
  ): number {
    if (!node || node.typeCode !== 'human_step') {
      return 0;
    }

    return (
      this.decisionRequirementCount(node.requiredDecisions) +
      this.stepTransitions(node).reduce(
        (count, transition) =>
          count +
          this.decisionRequirementCount(
            transition.requiredDecisions,
            transition.requiredDecisionKinds,
          ),
        0,
      )
    );
  }

  onEditorActionButtonClick(button: ButtonConfig): void {
    switch (button.id) {
      case 'connectedDryRun':
        if (this.isDraftEditorInteractionBlocked()) return;
        this.setDryRunExpanded(true);
        this.focusAfterRender(() => this.dryRunSection);
        break;
      case 'saveDraft':
        this.saveDraft();
        break;
      case 'cancelEdits':
        this.requestDiscardThen(() => this.resetDraftToBaseline());
        break;
      case 'validateDraft':
        this.validateDraft();
        break;
      default:
        break;
    }
  }
  setDryRunExpanded(open: boolean): void {
    this.dryRunExpanded.set(open);
    if (open) this.dryRunOpened.set(true);
  }

  selectStepWorkspaceScope(scope: 'behavior' | 'dashboard' | 'detail'): void {
    if (
      this.isDraftEditorInteractionBlocked() ||
      !this.flushPendingEditorChanges()
    )
      return;
    this.stepWorkspaceScope.set(scope);
  }

  selectStepScopeTab(id: string): void {
    if (id === 'work' || id === 'activity') this.selectBehaviorSection('readiness');
    else if (id === 'routing') this.selectBehaviorSection('routes');
    else if (id === 'settings')
      this.selectBehaviorSection(
        this.activeBehaviorTab() === 'advanced' ? this.behaviorSection() : 'details',
      );
    // Continue accepting the previous section IDs for existing callers and links.
    else if (id === 'behavior' || id === 'dashboard' || id === 'detail')
      this.selectStepWorkspaceScope(id);
  }

  selectWorkbenchTab(id: string): void {
    const item = this.workbenchNavigation.find((item) => item.id === id);
    if (item) this.selectWorkbenchView(item.id);
  }

  closeContextActions(details: HTMLDetailsElement): void {
    details.open = false;
    details.querySelector<HTMLElement>('summary')?.focus();
  }

  selectInspectorTab(id: string): void {
    if (id === 'advanced' && this.selectedMapNode()?.typeCode === 'human_step')
      this.selectBehaviorSection('details');
    else if (this.selectedMapNode()?.typeCode === 'human_step')
      this.selectBehaviorView(id);
    else if (id === 'overview' || id === 'transitions' || id === 'decisions')
      this.selectMapInspectorPane(id);
  }

  openStepAssignment(): void {
    const owner = this.stepAssignmentOwnerContext();
    if (
      this.isDraftEditorInteractionBlocked() ||
      !this.isStepEditorActive() || this.selectedMapNode()?.typeCode !== 'human_step' ||
      this.hasUnappliedEditorChanges() && !this.flushPendingEditorChanges() ||
      owner !== this.stepAssignmentOwnerContext()
    )
      return;
    this.requestResponsibilityFocus();
  }

  /** Open the current Work item's Staff-page owner without provisioning content. */
  openRequirementStaffPage(): void {
    if (this.isDraftEditorInteractionBlocked() || !this.isStepEditorActive() || this.selectedMapNode()?.typeCode !== 'human_step') return;
    const owner = this.stepAssignmentOwnerContext();
    if (!this.flushPendingEditorChanges() || owner !== this.stepAssignmentOwnerContext()) return;
    this.stepWorkspaceScope.set('detail');
    const context = JSON.stringify([owner, this.activeWorkbenchView(), this.activeWorkView()]);
    setTimeout(() => {
      if (this.destroyRef.destroyed || this.ownerViewState.focusSuspended() || this.stepWorkspaceScope() !== 'detail' ||
        context !== JSON.stringify([this.stepAssignmentOwnerContext(), this.activeWorkbenchView(), this.activeWorkView()])) return;
      const heading = this.nodeEditorHeading?.nativeElement;
      if (heading?.isConnected && !heading.closest('[hidden], [inert]')) heading.focus();
    });
  }

  selectBehaviorSection(
    section:
      | 'details'
      | 'readiness'
      | 'routes'
      | 'transitions'
      | 'notifications'
      | 'timers' | 'children',
  ): void {
    if (
      this.isDraftEditorInteractionBlocked() ||
      !this.flushPendingEditorChanges()
    )
      return;
    this.stepWorkspaceScope.set('behavior');
    this.behaviorSection.set(section);
    this.mapInspectorPane.set(
      this.selectedMapNode()?.typeCode === 'automated_step' ? 'overview' : section === 'readiness'
        ? 'decisions'
        : section === 'transitions'
          ? 'transitions'
          : 'overview',
    );
    if (section === 'readiness') {
      this.focusWorkDestination();
    }
    if (section === 'transitions') this.focusMapTransitionContext();
  }

  openNotificationOwner(request: WorkflowStepNotificationOwnerRequest): void {
    if (
      request.nodeId !== this.selectedMapNode()?.id ||
      this.selectedMapNode()?.typeCode !== 'human_step' ||
      this.isDraftEditorInteractionBlocked() ||
      !this.flushPendingEditorChanges() ||
      request.nodeId !== this.selectedMapNode()?.id
    )
      return;
    const eventIdentity =
      request.eventIdentity ??
      this.stepNotificationsEditor?.selectedEvent()?.identity ??
      null;
    let ownerPath = request.path;
    if (request.eventIdentity) {
      const matches =
        this.stepNotificationsEditor?.events.filter(
          (event) => event.identity === request.eventIdentity,
        ) ?? [];
      if (matches.length !== 1 || matches[0].ownerProblem) {
        this.editorMessage.set(
          'This notification event is missing or ambiguous. Choose its current event before inspecting its rule.',
        );
        return;
      }
      if (ownerPath) {
        const current = matches[0];
        if (
          request.kind !==
          (current.kind === 'decision' ? 'decisions' : 'transitions')
        )
          return;
        const stepPath = `$.nodes[id=${request.nodeId}]`;
        const scopePath =
          current.transitionIndex === null
            ? stepPath
            : `${stepPath}.allowedTransitions[${current.transitionIndex}]`;
        ownerPath =
          current.kind === 'decision'
            ? `${scopePath}.requiredDecisions[${current.decisionIndex}]`
            : scopePath;
      }
    }
    const draftId = this.currentDraft()?.id;
    if (!draftId) return;
    if (request.kind === 'decisions' && !ownerPath) {
      this.openStepDecisionChecks();
      this.notificationReturnContext.set({
        draftId,
        nodeId: request.nodeId,
        eventIdentity,
      });
      this.writeWorkbenchViewToUrl('map');
      return;
    }
    this.selectBehaviorSection('transitions');
    if (ownerPath && request.kind === 'decisions') {
      this.setInspectorPane('decisions');
      this.decisionEditor?.focusDecisionPath(
        ownerPath,
        request.nodeId,
        'review',
      );
    } else if (ownerPath)
      this.transitionEditor?.focusTransitionPath(
        ownerPath,
        request.nodeId,
        'review',
      );
    this.notificationReturnContext.set({
      draftId,
      nodeId: request.nodeId,
      eventIdentity,
    });
    this.writeWorkbenchViewToUrl('map');
  }

  returnToNotifications(): void {
    const target = this.notificationReturnContext();
    if (
      !target ||
      !this.canReturnToNotifications() ||
      this.isDraftEditorInteractionBlocked() ||
      !this.flushPendingEditorChanges() ||
      target !== this.notificationReturnContext() ||
      target.draftId !== this.currentDraft()?.id ||
      target.nodeId !== this.selectedMapNode()?.id
    )
      return;
    this.selectBehaviorSection('notifications');
    this.notificationReturnContext.set(null);
    if (this.stepNotificationsEditor) {
      this.stepNotificationsEditor.document = this.workingDocument();
      if (target.eventIdentity)
        this.stepNotificationsEditor.focusNotificationIdentity(
          target.eventIdentity,
        );
      else this.stepNotificationsEditor.focusPendingChanges();
    }
    this.writeWorkbenchViewToUrl('map');
  }

  returnToStatusRules(): void {
    const nodeId = this.selectedMapNode()?.id;
    if (
      this.selectedMapNode()?.typeCode !== 'human_step' ||
      this.isDraftEditorInteractionBlocked() ||
      !this.flushPendingEditorChanges() ||
      this.selectedMapNode()?.id !== nodeId
    )
      return;
    this.activeWorkbenchView.set('map');
    this.setInspectorPane('transitions');
    this.transitionEditor?.focusCurrentStatusRules();
    this.writeWorkbenchViewToUrl('map');
  }

  openStatusRuleDecisionChecks(nodeId: string): void {
    if (this.selectedMapNode()?.id !== nodeId) return;
    this.openStepDecisionChecks();
  }

  openStatusRuleRequirements(nodeId: string): void {
    if (
      this.selectedMapNode()?.id !== nodeId ||
      this.selectedMapNode()?.typeCode !== 'human_step'
    )
      return;
    this.selectBehaviorSection('readiness');
  }

  openWholeWorkflowDecisionChecks(): void {
    if (
      this.isDraftEditorInteractionBlocked() ||
      !this.flushPendingEditorChanges()
    )
      return;
    this.decisionInventoryCheckAddMode.set(false);
    this.activeWorkbenchView.set('map');
    this.isAddingStep.set(false);
    this.mapSelection.set(null);
    this.setInspectorPane('decisions');
    this.decisionEditor?.focusDecisionOverview(null);
    this.writeWorkbenchViewToUrl('map');
  }

  openStepDecisionChecks(): void {
    if (
      this.selectedMapNode()?.typeCode !== 'human_step' ||
      this.isDraftEditorInteractionBlocked() ||
      !this.flushPendingEditorChanges()
    )
      return;
    this.decisionInventoryCheckAddMode.set(false);
    this.setInspectorPane('decisions');
    this.decisionEditor?.focusDecisionOverview(
      this.selectedMapNode()?.id ?? null,
    );
  }

  selectBehaviorView(value: string): void {
    const section = this.behaviorSections.find(
      (candidate) => candidate.id === value,
    );
    if (section) this.selectBehaviorSection(section.id);
  }

  openStepNotifications(request: { nodeId: string; path: string }): void {
    if (
      request.nodeId !== this.selectedMapNode()?.id ||
      this.isDraftEditorInteractionBlocked() ||
      !this.flushPendingEditorChanges()
    )
      return;
    if (this.stepNotificationsEditor) {
      this.stepNotificationsEditor.document = this.workingDocument();
      if (
        this.stepNotificationsEditor.focusNotificationPath(
          request.path,
          request.nodeId,
        )
      )
        this.selectBehaviorSection('notifications');
    }
  }

  private requestResponsibilityFocus(dashboardId?: string): void {
    // A targeted dashboard finding still opens that dashboard's queue/access
    // settings; step assignment itself belongs to Work.
    this.stepWorkspaceScope.set(dashboardId === undefined ? 'behavior' : 'dashboard');
    if (dashboardId === undefined) {
      this.behaviorSection.set('readiness');
      this.revealWorkView('assignment');
    }
    this.mapInspectorPane.set('overview');
    const focus = {
      request: (this.behaviorResponsibilityFocus()?.request ?? 0) + 1,
      ...(dashboardId !== undefined ? { dashboardId } : {}),
    };
    this.behaviorResponsibilityFocus.set(focus);
    if (dashboardId === undefined) {
      const owner = this.stepAssignmentOwnerContext(), source = JSON.stringify(this.workingDocument()),
        view = this.workFormViewContext(), before = this.browserDocument.activeElement;
      setTimeout(() => {
        if (this.destroyRef.destroyed || this.ownerViewState.focusSuspended() || this.behaviorResponsibilityFocus() !== focus
          || !this.isHumanStepOwnerActive('behavior', 'readiness') || this.activeWorkView() !== 'assignment'
          || owner !== this.stepAssignmentOwnerContext() || source !== JSON.stringify(this.workingDocument())
          || view !== this.workFormViewContext()) return;
        const active = this.browserDocument.activeElement;
        if (active !== before && active !== this.browserDocument.body && active !== this.browserDocument.documentElement
          && active?.isConnected && !active.closest('[hidden],[inert]')) return;
        const heading = this.workViewHeading?.nativeElement;
        if (!this.canAuthor() && heading?.isConnected && !heading.closest('[hidden],[inert]')) heading.focus();
        else this.stepAssignmentPolicyEditor?.focusPendingChanges();
      });
    }
  }

  openStepChecklistTarget(target: StepChecklistTarget): void {
    if (
      this.isDraftEditorInteractionBlocked() ||
      !this.flushPendingEditorChanges()
    )
      return;
    this.activeWorkbenchView.set('map');
    this.writeWorkbenchViewToUrl('map');
    if (target === 'paths') this.openStepPaths();
    else if (target === 'outcomes') {
      this.selectStepWorkspaceScope('behavior');
      this.selectBehaviorSection('routes');
    } else if (target === 'detail' || target === 'dashboard')
      this.selectStepWorkspaceScope(target);
    else if (target === 'actions') this.openStepActions();
    else if (target === 'responsibility') {
      this.requestResponsibilityFocus();
    } else this.selectWorkbenchView('review');
    this.focusAfterRender(() => this.mapInspectorHeading);
  }

  private flushPendingEditorChanges(): boolean {
    if (!this.hasUnappliedEditorChanges()) return true;
    if (
      !this.canAuthor() ||
      this.flushingPendingChanges ||
      this.isDraftEditorInteractionBlocked()
    )
      return false;
    this.flushingPendingChanges = true;
    try {
      if (this.nodeFormDirty()) {
        this.applyNodeChanges();
        if (this.nodeFormDirty()) {
          this.focusPendingEditor('node');
          return false;
        }
      }
      const editors = [
        { kind: 'workflow-status' as const, dirty: () => this.workflowStatusFormDirty(), editor: this.workflowStatusEditor },
        { kind: 'timer' as const, dirty: () => this.timerFormDirty(), editor: this.timersEditor },
        { kind: 'child' as const, dirty: () => this.childWorkFormDirty(), editor: this.childWorkEditor },
        {
          kind: 'automation' as const,
          dirty: () => this.automatedStepFormDirty(),
          editor: this.automatedStepEditor,
        },
        {
          kind: 'automation' as const,
          dirty: () => this.automationFormDirty(),
          editor: this.automationEditor,
        },
        {
          kind: 'process-data' as const,
          dirty: () => this.processDataFormDirty(),
          editor: this.processDataEditor,
        },
        {
          kind: 'requirement' as const,
          dirty: () => this.requirementFormDirty(),
          editor: this.requirementsEditor,
        },
        {
          kind: 'notification' as const,
          dirty: () => this.notificationFormDirty(),
          editor: this.stepNotificationsEditor,
        },
        {
          kind: 'decision' as const,
          dirty: () => this.decisionFormDirty(),
          editor: this.decisionEditor,
        },
        {
          kind: 'transition' as const,
          dirty: () => this.transitionFormDirty(),
          editor: this.transitionEditor,
        },
        {
          kind: 'edge' as const,
          dirty: () => this.edgeFormDirty(),
          editor: this.edgeEditor,
        },
        {
          kind: 'assignment' as const,
          dirty: () => this.assignmentPolicyFormDirty(),
          editor: this.assignmentPolicyEditor,
        },
        {
          kind: 'step-assignment' as const,
          dirty: () => this.stepAssignmentPolicyFormDirty(),
          editor: this.stepAssignmentPolicyEditor,
        },
      ];
      for (const entry of editors) {
        if (!entry.dirty()) continue;
        if (entry.kind === 'child' && !this.childWorkEditor?.canSettleExistingChanges()) {
          // New relationships and new workflow results retain their explicit Apply boundary.
          this.focusPendingEditor('child');
          return false;
        }
        const editor = entry.editor;
        let applied = false;
        if (editor) {
          const previousBlocked = editor.interactionBlocked;
          const previousAuthor = editor.canAuthor;
          // A deliberate flush resolves sibling buffers in order; Angular inputs still
          // describe the earlier render and must not deadlock those sibling edits.
          editor.canAuthor = this.canAuthor();
          editor.interactionBlocked =
            this.isDraftEditorInteractionBlocked() ||
            (entry.kind === 'edge' &&
              this.mapSelection()?.kind === 'edge' &&
              !this.mapSelection()?.isAuthorable);
          try {
            applied = entry.kind === 'timer'
              ? this.settleTimer(this.workingDocument(), () => this.timersEditor!.applyPendingChanges(this.workingDocument()!))
              : entry.kind === 'process-data'
              ? this.settleProcessData(this.workingDocument(), () => editor.applyPendingChanges(this.workingDocument()!))
              : entry.kind === 'child'
                ? this.settleChildWork(this.workingDocument(), () => this.childWorkEditor!.settleExistingChanges(this.workingDocument()!))
              : editor.applyPendingChanges(this.workingDocument()!);
            if (entry.kind === 'transition') this.finalizeBypassDataProgressReceipt();
          } finally {
            editor.interactionBlocked = previousBlocked;
            editor.canAuthor = previousAuthor;
          }
        }
        if (!applied || entry.dirty()) {
          this.focusPendingEditor(entry.kind);
          return false;
        }
      }
      if (this.navigationFormDirty()) {
        const editor = this.navigationSettingsEditor;
        if (!editor) {
          this.focusPendingEditor('navigation');
          return false;
        }
        const previousDisabled = editor.disabled;
        editor.disabled =
          !this.canAuthor() || this.isDraftEditorInteractionBlocked();
        let applied: boolean;
        try {
          applied = this.settleNavigation(this.workingDocument(), () => editor.applyPendingChanges(this.workingDocument()!));
        } finally {
          editor.disabled = previousDisabled;
        }
        if (!applied || this.navigationFormDirty()) {
          this.focusPendingEditor('navigation');
          return false;
        }
      }
      if (this.workComponentsDirty()) {
        const editor = this.workComponentsEditor;
        if (!editor) {
          this.focusPendingEditor('work');
          return false;
        }
        const previousDisabled = editor.disabled;
        editor.disabled =
          !this.canAuthor() || this.isDraftEditorInteractionBlocked();
        let applied: boolean;
        try {
          applied = editor.applyPendingChanges(this.workingDocument()!);
        } finally {
          editor.disabled = previousDisabled;
        }
        if (!applied || this.workComponentsDirty()) {
          this.focusPendingEditor('work');
          return false;
        }
      }
      if (this.staffPageEditorDirty()) {
        const editor = this.pageEditors?.find(
          (editor) => editor.scope === 'detail',
        );
        if (!editor) {
          this.focusPendingEditor('staff-page');
          return false;
        }
        const previousDisabled = editor.disabled;
        editor.disabled =
          !this.canAuthor() || this.isDraftEditorInteractionBlocked();
        let applied: boolean;
        try {
          applied = editor.applyPendingChanges(this.workingDocument()!);
        } finally {
          editor.disabled = previousDisabled;
        }
        if (!applied || this.staffPageEditorDirty()) {
          this.focusPendingEditor('staff-page');
          return false;
        }
      }
      if (this.dashboardEditorDirty()) {
        const editor = this.pageEditors?.find(
          (editor) => editor.scope === 'dashboard',
        );
        if (!editor) {
          this.focusPendingEditor('dashboard');
          return false;
        }
        const previousDisabled = editor.disabled;
        const previousQueuePending = editor.queueChangesPending;
        editor.disabled =
          !this.canAuthor() || this.isDraftEditorInteractionBlocked();
        editor.queueChangesPending = this.stepAssignmentPolicyFormDirty();
        let applied: boolean;
        try {
          applied = this.settleDashboardName(this.workingDocument(), () => editor.applyPendingChanges(this.workingDocument()!));
        } finally {
          editor.disabled = previousDisabled;
          editor.queueChangesPending = previousQueuePending;
        }
        if (!applied || this.dashboardEditorDirty()) {
          this.focusPendingEditor('dashboard');
          return false;
        }
      }
      if (this.reviewRoutingDirty()) {
        for (const editor of this.pageEditors ?? []) {
          if (editor.scope === 'dashboard' || editor.scope === 'detail')
            continue;
          const previousDisabled = editor.disabled;
          let applied: boolean;
          editor.disabled =
            !this.canAuthor() || this.isDraftEditorInteractionBlocked();
          try {
            applied = editor.applyPendingChanges(this.workingDocument()!);
          } finally {
            editor.disabled = previousDisabled;
          }
          if (!applied) {
            this.focusPendingEditor('review');
            return false;
          }
        }
        if (this.reviewRoutingDirty()) {
          this.focusPendingEditor('review');
          return false;
        }
      }
      if (this.hasUnappliedEditorChanges()) {
        this.returnToPendingChanges();
        return false;
      }
      return true;
    } finally {
      this.flushingPendingChanges = false;
    }
  }

  saveDraft(): void {
    const draft = this.currentDraft();
    if (!draft || !this.canAuthor() || this.isDraftEditorInteractionBlocked())
      return;
    if (!this.flushPendingEditorChanges()) return;
    if (this.processNameError()) {
      this.revealWorkflowSettingsTask('general');
      this.ensureFormValid(this.identityForm);
      this.focusInvalidFormField(this.identityForm, () => this.identityFormElement,
        () => this.setupHeading, 'displayName');
      return;
    }
    const basicsValid = this.ensureFormValid(this.identityForm);
    const technicalValid =
      !this.identityTechnicalForm ||
      this.ensureFormValid(this.identityTechnicalForm);
    const availabilityValid =
      !this.identityAvailabilityForm ||
      this.ensureFormValid(this.identityAvailabilityForm);
    if (!basicsValid || !technicalValid || !availabilityValid) {
      this.activeWorkbenchView.set('setup');
      this.revealWorkflowSettingsTask(!basicsValid ? 'general' : !technicalValid ? 'technical' : 'general');
      if (!basicsValid)
        this.focusInvalidFormField(
          this.identityForm,
          () => this.identityFormElement,
          () => this.setupHeading,
        );
      else if (!technicalValid) {
        this.revealTechnicalView('identity');
        this.focusInvalidFormField(
          this.identityTechnicalForm,
          () => this.identityTechnicalFormElement,
          () => this.setupHeading,
        );
      } else {
        if (this.identityAvailabilityDetails)
          this.identityAvailabilityDetails.nativeElement.open = true;
        this.focusInvalidFormField(
          this.identityAvailabilityForm,
          () => this.identityAvailabilityFormElement,
          () => this.setupHeading,
        );
      }
      return;
    }
    if (!this.isEditorScopeShapeValid()) {
      this.revealWorkflowSettingsTask('general');
      if (this.identityAvailabilityDetails)
        this.identityAvailabilityDetails.nativeElement.open = true;
      this.focusInvalidFormField(
        this.identityAvailabilityForm,
        () => this.identityAvailabilityFormElement,
        () => this.setupHeading,
        'scopeKey',
      );
      return;
    }
    const document = this.workingDocument();
    if (!document) return;

    const submittedDocument = cloneDocument(document);
    const context = this.saveContextKey();
    const recovery = this.storedRecoverySnapshot();
    const requestId = ++this.saveRequestId;
    this.isSaving.set(true);
    this.operationError.set(null);
    this.editorMessage.set(null);
    this.saveIntent ??= createWorkflowDefinitionMutationIntent();
    this.saveSubscription?.unsubscribe();
    this.saveSubscription = this.lifecycleService
      .updateDraft(draft.id, draft.eTag, { document: submittedDocument }, this.saveIntent)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (savedDraft) => {
          if (!this.acceptSaveResponse(requestId, context)) return;
          this.isSaving.set(false);
          this.saveIntent = null;
          if (
            savedDraft.id !== draft.id ||
            savedDraft.draftCode !== draft.draftCode ||
            savedDraft.workflowCode !== draft.workflowCode ||
            savedDraft.document.workflowCode !== submittedDocument.workflowCode ||
            savedDraft.document.definitionVersionCode !== submittedDocument.definitionVersionCode ||
            JSON.stringify(savedDraft.document.packReference ?? null) !== JSON.stringify(submittedDocument.packReference ?? null) ||
            savedDraft.revision <= draft.revision ||
            !savedDraft.eTag || savedDraft.eTag === draft.eTag
          ) {
            this.showOperationError({
              title: 'Draft save changed',
              messages: ['The save response did not identify the requested next draft revision. Your current edits have been retained. Reload the latest revision before saving again.'],
              statusCode: 412,
            });
            this.preserveSaveConflict(draft, this.workingDocument() ?? submittedDocument);
            return;
          }
          const retainPending = this.hasUnappliedEditorChanges();
          if (retainPending && serializeDocument(savedDraft.document) !== serializeDocument(submittedDocument))
            this.captureRecoveryPendingInputs();
          const newerDocument = this.workingDocument();
          const retainDocument = newerDocument !== null &&
            serializeDocument(newerDocument) !== serializeDocument(submittedDocument);
          if (!this.archivedPendingInputs().length)
            this.clearStoredRecovery(recovery);
          this.recoveryDocument.set(null);
          const editingStepId =
            this.activeWorkbenchView() === 'map' &&
            ['human_step', 'automated_step'].includes(
              this.selectedMapNode()?.typeCode ?? '',
            )
              ? this.selectedMapNode()!.id
              : null;
          const editingScope = this.stepWorkspaceScope(),
            editingSection = this.behaviorSection(),
            editingPane = this.mapInspectorPane();
          if (retainDocument || retainPending || this.archivedPendingInputs().length) {
            if (!retainDocument) {
              this.prepareRetainedStepOwner(savedDraft.document);
              this.workingDocument.set(cloneDocument(savedDraft.document));
            }
            this.adoptSavedBaseline(savedDraft);
            // A verified acknowledgement advances edit authority. Reconcile current
            // owner inputs without replacing newer local content with the saved copy.
            const retainedDocument = this.workingDocument();
            if (retainDocument && retainedDocument && this.currentDraft() === savedDraft)
              this.workingDocument.set(cloneDocument(retainedDocument));
            this.recoveryBase.set(null);
            this.recoveryChoices.set({});
            this.hasReloadedLatestAfterConflict.set(false);
            if (this.archivedPendingInputs().length && this.matchesStoredRecovery(recovery))
              this.persistRecovery(savedDraft.document, this.workingDocument()!);
          } else {
            this.applyLoadedDraft(savedDraft);
          }
          this.validationHandoff.set(null);
          if (
            !retainPending && !retainDocument &&
            editingStepId &&
            this.openMapNodeById(editingStepId, false, editingPane)
          ) {
            this.stepWorkspaceScope.set(editingScope);
            this.behaviorSection.set(editingSection);
          }
          this.editorMessage.set(
            retainDocument || retainPending || this.archivedPendingInputs().length
              ? 'Draft saved. Newer local edits or captured pending fields still need review.'
              : 'Draft saved.',
          );
          this.loadDrafts();
        },
        error: (error: unknown) => {
          if (!this.acceptSaveResponse(requestId, context)) return;
          this.isSaving.set(false);
          const displayError = mapWorkflowRegistryDiagnosticError(error);
          this.showOperationError(displayError);
          if (this.handleArchivedDraftFailure(displayError)) return;
          if (displayError.statusCode === 412) {
            this.preserveSaveConflict(draft, this.workingDocument() ?? submittedDocument);
          }
        },
      });
  }

  private saveContextKey(): string {
    const draft = this.currentDraft();
    return JSON.stringify([
      draft?.id, draft?.draftCode, draft?.workflowCode, draft?.revision,
      draft?.eTag, draft?.canonicalContentHash, draft?.lifecycleStatusCode,
      draft?.document, this.baselineDocumentJson,
      this.roles.accessProfile?.()?.actor?.actorId ?? null,
      this.canAuthor(), this.canCreateWorkflowDraft(), this.registryReadsSuspended(),
    ]);
  }

  private acceptSaveResponse(requestId: number, context: string): boolean {
    if (this.destroyRef.destroyed || requestId !== this.saveRequestId) return false;
    if (context === this.saveContextKey()) return true;
    this.isSaving.set(false);
    this.saveIntent = null;
    this.editorMessage.set('The draft or author context changed during Save. Current local work and recovery copies have been retained.');
    return false;
  }

  private preserveSaveConflict(
    draft: WorkflowDefinitionDraftDetail,
    local: WorkflowDefinitionDocument,
  ): void {
    this.recoveryOwnerIdentity = this.recoveryIdentityKey();
    this.captureRecoveryPendingInputs();
    this.recoveryBase.set(cloneDocument(draft.document));
    this.recoveryDocument.set(cloneDocument(local));
    this.recoveryChoices.set({});
    this.recoveryStorageKey = this.storageKeyForDraft(draft);
    this.persistRecovery(draft.document, local);
    this.hasReloadedLatestAfterConflict.set(false);
    this.saveIntent = null;
  }

  private captureRecoveryPendingInputs(): void {
    if (!this.hasUnappliedEditorChanges()) return;
    const captured = this.capturePendingInputs();
    if (captured.length) {
      this.retainCapturedInputs(captured);
      this.archivedBufferPending.set(true);
    }
  }

  private retainCapturedInputs(captured: readonly {
    name: string; label: string; value: string; checked?: boolean;
  }[]): void {
    const retained = this.archivedPendingInputs();
    const identities = new Set(retained.map(field => JSON.stringify(field)));
    this.archivedPendingInputs.set([...retained,
      ...captured.filter(field => !identities.has(JSON.stringify(field)))]);
  }

  /** Detach a vanished owner only after retaining its input; workflow owners stay live. */
  private prepareRetainedStepOwner(document: WorkflowDefinitionDocument): boolean {
    const selection = this.mapSelection();
    const nodeId = this.stepEditorNodeId() ??
      (selection?.kind === 'node' ? selection.sourceId : this.nodeFormDirty() ? this.selectedNodeId() : null);
    if (!nodeId) return false;
    const before = this.workingDocument()?.nodes.filter(node => node.id === nodeId) ?? [];
    const graph = projectWorkflowDraftGraph(document);
    const after = graph.nodes.filter(node => node.sourceId === nodeId);
    const next = after.length === 1 ? after[0] : null;
    if (before.length === 1 && next !== null && next.sourceIndex !== null &&
      document.nodes[next.sourceIndex]?.typeCode === before[0].typeCode) {
      const current = this.graphSelectionForNode(next, graph.nodes);
      if (current.isAuthorable) {
        if (selection?.kind === 'node' && selection.sourceId === nodeId) this.mapSelection.set(current);
        return false;
      }
    }
    this.captureRecoveryPendingInputs();
    const workflowOwners = new Set(['workflow-assignment', 'navigation', 'process-data', 'automation', 'workflow-status']);
    for (const scope of this.editorPendingScopes()) {
      if (!workflowOwners.has(scope.id.split(':')[0]) && scope.state() !== 'clean') scope.discard();
    }
    for (const dirty of [this.nodeFormDirty, this.edgeFormDirty, this.reviewRoutingDirty,
      this.dashboardEditorDirty, this.staffPageEditorDirty, this.workComponentsDirty,
      this.transitionFormDirty, this.decisionFormDirty, this.requirementFormDirty,
      this.timerFormDirty, this.childWorkFormDirty, this.notificationFormDirty,
      this.stepAssignmentPolicyFormDirty, this.automatedStepFormDirty]) dirty.set(false);
    this.inlineStepNameEditing.set(false);
    this.behaviorUndoHistory.set([]);
    this.clearFocusedMapContext();
    this.stepEditorOpened.set(false);
    this.mapSelection.set(null);
    return true;
  }

  /** Advances the saved baseline while preserving work received after the request. */
  private adoptSavedBaseline(draft: WorkflowDefinitionDraftDetail): void {
    this.cancelValidation();
    this.currentDraft.set(draft);
    this.baselineDocumentJson = serializeDocument(draft.document);
    this.documentDirty.set(serializeDocument(this.workingDocument()!) !== this.baselineDocumentJson);
    this.workbenchStateChange.emit({ draftCode: draft.draftCode,
      workflowCode: draft.workflowCode, revision: draft.revision });
    const submitted = serializeDocument(draft.document);
    this.behaviorUndoHistory.update(history => {
      const first = history.findIndex(entry => serializeDocument(entry.before) === submitted);
      return first < 0 ? [] : history.slice(first);
    });
    this.automationUndoHistory.update(history => {
      const first = history.findIndex(entry => serializeDocument(entry.before) === submitted);
      return first < 0 ? [] : history.slice(first);
    });
  }

  validateDraft(automatic = false): void {
    const draft = this.currentDraft();
    if (
      !draft ||
      this.isArchivedDraft() ||
      !this.canValidate() ||
      this.hasUnsavedChanges() ||
      this.isDraftEditorInteractionBlocked()
    ) {
      return;
    }

    this.isValidating.set(true);
    this.automaticValidationKey = `${draft.id}:${draft.revision}:${draft.eTag}`;
    this.automaticValidationFailed.set(false);
    this.operationError.set(null);
    this.editorMessage.set(null);
    this.validationIntent ??= createWorkflowDefinitionMutationIntent();
    this.validateSubscription?.unsubscribe();
    const requestId = ++this.validationRequestId;
    this.validateSubscription = this.lifecycleService
      .validateDraft(draft.id, draft.eTag, this.validationIntent)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          const current = this.currentDraft();
          if (
            requestId !== this.validationRequestId ||
            current?.id !== draft.id ||
            current.revision !== draft.revision ||
            current.eTag !== draft.eTag
          ) {
            return;
          }
          this.isValidating.set(false);
          this.validationIntent = null;
          if (!this.canValidate()) {
            this.automaticValidationFailed.set(true);
            this.showOperationError(
              localValidationError(
                'Checks permission changed',
                'Your validation permission changed while checks were running. An authorized workflow validator must check this saved revision.',
              ),
            );
            return;
          }
          if (
            result.draftId !== draft.id ||
            result.revision !== draft.revision ||
            result.eTag !== draft.eTag
          ) {
            this.automaticValidationFailed.set(true);
            this.showOperationError({
              title: 'Draft validation changed',
              messages: [
                'The saved draft changed while validation was running. Reload the draft before validating again.',
              ],
              statusCode: 412,
            });
            return;
          }
          this.currentDraft.set({
            ...draft,
            revision: result.revision,
            eTag: result.eTag,
            validationStatusCode: result.validationStatusCode,
            compilerVersion: result.compilerVersion,
            canonicalContentHash: result.canonicalContentHash,
            runtimeBindingCode: result.runtimeBindingCode,
            isExecutable: result.isExecutable,
            validationDiagnostics: result.diagnostics,
          });
          this.workbenchStateChange.emit({
            draftCode: draft.draftCode,
            workflowCode: draft.workflowCode,
            revision: result.revision,
          });
          this.editorMessage.set(
            result.validationStatusCode === 'valid'
              ? 'Validation passed.'
              : 'Validation completed with diagnostics.',
          );
          if (
            result.validationStatusCode === 'invalid' &&
            (!automatic || this.activeWorkbenchView() === 'review')
          ) {
            this.selectWorkbenchView('review');
            this.selectReviewView('readiness');
          }
          this.loadDrafts();
        },
        error: (error: unknown) => {
          if (
            requestId !== this.validationRequestId ||
            this.currentDraft()?.id !== draft.id ||
            this.currentDraft()?.eTag !== draft.eTag
          ) {
            return;
          }
          this.isValidating.set(false);
          this.automaticValidationFailed.set(true);
          const displayError = mapWorkflowRegistryDiagnosticError(error);
          this.showOperationError(displayError);
          if (this.handleArchivedDraftFailure(displayError)) return;
          if (displayError.statusCode === 412) {
            this.validationIntent = null;
          }
        },
      });
  }

  private validateAutomatically(): void {
    const draft = this.currentDraft();
    if (
      !draft ||
      this.isDraftEditorInteractionBlocked() ||
      !this.canValidate() ||
      this.hasOpenDraftUnsavedChanges()
    )
      return;
    const key = `${draft.id}:${draft.revision}:${draft.eTag}`;
    if (this.automaticValidationKey === key) return;
    this.automaticValidationKey = key;
    this.validateDraft(true);
  }

  retryAutomaticValidation(): void {
    if (!this.automaticValidationFailed()) return;
    this.validateDraft();
  }

  onPublicationDraftValidated(
    result: WorkflowDefinitionDraftValidationResult,
  ): void {
    const draft = this.currentDraft();
    if (
      !draft ||
      draft.id !== result.draftId ||
      draft.revision !== result.revision ||
      draft.eTag !== result.eTag ||
      !this.canValidate()
    )
      return;
    this.automaticValidationKey = `${draft.id}:${draft.revision}:${draft.eTag}`;
    this.automaticValidationFailed.set(false);
    this.currentDraft.set({
      ...draft,
      validationStatusCode: result.validationStatusCode,
      validationDiagnostics: result.diagnostics,
      compilerVersion: result.compilerVersion,
      canonicalContentHash: result.canonicalContentHash,
      runtimeBindingCode: result.runtimeBindingCode,
      isExecutable: result.isExecutable,
    });
    if (result.validationStatusCode !== 'valid')
      this.selectReviewView('readiness');
  }

  onPublicationChecksFailed(
    error: WorkflowRegistryDiagnosticDisplayError,
  ): void {
    const draft = this.currentDraft();
    if (!draft) return;
    this.automaticValidationKey = `${draft.id}:${draft.revision}:${draft.eTag}`;
    this.automaticValidationFailed.set(true);
    this.showOperationError(error);
    this.handleArchivedDraftFailure(error);
  }

  reloadLatestAfterConflict(): void {
    const draft = this.currentDraft();
    if (!draft || !this.isRecoveryOwnerCurrent() || this.isDraftMutationInProgress() || this.isLoadingDraft()) {
      return;
    }
    if (!this.recoveryDocument() && this.workingDocument() &&
      (this.documentDirty() || this.hasUnappliedEditorChanges()))
      this.preserveSaveConflict(draft, this.workingDocument()!);
    if (this.recoveryDocument()) {
      this.captureRecoveryPendingInputs();
      this.persistRecovery(this.recoveryBase() ?? draft.document, this.recoveryDocument()!);
    }
    this.loadDraft(draft.id, false, this.recoveryDocument() !== null);
  }

  trackRecoveryConflict(_index: number, conflict: { path: string }): string {
    return conflict.path;
  }

  chooseRecoveryEdit(path: string, choice: DraftRecoveryChoice): void {
    if (
      !this.isRecoveryOwnerCurrent() ||
      this.areRecoveryActionsBlocked() ||
      !this.canAuthor() ||
      !this.recoveryComparison()?.conflicts.some(
        (conflict) => conflict.path === path,
      )
    )
      return;
    this.recoveryChoices.update((choices) => ({ ...choices, [path]: choice }));
    this.persistRecovery(this.recoveryBase()!, this.recoveryDocument()!);
  }

  recoveryValue(value: unknown, present: boolean): string {
    if (!present) return 'Removed / not present';
    if (value === null) return 'None';
    if (typeof value === 'string') return value || '(empty)';
    return JSON.stringify(value, null, 2);
  }

  restoreRecoveryDocument(): void {
    const document = this.recoveryComparison()?.document;
    if (!document || !this.isRecoveryOwnerCurrent() || !this.canAuthor() || this.areRecoveryActionsBlocked())
      return;
    // The server document remains the baseline and supplies the next ETag. The
    // recovery copy stays in this tab until this combined document is saved.
    const retainPending = this.hasUnappliedEditorChanges();
    this.captureRecoveryPendingInputs();
    const detachedOwner = this.prepareRetainedStepOwner(document);
    this.recoveryDocument.set(null);
    this.recoveryBase.set(null);
    this.recoveryChoices.set({});
    this.hasReloadedLatestAfterConflict.set(false);
    this.setWorkingDocument(document);
    this.mapDocumentToForms(document, retainPending);
    if (!this.documentDirty() && !this.archivedPendingInputs().length)
      this.clearStoredRecovery();
    this.editorMessage.set(
      this.documentDirty()
        ? 'Combined edits are ready to review. Save draft to keep them.'
        : 'Your choices match the latest saved draft. No further save is needed.',
    );
    if (this.hasUnappliedEditorChanges()) this.focusPendingMapEditor();
    else if (detachedOwner) this.focusAfterRender(() => this.draftEditorHeading);
  }

  discardRecoveryDocument(): void {
    if (!this.isRecoveryOwnerCurrent() || this.areRecoveryActionsBlocked()) return;
    if (!this.archivedPendingInputs().length) this.clearStoredRecovery();
    this.recoveryDocument.set(null);
    this.recoveryBase.set(null);
    this.recoveryChoices.set({});
    this.hasReloadedLatestAfterConflict.set(false);
    this.editorMessage.set(
      'Recovery copy discarded. The latest saved draft is unchanged.',
    );
    if (this.archivedPendingInputs().length && this.currentDraft() && this.workingDocument())
      this.persistRecovery(this.currentDraft()!.document, this.workingDocument()!);
  }

  discardCapturedRecoveryInputs(): void {
    if (!this.isRecoveryOwnerCurrent() || !this.archivedPendingInputs().length || this.isDraftMutationInProgress() ||
      this.isLoadingDraft() || this.confirmationInProgress || this.ownerViewState.focusSuspended()) return;
    const context = this.discardContextKey('draft');
    void this.confirmDraftChange({
      heading: 'Discard captured pending fields?',
      message: 'The captured field values in the recovery download will be discarded. Current live editor input and applied draft edits will remain.',
      confirmText: 'Discard captured fields', cancelText: 'Keep captured fields',
    }).then(confirmed => {
      if (this.destroyRef.destroyed || !confirmed) return;
      if (context !== this.discardContextKey('draft')) {
        this.reportStaleDiscard();
        return;
      }
      this.archivedPendingInputs.set([]);
      this.archivedBufferPending.set(false);
      const draft = this.currentDraft(), local = this.recoveryDocument() ?? this.workingDocument();
      if (draft && local && (this.recoveryDocument() || this.documentDirty()))
        this.persistRecovery(this.recoveryBase() ?? draft.document, local);
      else this.clearStoredRecovery();
      this.editorMessage.set('Captured recovery fields discarded. Current editor input and draft edits remain.');
      if (this.hasUnappliedEditorChanges() && !this.isDraftEditorInteractionBlocked()) this.focusPendingMapEditor();
      else this.focusAfterRender(() => this.draftEditorHeading);
    });
  }

  downloadRecoveryCopy(): void {
    if (!this.isRecoveryOwnerCurrent()) return;
    const draft = this.currentDraft(),
      local = this.recoveryDocument() ?? this.workingDocument();
    if (!draft || !local) return;
    const blob = new Blob(
      [
        JSON.stringify(
          {
            draftCode: draft.draftCode,
            workflowCode: draft.workflowCode,
            base: this.recoveryBase() ?? draft.document,
            local,
            choices: this.recoveryChoices(),
            pendingInputs: this.archivedPendingInputs().length
              ? this.archivedPendingInputs()
              : this.capturePendingInputs(),
          },
          null,
          2,
        ),
      ],
      { type: 'application/json' },
    );
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `workflow-draft-${draft.id}-recovery.json`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  private storageKeyForDraft(
    draft: WorkflowDefinitionDraftDetail,
  ): string | null {
    const actor = this.roles.accessProfile?.()?.actor;
    return actor
      ? `phms.workflow-recovery.v1:${encodeURIComponent(actor.actorId)}:${draft.id}:${encodeURIComponent(draft.draftCode)}`
      : null;
  }

  private recoveryIdentityKey(): string {
    const draft = this.currentDraft();
    return JSON.stringify([this.roles.accessProfile?.()?.actor?.actorId ?? null,
      draft?.id, draft?.draftCode, draft?.workflowCode]);
  }

  private isRecoveryOwnerCurrent(): boolean {
    return this.recoveryOwnerIdentity === null || this.recoveryOwnerIdentity === this.recoveryIdentityKey();
  }

  private persistRecovery(
    base: WorkflowDefinitionDocument,
    local: WorkflowDefinitionDocument,
  ): void {
    try {
      if (!this.recoveryStorageKey)
        throw new Error('No authenticated recovery identity');
      const key = this.recoveryStorageKey;
      if (key !== (this.currentDraft() ? this.storageKeyForDraft(this.currentDraft()!) : null))
        throw new Error('Recovery author context changed');
      const expected = this.recoveryStorageOwnedValue?.key === key
        ? this.recoveryStorageOwnedValue.value : null;
      if (sessionStorage.getItem(key) !== expected) {
        this.recoveryStorageKey = null;
        this.recoveryStorageWarning.set('A newer recovery copy is already stored in this tab. It was retained. Download the current local copy before leaving.');
        return;
      }
      const value = JSON.stringify({
          schemaVersion: 1,
          base,
          local,
          choices: this.recoveryChoices(),
          pendingInputs: this.archivedPendingInputs(),
        });
      sessionStorage.setItem(key, value);
      this.recoveryStorageOwnedValue = { key, value };
      this.recoveryStorageWarning.set(null);
    } catch {
      this.recoveryStorageWarning.set(
        'This browser could not retain the recovery copy through a refresh. Download a copy before leaving this tab.',
      );
    }
  }

  private restoreStoredRecovery(draft: WorkflowDefinitionDraftDetail): void {
    this.recoveryStorageKey = null;
    this.recoveryOwnerIdentity = null;
    this.recoveryStorageOwnedValue = null;
    this.recoveryStorageWarning.set(null);
    if (!this.canCreateWorkflowDraft()) return;
    const key = this.storageKeyForDraft(draft);
    if (!key) return;
    try {
      const text = sessionStorage.getItem(key);
      if (!text) {
        this.recoveryStorageOwnedValue = { key, value: null };
        return;
      }
      const snapshot = JSON.parse(text) as {
        schemaVersion?: number;
        base?: WorkflowDefinitionDocument;
        local?: WorkflowDefinitionDocument;
        choices?: Record<string, DraftRecoveryChoice>;
        pendingInputs?: {
          name: string;
          label: string;
          value: string;
          checked?: boolean;
        }[];
      };
      if (
        snapshot.schemaVersion !== 1 ||
        !isRecoverableWorkflowDocument(snapshot.base, draft.workflowCode) ||
        !isRecoverableWorkflowDocument(snapshot.local, draft.workflowCode) ||
        (snapshot.pendingInputs !== undefined &&
          (!Array.isArray(snapshot.pendingInputs) || !snapshot.pendingInputs.every(field =>
            field !== null && typeof field === 'object' && !Array.isArray(field) &&
            typeof field.name === 'string' && typeof field.label === 'string' &&
            typeof field.value === 'string' &&
            (field.checked === undefined || typeof field.checked === 'boolean'))))
      ) {
        this.recoveryStorageWarning.set(
          'The saved recovery copy could not be read. The server draft has not been changed.',
        );
        return;
      }
      this.recoveryStorageOwnedValue = { key, value: text };
      this.recoveryStorageKey = key;
      this.recoveryOwnerIdentity = this.recoveryIdentityKey();
      this.archivedPendingInputs.set(snapshot.pendingInputs ?? []);
      this.archivedBufferPending.set(this.archivedPendingInputs().length > 0);
      if (this.archivedPendingInputs().length &&
        serializeDocument(snapshot.local) === serializeDocument(draft.document) &&
        serializeDocument(snapshot.base) === serializeDocument(draft.document)) {
        this.recoveryBase.set(null);
        this.recoveryDocument.set(null);
        this.recoveryChoices.set({});
        this.hasReloadedLatestAfterConflict.set(false);
        return;
      }
      this.recoveryBase.set(snapshot.base);
      this.recoveryDocument.set(snapshot.local);
      // Choices applied to an earlier latest revision must be reviewed again.
      this.recoveryChoices.set({});
      this.hasReloadedLatestAfterConflict.set(true);
    } catch {
      this.recoveryStorageWarning.set(
        'The saved recovery copy could not be read. The server draft has not been changed.',
      );
    }
  }

  private storedRecoverySnapshot(): { key: string | null; value: string | null | undefined } {
    const key = this.recoveryStorageKey;
    try { return { key, value: key ? sessionStorage.getItem(key) : null }; }
    catch { return { key, value: undefined }; }
  }

  private matchesStoredRecovery(expected: { key: string | null; value: string | null | undefined }): boolean {
    const current = this.storedRecoverySnapshot();
    return expected.value !== undefined && current.key === expected.key && current.value === expected.value;
  }

  private clearStoredRecovery(expected?: { key: string | null; value: string | null | undefined }): void {
    if (expected) {
      if (!this.matchesStoredRecovery(expected)) return;
    }
    if (this.recoveryStorageKey) {
      try {
        if (this.recoveryStorageKey !== (this.currentDraft() ? this.storageKeyForDraft(this.currentDraft()!) : null))
          return;
        if (this.recoveryStorageOwnedValue?.key === this.recoveryStorageKey &&
          sessionStorage.getItem(this.recoveryStorageKey) !== this.recoveryStorageOwnedValue.value) {
          this.recoveryStorageKey = null;
          this.recoveryStorageWarning.set('A newer recovery copy is already stored in this tab. It was retained. Download the current local copy before leaving.');
          return;
        }
        sessionStorage.removeItem(this.recoveryStorageKey);
        if (this.recoveryStorageOwnedValue?.key === this.recoveryStorageKey)
          this.recoveryStorageOwnedValue = { key: this.recoveryStorageKey, value: null };
      } catch {
        this.recoveryStorageWarning.set(
          'The browser could not remove its recovery copy. It may appear again on refresh.',
        );
      }
    }
    this.recoveryStorageKey = null;
  }

  focusDiagnostic(locationTypeCode: string, locationId: string | null): void {
    if (locationTypeCode === 'node' && locationId) {
      const node = this.workingDocument()?.nodes.find(
        (candidate) => candidate.id === locationId,
      );
      if (node) {
        this.selectNode(node);
      }
    }
  }

  canFocusDiagnostic(diagnostic: WorkflowDefinitionDiagnostic): boolean {
    return (
      this.currentDraft() !== null &&
      this.canResolveValidationHandoff(diagnostic)
    );
  }

  private assignmentPolicyStep(
    pathOrId: string | null | undefined,
  ): string | null {
    const id =
      pathOrId?.match(/assignmentPolicies\[id=([^\]]+)\]/)?.[1] ?? pathOrId;
    if (!id) return null;
    const policies = (this.workingDocument()?.assignmentPolicies ?? []).filter(
      (policy) => policy?.id === id,
    );
    const policy = policies.length === 1 ? policies[0] : null;
    if (policy?.scopeCode !== 'step' || !policy.nodeId) return null;
    if (
      (this.workingDocument()?.assignmentPolicies ?? []).filter(
        (candidate) => candidate?.nodeId === policy.nodeId,
      ).length !== 1
    )
      return null;
    const nodes =
      this.workingDocument()?.nodes.filter(
        (node) => node.id === policy.nodeId && node.typeCode === 'human_step',
      ) ?? [];
    return nodes.length === 1 ? policy.nodeId : null;
  }

  private openAssignmentPolicy(pathOrId: string | null | undefined): void {
    const nodeId = this.assignmentPolicyStep(pathOrId);
    if (nodeId && this.openMapNodeById(nodeId, true, 'overview')) {
      this.requestResponsibilityFocus();
      setTimeout(() =>
        this.stepAssignmentPolicyEditor?.focusPolicyPath(pathOrId),
      );
    } else {
      this.revealTechnicalView('assignment');
      setTimeout(() => {
        this.assignmentPolicyEditor?.focusPolicyPath(pathOrId);
      });
    }
  }

  private stepBehaviorPathTargetsWork(path: string): boolean {
    return (path.includes('.declaration.behavior.commands') && !path.includes('outcomes')) ||
      path.includes('.declaration.behavior.completionRequirements') ||
      path.includes('.declaration.behavior.assignmentPolicyId') ||
      path.includes('.declaration.behavior.completionPolicy');
  }

  private stepDiagnosticTargetsRouting(diagnostic: WorkflowDefinitionDiagnostic): boolean {
    return [
      'workflow.definition.preparation.handoff_unavailable',
      'workflow.definition.review.binding_unavailable',
      'workflow.definition.staff.review_handoff_required',
      'workflow.definition.staff.review_handoff_form_incompatible',
    ].includes(diagnostic.code) ||
      diagnostic.path.includes('outcomes') ||
      !this.stepBehaviorPathTargetsWork(diagnostic.path);
  }

  private stepDiagnosticScope(
    diagnostic: WorkflowDefinitionDiagnostic,
  ): 'detail' | 'dashboard' | 'behavior' | null {
    if (
      [
        'workflow.definition.preparation.handoff_unavailable',
        'workflow.definition.review.binding_unavailable',
        'workflow.definition.staff.review_handoff_required',
        'workflow.definition.staff.review_handoff_form_incompatible',
      ].includes(diagnostic.code)
    )
      return 'behavior';
    if (diagnostic.path.includes('.declaration.detail')) return 'detail';
    if (diagnostic.path.includes('.declaration.dashboards')) return 'dashboard';
    if (diagnostic.path.includes('.declaration.behavior.assignmentPolicyId'))
      return 'behavior';
    if (
      diagnostic.path.includes('.declaration.behavior.commands') &&
      !diagnostic.path.includes('outcomes')
    )
      return 'behavior';
    if (diagnostic.path.includes('.declaration.behavior')) return 'behavior';
    return null;
  }

  readonly diagnosticBlockFocus = computed(() => {
    const handoff = this.validationHandoff();
    const id = handoff?.diagnostic.path.match(
      /\.detail\.blocks\[id=([^\]]+)\]/,
    )?.[1];
    return id ? { id } : null;
  });

  diagnosticFocusLabel(diagnostic: WorkflowDefinitionDiagnostic): string {
    if (diagnostic.code === 'hearing_eligibility.workflow_activity_subject_invalid')
      return 'Configure hearing lifecycle';
    if (this.isChildWorkDiagnostic(diagnostic)) return diagnostic.path.includes('.terminalOutcome') ? 'Open workflow result' : 'Open related work';
    if (this.isStepTimerDiagnostic(diagnostic)) return 'Open step timers';
    if (diagnostic.path.startsWith('$.processData'))
      return 'Open starting information';
    if (diagnostic.code === 'workflow.definition.staff.assignment_required')
      return 'Choose responsible roles';
    if (diagnostic.path.includes('stepProgressConfiguration'))
      return 'Open step progress';
    if (diagnostic.path.includes('statusConfiguration'))
      return 'Open custom statuses';
    if (this.isPublicationSettingsDiagnostic(diagnostic))
      return 'Review publication readiness';
    if (
      diagnostic.locationId &&
      diagnostic.path.includes('.notificationMessage')
    )
      return 'Open notification message';
    const scope = this.stepDiagnosticScope(diagnostic);
    if (scope)
      return scope === 'detail'
        ? 'Open Staff page'
        : scope === 'dashboard'
          ? 'Open dashboard settings'
          : this.stepDiagnosticTargetsRouting(diagnostic)
            ? 'Open Routing'
            : 'Open Work';
    if (this.isAssignmentPolicyDiagnostic(diagnostic)) {
      return this.assignmentPolicyStep(diagnostic.locationId || diagnostic.path)
        ? 'Open work assignment'
        : 'Inspect assignment policies in Setup';
    }
    if (this.isDecisionDiagnostic(diagnostic)) {
      return 'Inspect decision rules on the map';
    }
    if (
      diagnostic.path.includes('workflowTransitions') ||
      diagnostic.path.includes('allowedTransitions')
    ) {
      return 'Inspect status changes on the map';
    }
    if (
      diagnostic.locationTypeCode === 'edge' ||
      diagnostic.path.startsWith('$.edges')
    ) {
      return 'Inspect path on the map';
    }
    if (
      diagnostic.locationTypeCode === 'node' ||
      diagnostic.path.startsWith('$.nodes')
    ) {
      return 'Inspect step on the map';
    }
    return 'Open workflow settings';
  }

  diagnosticCorrectionHint(diagnostic: WorkflowDefinitionDiagnostic): string {
    if (diagnostic.code === 'hearing_eligibility.workflow_activity_subject_invalid')
      return 'In Workflow settings → Hearing lifecycle, choose Use Hearing as workflow subject. Save and validate again. Hearing enrollment creates the instance’s primary lifecycle link; adding a Hearing reference does not create that link.';
    if (
      diagnostic.path === '$.configurationCode' ||
      diagnostic.path === '$.configurationVersionCode'
    ) {
      return "You can keep editing and saving. Ask your platform administrator to restore or check this version's saved process settings before publication.";
    }
    if (diagnostic.path === '$.environment') {
      return diagnostic.code.endsWith('.changed')
        ? 'The platform settings changed after this revision was checked. Validate the saved revision again before publishing. Your draft content is unchanged.'
        : 'You can keep editing and saving. Ask your platform administrator to check the default settings for this process, then validate again before publishing.';
    }
    if (
      diagnostic.code === 'workflow.definition.preparation.handoff_unavailable'
    )
      return 'Choose a receiving review step below. If that step still needs its review actions, open its setup. Use Open Paths to correct conditional or return paths.';
    if (diagnostic.code === 'workflow.definition.review.binding_unavailable')
      return 'Choose the approval and changes-required destinations below, then apply the review routes.';
    if (diagnostic.code.endsWith('.view_unavailable')) {
      const viewId = diagnostic.path.match(/\.dashboards\[id=([^\]]+)\]/)?.[1];
      const view = this.selectedMapNode()?.declaration?.dashboards?.find(
        (v) => v.id === viewId,
      );
      if (view && !view.label.trim())
        return 'Enter a name in Dashboard label below.';
      return 'Check the dashboard label and its saved view contract. An unavailable view provider must be restored before publication.';
    }
    if (diagnostic.code.endsWith('.block_settings_invalid'))
      return "Correct the selected block's settings in the properties panel below.";
    if (diagnostic.code.endsWith('.block_invalid'))
      return "Check the selected block's region, width and installed version. Unavailable blocks remain preserved until their provider is restored or you remove them.";
    if (diagnostic.code.endsWith('.columns_invalid'))
      return 'Select at least one supported dashboard column.';
    return diagnostic.message;
  }

  diagnosticAreaLabel(diagnostic: WorkflowDefinitionDiagnostic): string {
    if (this.isChildWorkDiagnostic(diagnostic)) return diagnostic.path.includes('.terminalOutcome') ? 'Workflow result' : 'Related work';
    if (this.isStepTimerDiagnostic(diagnostic)) return 'Timers & escalation';
    if (diagnostic.path.startsWith('$.processData'))
      return 'Starting information';
    if (/^\$\.navigation(?:\.|$)/.test(diagnostic.path))
      return 'Workflow navigation';
    if (diagnostic.path.includes('stepProgressConfiguration'))
      return 'Step progress';
    if (diagnostic.path.includes('statusConfiguration')) return 'Statuses';
    if (this.isPublicationSettingsDiagnostic(diagnostic))
      return 'Publication readiness';
    if (
      diagnostic.locationId &&
      diagnostic.path.includes('.notificationMessage')
    )
      return 'Notification message';
    const scope = this.stepDiagnosticScope(diagnostic);
    if (scope)
      return scope === 'detail'
        ? 'Staff page'
        : scope === 'dashboard'
          ? 'Dashboard'
          : this.stepDiagnosticTargetsRouting(diagnostic) ? 'Routing' : 'Work';
    if (this.isAssignmentPolicyDiagnostic(diagnostic)) {
      return 'Assignment policies';
    }
    if (this.isDecisionDiagnostic(diagnostic)) {
      return 'Decision rules';
    }
    if (
      diagnostic.path.includes('workflowTransitions') ||
      diagnostic.path.includes('allowedTransitions')
    ) {
      return 'Status changes';
    }
    if (
      diagnostic.locationTypeCode === 'edge' ||
      diagnostic.path.startsWith('$.edges')
    ) {
      return 'Paths';
    }
    if (
      diagnostic.locationTypeCode === 'node' ||
      diagnostic.path.startsWith('$.nodes')
    ) {
      return 'Work steps';
    }
    return diagnostic.path.startsWith('$.triggers')
      ? 'Automated operations'
      : 'Workflow settings';
  }

  private isPublicationSettingsDiagnostic(
    diagnostic: WorkflowDefinitionDiagnostic,
  ): boolean {
    return [
      '$.environment',
      '$.configurationCode',
      '$.configurationVersionCode',
    ].includes(diagnostic.path);
  }

  diagnosticLocationLabel(diagnostic: WorkflowDefinitionDiagnostic): string {
    if (this.isChildWorkDiagnostic(diagnostic)) return this.childWorkDiagnosticTarget(diagnostic)?.label ?? 'Related work or workflow result';
    if (this.isStepTimerDiagnostic(diagnostic))
      return this.stepTimerDiagnosticTarget(diagnostic)?.label ?? 'Timers & escalation';
    const savedDocument = this.currentDraft()?.document;
    if (diagnostic.locationTypeCode === 'edge' && diagnostic.locationId) {
      const edge = savedDocument?.edges.find(
        (candidate) => candidate?.id === diagnostic.locationId,
      );
      if (edge) {
        return `${this.nodeDisplayLabel(edge.fromNodeId, savedDocument)} → ${this.nodeDisplayLabel(edge.toNodeId, savedDocument)}`;
      }
    }

    if (diagnostic.locationId) {
      const node = savedDocument?.nodes.find(
        (candidate) => candidate.id === diagnostic.locationId,
      );
      if (node) {
        return node.displayName || node.stepCode || node.id;
      }
    }

    return this.diagnosticAreaLabel(diagnostic);
  }

  focusValidationDiagnostic(
    diagnostic: WorkflowDefinitionDiagnostic,
    findingId?: string,
  ): void {
    if (
      !this.currentDraft() ||
      this.isDraftEditorInteractionBlocked() ||
      !this.canResolveValidationHandoff(diagnostic)
    ) {
      return;
    }
    const sourceDraft = this.currentDraft()!;
    this.requestMapEditorDeactivation(
      () => {
        const current = this.currentDraft();
        if (
          current?.id !== sourceDraft.id ||
          current.revision !== sourceDraft.revision ||
          current.eTag !== sourceDraft.eTag ||
          !this.canResolveValidationHandoff(diagnostic)
        )
          return;
        this.focusValidationDiagnosticAfterDeactivation(diagnostic);
        this.semanticReviewHandoff.set(null);
        const finding =
          this.reviewFindings().find(
            (item) => item.id === findingId && item.diagnostic === diagnostic,
          ) ??
          this.reviewFindings().find((item) => item.diagnostic === diagnostic);
        this.validationHandoff.set({
          diagnostic,
          revision: this.currentDraft()!.revision,
          findingId: finding?.id,
        });
        this.writeWorkbenchViewToUrl(this.activeWorkbenchView());
      },
      () => undefined,
    );
  }

  private focusValidationDiagnosticAfterDeactivation(
    diagnostic: WorkflowDefinitionDiagnostic,
  ): void {
    this.clearFocusedMapContext();
    if (diagnostic.code === 'hearing_eligibility.workflow_activity_subject_invalid') {
      this.revealWorkflowSettingsTask('hearing');
      this.focusAfterRender(() => this.hearingLifecycleHeading);
      return;
    }
    if (this.isChildWorkDiagnostic(diagnostic)) {
      const target = this.childWorkDiagnosticTarget(diagnostic);
      if (target && this.openMapNodeById(target.nodeId, true)) {
        this.stepWorkspaceScope.set('behavior');
        this.behaviorSection.set(target.terminal ? 'details' : 'children');
        setTimeout(() => this.childWorkEditor?.focusPendingChanges());
      }
      return;
    }
    if (this.isStepTimerDiagnostic(diagnostic)) {
      const target = this.stepTimerDiagnosticTarget(diagnostic);
      if (target && this.openMapNodeById(target.nodeId, true)) {
        this.stepWorkspaceScope.set('behavior');
        this.behaviorSection.set('timers');
        this.focusTimerPendingChanges(undefined, diagnostic.path);
      }
      return;
    }
    if (
      diagnostic.path.includes('statusConfiguration') ||
      diagnostic.path.includes('stepProgressConfiguration')
    ) {
      this.openStatusConfiguration(
        diagnostic.path,
        diagnostic.locationId,
        'validation',
      );
      return;
    }
    if (this.isPublicationSettingsDiagnostic(diagnostic)) {
      this.activeWorkbenchView.set('review');
      this.activeReviewView.set('readiness');
      this.focusAfterRender(() => this.reviewFindingsHeading);
      return;
    }
    if (
      diagnostic.path.includes('.notificationMessage') &&
      diagnostic.locationId &&
      this.openMapNodeById(diagnostic.locationId, false, 'overview')
    ) {
      this.stepWorkspaceScope.set('behavior');
      this.behaviorSection.set('notifications');
      this.notificationMessageFocus.set({
        path: diagnostic.path,
        nodeId: diagnostic.locationId,
        savedDocument: this.currentDraft()?.document,
      });
      return;
    }
    const scope = this.stepDiagnosticScope(diagnostic);
    if (
      scope &&
      diagnostic.locationId &&
      this.openMapNodeById(diagnostic.locationId, true)
    ) {
      this.stepWorkspaceScope.set(scope);
      this.behaviorFormFocus.set(null);
      this.stepActionFocus.set(null);
      this.behaviorSection.set(
        scope === 'behavior' && this.stepDiagnosticTargetsRouting(diagnostic)
          ? 'routes' : 'readiness',
      );
      if (scope === 'behavior' && !this.stepDiagnosticTargetsRouting(diagnostic))
        this.revealWorkView(diagnostic.path.includes('.completionRequirements') ? 'requirements' : 'components');
      if (
        diagnostic.path.includes('assignmentPolicyId') ||
        (scope === 'dashboard' &&
          /queueScope|audienceRoleCodes/.test(diagnostic.path))
      ) {
        this.requestResponsibilityFocus(
          /\.dashboards\[id=([^\]]+)\]/.exec(diagnostic.path)?.[1],
        );
      } else if (scope === 'behavior' || scope === 'detail') {
        const commands =
          this.selectedMapNode()?.declaration?.behavior.commands ?? [];
        const index = /commands\[(\d+)\]/.exec(diagnostic.path);
        const code = /commands\[code=([^\]]+)\]/.exec(diagnostic.path)?.[1];
        const matches = code
          ? commands.filter((command) => command.capability.key.code === code)
          : [];
        const savedCommand = index
          ? this.currentDraft()?.document.nodes.find(
              (node) => node.id === diagnostic.locationId,
            )?.declaration?.behavior.commands[Number(index[1])]
          : null;
        const currentMatches = savedCommand
          ? commands.filter((command) =>
              sameCapabilityReference(
                command.capability,
                savedCommand.capability,
              ),
            )
          : [];
        const command = index
          ? currentMatches.length === 1
            ? currentMatches[0]
            : null
          : matches.length === 1
            ? matches[0]
            : null;
        if (command) {
          if (
            /captureForm|formConfiguration/.test(diagnostic.path)
          ) {
            this.stepActionFocus.set(null);
            this.behaviorFormFocus.set({
              capability: command.capability,
              request: (this.behaviorFormFocus()?.request ?? 0) + 1,
            });
          } else {
            this.behaviorFormFocus.set(null);
            this.stepActionFocus.set({
              capability: command.capability,
              request: (this.stepActionFocus()?.request ?? 0) + 1,
              outcomes: scope === 'behavior' && this.stepDiagnosticTargetsRouting(diagnostic),
            });
          }
        }
      }
      this.validationHandoff.set({
        diagnostic,
        revision: this.currentDraft()!.revision,
      });
      return;
    }
    this.validationHandoff.set(null);
    if (diagnostic.path.startsWith('$.processData')) {
      this.revealProcessDataPath(diagnostic.path);
      this.validationHandoff.set({
        diagnostic,
        revision: this.currentDraft()!.revision,
      });
      return;
    }
    if (this.isAssignmentPolicyDiagnostic(diagnostic)) {
      this.openAssignmentPolicy(diagnostic.locationId || diagnostic.path);
      this.validationHandoff.set({
        diagnostic,
        revision: this.currentDraft()!.revision,
      });
      return;
    }
    if (diagnostic.locationTypeCode === 'edge' && diagnostic.locationId) {
      this.openMapEdgeBySourceId(diagnostic.locationId, true);
      return;
    }
    if (diagnostic.path === '$.edges') {
      this.openRoutingView('paths');
      this.mapSelection.set(null);
      this.edgeEditor?.focusEdgePath();
      return;
    }
    if (this.isDecisionDiagnostic(diagnostic)) {
      this.activeWorkbenchView.set('map');
      this.setInspectorPane('decisions');
      if (diagnostic.locationId) {
        this.openMapNodeById(diagnostic.locationId, false, 'decisions');
      } else {
        this.mapSelection.set(null);
      }
      this.decisionEditor?.focusDecisionPath(
        diagnostic.path,
        diagnostic.locationId,
      );
      return;
    }
    if (
      diagnostic.path.includes('workflowTransitions') ||
      diagnostic.path.includes('allowedTransitions')
    ) {
      this.activeWorkbenchView.set('map');
      this.setInspectorPane('transitions');
      if (diagnostic.locationId) {
        this.openMapNodeById(diagnostic.locationId, false, 'transitions');
      } else {
        this.mapSelection.set(null);
      }
      this.transitionEditor?.focusTransitionPath(
        diagnostic.path,
        diagnostic.locationId,
      );
      return;
    }
    if (
      diagnostic.locationTypeCode === 'node' ||
      diagnostic.path.startsWith('$.nodes')
    ) {
      this.activeWorkbenchView.set('map');
      this.setInspectorPane('overview');
      const node = diagnostic.locationId
        ? this.workingDocument()?.nodes.find(
            (candidate) => candidate.id === diagnostic.locationId,
          )
        : null;
      if (node) {
        this.openMapNodeById(node.id, true);
        this.behaviorSection.set('details');
      } else {
        this.mapSelection.set(null);
        this.focusAfterRender(() => this.mapInspectorHeading);
      }
      return;
    }

    this.focusSetupPath(diagnostic.path);
  }

  private focusSetupPath(path: string): void {
    if (/^\$\.statusConfiguration(?:\.|\[|$)/.test(path)) {
      this.workflowStatusOrigin = null;
      this.revealWorkflowSettingsTask('statuses');
      this.scheduleWorkflowStatusFocus(() => this.workflowStatusEditor?.focusSavedPath(path));
      return;
    }
    if (path.startsWith('$.triggers')) {
      this.openAutomation(path);
      return;
    }
    if (/^\$\.navigation(?:\.|$)/.test(path)) {
      this.revealWorkflowSettingsTask('navigation');
      setTimeout(() => this.navigationSettingsEditor?.focusPendingChanges());
      return;
    }
    if (/^\$\.processData(?:\.|$)/.test(path)) {
      this.revealProcessDataPath(path);
      return;
    }
    const technical =
      /^\$\.(workflowCode|definitionVersionCode|payloadSchemaVersion)(?:\.|$)/.test(
        path,
      );
    const numbering = /^\$\.numbering(?:\.|$)/.test(path);
    const availability = /^\$\.(scopeTypeCode|scopeKey)$/.test(path);
    this.revealWorkflowSettingsTask(technical ? 'technical' : numbering ? 'numbering' : 'general');
    if (numbering) {
      setTimeout(() => this.numberingSettingsEditor?.focusConfiguration(path));
      return;
    }
    if (technical) {
      this.revealTechnicalView('identity');
      if (path === '$.definitionVersionCode' && this.canAuthor())
        this.focusInvalidFormField(this.identityTechnicalForm, () => this.identityTechnicalFormElement,
          () => this.technicalHeading, 'definitionVersionCode');
      else this.focusAfterRender(() => this.technicalHeading);
      return;
    }
    const details = availability ? this.identityAvailabilityDetails : undefined;
    if (details) {
      details.nativeElement.open = true;
      if (availability && this.canAuthor()) {
        this.focusInvalidFormField(
          this.identityAvailabilityForm,
          () => this.identityAvailabilityFormElement,
          () => this.setupHeading,
          path.slice(2),
        );
      } else
        setTimeout(() =>
          details.nativeElement.querySelector<HTMLElement>('summary')?.focus(),
        );
      return;
    }
    const field = /^\$\.(displayName|description|subjectTypeCode|instanceCardinalityCode)$/.exec(
      path,
    )?.[1];
    if (field && this.canAuthor())
      this.focusInvalidFormField(
        this.identityForm,
        () => this.identityFormElement,
        () => this.setupHeading,
        field,
      );
    else this.focusAfterRender(() => this.setupHeading);
  }

  focusSemanticChange(change: WorkflowDefinitionSemanticChange): void {
    if (
      this.isDraftEditorInteractionBlocked() ||
      !this.canResolveSemanticHandoff(change)
    ) {
      return;
    }
    this.requestMapEditorDeactivation(
      () => {
        this.focusSemanticChangeAfterDeactivation(change);
        this.validationHandoff.set(null);
        const nodeId =
          /^\$\.nodes\[id=([^\]]+)\]/.exec(change.path)?.[1] ||
          (change.elementTypeCode === 'node' ? change.elementId : null);
        const nodes = this.currentDraft()!.document.nodes.filter(
          (node) => node.id === nodeId,
        );
        const area = change.path.includes('stepProgressConfiguration')
          ? 'Step progress'
          : change.path.includes('statusConfiguration')
            ? 'Statuses'
            : change.path.includes('.notificationMessage')
              ? 'Notification message'
              : change.path.includes('.declaration.detail')
                ? 'Staff page'
                : change.path.includes('.declaration.dashboards')
                  ? 'Dashboard'
                  : change.path.includes('.childLinks') ? 'Related work' : change.path.includes('.terminalOutcome') ? 'Workflow result' : change.path.includes('.timers') ? 'Timers & escalation'
                  : change.path.includes('.declaration.behavior')
                    ? this.stepBehaviorPathTargetsWork(change.path) ? 'Work' : 'Routing'
                    : 'Step settings';
        this.semanticReviewHandoff.set({
          summary:
            nodes.length === 1
              ? `${nodes[0].displayName || nodes[0].stepCode || nodeId} · ${area}`
              : change.summary,
          revision: this.currentDraft()!.revision,
        });
        this.writeWorkbenchViewToUrl(this.activeWorkbenchView());
      },
      () => undefined,
    );
  }

  private focusSemanticChangeAfterDeactivation(
    change: WorkflowDefinitionSemanticChange,
  ): void {
    this.clearFocusedMapContext();
    const nodeId = semanticChangeNodeId(change);
    if (
      change.path.includes('statusConfiguration') ||
      change.path.includes('stepProgressConfiguration')
    ) {
      this.openStatusConfiguration(change.path, nodeId, 'review');
      return;
    }
    if (
      nodeId &&
      change.path.includes('.notificationMessage') &&
      this.openMapNodeById(nodeId, false, 'overview')
    ) {
      this.stepWorkspaceScope.set('behavior');
      this.behaviorSection.set('notifications');
      this.notificationMessageFocus.set({
        path: change.path,
        nodeId,
        savedDocument: this.currentDraft()?.document,
      });
      return;
    }
    switch (change.elementTypeCode) {
      case 'action_trigger':
      case 'action':
        this.openAutomation(change.path);
        return;
      case 'assignment_policy':
        this.openAssignmentPolicy(change.elementId || change.path);
        return;
      case 'definition':
        this.focusSetupPath(change.path);
        return;
      case 'node': {
        this.activeWorkbenchView.set('map');
        this.setInspectorPane('overview');
        const node = this.workingDocument()?.nodes.find(
          (candidate) => candidate.id === change.elementId,
        );
        if (node) {
          this.openMapNodeById(node.id, true);
          this.stepWorkspaceScope.set(
            change.path.includes('.declaration.detail')
              ? 'detail'
              : change.path.includes('.declaration.dashboards')
                ? 'dashboard'
                : 'behavior',
          );
          this.behaviorSection.set(
            change.path.includes('.childLinks') ? 'children' : change.path.includes('.timers') ? 'timers' : change.path.includes('.declaration.behavior')
              ? this.stepBehaviorPathTargetsWork(change.path) ? 'readiness' : 'routes'
              : 'details',
          );
          if (this.stepWorkspaceScope() === 'behavior' && this.behaviorSection() === 'readiness')
            this.revealWorkView(change.path.includes('.completionRequirements') ? 'requirements' : 'components');
        } else {
          this.mapSelection.set(null);
          this.focusAfterRender(() => this.mapInspectorHeading);
        }
        return;
      }
      case 'edge':
        if (
          this.workingDocument()?.edges.some(
            (edge) => edge?.id === change.elementId,
          )
        ) {
          this.openMapEdgeBySourceId(change.elementId, true);
        } else {
          this.openRoutingView('paths');
          this.mapSelection.set(null);
          this.edgeEditor?.focusEdgePath('review');
        }
        return;
      case 'workflow_transition':
      case 'step_transition':
        this.openRoutingView('stateChanges');
        if (nodeId) {
          if (!this.openMapNodeById(nodeId, false, 'transitions')) {
            this.mapSelection.set(null);
          }
        } else {
          this.mapSelection.set(null);
        }
        this.transitionEditor?.focusTransitionPath(
          change.path,
          nodeId,
          'review',
        );
        return;
      case 'decision':
        this.activeWorkbenchView.set('map');
        this.setInspectorPane('decisions');
        if (nodeId) {
          if (!this.openMapNodeById(nodeId, false, 'decisions')) {
            this.mapSelection.set(null);
          }
        } else {
          this.mapSelection.set(null);
        }
        this.decisionEditor?.focusDecisionPath(change.path, nodeId, 'review');
        return;
      case 'notification':
        if (change.path.includes('.requiredDecisions[')) {
          this.activeWorkbenchView.set('map');
          this.setInspectorPane('decisions');
          if (nodeId) {
            if (!this.openMapNodeById(nodeId, false, 'decisions')) {
              this.mapSelection.set(null);
            }
          } else {
            this.mapSelection.set(null);
          }
          this.decisionEditor?.focusDecisionPath(change.path, nodeId, 'review');
        } else {
          this.openRoutingView('stateChanges');
          if (nodeId) {
            if (!this.openMapNodeById(nodeId, false, 'transitions')) {
              this.mapSelection.set(null);
            }
          } else {
            this.mapSelection.set(null);
          }
          this.transitionEditor?.focusTransitionPath(
            change.path,
            nodeId,
            'review',
          );
        }
        return;
      default:
        this.selectWorkbenchView('setup');
        this.focusAfterRender(() => this.setupHeading);
    }
  }

  private savedWorkflowStatusPath(path: string): string | null {
    const match = /^\$\.statusConfiguration\.(statuses|rules)\[(\d+)\](.*)$/.exec(path);
    if (!match) return path;
    const saved = this.currentDraft()?.document.statusConfiguration, current = this.workingDocument()?.statusConfiguration;
    if (!saved || !current || !Array.isArray(saved.statuses) || !Array.isArray(saved.rules) ||
      !Array.isArray(current.statuses) || !Array.isArray(current.rules)) return null;
    const collection = match[1] === 'statuses' ? 'statuses' : 'rules';
    const identity = (item: unknown): string | null => {
      if (!item || typeof item !== 'object' || Array.isArray(item)) return null;
      const value = (item as Record<string, unknown>)[collection === 'statuses' ? 'code' : 'id'];
      return typeof value === 'string' && value.length > 0 ? value : null;
    };
    const key = identity(saved[collection][Number(match[2])]);
    if (!key || saved[collection].filter(item => identity(item) === key).length !== 1 ||
      current[collection].filter(item => identity(item) === key).length !== 1) return null;
    return `$.statusConfiguration.${collection}[${collection === 'statuses' ? 'code' : 'id'}=${key}]${match[3]}`;
  }

  private openStatusConfiguration(
    path: string,
    locationId: string | null,
    source: 'validation' | 'review',
  ): void {
    if (/^\$\.statusConfiguration(?:\.|\[|$)/.test(path)) {
      this.workflowStatusOrigin = null;
      this.revealWorkflowSettingsTask('statuses');
      const exact = this.savedWorkflowStatusPath(path);
      this.scheduleWorkflowStatusFocus(() => this.workflowStatusEditor?.focusSavedPath(exact ?? '$.statusConfiguration'));
      return;
    }
    const nodeId =
      /^\$\.nodes\[id=([^\]]+)\]/.exec(path)?.[1] ||
      (path.startsWith('$.nodes') ? locationId : null);
    this.openRoutingView('stateChanges');
    if (nodeId) {
      if (!this.openMapNodeById(nodeId, false, 'transitions'))
        this.mapSelection.set(null);
    } else {
      this.mapSelection.set(null);
    }
    setTimeout(() =>
      this.transitionEditor?.focusTransitionPath(path, nodeId, source),
    );
  }

  isInheritedStep(nodeId: string): boolean {
    return this.inheritedStepCode(nodeId) !== null;
  }

  nodeTypeLabel(node: WorkflowDefinitionNode | null | undefined): string {
    switch (node?.typeCode) {
      case 'entry':
        return 'Start boundary';
      case 'human_step':
        return 'Work step';
      case 'automated_step':
        return 'Automated step';
      case 'routing_decision':
        return 'Routing decision';
      case 'terminal':
        return 'Completion boundary';
      default:
        return 'Workflow step';
    }
  }

  private nodeDisplayLabel(
    nodeId: string,
    document:
      | WorkflowDefinitionDocument
      | null
      | undefined = this.workingDocument(),
  ): string {
    const node = document?.nodes.find((candidate) => candidate.id === nodeId);
    return node?.displayName || node?.stepCode || node?.id || nodeId;
  }

  private loadDrafts(
    query: WorkflowDefinitionDraftQuery = this.draftQuery(),
    updateUrl = false,
  ): void {
    if (this.registryReadsSuspended()) return;
    const normalizedQuery = normalizeDraftQuery(query);
    this.draftQuery.set(normalizedQuery);
    this.setDraftFilterValues(normalizedQuery);
    if (updateUrl) {
      this.writeDraftQueryToUrl(normalizedQuery);
    }
    this.isLoadingDrafts.set(true);
    this.draftListError.set(null);
    this.draftListSubscription?.unsubscribe();
    const requestId = ++this.draftListRequestId;
    this.draftListSubscription = this.lifecycleService
      .getDrafts(normalizedQuery)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (page) => {
          if (requestId !== this.draftListRequestId) {
            return;
          }

          if (
            page.totalCount > 0 &&
            page.items.length === 0 &&
            page.pageNumber > 1
          ) {
            this.loadDrafts({ ...normalizedQuery, pageNumber: 1 }, true);
            return;
          }

          this.draftPage.set(page);
          this.syncDraftGridSelection();
          this.isLoadingDrafts.set(false);
        },
        error: (error: unknown) => {
          if (requestId !== this.draftListRequestId) {
            return;
          }

          const displayError = mapWorkflowRegistryDiagnosticError(error);
          this.draftListError.set(displayError);
          this.isLoadingDrafts.set(false);
          if (isReadAccessDenied(displayError))
            this.denyDraftReadAccess(displayError);
        },
      });
  }

  private loadAuthoringReferences(): void {
    if (this.registryReadsSuspended()) return;
    this.isLoadingAuthoringReferences.set(true);
    this.authoringReferencesError.set(null);
    this.authoringReferencesSubscription?.unsubscribe();
    const requestId = ++this.authoringReferencesRequestId;
    this.authoringReferencesSubscription = this.lifecycleService
      .getAuthoringReferences()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (references) => {
          if (requestId !== this.authoringReferencesRequestId) {
            return;
          }
          this.authoringReferences.set(references);
          this.refreshSetupFormConfigs();
          this.isLoadingAuthoringReferences.set(false);
        },
        error: (error: unknown) => {
          if (requestId !== this.authoringReferencesRequestId) {
            return;
          }
          const displayError = mapWorkflowRegistryDiagnosticError(error);
          this.authoringReferencesError.set(displayError);
          if (isReadAccessDenied(displayError))
            this.denyDraftReadAccess(displayError);
          this.isLoadingAuthoringReferences.set(false);
        },
      });
  }

  private loadDraft(
    draftId: number,
    updateUrl: boolean,
    preserveRecovery = false,
  ): void {
    if (this.registryReadsSuspended()) return;
    const recoveryContext = preserveRecovery ? this.saveContextKey() : null;
    const recoveryDraftCode = this.currentDraft()?.draftCode;
    const recoveryWorkflowCode = this.currentDraft()?.workflowCode;
    const recoveryLocal = preserveRecovery && this.workingDocument()
      ? serializeDocument(this.workingDocument()!) : null;
    this.isLoadingDraft.set(true);
    this.draftReadError.set(null);
    this.loadingDraftId = draftId;
    this.failedDraftLoad.set(null);
    this.operationError.set(null);
    this.editorMessage.set(null);
    this.draftLoadSubscription?.unsubscribe();
    const requestId = ++this.draftLoadRequestId;
    this.draftLoadSubscription = this.lifecycleService
      .getDraft(draftId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (draft) => {
          if (requestId !== this.draftLoadRequestId) {
            return;
          }

          this.isLoadingDraft.set(false);
          this.draftReadError.set(null);
          this.loadingDraftId = null;
          if (recoveryContext !== null &&
            (recoveryContext !== this.saveContextKey() || draft.id !== draftId ||
              draft.draftCode !== recoveryDraftCode || draft.workflowCode !== recoveryWorkflowCode ||
              draft.document.workflowCode !== recoveryWorkflowCode)) {
            this.editorMessage.set('The draft or author context changed during recovery reload. Current local work and recovery copies have been retained.');
            return;
          }
          if (preserveRecovery) {
            this.captureRecoveryPendingInputs();
            const local = this.workingDocument();
            if (local && serializeDocument(local) !== recoveryLocal) {
              this.recoveryDocument.set(cloneDocument(local));
              this.persistRecovery(this.recoveryBase() ?? this.currentDraft()!.document, local);
            }
          }
          this.applyLoadedDraft(draft, preserveRecovery, preserveRecovery);
          if (preserveRecovery && this.recoveryDocument()) {
            this.recoveryChoices.set({});
            this.hasReloadedLatestAfterConflict.set(true);
          }
          this.focusAfterRender(() => this.draftEditorHeading);
          if (updateUrl) {
            this.writeDraftIdToUrl(draft.id);
          }
        },
        error: (error: unknown) => {
          if (requestId !== this.draftLoadRequestId) {
            return;
          }

          this.isLoadingDraft.set(false);
          this.loadingDraftId = null;
          if (recoveryContext !== null && recoveryContext !== this.saveContextKey()) {
            this.editorMessage.set('The draft or author context changed during recovery reload. Current local work and recovery copies have been retained.');
            return;
          }
          const displayError = mapWorkflowRegistryDiagnosticError(error);
          const draftLoadError =
            displayError.statusCode === 404
              ? {
                  ...displayError,
                  title: 'Draft no longer available',
                  messages: [
                    'The selected draft no longer exists in this environment. Its stale link was cleared; choose an available draft or clone a published version to continue.',
                  ],
                }
              : displayError;
          this.showOperationError(
            draftLoadError,
            displayError.statusCode === 404
              ? null
              : { draftId, updateUrl, preserveRecovery },
          );
          this.draftReadError.set(draftLoadError);
          if (isReadAccessDenied(displayError)) {
            this.denyDraftReadAccess(displayError);
            return;
          }
          this.focusAfterRender(() => this.draftReadErrorSummary);
          const currentDraft = this.currentDraft();
          if (currentDraft) {
            this.writeDraftIdToUrl(currentDraft.id);
          } else if (displayError.statusCode === 404) {
            this.writeDraftIdToUrl(null);
          }
          this.syncDraftGridSelection();
        },
      });
  }

  private cancelDraftLoad(): void {
    this.draftLoadSubscription?.unsubscribe();
    this.draftLoadRequestId += 1;
    this.loadingDraftId = null;
    this.isLoadingDraft.set(false);
  }

  private clearCurrentDraft(updateUrl: boolean): void {
    this.workflowStatusOrigin = null; this.workflowStatusNavigation++;
    this.decisionInventoryRequest++; this.decisionInventoryNavigation++; this.decisionInventoryRequestedKey = ''; this.decisionInventoryCatalogKey.set('');
    this.decisionInventoryInitialized.set(false); this.decisionInventoryLoading.set(false); this.decisionInventoryError.set('');
    this.decisionInventoryCatalog.set(null); this.decisionInventoryActivityCatalog.set(null);
    this.decisionInventoryOrigin = null; this.decisionInventoryOwnerReturn = null; this.decisionInventoryCheckAddMode.set(false);
    this.activeWorkflowSettingsTask.set('general');
    this.initializedWorkflowSettingsTasks.set(new Set(['general']));
    this.saveRequestId++;
    this.saveSubscription?.unsubscribe();
    this.isSaving.set(false);
    this.ownerViewState.clear();
    this.behaviorResponsibilityFocus.set(null);
    this.dryRunExpanded.set(false);
    this.dryRunOpened.set(false);
    ++this.archivedDraftRequest;
    this.archivedBufferPending.set(false);
    this.archiveDetected.set(false);
    ++this.archivedVersionRequest;
    this.archivedPublishedVersion.set(null);
    this.archivedVersionLoading.set(false);
    this.archivedVersionError.set(false);
    this.archivedPendingInputs.set([]);
    this.resetStandardStepSupport();
    this.cancelDraftLoad();
    this.cancelValidation();
    this.validationHandoff.set(null);
    this.semanticReviewHandoff.set(null);
    this.lifecycleOperationInProgress.set(false);
    this.recoveryStorageKey = null;
    this.recoveryOwnerIdentity = null;
    this.recoveryBase.set(null);
    this.recoveryChoices.set({});
    this.stepEditorNodeId.set(null);
    this.stepEditorOpened.set(false);
    this.editingRouteSelection.set(null);
    this.automatedRouteFocus.set(null);
    this.editorMapReturnSelection = null;
    this.stepsDrawerOpen.set(false);
    this.stepEditorReturnFocus = null;
    this.currentDraft.set(null);
    this.workingDocument.set(null);
    this.baselineDocumentJson = '';
    this.documentDirty.set(false);
    this.nodeFormDirty.set(false);
    this.edgeFormDirty.set(false);
    this.reviewRoutingDirty.set(false);
    this.dashboardEditorDirty.set(false);
    this.staffPageEditorDirty.set(false);
    this.workComponentsDirty.set(false);
    this.transitionFormDirty.set(false);
    this.decisionFormDirty.set(false);
    this.requirementFormDirty.set(false);
    this.timerFormDirty.set(false);
    this.childWorkFormDirty.set(false);
    this.notificationFormDirty.set(false);
    this.assignmentPolicyFormDirty.set(false);
    this.stepAssignmentPolicyFormDirty.set(false);
    this.selectedNodeId.set(null);
    this.mapSelection.set(null);
    this.setInspectorPane('overview');
    this.operationError.set(null);
    this.draftReadError.set(null);
    this.failedDraftLoad.set(null);
    this.editorMessage.set(null);
    this.recoveryDocument.set(null);
    this.hasReloadedLatestAfterConflict.set(false);
    this.activeWorkbenchView.set('map');
    this.activeRoutingView.set('paths');
    this.workbenchStateChange.emit(null);
    this.resetStructuredEditors();
    if (updateUrl) {
      this.writeDraftIdToUrl(null);
    }
  }

  private applyLoadedDraft(
    draft: WorkflowDefinitionDraftDetail,
    preserveRecovery = false,
    retainPending = false,
  ): void {
    if (this.currentDraft()?.id !== draft.id) { this.workflowStatusOrigin = null; this.workflowStatusNavigation++; }
    const focusedStepId = this.currentDraft()?.id === draft.id && this.stepEditorOpened()
      ? this.stepEditorNodeId() : null;
    const focusedSteps = focusedStepId
      ? this.workingDocument()?.nodes.filter(node => node.id === focusedStepId) ?? [] : [];
    const focusedStepType = focusedSteps.length === 1 ? focusedSteps[0].typeCode : null;
    if (this.isSaving()) {
      this.saveRequestId++;
      this.saveSubscription?.unsubscribe();
      this.isSaving.set(false);
    }
    ++this.archivedDraftRequest;
    if (draft.lifecycleStatusCode === 'archived')
      clearArchivedDraftPublicationIntent(draft.id);
    if (
      draft.lifecycleStatusCode === 'archived' &&
      this.currentDraft()?.id === draft.id &&
      this.hasOpenDraftUnsavedChanges()
    ) {
      this.preserveArchivedLocalChanges(draft);
      return;
    }
    const retainCaptured = this.currentDraft()?.id === draft.id &&
      this.archivedPendingInputs().length > 0;
    retainPending = retainPending && this.currentDraft()?.id === draft.id;
    if (retainPending) this.prepareRetainedStepOwner(draft.document);
    this.archiveDetected.set(false);
    if (!retainCaptured) this.archivedBufferPending.set(false);
    ++this.archivedVersionRequest;
    this.archivedPublishedVersion.set(null);
    this.archivedVersionLoading.set(false);
    this.archivedVersionError.set(false);
    if (!retainCaptured) this.archivedPendingInputs.set([]);
    this.resetStandardStepSupport();
    this.cancelValidation();
    this.lifecycleOperationInProgress.set(false);
    this.validationHandoff.set(null);
    this.semanticReviewHandoff.set(null);
    const document = cloneDocument(draft.document);
    if (this.currentDraft()?.id !== draft.id) {
      const task = this.requestedWorkflowSettingsTask?.draftId === draft.id ? this.requestedWorkflowSettingsTask.task : 'general';
      this.activeWorkflowSettingsTask.set(task);
      this.initializedWorkflowSettingsTasks.set(new Set(['general', task]));
      const technicalView = this.requestedTechnicalView?.draftId === draft.id ? this.requestedTechnicalView.view : 'identity';
      this.activeTechnicalView.set(technicalView);
      this.initializedTechnicalViews.set(new Set(['identity', technicalView]));
      this.capabilityInventoryExpanded.set(technicalView === 'modules');
      this.quorumCalculatorOpen.set(false);
      this.ownerViewState.clear();
      this.behaviorResponsibilityFocus.set(null);
      this.stepEditorNodeId.set(null);
      this.stepEditorOpened.set(false);
      this.editingRouteSelection.set(null);
      this.automatedRouteFocus.set(null);
      this.editorMapReturnSelection = null;
      this.stepsDrawerOpen.set(false);
      this.stepEditorReturnFocus = null;
    }
    this.currentDraft.set(draft);
    this.workbenchStateChange.emit({
      draftCode: draft.draftCode,
      workflowCode: draft.workflowCode,
      revision: draft.revision,
      ...(draft.lifecycleStatusCode === 'archived'
        ? { lifecycleStatusCode: 'archived' as const }
        : {}),
    });
    this.workingDocument.set(document);
    this.baselineDocumentJson = serializeDocument(document);
    this.documentDirty.set(false);
    if (!retainPending) {
    this.nodeFormDirty.set(false);
    this.edgeFormDirty.set(false);
    this.reviewRoutingDirty.set(false);
    this.dashboardEditorDirty.set(false);
    this.staffPageEditorDirty.set(false);
    this.workComponentsDirty.set(false);
    this.transitionFormDirty.set(false);
    this.decisionFormDirty.set(false);
    this.requirementFormDirty.set(false);
    this.timerFormDirty.set(false);
    this.childWorkFormDirty.set(false);
    this.notificationFormDirty.set(false);
    this.assignmentPolicyFormDirty.set(false);
    this.stepAssignmentPolicyFormDirty.set(false);
    this.isAddingStep.set(false);
    }
    this.saveIntent = null;
    this.validationIntent = null;
    this.operationError.set(null);
    this.draftReadError.set(null);
    this.failedDraftLoad.set(null);
    this.hasReloadedLatestAfterConflict.set(false);
    if (!retainPending) this.resetStructuredEditors();
    if (!preserveRecovery) {
      this.recoveryDocument.set(null);
      this.recoveryBase.set(null);
      this.recoveryChoices.set({});
      this.restoreStoredRecovery(draft);
    }
    if (!retainPending) this.resetMapContextForWholeDocument(document);
    this.mapDocumentToForms(document, retainPending);
    if (!retainPending && this.requestedDesignerLocation?.draftId === draft.id) {
      this.restoreDesignerLocation(this.requestedDesignerLocation.location);
    }
    // Same-draft adoption can restore an inspect-first URL while its open owner is hidden in Settings.
    const restoredSteps = focusedStepId ? document.nodes.filter(node => node.id === focusedStepId) : [];
    if (!retainPending && restoredSteps.length === 1 && restoredSteps[0].typeCode === focusedStepType &&
      ['human_step', 'automated_step'].includes(focusedStepType ?? '') &&
      this.mapSelection()?.kind === 'node' && this.selectedMapNode()?.id === focusedStepId) {
      this.stepEditorNodeId.set(focusedStepId);
    }
    this.lastDesignerLocationKey = JSON.stringify(this.designerLocation());
    this.syncDraftGridSelection();
    if (draft.lifecycleStatusCode === 'archived')
      this.loadArchivedPublishedVersion(draft);
  }

  private cancelValidation(): void {
    this.validationRequestId += 1;
    this.validateSubscription?.unsubscribe();
    this.isValidating.set(false);
    this.automaticValidationFailed.set(false);
    this.automaticValidationKey = null;
    this.validationIntent = null;
  }

  private resetStandardStepSupport(): void {
    this.standardStepRequest += 1;
    this.standardStepSupport = null;
    this.standardStepLoading.set(false);
    this.standardStepError.set('');
  }

  private mapDocumentToForms(document: WorkflowDefinitionDocument, retainPending = false): void {
    this.identityFormValues = {
      displayName: document.displayName ?? '',
      description: document.description ?? '',
      payloadSchemaVersion: document.payloadSchemaVersion,
      workflowCode: document.workflowCode,
      definitionVersionCode: document.definitionVersionCode,
      subjectTypeCode: document.subjectTypeCode ?? '',
      scopeTypeCode: document.scopeTypeCode,
      scopeKey: document.scopeKey ?? '',
      configurationCode: document.configurationCode,
      configurationVersionCode: document.configurationVersionCode,
      instanceCardinalityCode: document.instanceCardinalityCode,
    };
    this.identityFormConfig = this.buildIdentityFormConfig();
    this.identityAvailabilityFormConfig =
      this.buildAvailabilityFormConfig('edit');

    if (retainPending && this.nodeFormDirty()) return;

    const orderedNodes = [...document.nodes].sort(
      (left, right) =>
        left.displayOrder - right.displayOrder ||
        left.id.localeCompare(right.id),
    );
    const retainedMapNode =
      this.mapSelection()?.kind === 'node' && this.mapSelection()?.isAuthorable
        ? this.selectedMapNode()
        : null;
    const selectedNode =
      retainedMapNode ??
      document.nodes.find((node) => node.id === this.selectedNodeId()) ??
      orderedNodes.find((node) => node.typeCode === 'human_step') ??
      orderedNodes[0];
    if (selectedNode) {
      this.loadNodeForm(selectedNode);
    } else {
      this.selectedNodeId.set(null);
      this.nodeFormValues = this.emptyNodeValues();
    }
  }

  private loadNodeForm(node: WorkflowDefinitionNode): void {
    this.inlineStepNameEditing.set(false);
    this.inlineStepNameError.set('');
    if (this.selectedNodeId() !== node.id) {
      this.behaviorUndoHistory.set([]);
      this.behaviorFormFocus.set(null);
      this.behaviorResponsibilityFocus.set(null);
      this.notificationMessageFocus.set(null);
      this.stepActionFocus.set(null);
    }
    this.isAddingStep.set(false);
    this.selectedNodeId.set(node.id);
    this.nodeFormValues = this.nodeToFormValues(node);
    this.nodeFormConfig = this.buildNodeFormConfig(
      false,
      this.inheritedStepCode(node.id) !== null,
    );
    this.nodeFormDirty.set(false);
  }

  enableSavedGraphExecution(): void {
    const document = this.workingDocument();
    if (
      !document ||
      !this.canAuthor() ||
      this.isDraftEditorInteractionBlocked() ||
      !['1.0', '1.1', '1.2'].includes(document.payloadSchemaVersion)
    )
      return;
    const payloadSchemaVersion = requireWorkflowPayloadVersion(
      document.payloadSchemaVersion,
      '1.3',
    );
    this.setWorkingDocument({ ...document, payloadSchemaVersion });
    this.identityFormValues = {
      ...this.identityFormValues,
      payloadSchemaVersion,
    };
  }

  private setWorkingDocument(document: WorkflowDefinitionDocument): void {
    const previous = this.workingDocument();
    if (!this.recordingBehaviorEdit) this.behaviorUndoHistory.set([]);
    if (!this.recordingAutomationEdit) this.automationUndoHistory.set([]);
    const cloned = cloneDocument(document);
    this.workingDocument.set(cloned);
    if (previous?.payloadSchemaVersion !== cloned.payloadSchemaVersion ||
      previous?.subjectTypeCode !== cloned.subjectTypeCode ||
      this.canEditProcessCardinality(previous) !== this.canEditProcessCardinality(cloned)) {
      this.identityFormValues = { ...this.identityFormValues,
        payloadSchemaVersion: cloned.payloadSchemaVersion,
        displayName: cloned.displayName ?? '', description: cloned.description ?? '',
        subjectTypeCode: cloned.subjectTypeCode ?? '' };
      this.refreshSetupFormConfigs();
    }
    this.documentDirty.set(
      serializeDocument(cloned) !== this.baselineDocumentJson,
    );
    if (
      this.recoveryStorageKey &&
      !this.recoveryDocument() &&
      this.currentDraft()
    ) {
      this.persistRecovery(this.currentDraft()!.document, cloned);
    }
    this.saveIntent = null;
    this.validationIntent = null;
  }

  private resetDraftToBaseline(): void {
    this.archivedBufferPending.set(false);
    this.archivedPendingInputs.set([]);
    this.clearStoredRecovery();
    const draft = this.currentDraft();
    if (!draft) {
      return;
    }

    const document = cloneDocument(draft.document);
    this.workingDocument.set(document);
    this.baselineDocumentJson = serializeDocument(document);
    this.documentDirty.set(false);
    this.nodeFormDirty.set(false);
    this.edgeFormDirty.set(false);
    this.reviewRoutingDirty.set(false);
    this.dashboardEditorDirty.set(false);
    this.staffPageEditorDirty.set(false);
    this.workComponentsDirty.set(false);
    this.transitionFormDirty.set(false);
    this.decisionFormDirty.set(false);
    this.requirementFormDirty.set(false);
    this.timerFormDirty.set(false);
    this.childWorkFormDirty.set(false);
    this.notificationFormDirty.set(false);
    this.assignmentPolicyFormDirty.set(false);
    this.stepAssignmentPolicyFormDirty.set(false);
    this.isAddingStep.set(false);
    this.saveIntent = null;
    this.validationIntent = null;
    this.operationError.set(null);
    this.recoveryDocument.set(null);
    this.hasReloadedLatestAfterConflict.set(false);
    this.editorMessage.set('Unsaved changes discarded.');
    this.resetStructuredEditors();
    this.resetMapContextForWholeDocument(document);
    this.mapDocumentToForms(document);
  }

  private resetMapContextForWholeDocument(
    document: WorkflowDefinitionDocument,
  ): void {
    const previousSelection = this.mapSelection();
    const graph = projectWorkflowDraftGraph(document);
    this.mapSelection.set(null);
    if (previousSelection?.kind === 'node') {
      const matchingNode = graph.nodes.find(
        (node) =>
          node.sourceIndex === previousSelection.sourceIndex &&
          node.sourceId === previousSelection.sourceId,
      );
      if (matchingNode) {
        this.mapSelection.set(
          this.graphSelectionForNode(matchingNode, graph.nodes),
        );
        return;
      }
    } else if (previousSelection?.kind === 'edge') {
      const matchingEdge = graph.edges.find(
        (edge) =>
          edge.sourceIndex === previousSelection.sourceIndex &&
          edge.sourceId === previousSelection.sourceId,
      );
      if (matchingEdge) {
        this.mapSelection.set(
          this.graphSelectionForEdge(matchingEdge, graph.edges),
        );
        return;
      }
    }

    if (this.mapInspectorPane() !== 'path') {
      return;
    }

    const firstEdge = graph.edges.find((edge) => edge.sourceIndex !== null);
    if (firstEdge) {
      this.mapSelection.set(this.graphSelectionForEdge(firstEdge, graph.edges));
    }
  }

  private resetStructuredEditors(): void {
    this.inlineStepNameEditing.set(false);
    this.inlineStepNameError.set('');
    this.automationEditor?.discardPendingChanges();
    this.automatedStepEditor?.discardPendingChanges();
    this.workflowStatusEditor?.discardPendingChanges();
    this.workflowStatusFormDirty.set(false);
    this.automationFormDirty.set(false);
    this.automatedStepFormDirty.set(false);
    this.automationUndoHistory.set([]);
    this.navigationSettingsEditor?.discardPendingChanges();
    this.navigationFormDirty.set(false);
    this.processDataEditor?.discardPendingChanges();
    this.processDataFormDirty.set(false);
    this.behaviorUndoHistory.set([]);
    this.behaviorFormFocus.set(null);
    this.behaviorResponsibilityFocus.set(null);
    this.notificationMessageFocus.set(null);
    this.stepActionFocus.set(null);
    this.edgeFormDirty.set(false);
    this.reviewRoutingDirty.set(false);
    this.dashboardEditorDirty.set(false);
    this.staffPageEditorDirty.set(false);
    this.workComponentsDirty.set(false);
    this.transitionFormDirty.set(false);
    this.decisionFormDirty.set(false);
    this.requirementFormDirty.set(false);
    this.timerFormDirty.set(false);
    this.childWorkFormDirty.set(false);
    this.notificationFormDirty.set(false);
    this.assignmentPolicyFormDirty.set(false);
    this.stepAssignmentPolicyFormDirty.set(false);
    this.structuredEditorResetVersion.update((version) => version + 1);
  }

  private moveSelectedStep(offset: -1 | 1): void {
    const document = this.workingDocument();
    const selectedNode = this.selectedNode();
    const steps = this.humanStepNodes();
    const selectedIndex = selectedNode
      ? steps.findIndex((step) => step.id === selectedNode.id)
      : -1;
    const otherStep = steps[selectedIndex + offset];
    if (
      !document ||
      !selectedNode ||
      selectedNode.typeCode !== 'human_step' ||
      !otherStep ||
      !this.canAuthor() ||
      this.nodeFormDirty() ||
      this.edgeFormDirty() ||
      this.reviewRoutingDirty() ||
      this.dashboardEditorDirty() ||
      this.staffPageEditorDirty() ||
      this.workComponentsDirty() ||
      this.transitionFormDirty() ||
      this.decisionFormDirty() ||
      this.requirementFormDirty() ||
      this.timerFormDirty() ||
      this.childWorkFormDirty() ||
      this.notificationFormDirty() ||
      this.isDraftEditorInteractionBlocked()
    ) {
      return;
    }

    if (selectedNode.displayOrder === otherStep.displayOrder) {
      this.showOperationError(
        localValidationError(
          'Initial step order needs attention',
          'These work steps share the same order value. Change one order value and apply it before moving the step.',
        ),
      );
      return;
    }

    const selectedOrder = selectedNode.displayOrder;
    const otherOrder = otherStep.displayOrder;
    const nextNodes = document.nodes.map((node) => {
      if (node.id === selectedNode.id) {
        return { ...node, displayOrder: otherOrder };
      }
      if (node.id === otherStep.id) {
        return { ...node, displayOrder: selectedOrder };
      }
      return node;
    });
    if (this.selectedMapNode()?.id === selectedNode.id)
      this.onBehaviorDocumentChange({ ...document, nodes: nextNodes });
    else this.setWorkingDocument({ ...document, nodes: nextNodes });
    const movedNode = nextNodes.find((node) => node.id === selectedNode.id);
    if (movedNode) {
      this.loadNodeForm(movedNode);
    }
    this.editorMessage.set(
      `${selectedNode.displayName || selectedNode.stepCode || selectedNode.id} moved ` +
        `${offset < 0 ? 'earlier' : 'later'} in the initial work-step order. ` +
        'Map paths were not changed.',
    );
  }

  private decisionRequirementCount(
    decisions: Array<WorkflowDecisionRequirement | null> | null | undefined,
    legacyDecisionKinds: string[] | null | undefined = null,
  ): number {
    const requirementKeys = new Set<string>();
    (Array.isArray(decisions) ? decisions : []).forEach((decision, index) => {
      if (
        decision &&
        typeof decision.decisionKind === 'string' &&
        decision.decisionKind.trim().length > 0
      ) {
        requirementKeys.add(this.decisionRequirementKey(decision));
      } else {
        requirementKeys.add(`incomplete-structured:${index}`);
      }
    });
    (Array.isArray(legacyDecisionKinds) ? legacyDecisionKinds : []).forEach((decisionKind, index) => {
      if (
        typeof decisionKind !== 'string' ||
        decisionKind.trim().length === 0
      ) {
        requirementKeys.add(`incomplete-legacy:${index}`);
        return;
      }
      requirementKeys.add(
        this.decisionRequirementKey({
          decisionKind,
          requiredOutcomeCode: 'approved',
          reaction: {
            reactionCode: 'permit',
            targetStateCode: null,
          },
          eligibleRoleCodes: null,
          ownerQueueCode: null,
        }),
      );
    });
    return requirementKeys.size;
  }

  private decisionRequirementKey(
    decision: WorkflowDecisionRequirement,
  ): string {
    return [
      decision.decisionKind,
      decision.requiredOutcomeCode ?? 'approved',
      decision.reaction?.reactionCode ?? 'permit',
      decision.reaction?.targetStateCode ?? '',
    ]
      .map((value) =>
        typeof value === 'string' ? value.trim().toLowerCase() : '',
      )
      .join('|');
  }

  private acceptMapSelection(
    request: WorkflowReadOnlyGraphSelectionRequest,
  ): void {
    this.retainedIssueStep.set(null);
    this.validationHandoff.set(null);
    this.semanticReviewHandoff.set(null);
    this.stepWorkspaceScope.set('behavior');
    let selection = request.selection;
    if (selection?.kind === 'edge') {
      const requested = selection;
      const graph = this.workingDocument()
        ? projectWorkflowDraftGraph(this.workingDocument()!)
        : null;
      const current = graph?.edges.find(
        (edge) =>
          edge.id === requested.rendererId &&
          edge.sourceId === requested.sourceId &&
          edge.sourceIndex === requested.sourceIndex,
      );
      if (!current || !graph) return;
      selection = this.graphSelectionForEdge(current, graph.edges);
    }
    this.isAddingStep.set(false);
    this.mapSelection.set(selection);

    if (!selection) {
      this.setInspectorPane('overview');
      if (request.moveFocusToInspector) {
        this.focusAfterRender(() => this.mapInspectorHeading);
      }
      return;
    }

    if (selection.kind === 'node') {
      this.behaviorSection.set('readiness');
      this.setInspectorPane('overview');
      const node =
        selection.sourceIndex === null
          ? null
          : (this.workingDocument()?.nodes[selection.sourceIndex] ?? null);
      if (node && selection.isAuthorable) {
        if (this.selectedNodeId() === node.id && !this.nodeFormDirty()) {
          if (request.moveFocusToInspector) {
            this.focusAfterRender(() => this.nodeEditorHeading);
          }
        } else {
          this.selectNode(node, request.moveFocusToInspector);
        }
      } else {
        this.selectedNodeId.set(null);
        if (request.moveFocusToInspector) {
          this.focusAfterRender(() => this.mapInspectorHeading);
        }
      }
      return;
    }

    this.setInspectorPane('path');
    if (request.moveFocusToInspector) this.focusAfterRender(() => this.routeInspection);
  }

  private openMapEdgeBySourceId(
    edgeId: string,
    moveFocusToInspector: boolean,
  ): boolean {
    const document = this.workingDocument();
    if (!document) {
      return false;
    }
    const graph = projectWorkflowDraftGraph(document);
    const matches = graph.edges.filter((edge) => edge.sourceId === edgeId);
    if (matches.length !== 1) {
      return false;
    }

    const selection = this.graphSelectionForEdge(matches[0], graph.edges);
    if (selection.isAuthorable && this.edgeEditor &&
      !this.edgeEditor.reviewExactEdge(document, selection.sourceId, matches[0].sourceIndex, moveFocusToInspector)) {
      this.editorMessage.set('The path editor has unfinished input or its target changed. Review that input before opening another route.');
      this.focusPendingEditor('edge');
      return false;
    }
    this.stepEditorNodeId.set(null);
    this.editingRouteSelection.set(selection);
    this.editorMapReturnSelection = selection;
    this.activeWorkbenchView.set('map');
    this.acceptMapSelection({
      selection,
      origin: 'picker',
      moveFocusToInspector,
    });
    if (selection.isAuthorable && !this.edgeEditor) this.reviewRouteAfterRender(selection, moveFocusToInspector);
    else if (!selection.isAuthorable) {
      if (this.edgeEditor) this.edgeEditor.document = document;
      this.edgeEditor?.reviewEdgeAt(matches[0].sourceIndex, moveFocusToInspector);
    }
    this.writeWorkbenchViewToUrl('map');
    return true;
  }

  private reviewRouteAfterRender(selection: WorkflowReadOnlyGraphSelection, moveFocus: boolean): void {
    const draftId = this.currentDraft()?.id;
    const target = selection.sourceIndex === null ? null : JSON.stringify(this.workingDocument()?.edges[selection.sourceIndex]);
    setTimeout(() => {
      const document = this.workingDocument();
      if (!document || this.currentDraft()?.id !== draftId ||
        selection.sourceIndex === null || JSON.stringify(document.edges[selection.sourceIndex]) !== target ||
        this.editingRouteSelection()?.rendererId !== selection.rendererId ||
        this.isDraftEditorInteractionBlocked()) return;
      if (workflowRouteInspection(document, selection)?.canOpenEditor && selection.sourceIndex !== null)
        this.edgeEditor?.reviewExactEdge(document, selection.sourceId, selection.sourceIndex, moveFocus);
    });
  }

  private openMapNodeById(
    nodeId: string,
    moveFocusToInspector: boolean,
    pane: WorkflowMapInspectorPane = 'overview',
    enterEditor = true,
    updateUrl = true,
  ): boolean {
    const document = this.workingDocument();
    if (!document) {
      return false;
    }
    const graph = projectWorkflowDraftGraph(document);
    const matches = graph.nodes.filter(
      (node) => node.sourceIndex !== null && node.sourceId === nodeId,
    );
    if (matches.length !== 1) {
      return false;
    }
    const selection = this.graphSelectionForNode(matches[0], graph.nodes);
    if (enterEditor && this.stepEditorNodeId() !== nodeId) {
      this.clearFocusedMapContext();
      this.editorMapReturnSelection = selection;
    }
    const current = this.mapSelection();
    const alreadySelected =
      current?.kind === 'node' && current.sourceIndex === selection.sourceIndex;
    if (!alreadySelected) {
      this.acceptMapSelection({
        selection,
        origin: 'picker',
        moveFocusToInspector: pane === 'overview' && moveFocusToInspector,
      });
    }
    this.activeWorkbenchView.set('map');
    if (enterEditor && ['human_step', 'automated_step', 'terminal'].includes(this.selectedMapNode()?.typeCode ?? '')) {
      this.stepEditorOpened.set(true);
      this.stepEditorNodeId.set(nodeId);
    }
    if (updateUrl) this.writeWorkbenchViewToUrl('map');
    this.setInspectorPane(pane);
    if (moveFocusToInspector) {
      this.focusAfterRender(() => this.mapInspectorHeading);
    }
    return true;
  }

  private isChildWorkDiagnostic(diagnostic: WorkflowDefinitionDiagnostic): boolean {
    return /^\$\.nodes\[id=[^\]]+\]\.(?:terminalOutcome|declaration\.behavior\.childLinks)(?:\[|\.|$)/.test(diagnostic.path);
  }
  private childWorkDiagnosticTarget(diagnostic: WorkflowDefinitionDiagnostic): {nodeId:string; label:string; terminal:boolean} | null {
    const path = /^\$\.nodes\[id=([^\]]+)\]\.(terminalOutcome|declaration\.behavior\.childLinks)(?:\[id=([^\]]+)\])?(?:\.([a-zA-Z]+))?$/.exec(diagnostic.path);
    if (!path || diagnostic.locationTypeCode !== 'child') return null;
    const [,nodeId,area,linkId] = path;
    const saved = this.currentDraft()?.document.nodes.filter(node => node.id === nodeId) ?? [];
    const current = this.workingDocument()?.nodes.filter(node => node.id === nodeId) ?? [];
    if (saved.length !== 1 || current.length !== 1 || saved[0].typeCode !== current[0].typeCode) return null;
    const terminal = area === 'terminalOutcome';
    if (terminal && current[0].typeCode !== 'terminal') return null;
    if (!terminal && current[0].typeCode !== 'human_step') return null;
    const before = terminal ? saved[0].terminalOutcome : linkId ? saved[0].declaration?.behavior.childLinks?.filter(link => link.id === linkId) : saved[0].declaration?.behavior.childLinks;
    const after = terminal ? current[0].terminalOutcome : linkId ? current[0].declaration?.behavior.childLinks?.filter(link => link.id === linkId) : current[0].declaration?.behavior.childLinks;
    if (!before || !after || (linkId && (!Array.isArray(before) || before.length !== 1 || !Array.isArray(after) || after.length !== 1)) || diagnostic.locationId !== (linkId ?? nodeId) || JSON.stringify(before) !== JSON.stringify(after)) return null;
    return {nodeId, terminal, label: `${current[0].displayName || current[0].stepCode || nodeId} · ${terminal ? 'Workflow result' : 'Related work'}`};
  }

  private isStepTimerDiagnostic(
    diagnostic: WorkflowDefinitionDiagnostic,
  ): boolean {
    return /^\$\.nodes\[id=[^\]]+\]\.declaration\.behavior\.timers(?:\[|\.|$)/.test(
      diagnostic.path,
    );
  }

  private stepTimerDiagnosticTarget(
    diagnostic: WorkflowDefinitionDiagnostic,
  ): { nodeId: string; label: string } | null {
    const path =
      /^\$\.nodes\[id=([^\]]+)\]\.declaration\.behavior\.timers(?:\[id=([^\]]+)\])?(?:\.(.+))?$/.exec(
        diagnostic.path,
      );
    if (!path) return null;
    const [, nodeId, timerId, suffix] = path;
    const saved =
      this.currentDraft()?.document.nodes.filter(
        (node) => node.id === nodeId,
      ) ?? [];
    const current =
      this.workingDocument()?.nodes.filter((node) => node.id === nodeId) ?? [];
    if (
      saved.length !== 1 ||
      current.length !== 1 ||
      saved[0].typeCode !== 'human_step' ||
      current[0].typeCode !== 'human_step'
    )
      return null;
    const savedTimers = saved[0].declaration?.behavior.timers;
    const currentTimers = current[0].declaration?.behavior.timers;
    if (!Array.isArray(savedTimers) || !Array.isArray(currentTimers))
      return null;
    const label = current[0].displayName || current[0].stepCode || nodeId;
    // Collection diagnostics identify the owner node; a rule diagnostic identifies
    // its timer. Never use the latter as a graph-node identity, even when IDs collide.
    if (!timerId)
      return !suffix &&
        diagnostic.locationId === nodeId &&
        ['node', 'timer'].includes(diagnostic.locationTypeCode) &&
        JSON.stringify(savedTimers) === JSON.stringify(currentTimers)
        ? { nodeId, label }
        : null;
    if (
      diagnostic.locationTypeCode !== 'timer' ||
      diagnostic.locationId !== timerId
    )
      return null;
    const savedRows = savedStepTimerRows(savedTimers),
      currentRows = savedStepTimerRows(currentTimers);
    if (!savedRows || !currentRows) return null;
    const savedMatches = savedRows.filter((row) => row.id === timerId);
    const currentMatches = currentRows.filter((row) => row.id === timerId);
    if (
      savedMatches.length !== 1 ||
      currentMatches.length !== 1 ||
      JSON.stringify(savedMatches[0].raw) !==
        JSON.stringify(currentMatches[0].raw)
    )
      return null;
    return {
      nodeId,
      label: `${label} · ${currentMatches[0].label || 'Timer'}`,
    };
  }

  private canResolveValidationHandoff(
    diagnostic: WorkflowDefinitionDiagnostic,
  ): boolean {
    if (this.isChildWorkDiagnostic(diagnostic)) return this.childWorkDiagnosticTarget(diagnostic) !== null;
    if (this.isStepTimerDiagnostic(diagnostic)) return this.stepTimerDiagnosticTarget(diagnostic) !== null;
    if (diagnostic.locationTypeCode === 'edge' && diagnostic.locationId) {
      return this.mapEdgeSelectionBySourceId(diagnostic.locationId) !== null;
    }

    if (
      diagnostic.locationId &&
      (this.isDecisionDiagnostic(diagnostic) ||
        diagnostic.path.includes('workflowTransitions') ||
        diagnostic.path.includes('allowedTransitions') ||
        diagnostic.locationTypeCode === 'node' ||
        diagnostic.path.startsWith('$.nodes'))
    ) {
      return this.mapNodeSelectionBySourceId(diagnostic.locationId) !== null;
    }

    return true;
  }

  private canResolveSemanticHandoff(
    change: WorkflowDefinitionSemanticChange,
  ): boolean {
    if (change.changeTypeCode === 'removed') {
      return true;
    }

    if (change.elementTypeCode === 'node') {
      return this.mapNodeSelectionBySourceId(change.elementId) !== null;
    }
    if (change.elementTypeCode === 'edge') {
      return this.mapEdgeSelectionBySourceId(change.elementId) !== null;
    }

    const nodeId = semanticChangeNodeId(change);
    if (
      nodeId &&
      (change.elementTypeCode === 'step_transition' ||
        change.elementTypeCode === 'decision' ||
        change.elementTypeCode === 'notification')
    ) {
      return this.mapNodeSelectionBySourceId(nodeId) !== null;
    }

    return true;
  }

  private mapEdgeSelectionBySourceId(
    edgeId: string,
  ): WorkflowReadOnlyGraphSelection | null {
    const document = this.workingDocument();
    if (!document) {
      return null;
    }
    const graph = projectWorkflowDraftGraph(document);
    const matches = graph.edges.filter((edge) => edge.sourceId === edgeId);
    return matches.length === 1
      ? this.graphSelectionForEdge(matches[0], graph.edges)
      : null;
  }

  private mapNodeSelectionBySourceId(
    nodeId: string,
  ): WorkflowReadOnlyGraphSelection | null {
    const document = this.workingDocument();
    if (!document) {
      return null;
    }
    const graph = projectWorkflowDraftGraph(document);
    const matches = graph.nodes.filter(
      (node) => node.sourceIndex !== null && node.sourceId === nodeId,
    );
    return matches.length === 1
      ? this.graphSelectionForNode(matches[0], graph.nodes)
      : null;
  }

  private resolveDecisionScopeRequest(
    request: WorkflowReadOnlyGraphDecisionScopeRequest,
  ): {
    readonly stepNodeId: string | null;
    readonly selection: WorkflowReadOnlyGraphSelection | null;
  } | null {
    const document = this.workingDocument();
    if (!document) {
      return null;
    }
    const graph = projectWorkflowDraftGraph(document);
    if (
      request.ownerSourceNodeId === null ||
      request.ownerSourceNodeIndex === null
    ) {
      return request.ownerSourceNodeId === null &&
        request.ownerSourceNodeIndex === null &&
        (graph.workflowDecisionGates?.length ?? 0) > 0
        ? { stepNodeId: null, selection: null }
        : null;
    }

    const sourceIndex = request.ownerSourceNodeIndex;
    if (!Number.isInteger(sourceIndex) || sourceIndex < 0) {
      return null;
    }
    const stepNode = document.nodes[sourceIndex] ?? null;
    if (
      !stepNode ||
      stepNode.id !== request.ownerSourceNodeId ||
      stepNode.typeCode !== 'human_step'
    ) {
      return null;
    }
    const projectedStep =
      graph.nodes.find(
        (node) =>
          node.sourceId === request.ownerSourceNodeId &&
          node.sourceIndex === sourceIndex,
      ) ?? null;
    if (!projectedStep || (projectedStep.decisionGates?.length ?? 0) === 0) {
      return null;
    }
    const selection = this.graphSelectionForNode(projectedStep, graph.nodes);
    if (!selection?.isAuthorable || selection.sourceIndex !== sourceIndex) {
      return null;
    }
    return {
      stepNodeId: request.ownerSourceNodeId,
      selection,
    };
  }

  private graphSelectionForNode(
    node: WorkflowReadOnlyGraphNode,
    candidates: readonly WorkflowReadOnlyGraphNode[],
  ): WorkflowReadOnlyGraphSelection {
    return {
      kind: 'node',
      rendererId: node.id,
      sourceId: node.sourceId,
      sourceIndex: node.sourceIndex,
      isValid: !node.isInvalid,
      isAuthorable:
        node.sourceIndex !== null &&
        !node.isInvalid &&
        candidates.filter((candidate) => candidate.sourceId === node.sourceId)
          .length === 1,
    };
  }

  private graphSelectionForEdge(
    edge: WorkflowReadOnlyGraphEdge,
    candidates: readonly WorkflowReadOnlyGraphEdge[],
  ): WorkflowReadOnlyGraphSelection {
    const raw =
      edge.sourceIndex === null
        ? null
        : this.workingDocument()?.edges[edge.sourceIndex];
    const hasRepairableIdentity =
      !!raw &&
      typeof raw === 'object' &&
      !Array.isArray(raw) &&
      typeof raw.id === 'string' &&
      !!raw.id.trim() &&
      raw.id === edge.sourceId &&
      typeof raw.fromNodeId === 'string' &&
      typeof raw.toNodeId === 'string' &&
      (raw.outcomeCode == null || typeof raw.outcomeCode === 'string') &&
      (raw.pathKindCode == null || typeof raw.pathKindCode === 'string');
    return {
      kind: 'edge',
      rendererId: edge.id,
      sourceId: edge.sourceId,
      sourceIndex: edge.sourceIndex,
      isValid: !edge.isInvalid,
      isAuthorable:
        hasRepairableIdentity &&
        candidates.filter((candidate) => candidate.sourceId === edge.sourceId)
          .length === 1,
    };
  }

  private focusMapTransitionContext(): void {
    const node = this.selectedMapNode();
    if (node?.typeCode === 'human_step') {
      this.transitionEditor?.focusStepTransitions(node.id);
      return;
    }
    if (!this.mapSelection()) {
      this.transitionEditor?.focusTransitionPath(
        '$.workflowTransitions',
        null,
        'review',
      );
    }
  }

  private focusMapDecisionContext(): void {
    const node = this.selectedMapNode();
    if (node?.typeCode === 'human_step') {
      this.decisionEditor?.focusStepDecisions(node.id);
      return;
    }
    if (!this.mapSelection()) {
      this.decisionEditor?.focusDecisionPath(
        '$.requiredDecisions',
        null,
        'review',
      );
    }
  }

  private confirmDraftChange(request: WorkflowDraftConfirmationRequest): Promise<boolean> {
    // A different target cannot inherit an open confirmation's consent.
    if (this.confirmationInProgress || this.ownerViewState.focusSuspended()) return Promise.resolve(false);
    this.confirmationInProgress = true;
    const previousFocus = this.browserDocument.activeElement;
    const focusContext = () => JSON.stringify([this.currentDraft()?.id, this.authoringMode(),
      this.selectedNodeId(), this.activeWorkbenchView(), this.activeWorkflowSettingsTask(), this.activeTechnicalView(), this.activeWorkView()]);
    const previousFocusContext = focusContext();
    const resumeFocus = this.ownerViewState.suspendFocus();
    const release = (outcome?: unknown) => {
      this.confirmationInProgress = false;
      resumeFocus();
      if (outcome !== true) {
        const barrier = this.processDataFocusBarrier;
        setTimeout(() => {
          const active = this.browserDocument.activeElement;
          if (barrier !== this.processDataFocusBarrier || this.confirmationInProgress || this.ownerViewState.focusSuspended() ||
            previousFocusContext !== focusContext() ||
            (active?.isConnected && active !== this.browserDocument.body && active !== this.browserDocument.documentElement)) return;
          if (previousFocus instanceof HTMLElement && previousFocus.isConnected &&
            !previousFocus.closest('[hidden], [inert]') && !previousFocus.matches(':disabled'))
            previousFocus.focus({ preventScroll: true });
        });
      }
    };
    try {
      const result = this.confirmation.confirm(request);
      // Register release first while retaining the existing caller promise timing.
      void result.then(release, release);
      return result;
    } catch (error) {
      release();
      throw error;
    }
  }

  /** Live adapters shared by scoped navigation and whole-draft snapshot checks. */
  private editorPendingScopes(): readonly WorkflowEditorPendingScope[] {
    const scope = <T extends object>(
      id: string,
      label: string,
      dirty: () => boolean,
      owner: () => T | undefined,
      discard: (editor: T) => void,
      values?: (editor: T) => unknown,
      kind?: WorkflowEditorPendingScope['kind'],
    ): WorkflowEditorPendingScope => {
      const editor = owner();
      return {
        id,
        label,
        kind,
        state: () => dirty() ? 'invalid' : 'clean',
        fingerprint: () => !dirty() ? 'clean' : JSON.stringify([
          editor && this.discardObjectId(editor),
          owner() === editor,
          dirty(),
          editor && values ? values(editor) : null,
        ]),
        discard: () => { if (editor) discard(editor); },
        focus: () => this.focusPendingMapEditor(),
      };
    };
    const page = (kind: 'behavior' | 'dashboard' | 'detail') =>
      this.pageEditors?.find((editor) => kind === 'behavior'
        ? editor.scope !== 'dashboard' && editor.scope !== 'detail'
        : editor.scope === kind);
    const draftId = this.currentDraft()?.id;
    const nodeId = this.selectedMapNode()?.id;
    const ownerScopes = (
      id: string,
      kind: WorkflowPendingEditorKind,
      dirty: () => boolean,
      owner: () => {
        pendingEditorScopes?: (latest?: () => WorkflowDefinitionDocument | null) => readonly WorkflowEditorPendingScope[];
      } | undefined,
      fallback: WorkflowEditorPendingScope,
    ): readonly WorkflowEditorPendingScope[] => {
      const editor = owner();
      if (!editor?.pendingEditorScopes) return [fallback];
      const nodeBound = !['assignment', 'navigation', 'process-data', 'workflow-status'].includes(kind) && id !== 'automation';
      const scopes = () => (owner()?.pendingEditorScopes?.(() => this.workingDocument()) ?? []).map(entry =>
        entry.apply && (id === 'timer' || id === 'process-data' || id === 'status-change' || id === 'child-work' || id === 'navigation'
          || id === 'dashboard' && /:dashboard-(name|element-label)$/.test(entry.id)) ? { ...entry,
          apply: () => {
            const applied = id === 'timer' ? this.settleTimer(this.workingDocument(), () => entry.apply!())
              : id === 'dashboard' ? this.settleDashboardName(this.workingDocument(), () => entry.apply!())
              : id === 'process-data' ? this.settleProcessData(this.workingDocument(), () => entry.apply!())
              : id === 'child-work' ? this.settleChildWork(this.workingDocument(), () => entry.apply!())
                : id === 'navigation' ? this.settleNavigation(this.workingDocument(), () => entry.apply!()) : entry.apply!();
            if (id === 'status-change') this.finalizeBypassDataProgressReceipt();
            return applied;
          },
        } : entry);
      // Keep unmodelled pending owners guarded until their own adapters are available.
      const hasUnmodelledInput = dirty() && scopes().every(entry => entry.state() === 'clean');
      const guardedScopes = () => hasUnmodelledInput ? [fallback, ...scopes()] : scopes();
      return workflowEditorPendingOwnerScopes({
        id, owner, scopes: guardedScopes, aggregatePending: dirty,
        canApply: () => this.canAuthor() && !this.isDraftEditorInteractionBlocked(),
        focus: entry => {
          if (this.currentDraft()?.id !== draftId || (nodeBound && this.selectedMapNode()?.id !== nodeId) || owner() !== editor) {
            this.focusPendingMapEditor();
            return;
          }
          if (entry === fallback) {
            fallback.focus();
            return;
          }
          this.focusPendingEditor(kind, () => {
            if (this.currentDraft()?.id === draftId && (!nodeBound || this.selectedMapNode()?.id === nodeId) && owner() === editor) {
              if (id === 'process-data') this.processDataEditor?.focusPendingChanges(true);
              else entry.focus();
            }
          });
        },
      }).map(entry => ({ ...entry, fingerprint: () => entry.state() === 'clean'
        ? entry.fingerprint() : JSON.stringify([editor && this.discardObjectId(editor), entry.fingerprint()]) }));
    };
    return [
      scope('step', 'step', this.nodeFormDirty, () => this,
        (editor) => editor.resetNodeForm(), (editor) => editor.nodeFormValues),
      ...ownerScopes('path', 'edge', this.edgeFormDirty, () => this.edgeEditor,
        scope('path', 'path', this.edgeFormDirty, () => this.edgeEditor,
          (editor) => editor.discardPendingChanges(), (editor) => editor.edgeFormValues)),
      ...ownerScopes('review-routing', 'review', this.reviewRoutingDirty, () => page('behavior'),
        scope('review-routing', 'Routing, action data and workflow status input', this.reviewRoutingDirty, () => page('behavior'),
          (editor) => editor.resetPendingChanges())),
      ...ownerScopes('status-change', 'transition', this.transitionFormDirty, () => this.transitionEditor,
        scope('status-change', 'status change', this.transitionFormDirty, () => this.transitionEditor,
          (editor) => editor.discardPendingChanges(), (editor) => editor.transitionFormValues)),
      ...ownerScopes('decision', 'decision', this.decisionFormDirty, () => this.decisionEditor,
        scope('decision', 'decision rule', this.decisionFormDirty, () => this.decisionEditor,
          (editor) => editor.discardPendingChanges(), (editor) => editor.decisionFormValues)),
      ...ownerScopes('requirement', 'requirement', this.requirementFormDirty, () => this.requirementsEditor,
        scope('requirement', 'completion requirement', this.requirementFormDirty, () => this.requirementsEditor,
          (editor) => editor.resetPendingChanges(), (editor) => [editor.draft, editor.pendingNoticeReference])),
      ...ownerScopes('timer', 'timer', this.timerFormDirty, () => this.timersEditor,
        scope('timer', 'step timer', this.timerFormDirty, () => this.timersEditor,
          (editor) => editor.discardPendingChanges(), (editor) => editor.draft)),
      ...ownerScopes('child-work', 'child', this.childWorkFormDirty, () => this.childWorkEditor,
        scope('child-work', 'related work or workflow result awaiting Apply', this.childWorkFormDirty, () => this.childWorkEditor,
          (editor) => editor.discardPendingChanges(), (editor) => [editor.draft, editor.outcomeDraft], 'modal-candidate')),
      ...ownerScopes('notification', 'notification', this.notificationFormDirty, () => this.stepNotificationsEditor,
        scope('notification', 'notification recipients', this.notificationFormDirty, () => this.stepNotificationsEditor,
          (editor) => editor.resetPendingChanges(), (editor) => editor.selectedEvent())),
      ...ownerScopes('workflow-assignment', 'assignment', this.assignmentPolicyFormDirty, () => this.assignmentPolicyEditor,
        scope('workflow-assignment', 'workflow assignment policy', this.assignmentPolicyFormDirty, () => this.assignmentPolicyEditor,
          (editor) => editor.discardPendingChanges(), (editor) => editor.formValues)),
      ...ownerScopes('navigation', 'navigation', this.navigationFormDirty, () => this.navigationSettingsEditor,
        scope('navigation', 'workflow navigation', this.navigationFormDirty, () => this.navigationSettingsEditor,
          (editor) => editor.discardPendingChanges(), (editor) => [editor.iconKey, editor.rows])),
      ...ownerScopes('process-data', 'process-data', this.processDataFormDirty, () => this.processDataEditor,
        scope('process-data', 'process data', this.processDataFormDirty, () => this.processDataEditor,
          (editor) => editor.discardPendingChanges(), (editor) => editor.capturePendingDataInputs?.() ?? editor.fields)),
      ...ownerScopes('workflow-status', 'workflow-status', this.workflowStatusFormDirty, () => this.workflowStatusEditor,
        scope('workflow-status', 'workflow status input', this.workflowStatusFormDirty, () => this.workflowStatusEditor,
          editor => editor.discardPendingChanges(), editor => editor.capturePendingStatusInputs())),
      ...ownerScopes('automation', 'automation', this.automationFormDirty, () => this.automationEditor,
        scope('automation', 'automated operations', this.automationFormDirty, () => this.automationEditor,
          (editor) => editor.discardPendingChanges(), (editor) => editor.triggers)),
      ...ownerScopes('automated-step', 'automation', this.automatedStepFormDirty, () => this.automatedStepEditor,
        scope('automated-step', 'automated activity', this.automatedStepFormDirty, () => this.automatedStepEditor,
          (editor) => editor.discardPendingChanges(), (editor) => [editor.automation, editor.outcomes])),
      ...ownerScopes('step-assignment', 'step-assignment', this.stepAssignmentPolicyFormDirty, () => this.stepAssignmentPolicyEditor,
        scope('step-assignment', 'step responsibility', this.stepAssignmentPolicyFormDirty, () => this.stepAssignmentPolicyEditor,
          (editor) => editor.discardPendingChanges(), (editor) => editor.formValues)),
      ...ownerScopes('work', 'work', this.workComponentsDirty, () => this.workComponentsEditor,
        scope('work', 'step action', this.workComponentsDirty, () => this.workComponentsEditor,
          (editor) => editor.resetPendingChanges())),
      ...ownerScopes('dashboard', 'dashboard', this.dashboardEditorDirty, () => page('dashboard'),
        scope('dashboard', 'dashboard label', this.dashboardEditorDirty, () => page('dashboard'),
          (editor) => editor.discardRouteEdits())),
      ...ownerScopes('staff-page', 'staff-page', this.staffPageEditorDirty, () => page('detail'),
        scope('staff-page', 'staff page text', this.staffPageEditorDirty, () => page('detail'),
          (editor) => editor.discardRouteEdits())),
    ];
  }

  private requestMapEditorDeactivation(
    accepted: () => void,
    cancelled: () => void,
    retainedScope?: 'status-change:transition-progress:step-progress',
  ): void {
    if (this.confirmationInProgress || this.ownerViewState.focusSuspended()) {
      cancelled();
      return;
    }
    if (!retainedScope && this.hasUnappliedEditorChanges() && this.flushPendingEditorChanges()) {
      accepted();
      return;
    }

    const scopes = this.editorPendingScopes().filter(scope => scope.id !== retainedScope);
    const pending = new WorkflowEditorPendingState<() => void>();
    const contextKey = () => JSON.stringify({
      draftId: this.currentDraft()?.id,
      revision: this.currentDraft()?.revision,
      nodeId: this.selectedNodeId(),
      stepEditorNodeId: this.stepEditorNodeId(),
      editingRoute: this.editingRouteSelection(),
      selection: this.mapSelection(),
      view: this.activeWorkbenchView(),
      settingsTask: this.activeWorkflowSettingsTask(),
      technicalView: this.activeTechnicalView(),
      workView: this.activeWorkView(),
      pane: this.mapInspectorPane(),
      canAuthor: this.canAuthor(),
      blocked: this.isDraftEditorInteractionBlocked(),
      document: this.workingDocument(),
    });
    const previousFlushing = this.flushingPendingChanges;
    this.flushingPendingChanges = true;
    let result: ReturnType<typeof pending.request>;
    try {
      result = pending.request(accepted, scopes, contextKey);
    } finally {
      this.flushingPendingChanges = previousFlushing;
    }
    if (result.accepted) {
      result.target();
      return;
    }
    const dirtyAreas = result.guard.scopes.map((entry) => entry.label);

    const listedAreas =
      dirtyAreas.length === 1
        ? dirtyAreas[0]
        : `${dirtyAreas.slice(0, -1).join(', ')} and ${dirtyAreas.at(-1)}`;
    void this.confirmDraftChange({
        heading: 'Discard unapplied editor changes?',
        message:
          `The unapplied ${listedAreas} will be discarded. ` +
          'Already-applied draft edits will remain.',
        confirmText: 'Discard editor changes',
        cancelText: 'Keep editing',
      })
      .then((confirmed) => {
        if (this.destroyRef.destroyed) return;
        if (!confirmed) {
          pending.keepEditing();
          cancelled();
          return;
        }
        const continuation = pending.discardAndContinue();
        if (continuation?.accepted) {
          continuation.target();
        } else {
          this.editorMessage.set('The editor input or context changed, or its reset was refused. Review the current input before trying again.');
          pending.keepEditing();
          cancelled();
        }
      });
  }

  private focusPendingMapEditor(): void {
    if (this.workflowStatusFormDirty()) { this.focusPendingEditor('workflow-status'); return; }
    if (this.staffPageEditorDirty()) {
      this.focusPendingEditor('staff-page');
      return;
    }
    if (this.dashboardEditorDirty()) {
      this.focusPendingEditor('dashboard');
      return;
    }
    if (this.workComponentsDirty()) {
      this.focusPendingEditor('work');
      return;
    }
    if (this.automatedStepFormDirty()) {
      this.focusPendingEditor('automation');
      return;
    }
    if (this.automationFormDirty()) {
      this.focusPendingEditor('automation');
      return;
    }
    if (this.childWorkFormDirty()) {
      this.focusPendingEditor('child');
      return;
    }
    if (this.timerFormDirty()) {
      this.focusPendingEditor('timer');
      return;
    }
    if (this.requirementFormDirty()) {
      this.focusPendingEditor('requirement');
      return;
    }
    if (this.notificationFormDirty()) {
      this.focusPendingEditor('notification');
      return;
    }
    if (this.stepAssignmentPolicyFormDirty()) {
      this.focusPendingEditor('step-assignment');
      return;
    }
    if (this.navigationFormDirty()) {
      this.focusPendingEditor('navigation');
      return;
    }
    if (this.processDataFormDirty()) {
      this.focusPendingEditor('process-data');
      return;
    }
    if (this.assignmentPolicyFormDirty()) {
      this.focusPendingEditor('assignment');
      return;
    }
    if (this.activeWorkbenchView() !== 'map') {
      return;
    }

    switch (this.mapInspectorPane()) {
      case 'overview':
        if (this.reviewRoutingDirty()) {
          this.focusPendingEditor('review');
        } else if (this.nodeFormDirty()) {
          this.focusAfterRender(() => this.nodeEditorHeading);
        }
        return;
      case 'path':
        if (this.edgeFormDirty() || this.reviewRoutingDirty()) {
          this.edgeEditor?.focusPendingChanges();
        }
        return;
      case 'transitions':
        if (this.transitionFormDirty()) {
          this.transitionEditor?.focusPendingChanges();
        }
        return;
      case 'decisions':
        if (this.decisionFormDirty()) {
          this.decisionEditor?.focusPendingChanges();
        }
        return;
    }
  }

  private normalizeWorkbenchView(
    view: WorkflowDraftWorkbenchView,
  ): WorkflowDraftWorkbenchView {
    switch (view) {
      case 'capabilities':
        this.revealTechnicalView('modules');
        return 'setup';
      case 'steps':
        this.setInspectorPane('overview');
        return 'map';
      case 'routing':
        this.setInspectorPane(
          this.activeRoutingView() === 'paths' ? 'path' : 'transitions',
        );
        return 'map';
      case 'decisions':
        this.setInspectorPane('decisions');
        return 'map';
      default:
        return view;
    }
  }

  /** Values that a discard would actually remove; clean catalog/focus metadata is excluded. */
  private discardContextKey(scope: 'draft' | 'node'): string {
    const draft = this.currentDraft();
    const node = this.selectedNode();
    const identity = [draft?.id, draft?.draftCode, draft?.revision, draft?.eTag,
      draft?.canonicalContentHash, draft?.lifecycleStatusCode];
    const permission = [this.roles.accessProfile?.()?.actor?.actorId ?? null,
      this.canAuthor(), this.canCreateWorkflowDraft(), this.registryReadsSuspended()];
    const nodeInput = [this.selectedNodeId(), this.isAddingStep(), this.nodeFormDirty(),
      this.nodeFormValues, this.inlineStepNameEditing(), this.inlineStepNameNodeId, this.inlineStepNameOriginal];
    if (scope === 'node') return JSON.stringify({
      scope, identity, permission, nodeInput,
      source: node && [node.id, node.typeCode, node.stepCode, node.displayName, node.displayOrder],
    });
    const recoveryKey = this.recoveryStorageKey ?? (draft ? this.storageKeyForDraft(draft) : null);
    let storedRecovery: string | null = null;
    try {
      storedRecovery = recoveryKey ? sessionStorage.getItem(recoveryKey) : null;
    } catch {
      storedRecovery = 'unavailable';
    }
    return JSON.stringify({
      scope, identity, permission,
      document: this.workingDocument(), baseline: this.baselineDocumentJson,
      documentDirty: this.documentDirty(),
      nodeInput: this.nodeFormDirty() ? nodeInput : null,
      identityInput: draft ? this.identityFormValues : null,
      authoring: [this.authoringMode(), this.authoringFormDirty(),
        this.authoringMode() ? this.authoringFormValues : null, this.selectedDefinitionState()],
      operations: [this.isSaving(), this.isLoadingDraft(), this.isRunningAuthoringAction(), this.lifecycleOperationInProgress()],
      pending: this.editorPendingScopes().filter(entry => entry.state() !== 'clean')
        .map(entry => [entry.id, entry.kind, entry.state(), entry.fingerprint()]),
      recovery: [recoveryKey, storedRecovery, this.recoveryDocument(), this.recoveryBase(),
        this.recoveryChoices(), this.archivedBufferPending(), this.archivedPendingInputs()],
      history: [this.discardObjectId(this.behaviorUndoHistory()), this.discardObjectId(this.automationUndoHistory())],
    });
  }

  private reportStaleDiscard(): void {
    const message = 'The draft, pending input or recovery copy changed while confirmation was open. Review the current work before discarding it.';
    this.editorMessage.set(message);
    if (this.authoringMode()) this.authoringMessage.set(message);
  }

  private discardObjectId(value: object): number {
    let id = this.discardObjectIds.get(value);
    if (id === undefined) {
      id = ++this.nextDiscardObjectId;
      this.discardObjectIds.set(value, id);
    }
    return id;
  }

  private requestDiscardThen(
    action: () => void,
    scope: 'draft' | 'node' = 'draft',
    onCancel?: () => void,
    restoreDraftUrlOnCancel = true,
    entryPointChecked = false,
    stillCurrent?: () => boolean,
  ): void {
    const cancel = () => {
      onCancel?.();
      if (this.authoringMode()) this.focusAuthoringInput();
      else this.focusPendingMapEditor();
      const draft = this.currentDraft();
      if (restoreDraftUrlOnCancel && scope === 'draft' && draft) this.writeDraftIdToUrl(draft.id);
    };
    if ((stillCurrent && !stillCurrent()) || this.confirmationInProgress || this.ownerViewState.focusSuspended()) {
      cancel();
      return;
    }
    if (scope === 'draft' && !entryPointChecked && this.entryPointState.isNavigationBlocked()) {
      const context = this.discardContextKey(scope);
      void Promise.resolve(this.entryPointState.confirmNavigation()).then(allowed => {
        if (this.destroyRef.destroyed) return;
        if (allowed && context === this.discardContextKey(scope))
          this.requestDiscardThen(action, scope, onCancel, restoreDraftUrlOnCancel, true, stillCurrent);
        else {
          if (allowed) this.reportStaleDiscard();
          cancel();
        }
      });
      return;
    }
    if (!this.hasUnsavedChanges()) {
      if (!stillCurrent || stillCurrent()) action(); else cancel();
      return;
    }

    const message =
      scope === 'node'
        ? 'Unapplied changes in the selected step will be lost. Already-applied draft edits will remain.'
        : 'Your local document and any unapplied step, dashboard, staff page, path, status-change, decision, or assignment-policy edits will be lost.';
    const context = this.discardContextKey(scope);
    void this.confirmDraftChange({
        heading: 'Discard unsaved changes?',
        message,
        confirmText: 'Discard changes',
        cancelText: 'Keep editing',
      })
      .then((confirmed) => {
        if (this.destroyRef.destroyed) return;
        if (!confirmed) {
          cancel();
          return;
        }
        if (context !== this.discardContextKey(scope) || (stillCurrent && !stillCurrent())) {
          this.reportStaleDiscard();
          cancel();
          return;
        }

        if (scope === 'draft') {
          this.discardAllLocalChanges();
        } else {
          this.resetNodeForm();
        }
        if (!stillCurrent || stillCurrent()) action(); else cancel();
      });
  }

  private focusAuthoringInput(): void {
    setTimeout(() => {
      const host = this.authoringFormElement?.nativeElement;
      if (!this.authoringMode() || this.ownerViewState.focusSuspended() || !host || host.closest('[hidden], [inert]')) return;
      host.querySelector<HTMLElement>(
        'input:not([type="hidden"]):not([disabled]),textarea:not([disabled]),select:not([disabled]),button:not([disabled])',
      )?.focus();
    });
  }

  private writeDraftIdToUrl(draftId: number | null): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams:
        draftId === null
          ? {
              registryView: 'drafts',
              versionView: null,
              draft: null,
              draftView: null,
              routingView: null,
              reviewView: null,
              draftNode: null,
              draftEdge: null,
              stepView: null,
              behaviorView: null,
              mapPane: null,
              statusRuleFrom: null,
              statusRuleTo: null,
              notificationEvent: null,
              notificationReturn: null,
            }
          : {
              draft: draftId,
              registryView: 'drafts',
              versionView: null,
              ...this.designerLocationQuery(),
            },
      queryParamsHandling: 'merge',
      preserveFragment: true,
      replaceUrl: true,
    });
  }

  private writeWorkbenchViewToUrl(view: WorkflowDraftWorkbenchView): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        draftView: view === 'map' ? null : view,
        routingView: null,
        ...this.designerLocationQuery(),
      },
      queryParamsHandling: 'merge',
      preserveFragment: true,
      replaceUrl: true,
    });
  }

  private designerLocation(): WorkflowDesignerLocation {
    const selection = this.mapSelection();
    const humanStep = this.selectedMapNode()?.typeCode === 'human_step';
    const scope = humanStep ? this.stepWorkspaceScope() : 'behavior';
    const nodeId = selection?.kind === 'node' ? selection.sourceId : null;
    const checksVisible =
      humanStep &&
      scope === 'behavior' &&
      this.behaviorSection() === 'transitions' &&
      this.mapInspectorPane() === 'decisions';
    const restoring = this.pendingStatusRuleRestore;
    const pending =
      checksVisible &&
      restoring &&
      restoring.draftId === this.currentDraft()?.id &&
      restoring.location.nodeId === nodeId
        ? restoring.location
        : null;
    const context = checksVisible
      ? this.decisionEditor?.statusCheckContext()
      : null;
    const transition =
      context?.scopeCode === 'step' &&
      context.stepNodeId === nodeId &&
      context.transitionIndex !== null
        ? this.selectedMapNode()?.allowedTransitions?.[context.transitionIndex]
        : null;
    const notificationsVisible =
      humanStep &&
      scope === 'behavior' &&
      this.behaviorSection() === 'notifications';
    const notificationRestore = this.pendingNotificationRestore;
    const pendingNotification =
      notificationsVisible &&
      notificationRestore &&
      notificationRestore.draftId === this.currentDraft()?.id &&
      notificationRestore.location.nodeId === nodeId
        ? notificationRestore.location
        : null;
    const returning = this.canReturnToNotifications()
      ? this.notificationReturnContext()
      : null;
    return {
      nodeId,
      edgeId: selection?.kind === 'edge' ? selection.sourceId : null,
      scope,
      section: humanStep || this.selectedMapNode()?.typeCode === 'automated_step' ? this.behaviorSection() : 'readiness',
      pane: scope === 'behavior' ? this.mapInspectorPane() : 'overview',
      statusRuleFrom:
        pending?.statusRuleFrom ?? transition?.fromStateCode ?? null,
      statusRuleTo: pending?.statusRuleTo ?? transition?.toStateCode ?? null,
      notificationEvent:
        pendingNotification?.notificationEvent ??
        (notificationsVisible
          ? (this.stepNotificationsEditor?.selectedEvent()?.identity ?? null)
          : (returning?.eventIdentity ?? null)),
      notificationReturn: !!returning,
    };
  }

  designerContextQuery(): Record<string, string | null> {
    return this.designerLocationQuery();
  }

  private designerLocationQuery(
    location = this.designerLocation(),
  ): Record<string, string | null> {
    return {
      draftNode: location.nodeId,
      draftEdge: location.edgeId,
      stepView: location.scope === 'behavior' ? null : location.scope,
      behaviorView: location.section === 'readiness' ? null : location.section,
      mapPane: location.pane === 'overview' ? null : location.pane,
      statusRuleFrom: location.statusRuleFrom,
      statusRuleTo: location.statusRuleTo,
      notificationEvent: location.notificationEvent,
      notificationReturn: location.notificationReturn ? '1' : null,
    };
  }

  private restoreDesignerLocation(location: WorkflowDesignerLocation): void {
    this.clearFocusedMapContext();
    const selection = location.nodeId
      ? this.mapNodeSelectionBySourceId(location.nodeId)
      : location.edgeId
        ? this.mapEdgeSelectionBySourceId(location.edgeId)
        : null;
    this.restoringDesignerLocation = true;
    const restorePair =
      this.currentDraft() &&
      location.nodeId &&
      location.statusRuleFrom &&
      location.statusRuleTo &&
      location.scope === 'behavior' &&
      location.section === 'transitions' &&
      location.pane === 'decisions'
        ? { draftId: this.currentDraft()!.id, location }
        : null;
    this.pendingStatusRuleRestore = restorePair;
    const restoreNotification =
      this.currentDraft() &&
      location.nodeId &&
      location.notificationEvent &&
      location.scope === 'behavior' &&
      location.section === 'notifications'
        ? { draftId: this.currentDraft()!.id, location }
        : null;
    this.pendingNotificationRestore = restoreNotification;
    this.notificationReturnContext.set(
      this.currentDraft() && location.nodeId && location.notificationReturn
        ? {
            draftId: this.currentDraft()!.id,
            nodeId: location.nodeId,
            eventIdentity: location.notificationEvent,
          }
        : null,
    );
    try {
      if (
        selection?.kind !== this.mapSelection()?.kind ||
        selection?.sourceId !== this.mapSelection()?.sourceId
      ) {
        this.acceptMapSelection({
          selection,
          origin: 'picker',
          moveFocusToInspector: false,
        });
      }
      const humanStep = this.selectedMapNode()?.typeCode === 'human_step';
      const automatedStep = this.selectedMapNode()?.typeCode === 'automated_step';
      if ((humanStep || automatedStep) && (location.scope !== 'behavior' || location.section !== 'readiness' || location.pane !== 'overview')) {
        this.stepEditorOpened.set(true);
        this.stepEditorNodeId.set(this.selectedMapNode()!.id);
      }
      this.setInspectorPane(
        selection || (!location.nodeId && !location.edgeId)
          ? location.pane
          : 'overview',
      );
      this.stepWorkspaceScope.set(humanStep ? location.scope : 'behavior');
      this.behaviorSection.set(humanStep || automatedStep ? location.section : 'readiness');
      if (
        humanStep &&
        location.scope === 'behavior' &&
        location.section === 'routes' &&
        location.pane === 'path'
      ) {
        this.selectStepPathContext(this.selectedMapNode()!, false);
      }
      if ((location.nodeId || location.edgeId) && !selection) {
        this.editorMessage.set(
          'The linked step or route is no longer available. Showing the workflow overview.',
        );
        this.writeWorkbenchViewToUrl(this.activeWorkbenchView());
      }
    } finally {
      this.restoringDesignerLocation = false;
    }
    if (restorePair) setTimeout(() => this.restoreStatusRulePair(restorePair));
    if (restoreNotification)
      setTimeout(() => this.restoreNotificationEvent(restoreNotification));
  }

  private restoreNotificationEvent(request: {
    draftId: number;
    location: WorkflowDesignerLocation;
  }): void {
    if (this.pendingNotificationRestore !== request) return;
    if (
      this.currentDraft()?.id !== request.draftId ||
      this.selectedMapNode()?.id !== request.location.nodeId ||
      this.stepWorkspaceScope() !== 'behavior' ||
      this.behaviorSection() !== 'notifications' ||
      this.hasUnappliedEditorChanges() ||
      this.isDraftEditorInteractionBlocked()
    ) {
      this.pendingNotificationRestore = null;
      return;
    }
    if (request.location.notificationEvent)
      this.stepNotificationsEditor?.focusNotificationIdentity(
        request.location.notificationEvent,
      );
    this.pendingNotificationRestore = null;
    this.writeWorkbenchViewToUrl(this.activeWorkbenchView());
  }

  private restoreStatusRulePair(request: {
    draftId: number;
    location: WorkflowDesignerLocation;
  }): void {
    if (this.pendingStatusRuleRestore !== request) return;
    const location = request.location;
    if (
      this.currentDraft()?.id !== request.draftId ||
      this.selectedMapNode()?.id !== location.nodeId ||
      this.stepWorkspaceScope() !== 'behavior' ||
      this.behaviorSection() !== 'transitions' ||
      this.mapInspectorPane() !== 'decisions' ||
      this.hasUnappliedEditorChanges() ||
      this.isDraftEditorInteractionBlocked()
    ) {
      this.pendingStatusRuleRestore = null;
      return;
    }
    const matches = (this.selectedMapNode()?.allowedTransitions ?? [])
      .map((transition, index) => ({ transition, index }))
      .filter(
        ({ transition }) =>
          transition?.fromStateCode === location.statusRuleFrom &&
          transition.toStateCode === location.statusRuleTo,
      );
    if (matches.length === 1) {
      this.decisionEditor?.focusTransitionDecisions({
        scopeCode: 'step',
        stepNodeId: location.nodeId,
        transitionIndex: matches[0].index,
      });
    } else {
      this.decisionEditor?.focusDecisionOverview(location.nodeId);
      this.editorMessage.set(
        "The linked status pair is missing or ambiguous. Showing this step's decision checks; choose a current status rule to inspect its checks.",
      );
    }
    this.pendingStatusRuleRestore = null;
    this.writeWorkbenchViewToUrl(this.activeWorkbenchView());
  }

  private writeReviewViewToUrl(view: WorkflowDraftReviewView): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        draftView: 'review',
        reviewView: view === 'readiness' ? null : view,
        ...this.designerLocationQuery(),
      },
      queryParamsHandling: 'merge',
      preserveFragment: true,
      replaceUrl: true,
    });
  }

  private openRoutingView(view: WorkflowDraftRoutingView): void {
    this.activeWorkbenchView.set('map');
    this.activeRoutingView.set(view);
    this.setInspectorPane(view === 'paths' ? 'path' : 'transitions');
    if (this.currentDraft()) {
      this.writeWorkbenchViewToUrl('map');
    }
  }

  private discardAllLocalChanges(): void {
    if (this.currentDraft()) {
      this.resetDraftToBaseline();
    }
    if (this.authoringMode()) {
      this.closeAuthoringForm();
    }
    this.authoringFormDirty.set(false);
  }

  draftDiscoveryRouteParams(query = this.draftQuery()) {
    return {
        draftSearch: query.search ?? null,
        draftWorkflow: query.workflowCode ?? null,
        draftBaseVersion: query.baseDefinitionVersionId ?? null,
        draftValidation: query.validationStatusCode ?? null,
        draftIncludeArchived: query.includeArchived === 'true' ? 'true' : null,
        draftIncludeHidden: query.includeHidden === 'true' ? 'true' : null,
        draftPage: query.pageNumber === 1 ? null : query.pageNumber,
        draftPageSize:
          query.pageSize === DEFAULT_DRAFT_PAGE_SIZE ? null : query.pageSize,
      };
  }

  private writeDraftQueryToUrl(query: WorkflowDefinitionDraftQuery): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: this.draftDiscoveryRouteParams(query),
      queryParamsHandling: 'merge',
      preserveFragment: true,
      replaceUrl: true,
    });
  }

  private requestRemoveStep(): void {
    const node = this.selectedNode();
    if (!node || !this.selectedStepCanBeRemoved()) {
      return;
    }

    const document = this.workingDocument();
    const pathCount =
      document?.edges.filter(
        (edge) => edge?.fromNodeId === node.id || edge?.toNodeId === node.id,
      ).length ?? 0;
    const stateChangeCount = this.stepTransitions(node).length;
    const decisionRuleCount = this.stepDecisionRuleCount(node);
    const responsibilityCount =
      document?.assignmentPolicies?.filter(
        (policy) => policy?.scopeCode === 'step' && policy.nodeId === node.id,
      ).length ?? 0;
    const draftId = this.currentDraft()?.id;
    const documentSnapshot = document ? serializeDocument(document) : null;
    const impact = [
      `${pathCount} connected map path${pathCount === 1 ? '' : 's'}`,
      `${stateChangeCount} state change${stateChangeCount === 1 ? '' : 's'}`,
      `${decisionRuleCount} decision rule${decisionRuleCount === 1 ? '' : 's'}`,
      `${responsibilityCount} attached responsibility polic${responsibilityCount === 1 ? 'y' : 'ies'}`,
    ].join(', ');
    const stepName = node.displayName || node.stepCode || node.id;
    void this.confirmDraftChange({
        heading: `Remove ${stepName}?`,
        message:
          `This removes the work step and its locally configured behavior: ${impact}. ` +
          'Other work steps and their behavior will not be changed.',
        confirmText: 'Remove step',
        cancelText: 'Keep step',
      })
      .then((confirmed) => {
        if (
          confirmed &&
          this.currentDraft()?.id === draftId &&
          this.selectedNodeId() === node.id &&
          this.selectedStepCanBeRemoved() &&
          !this.isDraftEditorInteractionBlocked() &&
          documentSnapshot === serializeDocument(this.workingDocument()!)
        ) {
          this.removeSelectedStep();
        }
      });
  }

  private syncDraftGridSelection(): void {
    const currentDraftId = this.currentDraft()?.id;
    if (this.draftGridApi?.isDestroyed?.()) this.draftGridApi = undefined;
    if (!this.draftGridApi) {
      return;
    }

    this.draftGridApi.forEachNode((node) => {
      node.setSelected(
        currentDraftId !== undefined && node.data?.id === currentDraftId,
      );
    });
  }

  private onDraftGridKeyboardEvent(
    params: SuppressKeyboardEventParams<WorkflowDefinitionDraftSummary>,
  ): boolean {
    if (!isNonInteractiveSpaceEvent(params.event)) {
      return false;
    }

    params.event.preventDefault();
    if (params.node.data && !params.event.repeat) {
      this.requestOpenDraft(params.node.data);
    }
    return true;
  }

  private setDraftFilterValues(query: WorkflowDefinitionDraftQuery): void {
    const advancedFiltersChanged =
      this.lastHydratedDraftAdvancedFilters === null ||
      this.lastHydratedDraftAdvancedFilters.workflowCode !==
        query.workflowCode ||
      this.lastHydratedDraftAdvancedFilters.pageSize !== query.pageSize ||
      this.lastHydratedDraftAdvancedFilters.includeArchived !==
        query.includeArchived;
    this.draftFilterFormValues = {
      search: query.search ?? '',
      workflowCode: query.workflowCode ?? '',
      validationStatusCode: query.validationStatusCode ?? '',
      pageSize: query.pageSize,
    };
    if (
      advancedFiltersChanged &&
      (query.workflowCode ||
        query.pageSize !== DEFAULT_DRAFT_PAGE_SIZE ||
        query.includeArchived === 'true')
    ) {
      this.advancedDraftFiltersOpen = true;
    }
    this.lastHydratedDraftAdvancedFilters = {
      workflowCode: query.workflowCode,
      pageSize: query.pageSize,
      includeArchived: query.includeArchived,
    };
  }

  private inheritedStepCode(nodeId: string): string | null {
    return (
      this.currentDraft()?.inheritedStepCodes?.find(
        (entry) => entry.nodeId === nodeId,
      )?.stepCode ?? null
    );
  }

  private isCreateScopeShapeValid(): boolean {
    return this.validateScopeShape(
      this.authoringFormValues.scopeTypeCode,
      this.authoringFormValues.scopeKey,
    );
  }

  private isEditorScopeShapeValid(): boolean {
    return this.validateScopeShape(
      this.identityFormValues.scopeTypeCode,
      this.identityFormValues.scopeKey,
    );
  }

  private validateScopeShape(scopeTypeCode: string, scopeKey: string): boolean {
    const hasScopeKey = scopeKey.trim().length > 0;
    if (scopeTypeCode === 'global' && hasScopeKey) {
      this.showOperationError(
        localValidationError(
          'Scope is inconsistent',
          'Global definitions cannot have a scope key.',
        ),
      );
      return false;
    }

    if (scopeTypeCode !== 'global' && !hasScopeKey) {
      this.showOperationError(
        localValidationError(
          'Scope key is required',
          'Jurisdiction, program, and combined scopes require a normalized scope key.',
        ),
      );
      return false;
    }

    return true;
  }

  private buildNewDocument(): WorkflowDefinitionDocument {
    const values = this.authoringFormValues;
    return {
      payloadSchemaVersion: '1.14',
      displayName: values.displayName.trim(),
      description: values.description.trim() || null,
      processData: { fields: [] },
      capabilityReferences: [],
      triggers: [],
      numbering: { mode: 'disabled' },
      workflowCode: values.workflowCode.trim(),
      definitionVersionCode: values.definitionVersionCode.trim(),
      subjectTypeCode: null,
      scopeTypeCode: values.scopeTypeCode,
      scopeKey:
        values.scopeTypeCode === 'global' ? null : values.scopeKey.trim(),
      configurationCode: '',
      configurationVersionCode: '',
      instanceCardinalityCode: values.instanceCardinalityCode,
      nodes: [
        {
          id: 'node.entry',
          typeCode: 'entry',
          displayOrder: 0,
          stepCode: null,
          displayName: 'Start',
          allowedTransitions: null,
          requiredDecisions: null,
        },
        {
          id: 'node.terminal',
          typeCode: 'terminal',
          displayOrder: 100,
          stepCode: null,
          displayName: 'Complete',
          allowedTransitions: null,
          requiredDecisions: null,
        },
      ],
      edges: [
        {
          id: 'edge.entry-terminal',
          fromNodeId: 'node.entry',
          toNodeId: 'node.terminal',
        },
      ],
      workflowTransitions: [],
      requiredDecisions: null,
    };
  }

  private buildAuthoringFormConfig(mode: AuthoringMode): InputConfig[] {
    if (mode === 'clone') {
      return [
        {
          name: 'workflowCode',
          type: EzFormInputType.TEXT,
          label: 'Selected workflow',
          row: 1,
          width: '1x',
          disabled: true,
        },
        {
          name: 'draftCode',
          type: EzFormInputType.TEXT,
          label: 'Draft code',
          row: 1,
          width: '1x',
          validation: [Validators.required, Validators.maxLength(120)],
        },
      ];
    }

    return [
      requiredTextInput('displayName', 'Process name', 1),
      {
        name: 'description',
        type: EzFormInputType.TEXT,
        label: 'Purpose (optional)',
        row: 2,
        width: '2x',
        validation: [Validators.maxLength(2000)],
      },
    ];
  }

  private buildIdentityFormConfig(): InputConfig[] {
    const values = () => this.identityValuesState();
    if (
      ['1.10', '1.11', '1.12', '1.13', '1.14', '1.15', '1.16', '1.17', '1.18', '1.19', '1.20', '1.21', '1.22'].includes(
        values().payloadSchemaVersion,
      )
    ) {
      const fields: InputConfig[] = [
        { ...requiredTextInput('displayName', 'Process name', 1), validation: [Validators.required, workflowProcessNameValidator] },
        {
          name: 'description',
          type: EzFormInputType.TEXTAREA,
          label: 'Purpose (optional)',
          row: 2,
          width: 'fill',
          validation: [Validators.maxLength(2000)],
        },
      ];
      if (this.canEditProcessCardinality(this.workingDocument()))
        fields.push({
          name: 'instanceCardinalityCode',
          type: EzFormInputType.SELECT,
          label: 'Concurrent process runs',
          options: computed(() =>
            this.retainedSetupOptions(
              CARDINALITY_OPTIONS.filter((option) => option.value === 'multiple'),
              values().instanceCardinalityCode,
            ),
          ),
          row: 3,
          width: '1x',
          validation: [Validators.required],
        });
      return fields;
    }
    return [
      {
        ...requiredTextInput('subjectTypeCode', 'Subject', 1),
        type: EzFormInputType.SELECT,
        options: computed(() =>
          this.retainedSetupOptions(
            (this.authoringReferences()?.subjectTypeCodes ?? []).map(
              (code) => ({ value: code, viewValue: this.setupCodeLabel(code) }),
            ),
            values().subjectTypeCode,
          ),
        ),
      },
      {
        name: 'instanceCardinalityCode',
        type: EzFormInputType.SELECT,
        label: 'Open workflows per subject',
        options: computed(() =>
          this.retainedSetupOptions(
            CARDINALITY_OPTIONS,
            values().instanceCardinalityCode,
          ),
        ),
        row: 1,
        width: '1x',
        validation: [Validators.required],
      },
    ];
  }

  canEditProcessCardinality(
    document: WorkflowDefinitionDocument | null,
  ): boolean {
    return (
      document !== null && ['1.17', '1.18', '1.19', '1.20', '1.21', '1.22'].includes(document.payloadSchemaVersion ?? '') &&
      document.subjectTypeCode === null &&
      document.processData != null
    );
  }

  private retainedSetupOptions(
    options: InputSelectOption[],
    value: string,
  ): InputSelectOption[] {
    return value && !options.some((option) => option.value === value)
      ? [
          ...options,
          {
            value,
            viewValue: `${value} (retained; unavailable in current choices)`,
          },
        ]
      : options;
  }

  private buildAvailabilityFormConfig(mode: 'create' | 'edit'): InputConfig[] {
    const values = () =>
      mode === 'create'
        ? this.authoringValuesState()
        : this.identityValuesState();
    return [
      {
        name: 'scopeTypeCode',
        type: EzFormInputType.SELECT,
        label: 'Available in',
        options: computed(() =>
          this.retainedSetupOptions(SCOPE_TYPE_OPTIONS, values().scopeTypeCode),
        ),
        row: 1,
        width: '1x',
        validation: [Validators.required],
      },
      {
        name: 'scopeKey',
        type: EzFormInputType.TEXT,
        label:
          values().scopeTypeCode === 'global'
            ? 'Scope key (leave blank)'
            : 'Scope key (required)',
        row: 1,
        width: '1x',
        show: computed(
          () => values().scopeTypeCode !== 'global' || !!values().scopeKey,
        ),
      },
    ];
  }

  get stepKeyValidationError(): string | null {
    const node = this.selectedNode();
    if (
      !this.nodeFormDirty() ||
      this.isAddingStep() ||
      !node ||
      this.isInheritedStep(node.id)
    )
      return null;
    const key = this.nodeFormValues.stepCode.trim();
    if (!key) return 'Enter a step key.';
    if (key.length > 120)
      return 'Use no more than 120 characters for the step key.';
    if (
      this.workingDocument()?.nodes.some(
        (candidate) =>
          ['human_step', 'automated_step'].includes(candidate.typeCode) &&
          candidate.id !== node.id &&
          candidate.stepCode === key,
      )
    )
      return 'Choose a unique step key.';
    return null;
  }

  private focusInvalidStepKey(): void {
    setTimeout(() => {
      if (this.ownerViewState.focusSuspended()) return;
      if (this.stepTechnicalDetails)
        this.stepTechnicalDetails.nativeElement.open = true;
      this.stepKeyInput?.nativeElement.focus();
    });
  }

  private buildNodeFormConfig(
    isNew: boolean,
    _inheritedStepCode: boolean,
  ): InputConfig[] {
    const disabled = !this.canAuthor();
    const isHumanStep =
      isNew ||
      ['human_step', 'automated_step'].includes(
        this.selectedNode()?.typeCode ?? '',
      );
    if (!isHumanStep) {
      return [];
    }

    if (isNew)
      return [
        {
          name: 'displayName',
          type: EzFormInputType.TEXT,
          label: 'Step name',
          row: 1,
          width: '2x',
          validation: [Validators.required, Validators.maxLength(160)],
          disabled,
        },
      ];

    const config: InputConfig[] = [
      {
        name: 'displayName',
        type: EzFormInputType.TEXT,
        label: 'Step name',
        row: 1,
        width: '2x',
        validation: [Validators.maxLength(160)],
        disabled,
      },
    ];
    return config;
  }

  private emptyAuthoringValues(): DraftAuthoringValues {
    return {
      displayName: '',
      description: '',
      workflowCode: '',
      draftCode: '',
      definitionVersionCode: '',
      subjectTypeCode: '',
      scopeTypeCode: 'global',
      scopeKey: '',
      configurationCode: '',
      configurationVersionCode: '',
      instanceCardinalityCode: 'multiple',
    };
  }

  private emptyIdentityValues(): DraftIdentityValues {
    return {
      displayName: '',
      description: '',
      payloadSchemaVersion: '1.0',
      workflowCode: '',
      definitionVersionCode: '',
      subjectTypeCode: '',
      scopeTypeCode: 'global',
      scopeKey: '',
      configurationCode: '',
      configurationVersionCode: '',
      instanceCardinalityCode: 'multiple',
    };
  }

  private emptyNodeValues(): DraftNodeValues {
    return {
      id: '',
      typeCode: 'human_step',
      displayOrder: 0,
      stepCode: '',
      displayName: '',
    };
  }

  private nodeToFormValues(node: WorkflowDefinitionNode): DraftNodeValues {
    return {
      id: node.id,
      typeCode: node.typeCode,
      displayOrder: node.displayOrder,
      stepCode: node.stepCode ?? '',
      displayName: node.displayName ?? '',
    };
  }

  private showOperationError(
    error: WorkflowRegistryDiagnosticDisplayError,
    failedDraftLoad: FailedDraftLoad | null = null,
  ): void {
    this.draftReadError.set(null);
    this.failedDraftLoad.set(failedDraftLoad);
    this.operationError.set(error);
    this.focusAfterRender(() => this.operationErrorSummary);
  }

  private isDecisionDiagnostic(
    diagnostic: WorkflowDefinitionDiagnostic,
  ): boolean {
    return (
      diagnostic.path.includes('requiredDecisions') ||
      diagnostic.code.startsWith('workflow.definition.decision.') ||
      diagnostic.code.startsWith('workflow.definition.reference.decision_') ||
      diagnostic.code === 'workflow.definition.reference.role_unknown' ||
      diagnostic.code === 'workflow.definition.reference.queue_unknown'
    );
  }

  private isAssignmentPolicyDiagnostic(
    diagnostic: WorkflowDefinitionDiagnostic,
  ): boolean {
    return (
      diagnostic.locationTypeCode === 'assignment_policy' ||
      diagnostic.path.includes('assignmentPolicies') ||
      diagnostic.code.startsWith('workflow.definition.assignment_policy.') ||
      diagnostic.code.startsWith('workflow.definition.reference.assignment_')
    );
  }

  private focusAfterRender(
    target: () => ElementRef<HTMLElement> | undefined,
    canFocus?: () => boolean,
  ): void {
    const barrier = this.processDataFocusBarrier;
    setTimeout(() => {
      if (
        barrier === this.processDataFocusBarrier &&
        !this.ownerViewState.focusSuspended() &&
        (!canFocus || canFocus())
      )
        target()?.nativeElement.focus();
    });
  }
  private focusInvalidFormField(
    form: EzFormComponent | undefined,
    element: () => ElementRef<HTMLElement> | undefined,
    fallback: () => ElementRef<HTMLElement> | undefined,
    fallbackFieldName?: string,
  ): void {
    setTimeout(() => {
      if (this.ownerViewState.focusSuspended()) return;
      const invalidName =
        Object.entries(form?.theFormGroup?.controls ?? {}).find(
          ([, control]) => control.invalid,
        )?.[0] ?? fallbackFieldName;
      const host = element()?.nativeElement;
      const fieldId =
        invalidName &&
        form?.getControlId?.({ name: invalidName } as InputConfig);
      const field = Array.from(
        host?.querySelectorAll<HTMLElement>('[id]') ?? [],
      ).find((candidate) => candidate.id === fieldId);
      const focusable = field?.matches('input,textarea,select,[tabindex]')
        ? field
        : field?.querySelector<HTMLElement>('input,textarea,select,[tabindex]');
      for (
        let details = focusable?.closest('details');
        details;
        details = details.parentElement?.closest('details')
      ) {
        details.open = true;
      }
      (focusable ?? fallback()?.nativeElement)?.focus();
    });
  }

  private ensureFormValid(form: EzFormComponent | undefined): boolean {
    const formGroup = form?.theFormGroup;
    if (!formGroup) {
      return false;
    }
    if (formGroup.valid) {
      return true;
    }

    formGroup.markAllAsTouched();
    this.showOperationError(
      localValidationError(
        'Review required fields',
        'Correct the highlighted fields before continuing.',
      ),
    );
    return false;
  }
}

function workflowProcessNameIssue(value: unknown): 'required' | 'maxlength' | null {
  return typeof value !== 'string' || !value.trim() ? 'required' : value.length > 120 ? 'maxlength' : null;
}

const workflowProcessNameValidator: ValidatorFn = control => {
  const issue = workflowProcessNameIssue(control.value);
  return issue === 'required' ? { required: true } : issue === 'maxlength'
    ? { maxlength: { requiredLength: 120, actualLength: String(control.value).length } } : null;
};

function requiredTextInput(
  name: string,
  label: string,
  row: number,
): InputConfig {
  return {
    name,
    type: EzFormInputType.TEXT,
    label,
    row,
    width: '1x',
    validation: [Validators.required, Validators.maxLength(120)],
  };
}

function normalizeDraftQuery(
  query: WorkflowDefinitionDraftQuery,
): WorkflowDefinitionDraftQuery {
  return {
    ...(query.includeHidden === 'true' ? { includeHidden: 'true' as const } : {}),
    ...(query.includeArchived === 'true'
      ? { includeArchived: 'true' as const }
      : {}),
    search: normalizeOptional(query.search),
    workflowCode: normalizeOptional(query.workflowCode),
    baseDefinitionVersionId: Number.isInteger(query.baseDefinitionVersionId) && Number(query.baseDefinitionVersionId) > 0 ? query.baseDefinitionVersionId : undefined,
    validationStatusCode: normalizeOptional(query.validationStatusCode),
    pageNumber:
      Number.isInteger(query.pageNumber) && query.pageNumber > 0
        ? query.pageNumber
        : 1,
    pageSize: normalizePageSize(query.pageSize),
  };
}

function readDraftQuery(queryParams: ParamMap): WorkflowDefinitionDraftQuery {
  return normalizeDraftQuery({
    search: queryParams.get('draftSearch') ?? undefined,
    workflowCode: queryParams.get('draftWorkflow') ?? undefined,
    baseDefinitionVersionId: readPositiveInteger(queryParams.get('draftBaseVersion')) ?? undefined,
    validationStatusCode: queryParams.get('draftValidation') ?? undefined,
    includeArchived:
      queryParams.get('draftIncludeArchived') === 'true' ? 'true' : undefined,
    includeHidden: queryParams.get('draftIncludeHidden') === 'true' ? 'true' : undefined,
    pageNumber: toPositiveInteger(queryParams.get('draftPage'), 1),
    pageSize: toPositiveInteger(
      queryParams.get('draftPageSize'),
      DEFAULT_DRAFT_PAGE_SIZE,
    ),
  });
}

function readDesignerLocation(queryParams: ParamMap): WorkflowDesignerLocation {
  const scope = queryParams.get('stepView');
  const section = queryParams.get('behaviorView');
  const pane = queryParams.get('mapPane');
  const nodeId = normalizeOptional(queryParams.get('draftNode')) ?? null;
  const from = normalizeOptional(queryParams.get('statusRuleFrom')) ?? null;
  const to = normalizeOptional(queryParams.get('statusRuleTo')) ?? null;
  const pairAvailable =
    !!nodeId &&
    scope !== 'dashboard' &&
    scope !== 'detail' &&
    section === 'transitions' &&
    pane === 'decisions' &&
    !!from &&
    !!to;
  const notificationContext =
    !!nodeId &&
    scope !== 'dashboard' &&
    scope !== 'detail' &&
    (section === 'notifications' ||
      (section === 'transitions' &&
        queryParams.get('notificationReturn') === '1'));
  const notificationEvent =
    normalizeOptional(queryParams.get('notificationEvent')) ?? null;
  return {
    nodeId,
    edgeId: nodeId
      ? null
      : (normalizeOptional(queryParams.get('draftEdge')) ?? null),
    scope: scope === 'dashboard' || scope === 'detail' ? scope : 'behavior',
    section:
      section === 'details' ||
      section === 'routes' ||
      section === 'transitions' ||
      section === 'timers' || section === 'children' ||
      section === 'notifications'
        ? section
        : 'readiness',
    pane:
      pane === 'path' || pane === 'transitions' || pane === 'decisions'
        ? pane
        : 'overview',
    statusRuleFrom: pairAvailable ? from : null,
    statusRuleTo: pairAvailable ? to : null,
    notificationEvent:
      notificationContext &&
      notificationEvent &&
      notificationEvent.length <= 4096
        ? notificationEvent
        : null,
    notificationReturn:
      notificationContext &&
      section === 'transitions' &&
      queryParams.get('notificationReturn') === '1',
  };
}

function readWorkbenchView(value: string | null): WorkflowDraftWorkbenchView {
  if (value === 'decision-inventory') return value;
  if (value === 'setup' || value === 'capabilities' || value === 'automation')
    return 'setup';
  return WORKBENCH_NAVIGATION.some((item) => item.id === value)
    ? (value as WorkflowDraftWorkbenchView)
    : 'map';
}

function readRoutingView(value: string | null): WorkflowDraftRoutingView {
  return value === 'stateChanges' ? value : 'paths';
}

function readLegacyMapInspectorPane(
  view: string | null,
  routingView: WorkflowDraftRoutingView,
): WorkflowMapInspectorPane | null {
  switch (view) {
    case 'steps':
      return 'overview';
    case 'routing':
      return routingView === 'paths' ? 'path' : 'transitions';
    case 'decisions':
      return 'decisions';
    default:
      return null;
  }
}

function readReviewView(value: string | null): WorkflowDraftReviewView {
  return value === 'changes' ? value : 'readiness';
}

function semanticChangeNodeId(
  change: WorkflowDefinitionSemanticChange,
): string | null {
  const pathMatch = /^\$\.nodes\[id=([^\]]+)\]/.exec(change.path);
  return pathMatch?.[1] ?? null;
}

function draftQueriesEqual(
  left: WorkflowDefinitionDraftQuery,
  right: WorkflowDefinitionDraftQuery,
): boolean {
  return (
    left.includeArchived === right.includeArchived &&
    left.includeHidden === right.includeHidden &&
    left.search === right.search &&
    left.workflowCode === right.workflowCode &&
    left.baseDefinitionVersionId === right.baseDefinitionVersionId &&
    left.validationStatusCode === right.validationStatusCode &&
    left.pageNumber === right.pageNumber &&
    left.pageSize === right.pageSize
  );
}

function normalizePageSize(value: number): number {
  return [10, 25, 50].includes(Number(value))
    ? Number(value)
    : DEFAULT_DRAFT_PAGE_SIZE;
}

function toPositiveInteger(value: unknown, fallback: number): number {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}

function normalizeOptional(
  value: string | null | undefined,
): string | undefined {
  const normalized = value?.trim();
  return normalized ? normalized : undefined;
}

function readPositiveInteger(value: string | null): number | null {
  if (!value || !/^\d+$/.test(value)) {
    return null;
  }

  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

function routeResolvesToExternalRegistryContext(
  queryParams: ParamMap,
): boolean {
  const requestedView = queryParams.get('registryView')?.trim().toLowerCase();
  if (requestedView === 'drafts') {
    return false;
  }
  if (requestedView === 'versions') {
    return true;
  }

  const hasImmutableSelection = Boolean(
    normalizeOptional(queryParams.get('workflow')) &&
    normalizeOptional(queryParams.get('version')),
  );
  if (requestedView === 'version' && hasImmutableSelection) {
    return true;
  }

  // Mirror the parent shell's legacy deep-link precedence: a draft ID owns
  // the route; otherwise a complete immutable selection opens Version and a
  // bare route opens Versions.
  return readPositiveInteger(queryParams.get('draft')) === null;
}

function serializeDocument(document: WorkflowDefinitionDocument): string {
  return JSON.stringify(document);
}

function cloneDocument(
  document: WorkflowDefinitionDocument,
): WorkflowDefinitionDocument {
  return JSON.parse(JSON.stringify(document)) as WorkflowDefinitionDocument;
}

function formatDateTime(value: unknown): string {
  if (typeof value !== 'string' || value.trim().length === 0) {
    return '';
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.valueOf()) ? value : parsed.toLocaleString();
}

function formatDate(value: unknown): string {
  if (typeof value !== 'string' || value.trim().length === 0) {
    return 'Not updated';
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.valueOf())
    ? value
    : parsed.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
}

function draftIdentitySearchValue(
  draft: WorkflowDefinitionDraftSummary | null | undefined,
): string {
  return draft
    ? `${draft.displayName ?? ''} ${draft.draftCode} ${draft.workflowCode}`
    : '';
}

function renderDraftIdentity(
  draft: WorkflowDefinitionDraftSummary | null | undefined,
): string {
  if (!draft) {
    return '';
  }

  return (
    '<div class="workflow-draft-identity">' + (draft.isFamilyHidden === true ? '<span class="workflow-draft-cell__secondary">Hidden family</span>' : '') +
    `<span class="workflow-draft-cell__primary">${escapeHtml(draft.draftCode)}</span>` +
    `<span class="workflow-draft-cell__secondary">${escapeHtml(draft.lifecycleStatusCode === 'archived' ? `Archived · Revision ${draft.revision} · read only` : `${draft.displayName || draft.workflowCode} · Revision ${draft.revision}`)}</span>` +
    (draft.baseDefinitionVersionCode ? `<span class="workflow-draft-cell__secondary">Based on ${escapeHtml(draft.baseDefinitionVersionCode)}</span>` : '') + '</div>'
  );
}

function renderDraftValidationStatus(
  draft: WorkflowDefinitionDraftSummary | null | undefined,
): string {
  if (!draft) {
    return '';
  }

  const status = draftValidationStatus(draft);
  return (
    `<span class="workflow-draft-status workflow-draft-status--${status.tone}">${escapeHtml(status.title)}</span>` +
    `<span class="workflow-draft-cell__secondary">${escapeHtml(status.detail)}</span>`
  );
}

function draftValidationStatus(draft: WorkflowDefinitionDraftSummary): {
  title: string;
  detail: string;
  tone: 'ready' | 'attention' | 'unchecked';
  searchValue: string;
} {
  if (draft.lifecycleStatusCode === 'archived')
    return {
      title: 'Archived · read only',
      detail: draft.publishedDefinitionVersionCode
        ? `Published as version ${draft.publishedDefinitionVersionCode}`
        : 'Published source retained',
      tone: 'unchecked',
      searchValue: 'Archived published read only',
    };
  switch (draft.validationStatusCode.trim().toLocaleLowerCase()) {
    case 'valid':
      return {
        title: 'Ready for review',
        detail: 'Validation passed',
        tone: 'ready',
        searchValue: 'Ready for review Validation passed',
      };
    case 'invalid':
      return {
        title: 'Needs attention',
        detail: 'Review validation issues',
        tone: 'attention',
        searchValue: 'Needs attention Review validation issues',
      };
    default:
      return {
        title: 'Not checked yet',
        detail: 'Validation has not run',
        tone: 'unchecked',
        searchValue: 'Not checked yet Validation has not run',
      };
  }
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function isNonInteractiveSpaceEvent(event: KeyboardEvent): boolean {
  if (event.key !== ' ' && event.code !== 'Space') {
    return false;
  }

  return !(
    event.target instanceof Element &&
    event.target.closest(
      "button, a, input, select, textarea, [role='button'], [role='link']",
    )
  );
}

function localValidationError(
  title: string,
  message: string,
): WorkflowRegistryDiagnosticDisplayError {
  return {
    title,
    messages: [message],
    statusCode: 400,
  };
}

function isReadAccessDenied(
  error: WorkflowRegistryDiagnosticDisplayError | null,
): boolean {
  return error?.statusCode === 401 || error?.statusCode === 403;
}
